import { useState, useEffect, useRef } from "react";
import {
  RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
  LineChart, Line, CartesianGrid,
} from "recharts";
import {
  Flame, GitBranch, Microscope, BarChart3, Star, Scale, Settings,
  LineChart as LineChartIcon, Search, RefreshCw, Rocket, X, Bot, Filter,
  ArrowUpDown, ArrowUp, ArrowDown, Radio, ShieldCheck, User, Building2,
  Calendar, Eye, AlertTriangle, CircleCheck, RotateCw, Globe, Code2, Users,
  Share2, Castle, MessageSquare, Paperclip, Award, FileText, Brain, Clock,
  Droplet, DollarSign, Link2, Briefcase, TrendingUp, TrendingDown, PartyPopper, Newspaper,
  ChevronDown, ChevronRight, ChevronLeft, Handshake, Target, Bell, Trophy, HelpCircle, Camera, MapPin,
  Lock, Inbox, Database, KeyRound, Download, Upload, ExternalLink, MessageSquarePlus,
} from "lucide-react";
import html2canvas from "html2canvas";
import { useAuth, describeAuthError } from './hooks/useAuth';
import { useWatchlist } from './hooks/useWatchlist';
import { useScoringWeights } from './hooks/useScoringWeights';
import { useNotes } from './hooks/useNotes';
import { getWebsiteTraffic, searchLinkedInPerson } from './services/monid';
import { AuthModal } from './components/AuthModal';
import { FeedbackModal } from './components/FeedbackModal';
import { submitUserFeedback } from './services/feedback';
import { EarlyBirdTab } from './components/EarlyBirdTab';
import { parseEarlyBirdShareId } from './utils/earlyBirdShareHash';
import { ADMIN_UID } from './constants';
import { rdSkill, fetchPipeline, fetchTrending } from './services/rootdata';
import { localizeRootDataTag, localizeRootDataTags } from './services/rootdataTagLabels';
import { cgGet, cgFindId } from './services/coingecko';
import { fetchDefiLlama } from './services/defillama';
import { fetchGithubSignal } from './services/github';
import { fetchDuneTokenInfo, parseDuneUrl, fetchDuneAnalytics, findOrPrepareDuneDashboard } from './services/dune';
import { fetchSocialSignals } from './services/social';
import { fetchTechIntel, fetchWebIntel, scoreProject } from './services/ai';
import { fetchFullProject } from './services/fetchFullProject';
import { computeVerdict } from './services/verdict';
import { downloadComplianceMarkdown } from './services/exportComplianceMarkdown';
import { fetchSignalsForHandles, fetchRecentSignals } from './services/investorSignals';
import { validateTokenUnlock } from './services/validateTokenUnlock';
import { fetchTalentRadar } from './services/talentRadar';
import { resolveTicker, fetchInsiderActivity } from './services/insiderActivity';
import { resolveSurfProjectName } from './services/surfNameResolver';
import { ValuationCard, getValuationEvidence } from './data-views/react/ValuationCard';
import { buildHtml as buildValuationHtml } from './data-views/html/valuationHtml';
import { UnlockCard, getUnlockEvidence } from './data-views/react/UnlockCard';
import { buildHtml as buildUnlockHtml } from './data-views/html/unlockHtml';
import { ProtocolFundamentalsCard, getProtocolEvidence } from './data-views/react/ProtocolFundamentalsCard';
import { MarketVolumeCard } from './data-views/react/MarketVolumeCard';
import { buildHtml as buildProtocolHtml } from './data-views/html/protocolFundamentalsHtml';
import { HoldersCard, getHoldersEvidence } from './data-views/react/HoldersCard';
import { buildHtml as buildHoldersHtml } from './data-views/html/holdersHtml';
import { getSocialEvidence } from './data-views/react/SocialMomentumCard';
import { buildHtml as buildNewsSignalsHtml } from './data-views/html/newsSignalsHtml';
import { VerdictCard } from './data-views/react/VerdictCard';
import { buildHtml as buildVerdictHtml } from './data-views/html/verdictHtml';
import { FirstDocCell } from './data-views/react/FirstDocCell';
import { loadCache as loadFirstDocCache, saveCacheBatch as saveFirstDocCacheBatch } from './data-views/firstDocCache';
import { useFirstDocs } from './data-views/useFirstDocs';
import { classifyKolAccount } from './services/classifyKolAccount';
import { loadLayer2Cache, saveLayer2Cache, fetchLayer2Classifications, mergeWithLayer2 } from './services/kolLayer2';
import { loadLayer3Cache, saveLayer3Cache, fetchLayer3Classifications, mergeWithLayer3 } from './services/kolLayer3';
import { fetchFirstSnapshot, fetchFirstDocSnapshot } from './services/wayback';
import { getCached, writeCache, appendValidation, getScoreHistory, submitFeedback, getResearchNotes, cacheIsDisplayable, getAnalysisHeatMap } from './services/projectCache';
import { synthesizeComplianceRisk } from './services/synthesizeComplianceRisk';
import { factsFromProject } from './services/synthesizeComplianceRiskCore';
import {
  HOLD_YEARS, DEFAULT_WEIGHTS, STAGE_WEIGHTS,
  detectStage, buildDims, calcComposite, effectiveComposite, rating, fmt,
  IRR_SCENARIOS, calcIRR, calcNarrativeChainQuadrant, alphaScore,
} from './utils/scoring';
import { track } from './utils/track';
import { LangProvider, useLang, useT } from './i18n';
import AdminDashboard from './components/AdminDashboard';

// ══════════════════════════════════════════════════════
//  CONFIG — RootData Skill API (via CRA proxy)
// ══════════════════════════════════════════════════════
// IS_DEV and RD_BASE are still used locally in App.jsx for proxyLogo
const IS_DEV = process.env.NODE_ENV === 'development';
// REACT_APP_PROXY 为空时表示前端和 API 在同一个域（Railway 全栈部署），使用相对路径
const RD_BASE = IS_DEV ? "" : (process.env.REACT_APP_PROXY || "");

function proxyLogo(url) {
  if (!url) return "";
  if (!url.includes("rootdata.com") || IS_DEV) return url;
  return `${RD_BASE}/img-proxy?url=${encodeURIComponent(url)}`;
}

// 悬浮进度组件用：把 AnalyzerPage 里的阶段性状态文案粗略映射成百分比。
// 这个管线没有真正的字节级/时间级进度，所以刻意做成"按已知阶段跳档"而不是
// 编一个假的连续数字——匹配不到的文案保持上一次的进度，不倒退。
function estimateAnalyzeProgressPct(text, prevPct) {
  if (!text) return prevPct || 0;
  if (/完成/.test(text)) return 100;
  if (/补充实时|补充链上|链上数据|情报|Dune|artemis/i.test(text)) return 80;
  if (/AI 评分|评分中|scoring/i.test(text)) return 55;
  if (/缓存|命中|多源交叉|识别中|拉取|提取文档|Extracting/i.test(text)) return 25;
  return prevPct || 15;
}

// 数据时效性标注：project_cache.scored_at → 相对时间文案。
// 超过 24 小时未更新时标橙，让"数据新不新鲜"本身也是一眼可扫的信号。
function freshnessLabel(scoredAtIso, lang) {
  if (!scoredAtIso) return null;
  const diffMs = Date.now() - new Date(scoredAtIso).getTime();
  if (!Number.isFinite(diffMs) || diffMs < 0) return null;
  const hours = diffMs / 3600000;
  const stale = hours >= 24;
  let text;
  if (lang === 'en') {
    text = hours < 1 ? '<1h ago' : hours < 24 ? `${Math.round(hours)}h ago` : `${Math.round(hours / 24)}d ago`;
  } else {
    text = hours < 1 ? '不到1小时前' : hours < 24 ? `${Math.round(hours)}小时前更新` : `${Math.round(hours / 24)}天前更新`;
  }
  return { text, stale };
}

// AI 偶发把 string[] 字段返成 [{risk,severity},...] 对象数组，
// 旧 Supabase 缓存里也存了这种坏 shape。
// 渲染前统一兜底转字符串，避免 React error #31 把详情页打成空白。
function safeText(x) {
  if (x == null) return "";
  if (typeof x === "string") return x;
  if (typeof x === "number" || typeof x === "boolean") return String(x);
  if (typeof x === "object") {
    const txt = x.risk || x.strength || x.catalyst || x.text || x.description || x.title || x.label || x.name || "";
    const sev = x.severity || x.level || x.impact;
    if (txt) return sev ? `[${String(sev).toUpperCase()}] ${txt}` : String(txt);
    try { return JSON.stringify(x); } catch { return "[object]"; }
  }
  return String(x);
}

// Proxy Twitter CDN images through Railway to bypass hotlinking restrictions
function proxyImg(url) {
  if (!url) return url;
  if (!url.startsWith('https://pbs.twimg.com/')) return url;
  return `${RD_BASE}/api/proxy-image?url=${encodeURIComponent(url)}`;
}

// Translate common Chinese terms in AI-generated content for English mode
const ZH_EN_MAP = [
  [/估算/g, "est."], [/私有/g, "Private"], [/市值/g, "mkt cap"], [/已上线/g, "Live"],
  [/未上市/g, "Pre-TGE"], [/未定/g, "TBD"], [/未披露/g, "Undisclosed"], [/上市公司/g, "Public co."],
  [/成熟期/g, "Mature"], [/增长期/g, "Growth"], [/早期/g, "Early"], [/产品阶段/g, "Product Stage"],
  [/代币未发行/g, "No token issued"], [/融资阶段未明确/g, "Funding stage unclear"],
  [/无公开GitHub仓库/g, "No public GitHub repos"], [/技术透明度低/g, "Low tech transparency"],
  [/无TVL/g, "No TVL"], [/交易量数据记录/g, "volume data"], [/无FDV/g, "No FDV"], [/市值数据/g, "market cap data"],
  [/建议仓位/g, "Position size"], [/入场逻辑/g, "Entry thesis"],
  [/成立/g, "Founded "], [/全球最大/g, "World's largest "], [/合规/g, "compliant "],
  [/之一/g, ""], [/赛道/g, "track"], [/三重定位/g, "triple positioning"],
  [/经济自由使命/g, "economic freedom mission"], [/机构采用/g, "institutional adoption"],
  [/叙事高度契合/g, "narrative highly aligned with"], [/当前监管周期/g, "current regulatory cycle"],
  [/按25%稀释推算锚点约/g, "anchor est. at 25% dilution ~"], [/估值未披露，按/g, "Valuation undisclosed, "],
  [/领投/g, "led"], [/融资/g, "raised"], [/无数据/g, "No data"],
  [/产品已落地/g, "Product launched"], [/白皮书阶段/g, "Whitepaper stage"],
  [/测试网/g, "Testnet"], [/主网上线/g, "Mainnet live"], [/企业级产品/g, "Enterprise product"],
  [/公司股权，无TGE计划/g, "Equity company, no TGE planned"],
  [/纳斯达克上市公司/g, "NASDAQ-listed"], [/多轮融资，累计/g, "multi-round, total "],
  [/多轮融资/g, "multi-round"], [/累计/g, "total "], [/万美元/g, "0K USD"],
  [/美元/g, "USD"], [/已有落地产品/g, "product live"], [/最新轮/g, "latest round "],
  [/完成/g, " raised "], [/持牌运营/g, "licensed"], [/企业级产品/g, "enterprise product"],
];
function enify(text) {
  if (!text || typeof text !== 'string') return text;
  let r = text;
  for (const [re, rep] of ZH_EN_MAP) r = r.replace(re, rep);
  return r;
}

const STORE_KEY = "alpha_os_v2";
const SIDEBAR_COLLAPSED_KEY = "alpha_os_sidebar_collapsed";
const DEAL_STATUS_KEY = "alpha_os_deal_status";
// NOTES_KEY migrated to useNotes hook (alpha_os_notes_v2)
const HISTORY_KEY = "alpha_os_history";
// localStorage 配额有限（一般 5-10MB/域名）；完整历史已在 Supabase project_cache 里，
// 本地只保留近期分析结果做快速访问，避免无限累积撑爆配额导致其他 setItem（如 Supabase
// auth token 写入）失败，表现为"登录报 QuotaExceededError"（2026-07-07 报告的真实 bug）。
const MAX_STORED_PROJECTS = 50;

// 静态 DIMS（默认权重）
const DIMS = buildDims(DEFAULT_WEIGHTS);

// ══════════════════════════════════════════════════════
//  STYLES
// ══════════════════════════════════════════════════════
// 风格 E — 冷灰极简仪表盘风：单一深色强调，语义色只用于分数/涨跌等真实数据编码
const C = {
  bg: "#fafafa", surface: "#ffffff", border: "#e4e4e7",
  text: "#18181b", muted: "#71717a", faint: "#f4f4f5",
  accent: "#18181b", accentSoft: "#f4f4f5",
};
const S = {
  page: { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'Helvetica Neue','PingFang SC',sans-serif", fontSize: 13 },
  sidebar: { width: 200, flexShrink: 0, background: C.surface, borderRight: `1px solid ${C.border}`, padding: "16px 12px", display: "flex", flexDirection: "column", gap: 4, position: "sticky", top: 0, height: "100vh", overflowY: "auto" },
  main: { flex: 1, padding: 20, overflowY: "auto", minWidth: 0 },
  panel: { width: 340, flexShrink: 0, background: C.surface, borderLeft: `1px solid ${C.border}`, overflowY: "auto", display: "flex", flexDirection: "column" },
  card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 14 },
  input: { padding: "7px 10px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 12, color: C.text, outline: "none", width: "100%", boxSizing: "border-box", fontFamily: "inherit" },
  label: { fontSize: 10, fontWeight: 700, color: C.muted, letterSpacing: 0.8, marginBottom: 4, display: "block", textTransform: "uppercase" },
  tag: { display: "inline-block", padding: "1px 6px", background: C.faint, border: `1px solid ${C.border}`, borderRadius: 4, fontSize: 10, color: "#3f3f46" },
  navBtn: (active) => ({ padding: "8px 10px", background: active ? C.accent : "transparent", border: "none", borderRadius: 6, color: active ? "#ffffff" : C.muted, fontSize: 12, fontWeight: active ? 600 : 400, cursor: "pointer", textAlign: "left", width: "100%", display: "flex", alignItems: "center", gap: 8 }),
  btn: (v = "default") => ({ padding: "6px 12px", background: v === "primary" ? C.text : v === "danger" ? "#fef2f2" : C.surface, border: `1px solid ${v === "primary" ? C.text : v === "danger" ? "#fca5a5" : C.border}`, borderRadius: 6, color: v === "primary" ? "#fff" : v === "danger" ? "#dc2626" : C.text, fontSize: 12, fontWeight: 500, cursor: "pointer" }),
  tabBtn: (active) => ({ flex: 1, padding: "8px 4px", background: "none", border: "none", borderBottom: `2px solid ${active ? C.text : "transparent"}`, color: active ? C.text : C.muted, fontSize: 11, fontWeight: active ? 600 : 400, cursor: "pointer" }),
  rBadge: (s) => { const r = rating(s); return { display: "inline-flex", alignItems: "center", padding: "1px 6px", background: r.bg, color: r.fg, border: `1px solid ${r.border}`, borderRadius: 4, fontSize: 11, fontWeight: 700 }; },
};

// ══════════════════════════════════════════════════════
//  文档来源徽章 — 用于「首次文档」列显示数据来源
// ══════════════════════════════════════════════════════
// DOC_SOURCE_META + DocSourceBadge 已迁移到 src/data-views/react/FirstDocCell.jsx
// 在文件顶部 import 使用

// ══════════════════════════════════════════════════════
//  MOBILE DETECTION
// ══════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════
//  Surf API helpers
// ══════════════════════════════════════════════════════

/** 项目名 → Surf q 参数（小写首词，去除特殊字符） */
function toSurfQ(name) {
  return (name || '').toLowerCase().split(/[\s_()]+/)[0].replace(/[^a-z0-9-]/g, '');
}

function surfFetch(path, params = {}) {
  const qs = new URLSearchParams({ path, ...params }).toString();
  return fetch(`/api/surf?${qs}`)
    .then(r => r.json())
    .then(d => { if (d?.error) throw new Error(d.error.message || 'Surf error'); return d; });
}

/** Mindshare 30日时序 hook */
function useSurfMindshare(projectName) {
  const [state, setState] = useState({ data: null, loading: true });
  useEffect(() => {
    if (!projectName) { setState({ data: null, loading: false }); return; }
    let cancelled = false;
    // 走 resolver（EDGE Markets 教训：直接首词 "edge" 命中 edgeX）
    resolveSurfProjectName(projectName)
      .then(q => {
        if (cancelled) return null;
        if (!q) { setState({ data: null, loading: false }); return null; }
        return surfFetch('/social/mindshare', { q, interval: '1d' });
      })
      .then(res => { if (!cancelled && res) setState({ data: res.data || [], loading: false }); })
      .catch(() => { if (!cancelled) setState({ data: null, loading: false }); });
    return () => { cancelled = true; };
  }, [projectName]);
  return state;
}

function useSurfSmartFollowers(projectName) {
  const [state, setState] = useState({ delta: null, latest: null, loading: true });
  useEffect(() => {
    if (!projectName) { setState({ delta: null, latest: null, loading: false }); return; }
    let cancelled = false;
    resolveSurfProjectName(projectName)
      .then(q => {
        if (cancelled) return null;
        if (!q) { setState({ delta: null, latest: null, loading: false }); return null; }
        return surfFetch('/social/smart-followers-history', { q, interval: '1d' });
      })
      .then(res => {
        if (cancelled || !res) return;
        const data = res.data || [];
        if (data.length < 2) { setState({ delta: null, latest: null, loading: false }); return; }
        const latest = data[data.length - 1].value;
        const prev7 = data[Math.max(0, data.length - 8)].value;
        const delta = prev7 > 0 ? Number(((latest - prev7) / prev7 * 100).toFixed(1)) : null;
        setState({ delta, latest, loading: false });
      })
      .catch(() => { if (!cancelled) setState({ delta: null, latest: null, loading: false }); });
    return () => { cancelled = true; };
  }, [projectName]);
  return state;
}

function useSurfTokenHolders(contract, chain) {
  const [state, setState] = useState({ data: null, loading: true });
  useEffect(() => {
    if (!contract) { setState({ data: null, loading: false }); return; }
    let cancelled = false;
    surfFetch('/token/holders', { contract, chain: chain || 'ethereum', limit: 10 })
      .then(res => { if (!cancelled) setState({ data: res.data || [], loading: false }); })
      .catch(() => { if (!cancelled) setState({ data: null, loading: false }); });
    return () => { cancelled = true; };
  }, [contract, chain]);
  return state;
}

/** 真实网站流量（Monid → Semrush，取代下面那张卡片原本纯 AI 猜的估算）*/
function useMonidTraffic(website) {
  const [state, setState] = useState({ traffic: null, loading: true });
  useEffect(() => {
    if (!website) { setState({ traffic: null, loading: false }); return; }
    let cancelled = false;
    getWebsiteTraffic(website)
      .then(traffic => { if (!cancelled) setState({ traffic, loading: false }); })
      .catch(() => { if (!cancelled) setState({ traffic: null, loading: false }); });
    return () => { cancelled = true; };
  }, [website]);
  return state;
}

function SmartFollowersBadge({ name }) {
  const sf = useSurfSmartFollowers(name);
  if (sf.loading) return <span style={{ color: "#475569", fontSize: 10 }}>…</span>;
  if (sf.delta === null) return <span style={{ color: "#475569", fontSize: 10 }}>—</span>;
  const isUp = sf.delta >= 0;
  const fmtN = v => v >= 1000 ? `${(v/1000).toFixed(0)}K` : String(Math.round(v));
  return (
    <span style={{ fontSize: 10, fontWeight: 700, color: isUp ? "#10b981" : "#ef4444" }}>
      {isUp ? '↑' : '↓'}{Math.abs(sf.delta)}% {sf.latest ? `(${fmtN(sf.latest)})` : ''}
    </span>
  );
}

/** 最新新闻 hook（trending + recent 各5条） */
function useSurfNews(projectName) {
  const [state, setState] = useState({ trending: [], recent: [], loading: true });
  useEffect(() => {
    if (!projectName) { setState({ trending: [], recent: [], loading: false }); return; }
    let cancelled = false;
    // 走 resolver 拿真正命中 Surf 索引的 variant（EDGE Markets 教训：
    // 直接取首词 "edge" 命中 edgeX 污染。resolver 内置停用词 + ≥6 字符门槛）
    resolveSurfProjectName(projectName)
      .then(project => {
        if (cancelled) return null;
        if (!project) { setState({ trending: [], recent: [], loading: false }); return null; }
        return Promise.all([
          surfFetch('/news/feed', { project, limit: 5, sort_by: 'trending' }),
          surfFetch('/news/feed', { project, limit: 5, sort_by: 'recency' }),
        ]);
      })
      .then(results => {
        if (!results || cancelled) return;
        const [tr, rc] = results;
        setState({ trending: tr.data || [], recent: rc.data || [], loading: false });
      })
      .catch(() => { if (!cancelled) setState({ trending: [], recent: [], loading: false }); });
    return () => { cancelled = true; };
  }, [projectName]);
  return state;
}

/** 迷你折线图（用于矩阵内叙事趋势） */
function MiniSparkline({ data, color = "#3b82f6", height = 28 }) {
  if (!data?.length) return null;
  const vals = data.map(d => d.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const range = max - min || 1;
  const W = 300, H = height, pad = 2;
  const step = (W - pad * 2) / Math.max(vals.length - 1, 1);
  const yv = v => H - pad - ((v - min) / range) * (H - pad * 2);
  const pts = vals.map((v, i) => `${pad + i * step},${yv(v)}`).join(' ');
  const last = vals[vals.length - 1];
  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: '100%', height, display: 'block' }} preserveAspectRatio="none">
      <defs>
        <linearGradient id={`sg-${color.replace('#','')}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.15"/>
          <stop offset="100%" stopColor={color} stopOpacity="0.02"/>
        </linearGradient>
      </defs>
      <polygon points={`${pts} ${pad + (vals.length-1)*step},${H} ${pad},${H}`} fill={`url(#sg-${color.replace('#','')})`}/>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round"/>
      <circle cx={pad + (vals.length-1)*step} cy={yv(last)} r="2.5" fill={color}/>
    </svg>
  );
}

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return isMobile;
}

// ══════════════════════════════════════════════════════
//  MOBILE BOTTOM NAV
// ══════════════════════════════════════════════════════
function MobileNav({ page, setPage, NAV, t }) {
  // 只展示最常用的6个导航项（含热榜——之前被漏掉，移动端完全没有入口，只能靠手动输入 #trending），
  // 图标复用桌面 NAV 里已有的 lucide Icon 组件
  const mobileIds = ["trending", "pipeline", "analyze", "portfolio", "watchlist", "compare"];
  const mobileNav = mobileIds.map(id => NAV.find(n => n.id === id)).filter(Boolean);
  return (
    <nav style={{
      position: "fixed", bottom: 0, left: 0, right: 0, zIndex: 200,
      background: "#fff", borderTop: `1px solid ${C.border}`,
      display: "flex", alignItems: "stretch",
      paddingBottom: "env(safe-area-inset-bottom, 0px)",
    }}>
      {mobileNav.map(n => (
        <button
          key={n.id}
          onClick={() => { setPage(n.id); track('page_view', { page: n.id }); }}
          style={{
            flex: 1, display: "flex", flexDirection: "column", alignItems: "center",
            justifyContent: "center", gap: 3, padding: "8px 4px",
            background: "none", border: "none", cursor: "pointer",
            color: page === n.id ? C.text : C.muted,
            fontSize: 9, fontWeight: page === n.id ? 700 : 400,
            borderTop: `2px solid ${page === n.id ? C.text : "transparent"}`,
          }}
        >
          <n.Icon size={20} strokeWidth={2} />
          <span>{n.label}</span>
        </button>
      ))}
    </nav>
  );
}

// ══════════════════════════════════════════════════════
//  MOBILE LIST CARD — 移动端列表页共用卡片外壳（早期雷达 / 二级热榜 / Deal Ranking）
//  只提供外壳（surface/border/圆角/间距 + 头部/标签/摘要/底部四个插槽），具体字段由各页面自己拼，
//  不做成通用 schema 引擎——4 个页面的数据形状差异真实存在（见 KOL 雷达那次评估），
//  硬套同一份字段配置只会让这层壳变得又臃肿又难改。
// ══════════════════════════════════════════════════════
function MobileListCard({ onClick, header, badge, tags, summary, footerLeft, footerRight }) {
  return (
    <div onClick={onClick} style={{
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14,
      cursor: onClick ? "pointer" : "default",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
        <div style={{ flex: 1, minWidth: 0 }}>{header}</div>
        {badge && <div style={{ flexShrink: 0 }}>{badge}</div>}
      </div>
      {tags && <div style={{ marginTop: 8 }}>{tags}</div>}
      {summary && <div style={{ marginTop: 8 }}>{summary}</div>}
      {(footerLeft || footerRight) && (
        <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 10, paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
          <div style={{ minWidth: 0 }}>{footerLeft}</div>
          <div style={{ flexShrink: 0, display: "flex", alignItems: "center", gap: 10 }}>{footerRight}</div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  KOL RADAR — MOBILE CARD（默认收起，展开看 VC/Degen/Corporate 分布 + Notable KOLs）
// ══════════════════════════════════════════════════════
function KolMobileCard({
  p, classification, cat, catColor, identity, identColor, bioStructured, bio,
  createdApprox, sig, isWatched, twitterUrl, lang,
  onToggleWatch, onAnalyze, onNotable, cacheEntry = {}, heat,
}) {
  const [expanded, setExpanded] = useState(false);
  const { displayType, confidence } = classification;
  const typeCfg = {
    project: { Icon: Building2, label: lang === 'en' ? 'Project' : '项目', color: '#3b82f6' },
    person:  { Icon: User,      label: lang === 'en' ? 'Person'  : '个人', color: '#a78bfa' },
  }[displayType];
  const confLabel = confidence >= 0.7 ? (lang === 'en' ? 'high' : '高信心') : confidence >= 0.4 ? (lang === 'en' ? 'mid' : '中信心') : (lang === 'en' ? 'low' : '低信心');
  const fresh = freshnessLabel(cacheEntry.scoredAt, lang);
  const hasNotable = (p.notableImages && p.notableImages.length > 0) || p.notableCount > 0;

  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        {p.avatar
          ? <img src={p.avatar} alt="" style={{ width: 36, height: 36, borderRadius: "50%", flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />
          : <div style={{ width: 36, height: 36, borderRadius: "50%", background: C.faint, flexShrink: 0 }} />}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name || p.username}</div>
          <div style={{ fontSize: 11, color: "#a1a1aa", marginTop: 1 }}>@{p.username}</div>
          <div style={{ display: "flex", gap: 4, marginTop: 6, flexWrap: "wrap" }}>
            <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: `${identColor}22`, color: identColor }}>{identity}</span>
            {typeCfg && (
              <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: `${typeCfg.color}22`, color: typeCfg.color, display: "inline-flex", alignItems: "center", gap: 3 }}>
                <typeCfg.Icon size={9} strokeWidth={2} />{typeCfg.label} · {confLabel}
              </span>
            )}
            <span style={{ fontSize: 9, fontWeight: 600, padding: "2px 6px", borderRadius: 4, background: `${catColor}22`, color: catColor }}>{cat}</span>
          </div>
        </div>
        <span style={{
          flexShrink: 0, fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999,
          background: sig >= 50 ? "#fff7ed" : sig >= 20 ? "#fffbeb" : C.faint,
          color: sig >= 50 ? "#c2410c" : sig >= 20 ? "#b45309" : C.muted,
        }}>{sig}</span>
      </div>

      <button onClick={() => setExpanded(v => !v)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 4,
        marginTop: 10, padding: "6px 0 0", background: "none", border: "none", borderTop: `1px solid ${C.border}`,
        color: C.muted, fontSize: 11, cursor: "pointer",
      }}>
        {expanded ? (lang === 'en' ? 'Show less' : '收起') : (lang === 'en' ? 'Show more' : '展开更多')}
        <ChevronDown size={13} strokeWidth={2} style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform .15s" }} />
      </button>

      {expanded && (
        <div style={{ marginTop: 10 }}>
          {bio && <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 8 }}>{bio}</div>}
          {bioStructured?.signal && <div style={{ fontSize: 11, color: "#b45309", fontStyle: "italic", marginBottom: 8 }}>{bioStructured.signal}</div>}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 }}>
            <div style={{ textAlign: "center", background: C.faint, borderRadius: 8, padding: "6px 0" }}>
              <div style={{ fontSize: 9, color: "#60a5fa" }}>VC</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>{p.vcCount || 0}</div>
            </div>
            <div style={{ textAlign: "center", background: C.faint, borderRadius: 8, padding: "6px 0" }}>
              <div style={{ fontSize: 9, color: "#a78bfa" }}>Degen</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>{p.degenCount || 0}</div>
            </div>
            <div style={{ textAlign: "center", background: C.faint, borderRadius: 8, padding: "6px 0" }}>
              <div style={{ fontSize: 9, color: "#34d399" }}>Corporate</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginTop: 2 }}>{p.corpCount || 0}</div>
            </div>
          </div>
          {p.newKols > 0 && (
            <div style={{ marginTop: 8, fontSize: 11, color: "#f97316", fontWeight: 700 }}>
              +{p.newKols} new KOL{p.totalKols > 0 ? ` · ↗ 24h +${Math.round((p.kols24h || 0) / p.totalKols * 100)}%` : ''}
            </div>
          )}
          {hasNotable && (
            <div onClick={onNotable} style={{ marginTop: 8, cursor: "pointer" }}>
              <div style={{ fontSize: 9, color: "#a1a1aa", marginBottom: 3 }}>Notable KOLs</div>
              <div style={{ fontSize: 11, color: "#60a5fa" }}>
                {lang === 'en' ? `Tap to view ${p.notableCount || (p.notableImages || []).length}` : `点击查看 ${p.notableCount || (p.notableImages || []).length} 人`}
              </div>
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 10, fontSize: 11, color: C.muted }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Users size={12} strokeWidth={2} />{p.followers ?? '—'}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 4 }}><Calendar size={12} strokeWidth={2} />{createdApprox}</span>
          </div>
          {classification.type === 'project' && (
            <div style={{ marginTop: 8, fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 4 }}>
              Surf: <SmartFollowersBadge name={p.name || p.username} />
            </div>
          )}
        </div>
      )}

      <div style={{ borderTop: `1px solid ${C.border}`, marginTop: 10, paddingTop: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: C.muted }}>
          {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3, color: fresh.stale ? "#c2410c" : C.muted }}><Clock size={11} strokeWidth={2} />{fresh.text}</span>}
          {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={11} strokeWidth={2} />{lang === 'en' ? `${heat} analyses` : `分析 ${heat} 次`}</span>}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button onClick={onToggleWatch} aria-label="watchlist" style={{ background: "none", border: "none", cursor: "pointer", display: "flex", color: isWatched ? "#f59e0b" : C.muted }}>
            <Star size={16} strokeWidth={2} fill={isWatched ? "#f59e0b" : "none"} />
          </button>
          {cat === 'Person' ? (
            <a href={twitterUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, fontWeight: 700, color: C.text, textDecoration: "none" }}>Twitter</a>
          ) : (
            <button onClick={onAnalyze} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.text }}>
              {cacheEntry.cached ? (lang === 'en' ? 'View' : '查看') : (lang === 'en' ? 'Analyze' : '分析')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  LANGUAGE TOGGLE
// ══════════════════════════════════════════════════════
function LangToggle() {
  const { lang, setLang } = useLang();
  return (
    <div style={{ display: "flex", gap: 3, marginBottom: 4 }}>
      {["zh", "en"].map(l => (
        <button
          key={l}
          onClick={() => setLang(l)}
          style={{
            flex: 1,
            padding: "4px 0",
            fontSize: 10,
            fontWeight: lang === l ? 700 : 400,
            background: lang === l ? C.text : C.surface,
            color: lang === l ? "#fff" : C.muted,
            border: `1px solid ${lang === l ? C.text : C.border}`,
            borderRadius: 4,
            cursor: "pointer",
            letterSpacing: 0.5,
          }}
        >
          {l === "zh" ? "中文" : "EN"}
        </button>
      ))}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  SUB-COMPONENTS
// ══════════════════════════════════════════════════════
function ScoreBar({ value, color, height = 3 }) {
  return (
    <div style={{ height, background: C.faint, borderRadius: 2 }}>
      <div style={{ width: `${value || 0}%`, height: "100%", background: color, borderRadius: 2, transition: "width .4s" }} />
    </div>
  );
}

function StatBlock({ label, value, sub }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, lineHeight: 1 }}>{value || "—"}</div>
      {sub && <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>{sub}</div>}
    </div>
  );
}

// ── 全局市场背景小组件（侧边栏常驻）──────────────────────────────────────
function MarketContextWidget({ lang }) {
  const [mc, setMc] = useState(null);
  useEffect(() => {
    fetch(`${IS_DEV ? "" : (process.env.REACT_APP_PROXY || "")}/api/market-context`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setMc(d); })
      .catch(() => {});
  }, []);
  if (!mc) return null;
  const fg = mc.fearGreed;
  const as = mc.altSeason;
  const fgColor = !fg ? "#6b7280"
    : fg.value >= 70 ? "#059669" : fg.value >= 50 ? "#22c55e"
    : fg.value >= 30 ? "#d97706" : "#dc2626";
  return (
    <div style={{ marginTop: 10, padding: "8px 10px", background: C.faint, borderRadius: 8, border: `1px solid ${C.border}` }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 6 }}>
        {lang === 'en' ? 'Market Context' : '市场背景'}
      </div>
      {fg && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: C.muted }}>{lang === 'en' ? 'Fear & Greed' : '恐慌贪婪'}</span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: fgColor }}>
            <span style={{ width: 7, height: 7, borderRadius: "50%", background: fgColor, flexShrink: 0 }} /> {fg.value}
          </span>
        </div>
      )}
      {mc.btcDominance && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span style={{ fontSize: 10, color: C.muted }}>BTC Dom</span>
          <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{mc.btcDominance}%</span>
        </div>
      )}
      {as && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: 10, color: C.muted }}>{lang === 'en' ? 'Season' : '季节'}</span>
          <span style={{ fontSize: 10, fontWeight: 700, color: as.color, background: `${as.color}18`, padding: "1px 6px", borderRadius: 4 }}>
            {lang === 'en' ? as.label_en : as.label}
          </span>
        </div>
      )}
      {mc.totalMarketCap && (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 4, paddingTop: 4, borderTop: `1px solid ${C.border}` }}>
          <span style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'Total MCap' : '总市值'}</span>
          <span style={{ fontSize: 10, color: C.muted }}>
            ${(mc.totalMarketCap / 1e12).toFixed(2)}T
            {mc.marketCapChange24h != null && (
              <span style={{ marginLeft: 4, color: mc.marketCapChange24h >= 0 ? "#059669" : "#dc2626" }}>
                {mc.marketCapChange24h >= 0 ? "+" : ""}{mc.marketCapChange24h}%
              </span>
            )}
          </span>
        </div>
      )}
    </div>
  );
}

function UnlockBadge({ level }) {
  const t = useT();
  const cfg = { Green: { bg: "#ecfdf5", fg: "#059669", key: "unlock.low" }, Yellow: { bg: "#fffbeb", fg: "#d97706", key: "unlock.medium" }, Red: { bg: "#fef2f2", fg: "#dc2626", key: "unlock.high" } };
  const c = cfg[level] || cfg.Yellow;
  return <span style={{ ...S.tag, background: c.bg, color: c.fg, border: "none" }}>● {t(c.key)}</span>;
}

// ══════════════════════════════════════════════════════
//  IRR PANEL
// ══════════════════════════════════════════════════════
function IRRPanel({ project }) {
  const t = useT();
  const { lang } = useLang();
  const lastRound = project.fundingRounds?.[0];
  // 已上线代币优先用 CoinGecko FDV，但需通过合理性校验
  // 若 cgFDV 远小于项目总融资（说明抓错代币了），则 fallback 到融资估值
  const cgFdvRaw = project.cgData?.market_data?.fully_diluted_valuation?.usd;
  const totalFunding = project.details?.total_funding || 0;
  const cgFdvValid = cgFdvRaw && cgFdvRaw > totalFunding * 0.5;
  const cgFdv = cgFdvValid ? cgFdvRaw : null;
  // 改进1：估值未披露时用融资金额÷25%推算隐含估值，避免用融资额当入场价
  const roundVal = lastRound?.valuation || 0;
  const roundAmt = lastRound?.amount || 0;
  const impliedVal = roundVal > 0 ? roundVal : roundAmt > 0 ? roundAmt / 0.25 : totalFunding;
  const entryVal = cgFdv || impliedVal;
  const entryLabel = cgFdv ? t('irr.current_fdv') : roundVal > 0 ? t('irr.latest_round') : t('irr.implied_val');
  const fairVal = project.analysis?.fair_value_usd;
  const irrData = calcIRR(entryVal, fairVal);
  const alpha = alphaScore(project.analysis?.composite || 0, irrData, project.analysis?.risk_penalty || 0);

  if (!irrData) return (
    <div style={{ ...S.card, color: C.muted, textAlign: "center", padding: 24 }}>
      {t('irr.no_data')}<br />
      <span style={{ fontSize: 11 }}>{lang === 'en' ? 'Requires funding round valuation or AI fair value' : '需要融资轮次估值或 AI 公允价值'}</span>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>ALPHA SCORE</div>
          <div style={{ fontSize: 40, fontWeight: 900, color: alpha > 150 ? "#059669" : alpha > 80 ? "#2563eb" : "#6b7280", lineHeight: 1 }}>{alpha}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{t('irr.alpha_formula')}</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 10, color: C.muted }}>{t('irr.expected_mult')}</div>
          <div style={{ fontSize: 24, fontWeight: 800 }}>{irrData.expectedMult.toFixed(2)}x</div>
          <div style={{ fontSize: 10, color: C.muted }}>{t('irr.expected_irr')}</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: irrData.expectedIRR > 0.3 ? "#059669" : "#d97706" }}>
            {(irrData.expectedIRR * 100).toFixed(1)}%
          </div>
        </div>
      </div>

      <div style={{ ...S.card, display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
        <StatBlock label={t('irr.entry_val')} value={fmt(entryVal)} sub={entryLabel} />
        <StatBlock label={t('irr.fair_val')} value={fmt(fairVal)} sub={project.analysis?.valuation_basis ? (lang === 'en' ? (project.analysis.valuation_basis_en || project.analysis.valuation_basis) : project.analysis.valuation_basis).slice(0, 30) : t('irr.ai_estimate')} />
        <StatBlock label={t('irr.under_mult')} value={`${irrData.underMult.toFixed(2)}x`} sub={irrData.underMult > 1 ? t('irr.undervalued') : t('irr.overvalued')} />
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>
          {lang === 'en' ? `3-Scenario IRR (Hold ${HOLD_YEARS} Yrs)` : `三情景 IRR（持有 ${HOLD_YEARS} 年）`}
        </div>
        {irrData.scenarios.map(s => (
          <div key={s.label} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ fontWeight: 600, fontSize: 12 }}>{lang === 'en' ? s.label_en : s.label} <span style={{ fontSize: 10, color: C.muted }}>p={Math.round(s.prob * 100)}%</span></span>
              <span style={{ fontWeight: 700, color: s.color }}>{(s.irr * 100).toFixed(0)}% IRR · {s.exitMult.toFixed(1)}x</span>
            </div>
            <ScoreBar value={Math.min(s.irr * 100, 100)} color={s.color} height={4} />
          </div>
        ))}
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>{t('irr.return_dist')}</div>
        <ResponsiveContainer width="100%" height={110}>
          <BarChart data={irrData.scenarios.map(s => ({ name: lang === 'en' ? s.label_en : s.label, IRR: parseFloat((s.irr * 100).toFixed(1)) }))}>
            <XAxis dataKey="name" tick={{ fontSize: 10, fill: C.muted }} axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 9, fill: C.muted }} axisLine={false} tickLine={false} unit="%" />
            <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} formatter={v => [`${v}%`, "IRR"]} />
            <Bar dataKey="IRR" radius={[3, 3, 0, 0]}
              fill="#3b82f6"
              label={{ position: "top", fontSize: 10, fill: C.muted, formatter: v => `${v}%` }} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* FDV 分层投资建议 */}
      {project.analysis?.fdv_tiers?.length > 0 && (
        <div style={S.card}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>{t('irr.fdv_tiers')}</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: C.faint }}>
                {[t('irr.tier_range'), t('irr.tier_rating'), t('irr.tier_position'), t('irr.tier_return')].map(h => (
                  <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: C.muted, fontSize: 10 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {project.analysis.fdv_tiers.map((tier, i) => {
                const rColor = tier.rating === "S" ? "#059669" : tier.rating === "A" ? "#2563eb" : tier.rating === "B" ? "#d97706" : C.muted;
                const rBg   = tier.rating === "S" ? "#ecfdf5" : tier.rating === "A" ? "#eff6ff" : tier.rating === "B" ? "#fffbeb" : C.faint;
                return (
                  <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                    <td style={{ padding: "6px 8px", fontWeight: 600 }}>{lang === 'en' ? enify(tier.fdv_range) : tier.fdv_range}</td>
                    <td style={{ padding: "6px 8px" }}>
                      <span style={{ background: rBg, color: rColor, padding: "1px 7px", borderRadius: 4, fontWeight: 700, fontSize: 12 }}>{tier.rating}</span>
                    </td>
                    <td style={{ padding: "6px 8px", color: C.muted }}>{lang === 'en' ? (tier.position_en || tier.position) : tier.position}</td>
                    <td style={{ padding: "6px 8px", fontWeight: 700, color: rColor }}>{tier.expected_return}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* 当前档位高亮提示 */}
          <div style={{ marginTop: 8, fontSize: 10, color: C.muted, borderTop: `1px solid ${C.faint}`, paddingTop: 7 }}>
            💡 {t('irr.current_entry')} {fmt(entryVal)} ({entryLabel})
          </div>
        </div>
      )}

      {/* 退出路径 */}
      {project.analysis?.exit_paths?.length > 0 && (
        <div style={S.card}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>{t('irr.exit_paths')}</div>
          {(lang === 'en' && project.analysis.exit_paths_en?.length ? project.analysis.exit_paths_en : project.analysis.exit_paths).map((p, i) => (
            <div key={i} style={{ fontSize: 11, marginBottom: 5, display: "flex", gap: 8, alignItems: "flex-start" }}>
              <span style={{ color: "#3b82f6", fontWeight: 700, flexShrink: 0 }}>{["①","②","③"][i]}</span>
              <span style={{ color: "#374151", lineHeight: 1.5 }}>{typeof p === "string" ? p : (p.trigger ? `${p.trigger}${p.scenario ? (lang === 'en' ? ": " : "：") + p.scenario : ""}${p.target_multiple ? ` (${p.target_multiple})` : ""}` : JSON.stringify(p))}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  NOTES DIARY 跟进笔记组件
// ══════════════════════════════════════════════════════
const NOTE_TYPES = [
  { id: "research",  label: "调研",   label_en: "Research",  color: "#2563eb", bg: "#eff6ff" },
  { id: "meeting",   label: "会议",   label_en: "Meeting",   color: "#059669", bg: "#ecfdf5" },
  { id: "concern",   label: "风险",   label_en: "Risk",      color: "#dc2626", bg: "#fef2f2" },
  { id: "catalyst",  label: "催化剂", label_en: "Catalyst",  color: "#d97706", bg: "#fffbeb" },
  { id: "decision",  label: "决策",   label_en: "Decision",  color: "#7c3aed", bg: "#f5f3ff" },
  { id: "other",     label: "其他",   label_en: "Other",     color: "#6b7280", bg: "#f3f4f6" },
];

// ══════════════════════════════════════════════════════
//  WEB INTEL PANEL — 情报 Tab
// ══════════════════════════════════════════════════════
function WebIntelPanel({ project }) {
  const wi = project.webIntel;
  const d  = project.details;
  const team = project.teamMembers || [];
  const ts   = project.teamSignals || null;
  const gs   = project.githubSignal || null;
  // 团队人才背景信号（顶尖机构履历 / 连续创业者）——来自 GitHub 贡献者 bio/company
  // 关键词匹配（见 founderPedigree.js），零额外网络请求，数据本身已经在 enhancedTeam 里
  const pedigreeHits = (project.enhancedTeam?.topContributors || []).filter(c => c.pedigreeSignal);
  // 建立 teamMembers 名字→数据的快查表，用于在 wi.founders 卡片中补充 LinkedIn
  const teamByName = Object.fromEntries(
    team.map(m => [m.name?.toLowerCase?.() || "", m])
  );
  const { lang } = useLang();
  const t = useT();
  // 真实网站流量（Monid → Semrush）——有的话取代下面那张卡片原本纯 AI 猜的估算
  const { traffic: realTraffic } = useMonidTraffic(d?.social_media?.website);

  // 高管交易信号（SEC Form 4）——只对少数已上市项目适用（比如 Circle/CRCL），
  // resolveTicker 解析不到就是 null，绝大多数项目直接不发起请求。
  const tickerInfo = resolveTicker(project.name);
  const [insiderData, setInsiderData] = useState(null);
  const [insiderLoading, setInsiderLoading] = useState(false);
  // LinkedIn 人员搜索（按人名手动触发，Monid → Apify，~5-15s，$0.075/次封顶）——
  // 按创始人姓名 keyed，{ [name]: { loading, results } }
  const [linkedinSearch, setLinkedinSearch] = useState({});
  const runLinkedInSearch = (name) => {
    if (!name || linkedinSearch[name]?.loading) return;
    setLinkedinSearch(prev => ({ ...prev, [name]: { loading: true, results: null } }));
    searchLinkedInPerson(name).then(results => {
      setLinkedinSearch(prev => ({ ...prev, [name]: { loading: false, results } }));
    });
  };
  useEffect(() => {
    if (!tickerInfo) { setInsiderData(null); return; }
    setInsiderLoading(true);
    fetchInsiderActivity(tickerInfo.cik, RD_BASE).then((data) => {
      setInsiderData(data);
      setInsiderLoading(false);
    });
  }, [tickerInfo?.cik]);

  // Twitter handle 标准化 → 匹配 teamSignals.twitterAges
  const getTwitterAge = (twitterUrl) => {
    if (!twitterUrl || !ts?.twitterAges) return null;
    const handle = twitterUrl.replace(/^https?:\/\/(twitter|x)\.com\//, '').replace(/^@/, '').split('/')[0].toLowerCase();
    return ts.twitterAges[handle] || null;
  };

  const fmtVisits = (n) => {
    if (!n) return "—";
    if (n >= 1e6) return (n / 1e6).toFixed(1) + "M";
    if (n >= 1e4) return (n / 1e4).toFixed(1) + "万";
    return n.toLocaleString();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>

      {/* 网站流量 */}
      <div style={S.card}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Globe size={12} strokeWidth={2} /> {realTraffic ? (lang === 'en' ? 'Website Traffic (Semrush)' : '网站流量（Semrush 真实数据）') : (lang === 'en' ? 'Website Traffic (AI Estimate)' : '网站流量（AI 估算）')}</span>
          {d?.social_media?.website && (
            <a href={d.social_media.website} target="_blank" rel="noreferrer"
              style={{ fontSize: 10, color: "#2563eb", fontWeight: 400, textDecoration: "none" }}>
              {d.social_media.website.replace(/https?:\/\//, "").replace(/\/$/, "")} ↗
            </a>
          )}
        </div>
        {realTraffic ? (() => {
          const fmtN = v => { const n = Number(v); if (!n) return "—"; return n >= 1e6 ? `${(n/1e6).toFixed(1)}M` : n >= 1e3 ? `${(n/1e3).toFixed(0)}K` : String(Math.round(n)); };
          const pct = v => v == null ? "—" : `${(Number(v) * 100).toFixed(0)}%`;
          const mins = Math.floor((Number(realTraffic.time_on_site) || 0) / 60);
          const secs = Math.round((Number(realTraffic.time_on_site) || 0) % 60);
          return (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
                <div style={{ background: C.faint, borderRadius: 6, padding: 10 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{lang === 'en' ? 'Monthly Visits' : '月访问量'}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#2563eb" }}>{fmtN(realTraffic.visits)}</div>
                </div>
                <div style={{ background: C.faint, borderRadius: 6, padding: 10 }}>
                  <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{lang === 'en' ? 'Unique Visitors' : '独立访客'}</div>
                  <div style={{ fontSize: 16, fontWeight: 800, color: "#2563eb" }}>{fmtN(realTraffic.users)}</div>
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center", fontSize: 11 }}>
                <span style={{ color: C.muted }}>{lang === 'en' ? 'Semrush Rank' : 'Semrush 排名'}</span>
                <span style={{ color: C.text, fontWeight: 700 }}>#{fmtN(realTraffic.rank)}</span>
                <span style={{ color: C.muted }}>{lang === 'en' ? 'Bounce Rate' : '跳出率'}</span>
                <span style={{ color: C.text, fontWeight: 700 }}>{pct(realTraffic.bounce_rate)}</span>
                <span style={{ color: C.muted }}>{lang === 'en' ? 'Avg. Visit' : '平均停留'}</span>
                <span style={{ color: C.text, fontWeight: 700 }}>{mins}m {secs}s</span>
                <span style={{ color: C.muted }}>{lang === 'en' ? 'Desktop/Mobile' : '桌面/移动'}</span>
                <span style={{ color: C.text, fontWeight: 700 }}>{pct(realTraffic.desktop_share)} / {pct(realTraffic.mobile_share)}</span>
              </div>
              <div style={{ fontSize: 10, color: C.muted, background: C.faint, padding: "4px 8px", borderRadius: 4 }}>
                Semrush · {lang === 'en' ? 'estimated traffic, not verified analytics' : '估算流量，非站方真实统计'}
              </div>
            </>
          );
        })() : wi?.techIntel?.traffic ? (
          <>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 12 }}>
              <div style={{ background: C.faint, borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{lang === 'en' ? 'Monthly Visits (Est.)' : '月访问量（估算）'}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#2563eb" }}>
                  {wi.techIntel.traffic.monthly_visits_estimate || "—"}
                </div>
              </div>
              <div style={{ background: C.faint, borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 3 }}>{lang === 'en' ? 'Traffic Scale' : '流量规模'}</div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {lang === 'en' ? (wi.techIntel.traffic.traffic_tier_en || wi.techIntel.traffic.traffic_tier || "—") : (wi.techIntel.traffic.traffic_tier || "—")}
                </div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
              <span style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'Growth Trend' : '增长趋势'}</span>
              <span style={{ ...S.tag, fontSize: 10,
                background: wi.techIntel.traffic.growth_trend === "快速增长" ? "#ecfdf5" : C.faint,
                color: wi.techIntel.traffic.growth_trend === "快速增长" ? "#059669" :
                       wi.techIntel.traffic.growth_trend === "下降" ? "#dc2626" : C.text }}>
                {lang === 'en' ? (wi.techIntel.traffic.growth_trend_en || wi.techIntel.traffic.growth_trend || 'Unknown') : (wi.techIntel.traffic.growth_trend || '未知')}
              </span>
              <span style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'Primary Audience' : '主要用户'}</span>
              <span style={{ fontSize: 11, color: C.text }}>{lang === 'en' ? (wi.techIntel.traffic.primary_audience_en || wi.techIntel.traffic.primary_audience || "—") : (wi.techIntel.traffic.primary_audience || "—")}</span>
            </div>
            {wi.techIntel.traffic.top_regions?.length > 0 && (
              <div style={{ marginBottom: 8 }}>
                <div style={{ fontSize: 10, color: C.muted, marginBottom: 5 }}>{lang === 'en' ? 'Top Regions' : '主要流量地区'}</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {wi.techIntel.traffic.top_regions.map((r, i) => (
                    <span key={i} style={{ ...S.tag, background: "#eff6ff", color: "#2563eb", fontSize: 10 }}>{r}</span>
                  ))}
                </div>
              </div>
            )}
            <div style={{ fontSize: 10, color: C.muted, background: C.faint, padding: "4px 8px", borderRadius: 4 }}>
              {lang === 'en' ? 'Basis: ' : '依据：'}{lang === 'en' ? (wi.techIntel.traffic.traffic_basis_en || wi.techIntel.traffic.traffic_basis) : wi.techIntel.traffic.traffic_basis} ·
              <span style={{ color: "#d97706" }}> ⚠ {lang === 'en' ? 'AI estimate, for reference only' : 'AI 估算，仅供参考'}</span>
            </div>
          </>
        ) : (
          <div style={{ fontSize: 11, color: C.muted }}>
            {lang === 'en' ? 'Traffic data pending... (re-analyze to fetch)' : '流量数据分析中...（需重新分析项目获取）'}
          </div>
        )}
      </div>

      {/* 项目画像（Step 2c 综合合成：技术栈 + 产品功能 + 阶段 + 风险）*/}
      {project.projectContext && (project.projectContext.contextSummary || project.projectContext.productFeatures?.length > 0) && (
        <div style={S.card}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Brain size={12} strokeWidth={2} /> {lang === 'en' ? 'Project Profile (AI Synthesized)' : '项目画像（AI 综合）'}</span>
            <span style={{ display: "flex", gap: 4 }}>
              {project.projectContext.productMaturity?.stage && (
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 700,
                  background:
                    project.projectContext.productMaturity.stage === 'mature' ? '#dcfce7' :
                    project.projectContext.productMaturity.stage === 'scaling' ? '#bbf7d0' :
                    project.projectContext.productMaturity.stage === 'mainnet' ? '#dbeafe' :
                    project.projectContext.productMaturity.stage === 'testnet' ? '#fef3c7' :
                    project.projectContext.productMaturity.stage === 'devnet' ? '#fde68a' :
                    project.projectContext.productMaturity.stage === 'idea' ? '#fecaca' : C.faint,
                  color:
                    project.projectContext.productMaturity.stage === 'mature' ? '#166534' :
                    project.projectContext.productMaturity.stage === 'scaling' ? '#16a34a' :
                    project.projectContext.productMaturity.stage === 'mainnet' ? '#1d4ed8' :
                    project.projectContext.productMaturity.stage === 'testnet' ? '#92400e' :
                    project.projectContext.productMaturity.stage === 'devnet' ? '#a16207' :
                    project.projectContext.productMaturity.stage === 'idea' ? '#dc2626' : C.muted,
                }}>
                  {project.projectContext.productMaturity.stage.toUpperCase()}
                </span>
              )}
              {project.projectContext.technicalDepth?.score != null && (
                <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, fontWeight: 700,
                  background: project.projectContext.technicalDepth.score >= 7 ? "#ecfdf5"
                            : project.projectContext.technicalDepth.score >= 4 ? "#fef3c7" : "#fef2f2",
                  color:      project.projectContext.technicalDepth.score >= 7 ? "#059669"
                            : project.projectContext.technicalDepth.score >= 4 ? "#ca8a04" : "#dc2626",
                }}>
                  {lang === 'en' ? 'Tech Depth' : '技术深度'} {project.projectContext.technicalDepth.score}/10
                </span>
              )}
            </span>
          </div>

          {/* 一句话画像 */}
          {project.projectContext.contextSummary && (
            <div style={{ fontSize: 12, color: C.text, fontStyle: "italic", marginBottom: 10, padding: "8px 10px", background: C.faint, borderRadius: 6, lineHeight: 1.5 }}>
              "{lang === 'en' ? (project.projectContext.contextSummary_en || project.projectContext.contextSummary) : project.projectContext.contextSummary}"
              {lang === 'en' && !project.projectContext.contextSummary_en && t('misc.ai_content_note') && (
                <span style={{ color: C.muted, fontSize: 10, fontStyle: "normal", marginLeft: 6 }}>{t('misc.ai_content_note')}</span>
              )}
            </div>
          )}

          {/* productMaturity 详细依据 */}
          {project.projectContext.productMaturity?.evidence && (
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              <strong style={{ color: C.text }}>{lang === 'en' ? 'Stage evidence: ' : '阶段依据：'}</strong>
              {lang === 'en' ? (project.projectContext.productMaturity.evidence_en || project.projectContext.productMaturity.evidence) : project.projectContext.productMaturity.evidence}
              {lang === 'en' && !project.projectContext.productMaturity.evidence_en && t('misc.ai_content_note') && (
                <span style={{ color: C.muted, fontSize: 10, marginLeft: 6 }}>{t('misc.ai_content_note')}</span>
              )}
            </div>
          )}

          {/* 技术栈 */}
          {project.projectContext.techStack?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 4 }}>
                {lang === 'en' ? 'TECH STACK' : '技术栈'}
              </div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {project.projectContext.techStack.map((t, i) => (
                  <span key={i} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, background: "#eff6ff", color: "#2563eb", fontWeight: 600 }}>{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* 产品功能列表 */}
          {project.projectContext.productFeatures?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 4 }}>
                {lang === 'en' ? `PRODUCT FEATURES (${project.projectContext.productFeatures.length})` : `产品功能（${project.projectContext.productFeatures.length} 个）`}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {project.projectContext.productFeatures.map((f, i) => {
                  const maturityColor = f.maturity === 'live' ? "#16a34a" : f.maturity === 'beta' ? "#ca8a04" : "#6b7280";
                  return (
                    <div key={i} style={{ fontSize: 11, padding: "5px 8px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, borderLeft: `3px solid ${maturityColor}` }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
                        <span style={{ fontWeight: 600, color: C.text }}>
                          {lang === 'en' ? (f.name_en || f.name) : f.name}
                          {lang === 'en' && !f.name_en && t('misc.ai_content_note') && (
                            <span style={{ color: C.muted, fontSize: 9, fontWeight: 400, marginLeft: 4 }}>{t('misc.ai_content_note')}</span>
                          )}
                        </span>
                        <span style={{ fontSize: 9, color: maturityColor, fontWeight: 700, textTransform: "uppercase" }}>{f.maturity}</span>
                      </div>
                      {f.description && <div style={{ color: C.muted, marginTop: 2 }}>{lang === 'en' ? (f.description_en || f.description) : f.description}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* 近期动态 */}
          {project.projectContext.recentMomentum?.summary && (
            <div style={{ fontSize: 11, color: C.text, marginBottom: 8, padding: "6px 8px", background: "#f0f9ff", borderRadius: 4, borderLeft: "3px solid #0ea5e9" }}>
              <div style={{ fontSize: 9, color: "#0369a1", fontWeight: 700, marginBottom: 2 }}>
                {lang === 'en' ? `RECENT MOMENTUM (${project.projectContext.recentMomentum.productEvents30d || 0} events in 30d)` : `近期动态（30 天 ${project.projectContext.recentMomentum.productEvents30d || 0} 个事件）`}
              </div>
              {lang === 'en' ? (project.projectContext.recentMomentum.summary_en || project.projectContext.recentMomentum.summary) : project.projectContext.recentMomentum.summary}
              {lang === 'en' && !project.projectContext.recentMomentum.summary_en && t('misc.ai_content_note') && (
                <span style={{ color: C.muted, fontSize: 9, marginLeft: 6 }}>{t('misc.ai_content_note')}</span>
              )}
              {project.projectContext.recentMomentum.keyEvents?.length > 0 && (
                <ul style={{ margin: "6px 0 0 16px", padding: 0, fontSize: 10, color: C.muted }}>
                  {project.projectContext.recentMomentum.keyEvents.map((e, i) => (
                    <li key={i}>{lang === 'en' ? (project.projectContext.recentMomentum.keyEvents_en?.[i] || e) : e}</li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* 技术深度依据 */}
          {project.projectContext.technicalDepth?.basis && (
            <div style={{ fontSize: 11, color: C.text, marginBottom: 8 }}>
              <strong style={{ color: C.text }}>{lang === 'en' ? 'Tech depth basis: ' : '技术深度依据：'}</strong>
              <span style={{ color: C.muted }}>{lang === 'en' ? (project.projectContext.technicalDepth.basis_en || project.projectContext.technicalDepth.basis) : project.projectContext.technicalDepth.basis}</span>
              {lang === 'en' && !project.projectContext.technicalDepth.basis_en && t('misc.ai_content_note') && (
                <span style={{ color: C.muted, fontSize: 10, marginLeft: 6 }}>{t('misc.ai_content_note')}</span>
              )}
            </div>
          )}

          {/* 竞争定位 */}
          {project.projectContext.competitivePosition && (
            <div style={{ fontSize: 11, color: C.text, marginBottom: 8 }}>
              <strong style={{ color: C.text }}>{lang === 'en' ? 'Competitive position: ' : '竞争定位：'}</strong>
              <span style={{ color: C.muted }}>{lang === 'en' ? (project.projectContext.competitivePosition_en || project.projectContext.competitivePosition) : project.projectContext.competitivePosition}</span>
              {lang === 'en' && !project.projectContext.competitivePosition_en && t('misc.ai_content_note') && (
                <span style={{ color: C.muted, fontSize: 10, marginLeft: 6 }}>{t('misc.ai_content_note')}</span>
              )}
            </div>
          )}

          {/* 证据标签 */}
          {project.projectContext.evidenceTags?.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
              {project.projectContext.evidenceTags.map((t, i) => (
                <span key={i} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "#ecfdf5", color: "#059669", fontWeight: 600 }}>✓ {t}</span>
              ))}
            </div>
          )}

          {/* 已识别风险 */}
          {project.projectContext.knownRisks?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>
                ⚠ {lang === 'en' ? 'Known Risks' : '已识别风险'}
              </div>
              {project.projectContext.knownRisks.map((r, i) => {
                const rEn = project.projectContext.knownRisks_en?.[i];
                return (
                  <div key={i} style={{ fontSize: 11, color: C.text, paddingLeft: 12, marginBottom: 2 }}>
                    • {safeText(lang === 'en' ? (rEn || r) : r)}
                    {lang === 'en' && !rEn && t('misc.ai_content_note') && (
                      <span style={{ color: C.muted, fontSize: 9, marginLeft: 4 }}>{t('misc.ai_content_note')}</span>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* 数据源 */}
          {project.projectContext.sourcesUsed?.length > 0 && (
            <div style={{ fontSize: 9, color: C.muted, marginTop: 8, textAlign: "right" }}>
              {lang === 'en' ? 'Sources: ' : '数据源：'}{project.projectContext.sourcesUsed.join(' + ')}
              {project.projectContext.generatedAt && ` · ${project.projectContext.generatedAt.slice(0, 10)}`}
            </div>
          )}
        </div>
      )}

      {/* 项目近况（AI 合成 surfNews → 5-8 条结构化近期动态）*/}
      {project.recentUpdatesSummary?.updates?.length > 0 && (
        <div style={S.card}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Newspaper size={12} strokeWidth={2} /> {lang === 'en' ? 'Project Recent Updates' : '项目近况'}</span>
            <span style={{ fontSize: 9, color: C.muted, fontWeight: 400 }}>
              {lang === 'en'
                ? `${project.recentUpdatesSummary.updates.length} key events from ${project.recentUpdatesSummary.totalNewsScanned} news`
                : `${project.recentUpdatesSummary.updates.length} 条要点（扫描 ${project.recentUpdatesSummary.totalNewsScanned} 条新闻）`}
            </span>
          </div>

          {/* 一句话总结 */}
          {project.recentUpdatesSummary.oneLineSummary && (
            <div style={{ fontSize: 12, color: C.text, fontStyle: "italic", marginBottom: 10, padding: "8px 10px", background: C.faint, borderRadius: 6, lineHeight: 1.5 }}>
              "{project.recentUpdatesSummary.oneLineSummary}"
            </div>
          )}

          {/* 主题词 */}
          {project.recentUpdatesSummary.keyThemes?.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 12 }}>
              {project.recentUpdatesSummary.keyThemes.map((t, i) => (
                <span key={i} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "#eff6ff", color: "#2563eb", fontWeight: 600 }}>
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* 时间线 */}
          <div style={{ position: "relative", paddingLeft: 18 }}>
            <div style={{ position: "absolute", left: 5, top: 4, bottom: 4, width: 1.5, background: C.border }} />
            {project.recentUpdatesSummary.updates.map((u, i) => {
              const categoryMeta = {
                product:     { Icon: Settings,   labelZh: "产品", labelEn: "Product",     color: "#2563eb" },
                partnership: { Icon: Handshake,  labelZh: "合作", labelEn: "Partnership", color: "#7c3aed" },
                funding:     { Icon: DollarSign,  labelZh: "融资", labelEn: "Funding",     color: "#059669" },
                security:    { Icon: ShieldCheck, labelZh: "安全", labelEn: "Security",    color: "#dc2626" },
                milestone:   { Icon: Target,      labelZh: "里程碑", labelEn: "Milestone", color: "#ea580c" },
                regulatory:  { Icon: Scale,       labelZh: "监管", labelEn: "Regulatory",  color: "#92400e" },
                other:       { Icon: MapPin,      labelZh: "动态", labelEn: "Update",      color: "#6b7280" },
              }[u.category] || { Icon: MapPin, labelZh: "动态", labelEn: "Update", color: "#6b7280" };
              const sentimentBg =
                u.sentiment === "positive" ? "#ecfdf5" :
                u.sentiment === "negative" ? "#fef2f2" : "#f9fafb";
              return (
                <div key={i} style={{ position: "relative", marginBottom: 12, paddingLeft: 6 }}>
                  {/* 圆点 */}
                  <div style={{ position: "absolute", left: -16, top: 6, width: 8, height: 8, borderRadius: "50%", background: categoryMeta.color, border: `2px solid ${C.surface}` }} />
                  {/* 头部：日期 + 类别徽章 + 情绪 */}
                  <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 4 }}>
                    <span style={{ fontSize: 10, color: C.muted, fontWeight: 600 }}>{u.date}</span>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, padding: "1px 6px", borderRadius: 8, background: categoryMeta.color + "20", color: categoryMeta.color, fontWeight: 700 }}>
                      <categoryMeta.Icon size={9} strokeWidth={2} /> {lang === 'en' ? categoryMeta.labelEn : categoryMeta.labelZh}
                    </span>
                    {u.sourceName && (
                      <span style={{ fontSize: 9, color: C.muted }}>{u.sourceName}</span>
                    )}
                  </div>
                  {/* 标题（可点击跳源 URL）*/}
                  <div style={{ marginBottom: 4 }}>
                    {u.sourceUrl ? (
                      <a href={u.sourceUrl} target="_blank" rel="noreferrer"
                        style={{ fontSize: 12, fontWeight: 600, color: C.text, textDecoration: "none", lineHeight: 1.4 }}>
                        {u.title} <span style={{ fontSize: 9, color: "#2563eb" }}>↗</span>
                      </a>
                    ) : (
                      <span style={{ fontSize: 12, fontWeight: 600, color: C.text, lineHeight: 1.4 }}>{u.title}</span>
                    )}
                  </div>
                  {/* AI 评语 */}
                  {u.aiComment && (
                    <div style={{ fontSize: 11, color: C.text, padding: "6px 8px", background: sentimentBg, borderRadius: 4, borderLeft: `3px solid ${categoryMeta.color}` }}>
                      {u.aiComment}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {project.recentUpdatesSummary.generatedAt && (
            <div style={{ fontSize: 9, color: C.muted, marginTop: 6, textAlign: "right" }}>
              {lang === 'en' ? 'AI synthesized: ' : 'AI 合成于：'}{project.recentUpdatesSummary.generatedAt.slice(0, 10)}
            </div>
          )}
        </div>
      )}

      {/* 团队质量摘要（合成层：聚合 Layer A/B/C + RootData 给出 qualityScore + 强项/关注）
          位置：放在 RootData 团队 + Enhanced Team 之前，作为"总览先看"的入口 */}
      {project.enhancedTeam?.summary && (
        <div style={S.card}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><BarChart3 size={12} strokeWidth={2} /> {lang === 'en' ? 'Team Quality Summary' : '团队质量摘要'}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{
                fontSize: 18, fontWeight: 900,
                color: project.enhancedTeam.summary.qualityScore >= 75 ? "#16a34a" :
                       project.enhancedTeam.summary.qualityScore >= 50 ? "#ca8a04" : "#dc2626",
              }}>
                {project.enhancedTeam.summary.qualityScore}
              </span>
              <span style={{ fontSize: 10, color: C.muted }}>/100</span>
            </span>
          </div>
          {project.enhancedTeam.summary.oneLineSummary && (
            <div style={{ fontSize: 12, color: C.text, marginBottom: 10, fontStyle: "italic", lineHeight: 1.5 }}>
              "{project.enhancedTeam.summary.oneLineSummary}"
            </div>
          )}
          <div style={{ display: "flex", gap: 12, marginBottom: 8, fontSize: 11, color: C.muted }}>
            <span>{lang === 'en' ? 'Doxxed: ' : '实名：'}<strong style={{ color: project.enhancedTeam.summary.hasDoxxedFounders ? "#16a34a" : "#dc2626" }}>{project.enhancedTeam.summary.hasDoxxedFounders ? '✓' : '✗'}</strong></span>
            <span>{lang === 'en' ? 'Team size: ' : '团队规模：'}<strong style={{ color: C.text }}>{project.enhancedTeam.summary.teamSize}</strong></span>
          </div>
          {project.enhancedTeam.summary.prominentBackgrounds?.length > 0 && (
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
              {project.enhancedTeam.summary.prominentBackgrounds.map((bg, i) => (
                <span key={i} style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: "#ecfdf5", color: "#059669", fontWeight: 600 }}>{bg}</span>
              ))}
            </div>
          )}
          {project.enhancedTeam.summary.strengths?.length > 0 && (
            <div style={{ marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#16a34a", marginBottom: 4 }}>✓ {lang === 'en' ? 'Strengths' : '强项'}</div>
              {project.enhancedTeam.summary.strengths.map((s, i) => (
                <div key={i} style={{ fontSize: 11, color: C.text, paddingLeft: 12, marginBottom: 2 }}>• {s}</div>
              ))}
            </div>
          )}
          {project.enhancedTeam.summary.concerns?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "#dc2626", marginBottom: 4 }}>⚠ {lang === 'en' ? 'Concerns' : '关注'}</div>
              {project.enhancedTeam.summary.concerns.map((c, i) => (
                <div key={i} style={{ fontSize: 11, color: C.text, paddingLeft: 12, marginBottom: 2 }}>• {c}</div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 创始人 / 团队（来自 RootData）*/}
      <div style={S.card}>
        <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>
          <Users size={11} strokeWidth={2} /> {lang === 'en' ? 'Founders / Core Team (RootData)' : '创始人 / 核心团队（RootData）'}
        </div>
        {/* 官方项目 Twitter */}
        {d?.social_media?.X && (
          <div style={{ marginBottom: 10, padding: "6px 10px", background: "#eff6ff", borderRadius: 6,
            display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span style={{ fontSize: 10, color: "#2563eb", fontWeight: 700 }}>{lang === 'en' ? 'Official Project 𝕏' : '项目官方 𝕏'}</span>
              <span style={{ fontSize: 12, color: C.text, marginLeft: 8, fontWeight: 600 }}>
                @{(d.social_media.X || "").split("/").filter(Boolean).pop()}
              </span>
            </div>
            <a href={d.social_media.X} target="_blank" rel="noreferrer"
              style={{ fontSize: 11, color: "#2563eb", textDecoration: "none" }}>{lang === 'en' ? 'View ↗' : '查看 ↗'}</a>
          </div>
        )}
        {/* RootData 人物列表 */}
        {wi?.founders?.length > 0 ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {wi.founders.map((f, i) => (
              <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: C.surface }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {f.logo && <img src={proxyLogo(f.logo)} alt="" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} onError={e => e.target.style.display = "none"} />}
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{f.name}</div>
                      {f.twitter && (
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          <a href={f.twitter} target="_blank" rel="noreferrer"
                            style={{ fontSize: 11, color: "#2563eb", textDecoration: "none" }}>
                            @{f.twitter.split("/").filter(Boolean).pop()}
                          </a>
                          {/* Twitter 账号年龄徽章 */}
                          {(() => {
                            const age = getTwitterAge(f.twitter);
                            if (!age) return null;
                            const isNew = age.ageYears < 1;
                            return (
                              <span style={{
                                fontSize: 9, padding: "1px 5px", borderRadius: 3, fontWeight: 700,
                                background: isNew ? "#fef2f2" : "#f0fdf4",
                                color: isNew ? "#dc2626" : "#16a34a",
                              }} title={`账号创建于 ${age.created_at?.slice(0, 10)}`}>
                                {isNew ? `⚠ ${(age.ageYears * 12).toFixed(0)}mo` : `✅ ${age.ageYears.toFixed(1)}y`}
                              </span>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {f.twitter && (
                      <a href={f.twitter} target="_blank" rel="noreferrer"
                        style={{ ...S.btn("default"), fontSize: 10, padding: "2px 8px", textDecoration: "none" }}>
                        𝕏
                      </a>
                    )}
                    {f.rootdataurl && (
                      <a href={f.rootdataurl} target="_blank" rel="noreferrer"
                        style={{ ...S.btn("default"), fontSize: 10, padding: "2px 8px", textDecoration: "none" }}>
                        RD
                      </a>
                    )}
                    {(() => {
                      const tm = teamByName[f.name?.toLowerCase?.() || ""];
                      const linkedInUrl = tm?.linkedIn;
                      const linkedInSearchUrl = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((f.name || '') + ' ' + (d?.project_name || ''))}`;
                      if (linkedInUrl) {
                        return (
                          <a href={linkedInUrl} target="_blank" rel="noreferrer"
                            style={{ ...S.btn("default"), fontSize: 10, padding: "2px 8px", textDecoration: "none", background: "#e8f4fd", color: "#0a66c2", borderColor: "#0a66c2" }}>
                            in
                          </a>
                        );
                      }
                      const search = linkedinSearch[f.name] || {};
                      return (
                        <button
                          onClick={() => runLinkedInSearch(f.name)}
                          disabled={search.loading || !f.name?.includes(' ')}
                          title={lang === 'en' ? 'Search LinkedIn (Monid, ~10s)' : '搜索 LinkedIn（走 Monid，约 10 秒）'}
                          style={{ ...S.btn("default"), fontSize: 10, padding: "2px 8px", color: C.muted, cursor: search.loading ? "wait" : "pointer" }}>
                          {search.loading ? '…' : 'in?'}
                        </button>
                      );
                    })()}
                  </div>
                </div>
                {(() => {
                  const search = linkedinSearch[f.name];
                  if (!search?.results) return null;
                  const linkedInSearchUrl = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((f.name || '') + ' ' + (d?.project_name || ''))}`;
                  return search.results.length === 0 ? (
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                      {lang === 'en' ? 'No LinkedIn match found.' : '没有找到匹配的 LinkedIn 账号。'}
                      <a href={linkedInSearchUrl} target="_blank" rel="noreferrer" style={{ marginLeft: 4, color: "#0a66c2" }}>
                        {lang === 'en' ? 'Search manually' : '手动搜索'} ↗
                      </a>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 3, marginTop: 4 }}>
                      {search.results.map((p, pi) => (
                        <a key={pi} href={p.linkedinUrl} target="_blank" rel="noreferrer"
                          style={{ fontSize: 10, color: "#0a66c2", textDecoration: "none", display: "flex", gap: 6, alignItems: "center" }}>
                          <span style={{ fontWeight: 700 }}>{p.name}</span>
                          <span style={{ color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.position || p.location?.linkedinText || ''}</span>
                        </a>
                      ))}
                    </div>
                  );
                })()}
                {f.introduce && (
                  <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.6, background: C.faint,
                    padding: "6px 8px", borderRadius: 5, maxHeight: 120, overflowY: "auto" }}>
                    {f.introduce}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'No team found on RootData. Visit the RootData project page directly.' : '未从 RootData 找到关联人物，可直接访问 RootData 项目页查看'}</div>
        )}
      </div>

      {/* 团队增强（Phase 1: Layer A 官网团队页 + Layer C GitHub 贡献者） */}
      {/* 即使没识别到也渲染卡片，让用户能看到 🔍 诊断信息 */}
      {project.enhancedTeam && (
        <div style={S.card}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><Users size={12} strokeWidth={2} /> {lang === 'en' ? 'Enhanced Team (early-project intel)' : '团队增强（早期项目情报）'}</span>
            <span style={{ display: "flex", gap: 4, alignItems: "center" }}>
              {(project.enhancedTeam.sourcesUsed || []).map(src => {
                const bg =
                  src === 'website'      ? '#eff6ff' :
                  src === 'github'       ? '#f0fdf4' :
                  src === 'linkedin'     ? '#dbeafe' :
                  src === 'rootdata'     ? '#f3e8ff' :
                  src === 'websearch'    ? '#fef3c7' :
                  src === 'ai-knowledge' ? '#fce7f3' :
                  '#fef3c7';
                const fg =
                  src === 'website'      ? '#2563eb' :
                  src === 'github'       ? '#16a34a' :
                  src === 'linkedin'     ? '#0a66c2' :
                  src === 'rootdata'     ? '#7c3aed' :
                  src === 'websearch'    ? '#92400e' :
                  src === 'ai-knowledge' ? '#be185d' :
                  '#1d9bf0';
                const label =
                  src === 'website'      ? (lang === 'en' ? 'Website' : '官网') :
                  src === 'github'       ? 'GitHub' :
                  src === 'twitter'      ? 'Twitter' :
                  src === 'linkedin'     ? 'LinkedIn' :
                  src === 'rootdata'     ? 'RootData' :
                  src === 'websearch'    ? (lang === 'en' ? 'Web' : '网搜') :
                  src === 'ai-knowledge' ? (lang === 'en' ? 'AI recall' : 'AI 召回') :
                  src;
                return (
                  <span key={src} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: bg, color: fg }}>
                    {label}
                  </span>
                );
              })}
              {/* AI 知识库召回时显示明显的"AI 推测"提示 + 说明 */}
              {project.enhancedTeam.sourcesUsed?.includes('ai-knowledge') && (
                <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: '#fef3c7', color: '#92400e', fontStyle: 'italic' }}
                  title={project.enhancedTeam.aiKnowledgeNote || (lang === 'en' ? 'Recalled from AI training data — verify before trust' : 'AI 训练数据召回 — 需人工核对')}>
                  ⚠ {lang === 'en' ? 'AI guess' : 'AI 推测'}
                </span>
              )}
              <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: C.faint, color: C.muted }}>
                {project.enhancedTeam.confidence === 'high'   ? (lang === 'en' ? 'High' : '高') :
                 project.enhancedTeam.confidence === 'medium' ? (lang === 'en' ? 'Medium' : '中') :
                                                                (lang === 'en' ? 'Low' : '低')}
              </span>
              {/* 🔍 诊断按钮 —— 打印 _debug 到控制台 + 弹窗摘要 */}
              {project.enhancedTeam._debug && (
                <button onClick={() => {
                  const d = project.enhancedTeam._debug;
                  console.log('[team-debug] full pipeline state', d);
                  const probedSummary = (d.probedCandidates || []).map(p =>
                    `${p.url.replace(/^https?:\/\//, '').slice(0, 50)}  ok=${p.ok} status=${p.status||'?'} html=${p.htmlLen} kw=${p.hasKeywords}${p.error ? ' err='+p.error : ''}`
                  ).join('\n  ');
                  const claudeSummary = d.compressedLen != null
                    ? `Claude 抽取：\n  压缩后 HTML=${d.compressedLen} 字符\n  Claude 状态=${d.claudeStatus}\n  返回 founders 数=${d.claudeFoundersCount}\n  错误=${d.claudeError || '无'}\n  原始返回前 500 字符：${(d.claudeRawText || '').slice(0, 500)}`
                    : 'Claude 没被调用（probe 失败）';
                  alert(
`[团队增强诊断 · ${project.name || '项目'}]

官网：${d.websiteProbed || '无'}
Twitter bio 兜底 URL：${d.twitterBioUrl || '无'}  使用：${d.twitterFallbackUsed ? '是' : '否'}

探测候选（${(d.probedCandidates || []).length} 个）：
  ${probedSummary || '无'}

胜出 URL：${d.winnerUrl || '无'}

${claudeSummary}

GitHub：${d.githubAttempted ? `已尝试，contributors=${d.githubContributors}` : '未尝试'}
Layer B Twitter：${d.layerBAttempted ? `已尝试 handles=${(d.layerBHandles||[]).join(',')}` : '未尝试'}

耗时(ms)：${JSON.stringify(d.timings || {})}

完整数据已打到控制台 [team-debug]，按 F12 打开 DevTools Console 查看`
                  );
                }} style={{
                  display: "inline-flex", alignItems: "center", gap: 3,
                  fontSize: 9, padding: "1px 6px", borderRadius: 4,
                  background: '#fef3c7', color: '#92400e', border: 'none', cursor: 'pointer',
                }} title={lang === 'en' ? 'Show diagnostics' : '诊断信息'}>
                  <Search size={9} strokeWidth={2} /> {lang === 'en' ? 'Debug' : '诊断'}
                </button>
              )}
            </span>
          </div>

          {/* 未识别到任何团队成员时显示提示 */}
          {project.enhancedTeam.founders?.length === 0 && project.enhancedTeam.topContributors?.length === 0 && (
            <div style={{ padding: 10, background: C.faint, borderRadius: 6, fontSize: 11, color: C.muted }}>
              {lang === 'en'
                ? 'No team members found via website / GitHub. Click Debug to see which steps failed.'
                : '官网/GitHub 均未识别到团队成员。点击 诊断 查看哪个环节失败。'}
            </div>
          )}

          {project.enhancedTeam.founders?.length > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: project.enhancedTeam.topContributors?.length ? 12 : 0 }}>
              {project.enhancedTeam.founders.map((f, i) => (
                <div key={i} style={{ border: `1px solid ${C.border}`, borderRadius: 6, padding: 10, background: C.surface }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4, gap: 10 }}>
                    <div style={{ display: "flex", gap: 10, alignItems: "flex-start", flex: 1, minWidth: 0 }}>
                      {/* 头像（onError 失败时隐藏，避免 broken 图标）*/}
                      {f.avatarUrl && (
                        <img src={f.avatarUrl} alt=""
                          style={{ width: 40, height: 40, borderRadius: "50%", objectFit: "cover", flexShrink: 0, background: C.faint }}
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{f.name}</span>
                          {f.profileUrl && (
                            <a href={f.profileUrl} target="_blank" rel="noreferrer"
                              style={{ fontSize: 9, color: "#2563eb", textDecoration: "none" }} title={lang === 'en' ? 'Profile page' : '个人详情页'}>
                              ↗
                            </a>
                          )}
                        </div>
                        {f.role && <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{f.role}</div>}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", justifyContent: "flex-end" }}>
                      {f.twitter && (
                        <a href={`https://twitter.com/${f.twitter.replace(/^@/, '')}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 10, color: "#2563eb", textDecoration: "none", padding: "1px 6px", background: "#eff6ff", borderRadius: 4 }}>
                          𝕏 {f.twitter.startsWith('@') ? f.twitter : '@' + f.twitter}
                        </a>
                      )}
                      {f.github && (
                        <a href={`https://github.com/${f.github}`} target="_blank" rel="noreferrer"
                          style={{ fontSize: 10, color: "#1f2937", textDecoration: "none", padding: "1px 6px", background: C.faint, borderRadius: 4 }}>
                          GH @{f.github}
                        </a>
                      )}
                      {f.linkedin && (
                        <a href={f.linkedin} target="_blank" rel="noreferrer"
                          style={{ fontSize: 10, color: "#0a66c2", textDecoration: "none", padding: "1px 6px", background: "#eff6ff", borderRadius: 4 }}>
                          LinkedIn
                        </a>
                      )}
                    </div>
                  </div>
                  {f.bio && <div style={{ fontSize: 11, color: C.text, marginTop: 4, lineHeight: 1.5 }}>{f.bio}</div>}
                  {f.priorWork?.length > 0 && (
                    <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 6 }}>
                      {f.priorWork.map((p, j) => (
                        <span key={j} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: C.faint, color: C.text }}>{p}</span>
                      ))}
                    </div>
                  )}
                  {f.sources?.length > 0 && (
                    <div style={{ fontSize: 9, color: C.muted, marginTop: 6 }}>
                      {lang === 'en' ? 'Source: ' : '来源：'}{f.sources.join(' + ')}
                    </div>
                  )}
                  {/* Layer B: Twitter 推文分析 */}
                  {f.tweetAnalysis && (
                    <div style={{ marginTop: 8, padding: 8, background: C.faint, borderRadius: 6, borderLeft: "3px solid #1d9bf0" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "#1d9bf0", marginBottom: 6 }}>
                        <MessageSquare size={11} strokeWidth={2} /> {lang === 'en' ? `Tweet analysis (${f.tweetAnalysis.tweetCount} recent)` : `推文分析（最近 ${f.tweetAnalysis.tweetCount} 条）`}
                      </div>
                      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 6 }}>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'Tech Depth' : '技术深度'}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: f.tweetAnalysis.techDepth >= 7 ? "#16a34a" : f.tweetAnalysis.techDepth >= 4 ? "#ca8a04" : "#dc2626" }}>
                            {f.tweetAnalysis.techDepth}/10
                          </div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'Market Sense' : '市场判断'}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: f.tweetAnalysis.marketJudgment >= 7 ? "#16a34a" : f.tweetAnalysis.marketJudgment >= 4 ? "#ca8a04" : "#dc2626" }}>
                            {f.tweetAnalysis.marketJudgment}/10
                          </div>
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <div style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'Signal/Noise' : '信噪比'}</div>
                          <div style={{ fontSize: 14, fontWeight: 700, color: f.tweetAnalysis.signalToNoise >= 7 ? "#16a34a" : f.tweetAnalysis.signalToNoise >= 4 ? "#ca8a04" : "#dc2626" }}>
                            {f.tweetAnalysis.signalToNoise}/10
                          </div>
                        </div>
                      </div>
                      {f.tweetAnalysis.summary && (
                        <div style={{ fontSize: 11, color: C.text, marginBottom: 6, fontStyle: "italic" }}>
                          "{f.tweetAnalysis.summary}"
                        </div>
                      )}
                      {f.tweetAnalysis.topTopics?.length > 0 && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 4 }}>
                          {f.tweetAnalysis.topTopics.map((t, j) => (
                            <span key={j} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 8, background: "#eff6ff", color: "#1d4ed8" }}>#{t}</span>
                          ))}
                        </div>
                      )}
                      {(f.tweetAnalysis.greenFlags?.length > 0 || f.tweetAnalysis.redFlags?.length > 0) && (
                        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
                          {f.tweetAnalysis.greenFlags?.map((g, j) => (
                            <span key={'g' + j} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: "#ecfdf5", color: "#059669" }}>✓ {g}</span>
                          ))}
                          {f.tweetAnalysis.redFlags?.map((r, j) => (
                            <span key={'r' + j} style={{ fontSize: 9, padding: "1px 6px", borderRadius: 4, background: "#fef2f2", color: "#dc2626" }}>⚠ {r}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {project.enhancedTeam.topContributors?.length > 0 && (
            <div>
              <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>
                {lang === 'en' ? `GitHub top contributors (${project.enhancedTeam.topContributors.length})` : `GitHub 前 ${project.enhancedTeam.topContributors.length} 贡献者`}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {project.enhancedTeam.topContributors.map((c, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", fontSize: 11, padding: "4px 8px", background: C.faint, borderRadius: 4 }}>
                    <a href={`https://github.com/${c.username}`} target="_blank" rel="noreferrer"
                      style={{ color: "#1f2937", textDecoration: "none", fontWeight: 600 }}>
                      @{c.username}
                    </a>
                    <span style={{ color: C.muted }}>
                      {c.commitCount} {lang === 'en' ? 'commits' : '次提交'}
                      {c.followers > 0 && ` · ${c.followers.toLocaleString()} ${lang === 'en' ? 'followers' : '粉丝'}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {project.enhancedTeam.scrapedTeamPageUrl && (
            <div style={{ fontSize: 9, color: C.muted, marginTop: 8, textAlign: "right" }}>
              {lang === 'en' ? 'Scraped from: ' : '抓取自：'}
              <a href={project.enhancedTeam.scrapedTeamPageUrl} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none" }}>
                {project.enhancedTeam.scrapedTeamPageUrl.replace(/^https?:\/\//, '')} ↗
              </a>
            </div>
          )}
        </div>
      )}

      {/* 团队信号卡片 — GitHub 活跃度 + 孵化器 + 风险摘要 */}
      {(gs || ts?.pitchRecord || pedigreeHits.length > 0) && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 12, textTransform: "uppercase" }}>
            <Users size={11} strokeWidth={2} /> {lang === 'en' ? 'Team Signals' : '团队信号'}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>

            {/* GitHub 活跃度 */}
            {gs && (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700 }}>
                  GitHub
                  {gs.url && <a href={gs.url} target="_blank" rel="noreferrer" style={{ color: "#2563eb", textDecoration: "none", marginLeft: 6, fontWeight: 400 }}>↗</a>}
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {/* Fork 警告 */}
                  {gs.isFork && (
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "#fef2f2", color: "#dc2626", fontWeight: 700 }}>
                      ⚠ {lang === 'en' ? 'Forked repo' : 'Fork 仓库'}
                    </span>
                  )}
                  {/* 贡献者数量 */}
                  {gs.contributorCount != null && (
                    <span style={{
                      fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 700,
                      background: gs.contributorCount <= 1 ? "#fef2f2" : gs.contributorCount >= 3 ? "#f0fdf4" : "#fefce8",
                      color: gs.contributorCount <= 1 ? "#dc2626" : gs.contributorCount >= 3 ? "#16a34a" : "#ca8a04",
                    }}>
                      {gs.contributorCount <= 1
                        ? `⚠ ${lang === 'en' ? 'Solo dev' : '单人开发'}`
                        : `✅ ${gs.contributorCount} ${lang === 'en' ? 'contributors' : '位贡献者'}`}
                    </span>
                  )}
                  {/* 4 周提交 */}
                  {gs.commits4w != null && (
                    <span style={{
                      fontSize: 10, padding: "2px 7px", borderRadius: 4,
                      background: gs.commits4w === 0 ? "#fef2f2" : gs.commits4w < 5 ? "#fefce8" : "#f0fdf4",
                      color: gs.commits4w === 0 ? "#dc2626" : gs.commits4w < 5 ? "#ca8a04" : "#16a34a",
                    }}>
                      {gs.commits4w === 0
                        ? `⚠ ${lang === 'en' ? 'No commits (4w)' : '4周零提交'}`
                        : `${gs.commits4w} ${lang === 'en' ? 'commits (4w)' : '次提交（4周）'}`}
                    </span>
                  )}
                  {/* Stars */}
                  {gs.stars != null && (
                    <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: C.faint, color: C.text }}>
                      ⭐ {gs.stars.toLocaleString()}
                    </span>
                  )}
                </div>
                {/* 最后推送 */}
                {gs.lastPushed && (
                  <div style={{ fontSize: 10, color: C.muted }}>
                    {lang === 'en' ? 'Last push:' : '最后推送：'} {gs.lastPushed.slice(0, 10)}
                    {gs.repoCreatedAt && ` · ${lang === 'en' ? 'Repo created:' : '仓库创建：'} ${gs.repoCreatedAt.slice(0, 10)}`}
                  </div>
                )}
              </div>
            )}

            {/* 孵化器 / 加速器背书 */}
            {ts?.pitchRecord?.hasPitchRecord && (
              <div style={{ borderTop: gs ? `1px solid ${C.border}` : 'none', paddingTop: gs ? 8 : 0 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 6 }}>
                  {lang === 'en' ? 'Accelerator / Incubator' : '孵化器 / 加速器'}
                </div>
                {ts.pitchRecord.accelerators?.length > 0 && (
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 4 }}>
                    {ts.pitchRecord.accelerators.map((a, i) => (
                      <span key={i} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "#eff6ff", color: "#2563eb", fontWeight: 700 }}>
                        ✅ {a}
                      </span>
                    ))}
                  </div>
                )}
                {ts.pitchRecord.surfMentions?.length > 0 && ts.pitchRecord.accelerators?.length === 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    {ts.pitchRecord.surfMentions.slice(0, 2).map((m, i) => (
                      <a key={i} href={m.url} target="_blank" rel="noreferrer"
                        style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 10, color: "#2563eb", textDecoration: "none" }}>
                        <Newspaper size={10} strokeWidth={2} /> {(m.title || '').slice(0, 60)}{m.title?.length > 60 ? '…' : ''}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 人才背景信号 — 顶尖机构履历 / 连续创业者（GitHub bio/company 关键词匹配） */}
            {pedigreeHits.length > 0 && (
              <div style={{ borderTop: (gs || ts?.pitchRecord?.hasPitchRecord) ? `1px solid ${C.border}` : 'none', paddingTop: (gs || ts?.pitchRecord?.hasPitchRecord) ? 8 : 0 }}>
                <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 6 }}>
                  {lang === 'en' ? 'Talent Background' : '人才背景'}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  {pedigreeHits.map((c, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 5 }}>
                      <span style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, background: "#faf5ff", color: "#9333ea", fontWeight: 700, whiteSpace: "nowrap" }}>
                        {c.pedigreeSignal.type === 'repeat_founder' ? '🔁' : '🏛'} {c.name}
                      </span>
                      <span style={{ fontSize: 10, color: C.muted, lineHeight: 1.4 }}>{c.pedigreeSignal.evidence}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 无团队信号时的提示 */}
            {!gs && !ts?.pitchRecord?.hasPitchRecord && pedigreeHits.length === 0 && (
              <div style={{ fontSize: 11, color: C.muted }}>
                {lang === 'en' ? 'Re-analyze to fetch GitHub activity and pitch records.' : '重新分析以获取 GitHub 活跃度和路演记录。'}
              </div>
            )}
          </div>
        </div>
      )}

      {/* 高管交易卡片 — SEC Form 4，只在项目能解析到已上市公司 ticker 时渲染 */}
      {tickerInfo && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 12, textTransform: "uppercase" }}>
            <Users size={11} strokeWidth={2} /> {lang === 'en' ? 'Insider Trading' : '高管交易'}
            <span style={{ fontWeight: 400, textTransform: "none", color: C.muted }}>· {tickerInfo.ticker}</span>
          </div>
          {insiderLoading ? (
            <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'Loading SEC filings…' : '正在加载 SEC 披露数据…'}</div>
          ) : !insiderData || insiderData.transactions.length === 0 ? (
            <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'No recent Form 4 filings.' : '近期没有 Form 4 披露记录。'}</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {insiderData.transactions.slice(0, 12).map((t, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 10, flexWrap: "wrap", borderBottom: i < insiderData.transactions.length - 1 ? `1px solid ${C.border}` : 'none', paddingBottom: 6 }}>
                  <span style={{ fontWeight: 700, color: C.text, whiteSpace: "nowrap" }}>{t.insiderName || '—'}</span>
                  <span style={{ color: C.muted, whiteSpace: "nowrap" }}>{t.insiderTitle || (t.isDirector ? (lang === 'en' ? 'Director' : '董事') : t.isTenPercentOwner ? (lang === 'en' ? '10% Owner' : '大股东') : '')}</span>
                  <span style={{
                    padding: "1px 6px", borderRadius: 3, fontWeight: 700, whiteSpace: "nowrap",
                    background: t.codeLabel.kind === 'buy' ? "#f0fdf4" : t.codeLabel.kind === 'sell' ? "#fef2f2" : C.faint,
                    color: t.codeLabel.kind === 'buy' ? "#16a34a" : t.codeLabel.kind === 'sell' ? "#dc2626" : C.muted,
                  }}>
                    {lang === 'en' ? t.codeLabel.en : t.codeLabel.zh}
                  </span>
                  <span style={{ color: C.text, whiteSpace: "nowrap" }}>{t.shares.toLocaleString()} {lang === 'en' ? 'sh' : '股'}</span>
                  <span style={{ color: C.muted, whiteSpace: "nowrap" }}>@ ${t.pricePerShare.toFixed(2)}</span>
                  {t.is10b51Plan && (
                    <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: C.faint, color: C.muted }} title={lang === 'en' ? 'Pre-scheduled 10b5-1 plan trade, not a discretionary decision' : '10b5-1 计划内既定交易，非当下主动决策'}>
                      10b5-1
                    </span>
                  )}
                  {t.nearEarnings && (
                    <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: "#fefce8", color: "#ca8a04", fontWeight: 700 }} title={lang === 'en' ? 'Within 5 days of a recent 8-K filing' : '距最近一次 8-K 披露 5 天以内'}>
                      {lang === 'en' ? 'Near filing' : '近披露'}
                    </span>
                  )}
                  <span style={{ marginLeft: "auto", fontSize: 9, color: C.muted, whiteSpace: "nowrap" }}>{t.transactionDate}</span>
                </div>
              ))}
              <a href={`https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${tickerInfo.cik}&type=4&dateb=&owner=include&count=40`} target="_blank" rel="noreferrer"
                style={{ fontSize: 10, color: "#2563eb", textDecoration: "none", marginTop: 2 }}>
                {lang === 'en' ? 'View all filings on SEC EDGAR ↗' : '在 SEC EDGAR 查看全部披露 ↗'}
              </a>
            </div>
          )}
        </div>
      )}

      {/* 白皮书 / 文档摘要 */}
      {(() => {
        // 文档链接优先级：RootData docsUrl → Wayback firstDoc 原始 URL
        let effectiveDocsUrl = wi?.docsUrl || "";
        let docsSource = "";
        if (!effectiveDocsUrl) {
          const firstDocEntry = project.timeline?.find(t => t.key === "firstDoc");
          if (firstDocEntry?.url) {
            // 从 Wayback URL 提取原始 URL：https://web.archive.org/web/20241204033538/https://docs.xxx.fi/
            const wbMatch = firstDocEntry.url.match(/web\.archive\.org\/web\/\d+\/(.+)/);
            if (wbMatch) {
              const orig = wbMatch[1].startsWith("http") ? wbMatch[1] : `https://${wbMatch[1]}`;
              effectiveDocsUrl = orig;
              docsSource = "wayback";
            }
          }
        }
        return (
          <div style={S.card}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><FileText size={12} strokeWidth={2} /> {lang === 'en' ? 'Whitepaper / Docs' : '白皮书 / 技术文档'}</span>
              {effectiveDocsUrl && (
                <a href={effectiveDocsUrl} target="_blank" rel="noreferrer"
                  style={{ fontSize: 10, color: "#2563eb", fontWeight: 400, textDecoration: "none" }}>
                  {docsSource === "wayback"
                    ? (lang === 'en' ? 'View (via Archive) ↗' : '查看文档（来自存档）↗')
                    : (lang === 'en' ? 'View ↗' : '查看原文 ↗')}
                </a>
              )}
            </div>
            {wi?.whitepaperSummary ? (
              <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.8, maxHeight: 200,
                overflowY: "auto", background: C.faint, padding: "8px 10px", borderRadius: 6 }}>
                {wi.whitepaperSummary}
                {docsSource === "wayback" && (
                  <div style={{ marginTop: 6, fontSize: 10, color: "#d97706" }}>
                    ⚠ {lang === 'en' ? 'Docs link discovered via Wayback Machine archive' : '文档链接来自 Wayback Machine 存档发现'}
                  </div>
                )}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: C.muted }}>
                {effectiveDocsUrl
                  ? (lang === 'en' ? 'Docs link found but summary pending — re-analyze to generate' : '已发现文档链接，摘要待生成 — 重新分析即可获取')
                  : (lang === 'en' ? 'No whitepaper or docs link found' : '未找到白皮书或技术文档链接')}
              </div>
            )}
          </div>
        );
      })()}

      {/* 官网产品描述 */}
      {wi?.websiteKeywords?.[0] && (
        <div style={S.card}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>
            <MessageSquare size={12} strokeWidth={2} /> {lang === 'en' ? 'Website Description (Meta)' : '官网产品描述（Meta）'}
          </div>
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7, fontStyle: "italic" }}>
            "{wi.websiteKeywords[0]}"
          </div>
        </div>
      )}

      {/* 技术产品深度分析（Claude 二次分析）
          已隐藏（2026-06-12）：内容已被 🧠 项目画像（projectContext）超集覆盖，
          但 fetchTechIntel API 和 scoreProject prompt 依赖（traffic / features / stage）仍在 raw 中保留。
          仅 UI 卡停止渲染。 */}
      {false && wi?.techIntel && (
        <div style={S.card}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 12, textTransform: "uppercase" }}>
            🔬 {lang === 'en' ? 'Tech & Product Analysis (AI)' : '技术产品分析（AI 深度分析）'}
          </div>

          {/* 产品阶段 + GitHub */}
          <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
            <span style={{ ...S.tag, background: "#eff6ff", color: "#2563eb", fontSize: 11, padding: "3px 10px" }}>
              📍 {lang === 'en' ? (wi.techIntel.product_stage_en || wi.techIntel.product_stage) : wi.techIntel.product_stage}
            </span>
            <span style={{ ...S.tag, fontSize: 11, padding: "3px 10px",
              background: (wi.techIntel.github_activity?.includes("活跃") || wi.techIntel.github_activity_en?.toLowerCase().includes("active")) ? "#ecfdf5" : C.faint,
              color: (wi.techIntel.github_activity?.includes("活跃") || wi.techIntel.github_activity_en?.toLowerCase().includes("active")) ? "#059669" : C.muted }}>
              ⚡ GitHub: {lang === 'en' ? (wi.techIntel.github_activity_en || wi.techIntel.github_activity) : wi.techIntel.github_activity}
            </span>
          </div>

          {/* 核心功能 */}
          {wi.techIntel.product_features?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 5 }}>{lang === 'en' ? 'Core Features' : '核心功能'}</div>
              {(lang === 'en' && wi.techIntel.product_features_en?.length ? wi.techIntel.product_features_en : wi.techIntel.product_features).map((f, i) => (
                <div key={i} style={{ fontSize: 11, color: "#374151", marginBottom: 3, display: "flex", gap: 6 }}>
                  <span style={{ color: "#10b981", fontWeight: 700 }}>✓</span>{f}
                </div>
              ))}
            </div>
          )}

          {/* 技术栈 */}
          {wi.techIntel.tech_stack?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 5 }}>{lang === 'en' ? 'Tech Stack' : '技术栈'}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {wi.techIntel.tech_stack.map((t, i) => <span key={i} style={{ ...S.tag, fontSize: 10 }}>{t}</span>)}
              </div>
            </div>
          )}

          {/* 差异化优势 */}
          {wi.techIntel.differentiators?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 5 }}>{lang === 'en' ? 'Differentiators' : '技术差异化'}</div>
              {(lang === 'en' && wi.techIntel.differentiators_en?.length ? wi.techIntel.differentiators_en : wi.techIntel.differentiators).map((d, i) => (
                <div key={i} style={{ fontSize: 11, color: "#374151", marginBottom: 3, display: "flex", gap: 6 }}>
                  <span style={{ color: "#f59e0b", fontWeight: 700 }}>★</span>{d}
                </div>
              ))}
            </div>
          )}

          {/* 可量化数据 */}
          {wi.techIntel.data_evidence?.filter(Boolean).length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 5 }}>{lang === 'en' ? 'Quantifiable Data' : '可量化数据'}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {(lang === 'en' && wi.techIntel.data_evidence_en?.length ? wi.techIntel.data_evidence_en : wi.techIntel.data_evidence).map((d, i) => (
                  <span key={i} style={{ ...S.tag, background: "#ecfdf5", color: "#059669", fontSize: 10 }}>{d}</span>
                ))}
              </div>
            </div>
          )}

          {/* 竞品对比 + 评分依据 */}
          <div style={{ borderTop: `1px solid ${C.faint}`, paddingTop: 8, marginTop: 4 }}>
            {wi.techIntel.competitor_comparison && (
              <div style={{ fontSize: 11, color: C.muted, marginBottom: 4 }}>
                {lang === 'en' ? 'Competitor Comparison: ' : '竞品对比：'}{lang === 'en' ? (wi.techIntel.competitor_comparison_en || wi.techIntel.competitor_comparison) : wi.techIntel.competitor_comparison}
              </div>
            )}
            {wi.techIntel.tech_score_basis && (
              <div style={{ fontSize: 10, color: C.muted, background: "#fffbeb",
                padding: "4px 8px", borderRadius: 4, borderLeft: "3px solid #f59e0b" }}>
                {lang === 'en' ? 'Score Basis: ' : '评分依据：'}{lang === 'en' ? (wi.techIntel.tech_score_basis_en || wi.techIntel.tech_score_basis) : wi.techIntel.tech_score_basis}
              </div>
            )}
          </div>

          <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>
            ⚠ {lang === 'en' ? 'Based on AI knowledge base; may not reflect current state. Verify with official sources.' : '基于 AI 知识库分析，可能不反映最新状态，建议结合官网核实'}
          </div>
        </div>
      )}
      )}

      {/* 外部链接 */}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        {d?.social_media?.website && (
          <a href={d.social_media.website} target="_blank" rel="noreferrer" style={{ ...S.btn("default"), textDecoration: "none", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }}><Globe size={12} strokeWidth={2} /> {lang === 'en' ? 'Website' : '官网'}</a>
        )}
        {d?.rootdataurl && (
          <a href={d.rootdataurl} target="_blank" rel="noreferrer" style={{ ...S.btn("default"), textDecoration: "none", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }}><BarChart3 size={12} strokeWidth={2} /> RootData</a>
        )}
        {d?.social_media?.twitter && (
          <a href={d.social_media.twitter} target="_blank" rel="noreferrer" style={{ ...S.btn("default"), textDecoration: "none", fontSize: 11 }}>𝕏 Twitter</a>
        )}
        {wi?.docsUrl && (
          <a href={wi.docsUrl} target="_blank" rel="noreferrer" style={{ ...S.btn("default"), textDecoration: "none", fontSize: 11, display: "inline-flex", alignItems: "center", gap: 5 }}><FileText size={12} strokeWidth={2} /> Docs</a>
        )}
      </div>

    </div>
  );
}

function NotesDiary({ notes = [], onAdd, projectName, history = [] }) {
  const { lang } = useLang();
  const [text, setText] = useState("");
  const [type, setType] = useState("research");
  const textRef = useRef(null);

  const handleAdd = () => {
    if (!text.trim()) return;
    onAdd({
      id: Date.now(),
      text: text.trim(),
      type,
      createdAt: new Date().toISOString(),
    });
    setText("");
    textRef.current?.focus();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {/* 输入区 */}
      <div style={S.card}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>
          {lang === 'en' ? 'Add Follow-up Note' : '添加跟进记录'}
        </div>
        {/* 类型选择 */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 8 }}>
          {NOTE_TYPES.map(nt => (
            <button key={nt.id} onClick={() => setType(nt.id)}
              style={{ padding: "2px 8px", borderRadius: 4, border: `1px solid ${type === nt.id ? nt.color : C.border}`,
                background: type === nt.id ? nt.bg : C.surface, color: type === nt.id ? nt.color : C.muted,
                fontSize: 11, fontWeight: type === nt.id ? 700 : 400, cursor: "pointer" }}>
              {lang === 'en' ? nt.label_en : nt.label}
            </button>
          ))}
        </div>
        <textarea ref={textRef} value={text} onChange={e => setText(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && e.metaKey) handleAdd(); }}
          placeholder={lang === 'en'
            ? `Log your ${NOTE_TYPES.find(t => t.id === type)?.label_en || ""}... (⌘+Enter to submit)`
            : `记录你的${NOTE_TYPES.find(t => t.id === type)?.label || ""}... (⌘+Enter 提交)`}
          rows={3}
          style={{ ...S.input, resize: "vertical", fontFamily: "inherit", lineHeight: 1.6, marginBottom: 8 }} />
        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <button onClick={handleAdd} disabled={!text.trim()}
            style={{ ...S.btn("primary"), fontSize: 11, padding: "5px 14px", opacity: text.trim() ? 1 : 0.5 }}>
            {lang === 'en' ? '+ Add Note' : '+ 添加记录'}
          </button>
        </div>
      </div>

      {/* 笔记列表 */}
      {notes.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {[...notes].reverse().map(note => {
            const t = NOTE_TYPES.find(t => t.id === note.type) || NOTE_TYPES[5];
            const d = new Date(note.createdAt);
            return (
              <div key={note.id} style={{ ...S.card, borderLeft: `3px solid ${t.color}`, padding: "10px 12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: t.color, background: t.bg,
                    padding: "1px 7px", borderRadius: 3 }}>{lang === 'en' ? t.label_en : t.label}</span>
                  <span style={{ fontSize: 10, color: C.muted }}>
                    {d.toLocaleDateString(lang === 'en' ? "en-US" : "zh-CN")} {d.toLocaleTimeString(lang === 'en' ? "en-US" : "zh-CN", { hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>
                <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>{note.text}</div>
              </div>
            );
          })}
        </div>
      ) : (
        <div style={{ ...S.card, textAlign: "center", padding: 24, color: C.muted }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}><FileText size={20} strokeWidth={2} /></div>
          <div style={{ fontSize: 12 }}>{lang === 'en' ? 'No follow-up notes yet' : '还没有跟进记录'}</div>
          <div style={{ fontSize: 11, marginTop: 4 }}>{lang === 'en' ? 'Log research findings, meeting notes, risks and decisions' : '记录调研发现、会议纪要、风险点和决策依据'}</div>
        </div>
      )}

    </div>
  );
}

// ══════════════════════════════════════════════════════
//  CHAIN SIGNALS PANEL
// ══════════════════════════════════════════════════════
function CommitSparkline({ weeks }) {
  if (!weeks || weeks.length === 0) return null;
  const max = Math.max(...weeks, 1);
  return (
    <div style={{ display: "flex", gap: 3, alignItems: "flex-end", height: 24 }}>
      {weeks.map((v, i) => (
        <div key={i} style={{
          width: 10, height: Math.max(2, Math.round((v / max) * 24)),
          background: v > 0 ? "#16a34a" : "#e4e4e7", borderRadius: 2,
        }} title={`第${i + 1}周: ${v} commits`} />
      ))}
    </div>
  );
}

// 30天 DEX 交易量柱状迷你图（Dune Analytics）
// ── Dune 30日 DEX 成交量图表（SVG，带日期标签 + 交易者折线）────
function DuneVolumeChart({ days, compact = false }) {
  if (!days || days.length === 0) return null;
  const W = 100; // viewBox 百分比宽度单位
  const BAR_H = compact ? 36 : 64;
  const LABEL_H = compact ? 0 : 14;
  const TRADER_H = compact ? 0 : 14;
  const totalH = BAR_H + LABEL_H + TRADER_H;
  const n = days.length;
  const vols = days.map(d => d.volume_usd || 0);
  const maxVol = Math.max(...vols, 1);
  const traders = days.map(d => d.unique_traders || 0);
  const maxTraders = Math.max(...traders, 1);
  const barW = W / n;
  const gap = Math.max(0.3, barW * 0.12);

  // 稀疏标签：每 ~7 天一个
  const labelStep = Math.ceil(n / 5);
  const fmtDay = (s) => {
    if (!s) return '';
    const parts = s.split('-');
    return parts.length >= 3 ? `${parts[1]}/${parts[2]}` : s;
  };
  const fmtVol = (v) =>
    v >= 1e9 ? `$${(v/1e9).toFixed(1)}B`
    : v >= 1e6 ? `$${(v/1e6).toFixed(1)}M`
    : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K`
    : `$${v.toFixed(0)}`;

  // 趋势色：高量=紫，低量=暗
  const barColor = (v) => {
    const ratio = v / maxVol;
    if (ratio > 0.6) return '#818cf8';
    if (ratio > 0.3) return '#6366f1';
    if (ratio > 0) return '#4338ca';
    return '#e4e4e7';
  };

  return (
    <svg
      viewBox={`0 0 ${W} ${totalH}`}
      style={{ width: '100%', height: compact ? 44 : 100, display: 'block', overflow: 'visible' }}
      preserveAspectRatio="none"
    >
      {/* 量柱 */}
      {days.map((d, i) => {
        const bh = Math.max(1.5, (vols[i] / maxVol) * BAR_H);
        const x = i * barW + gap / 2;
        const w = barW - gap;
        const y = BAR_H - bh;
        const tip = `${d.day}  成交量 ${fmtVol(vols[i])}  交易 ${d.trades?.toLocaleString() || 0} 笔  独立交易者 ${traders[i].toLocaleString()}`;
        return (
          <rect
            key={i}
            x={x} y={y} width={w} height={bh}
            fill={barColor(vols[i])}
            rx={0.4}
            opacity={0.85}
          >
            <title>{tip}</title>
          </rect>
        );
      })}
      {/* X 轴日期标签 */}
      {!compact && days.map((d, i) => {
        if (i % labelStep !== 0 && i !== n - 1) return null;
        const cx = (i + 0.5) * barW;
        return (
          <text key={`lbl-${i}`} x={cx} y={BAR_H + 10} textAnchor="middle"
            fontSize="3.5" fill="#64748b" fontFamily="sans-serif">
            {fmtDay(d.day)}
          </text>
        );
      })}
      {/* 独立交易者折线（右轴归一化） */}
      {!compact && traders.some(t => t > 0) && (() => {
        const pts = traders.map((t, i) => {
          const cx = (i + 0.5) * barW;
          const cy = BAR_H + LABEL_H + TRADER_H - Math.max(1, (t / maxTraders) * (TRADER_H - 2)) - 1;
          return `${cx},${cy}`;
        }).join(' ');
        return (
          <polyline
            points={pts}
            fill="none"
            stroke="#22d3ee"
            strokeWidth="0.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity="0.7"
          />
        );
      })()}
    </svg>
  );
}

// 保留旧名以兼容现有调用点
const VolumeSparkline = ({ days }) => <DuneVolumeChart days={days} compact />;

// ══════════════════════════════════════════════════════
//  SHARE CARD（分享卡片，用于截图导出）
// ══════════════════════════════════════════════════════
function ShareCard({ project, innerRef, surfMindshare }) {
  const { lang } = useLang();
  const a = project.analysis;
  if (!a) return null;

  const r          = rating(a.composite || 0);
  const ratingColor = r.fg;
  const stage      = STAGE_WEIGHTS[a.stage_key || "early"];
  const logo       = proxyLogo(project.details?.logo_url);
  const token      = project.details?.token_symbol;
  const now        = new Date();
  const dateStr    = `${now.getFullYear()}/${String(now.getMonth()+1).padStart(2,"0")}/${String(now.getDate()).padStart(2,"0")}`;

  const stageColor = a.stage_key === "tge_post" ? "#8B5CF6" : a.stage_key === "growth" ? "#10B981" : "#F59E0B";
  const stageLabel = lang === 'en' ? (stage?.label_en || "Early Stage") : (stage?.label || "早期阶段");

  // 六维按得分排序
  const sortedDims = [...DIMS].sort((x, y) => (a.scores?.[y.key] || 0) - (a.scores?.[x.key] || 0));

  // 关键指标
  const tvl      = project.defiData?.protocol?.tvl;
  const poolSize = project.duneTokenInfo?.poolSize;
  const commits  = project.githubSignal?.commits4w;
  const tw       = project.cgData?.community_data?.twitter_followers;
  const metricItems = [
    tvl      && { icon: "📈", label: "TVL",      value: fmt(tvl) },
    poolSize && { icon: "💧", label: lang === 'en' ? "Liquidity" : "流动性", value: fmt(poolSize) },
    commits  != null && { icon: "⚡", label: "Commits", value: commits },
    tw       && { icon: "𝕏",  label: lang === 'en' ? "Followers" : "关注", value: tw >= 1000 ? `${(tw/1000).toFixed(0)}K` : tw },
  ].filter(Boolean).slice(0, 4);

  // 核心洞察文本
  const insight = a.moat || (a.summary || "").slice(0, 85) + ((a.summary || "").length > 85 ? "…" : "");

  return (
    <div ref={innerRef} style={{
      width: 600,
      background: "linear-gradient(150deg, #060B18 0%, #0C1526 50%, #08101E 100%)",
      fontFamily: "'PingFang SC','Helvetica Neue','Arial',sans-serif",
      color: "#f1f5f9",
      borderRadius: 20,
      overflow: "hidden",
      border: "1px solid #1A2744",
    }}>

      {/* ── HEADER BAR (48px) ── */}
      <div style={{
        height: 48, padding: "0 24px",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        borderBottom: "1px solid",
        borderImage: "linear-gradient(90deg, #1A2744, transparent) 1",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ width: 10, height: 10, background: "#3B82F6", borderRadius: 2, transform: "rotate(45deg)" }} />
          <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: "0.08em", fontVariant: "small-caps", color: "#93C5FD" }}>PrimaryLens</span>
        </div>
        <span style={{ fontSize: 11, color: "#64748B" }}>{dateStr}</span>
      </div>

      {/* ── MAIN CONTENT ── */}
      <div style={{ padding: "24px 28px" }}>

        {/* Row A — Project Identity */}
        <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20 }}>
          {logo ? (
            <img src={logo} alt="" crossOrigin="anonymous"
              style={{ width: 64, height: 64, borderRadius: 14, objectFit: "contain", background: "#1E293B", padding: 4, flexShrink: 0 }} />
          ) : (
            <div style={{
              width: 64, height: 64, borderRadius: 14, flexShrink: 0,
              background: `linear-gradient(135deg, ${ratingColor}33, #1E293B)`,
              border: `1px solid ${ratingColor}44`,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <span style={{ fontSize: 26, fontWeight: 900, color: ratingColor }}>
                {(project.name || "?")[0].toUpperCase()}
              </span>
            </div>
          )}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 22, fontWeight: 800, color: "#F8FAFC", lineHeight: 1.2, marginBottom: 6 }}>{project.name}</div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {token && (
                <span style={{ fontSize: 11, background: "#1E293B", color: "#94A3B8", padding: "2px 9px", borderRadius: 6 }}>
                  ${token}
                </span>
              )}
              <span style={{ fontSize: 11, background: stageColor + "22", color: stageColor, padding: "2px 9px", borderRadius: 6, fontWeight: 600 }}>
                {stageLabel}
              </span>
            </div>
          </div>
          {/* Large rating badge */}
          <div style={{
            width: 54, height: 54, borderRadius: 12, flexShrink: 0,
            background: ratingColor,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
          }}>
            <span style={{ fontSize: 28, fontWeight: 900, color: "#fff", lineHeight: 1 }}>{r.label}</span>
          </div>
        </div>

        {/* Row B — Score circle + dimension bars */}
        <div style={{ display: "flex", gap: 24, alignItems: "center", marginTop: 20 }}>
          {/* Score circle */}
          <div style={{
            width: 92, height: 92, borderRadius: "50%", flexShrink: 0,
            border: `3px solid ${ratingColor}`,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
          }}>
            <div style={{ fontSize: 32, fontWeight: 900, color: ratingColor, lineHeight: 1 }}>{a.composite}</div>
            <div style={{ fontSize: 9, color: "#64748B", marginTop: 3 }}>综合评分</div>
          </div>

          {/* Dimension bars */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 6 }}>
            {sortedDims.map(d => {
              const sc = a.scores?.[d.key] || 0;
              return (
                <div key={d.key} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ fontSize: 10, color: "#94A3B8", width: 32, flexShrink: 0 }}>{d.short}</div>
                  <div style={{ flex: 1, height: 5, background: "#1A2744", borderRadius: 3 }}>
                    <div style={{ width: `${sc}%`, height: "100%", background: d.color, borderRadius: 3 }} />
                  </div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: d.color, width: 24, textAlign: "right", flexShrink: 0 }}>{sc}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: "#1A2744", margin: "20px 0" }} />

        {/* Row C — Key Insight */}
        {insight && (
          <div style={{
            background: "rgba(59,130,246,0.07)",
            borderLeft: "3px solid #3B82F6",
            borderRadius: "0 10px 10px 0",
            padding: "12px 16px",
          }}>
            <div style={{ fontSize: 10, color: "#3B82F6", fontWeight: 700, marginBottom: 4 }}>💡 核心洞察</div>
            <div style={{ fontSize: 12.5, color: "#CBD5E1", lineHeight: 1.7 }}>{insight}</div>
          </div>
        )}

        {/* Row C2 — Investment Verdict (compact) — PR #2.C 新增 */}
        <div style={{ marginTop: 12 }}>
          <VerdictCard project={project} a={a} lang={lang} mode="compact" />
        </div>

        {/* Row D — Key Metrics */}
        {metricItems.length > 0 && (
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            {metricItems.map(({ icon, label, value }) => (
              <div key={label} style={{
                flex: 1, background: "rgba(255,255,255,0.04)", borderRadius: 8,
                padding: "9px 12px", textAlign: "center",
              }}>
                <div style={{ fontSize: 10, color: "#64748B", marginBottom: 4 }}>{icon} {label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#F1F5F9" }}>{value}</div>
              </div>
            ))}
          </div>
        )}

        {/* Mindshare sparkline row */}
        {surfMindshare?.data?.length > 1 && (() => {
          const ms = surfMindshare.data;
          const latest = ms[ms.length - 1].value;
          const prev7 = ms[Math.max(0, ms.length - 8)].value;
          const delta = prev7 > 0 ? Number(((latest - prev7) / prev7 * 100).toFixed(1)) : null;
          const fmtMs = v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : String(Math.round(v));
          const isUp = delta !== null && delta >= 0;
          return (
            <div style={{ margin: "14px 0 0", padding: "10px 14px", background: "rgba(167,139,250,0.08)", borderRadius: 8, border: "1px solid rgba(167,139,250,0.2)" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                <span style={{ fontSize: 9, color: "#a78bfa", fontWeight: 700, letterSpacing: 0.8, textTransform: "uppercase" }}>Twitter Mindshare · Surf</span>
                <span style={{ fontSize: 11, fontWeight: 800, color: isUp ? "#10b981" : "#ef4444" }}>
                  {fmtMs(latest)} {delta !== null ? `${isUp ? '↑' : '↓'}${Math.abs(delta)}% 7d` : ''}
                </span>
              </div>
              <MiniSparkline data={ms} color="#a78bfa" height={20} />
            </div>
          );
        })()}
      </div>

      {/* ── 四象限信号标签 ── */}
      {(() => {
        const q = calcNarrativeChainQuadrant(project);
        if (!q) return null;
        return (
          <div style={{ margin: "0 28px 20px", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{
              display: "flex", alignItems: "center", gap: 6,
              background: q.bg || "rgba(255,255,255,0.04)",
              border: `1px solid ${q.color}44`,
              borderRadius: 20, padding: "5px 14px",
            }}>
              <span style={{ fontSize: 13 }}>{q.emoji}</span>
              <span style={{ fontSize: 11, fontWeight: 700, color: q.color }}>{q.label}</span>
            </div>
            <span style={{ fontSize: 11, color: "#64748B", flex: 1 }}>{q.desc}</span>
          </div>
        );
      })()}

      {/* ── FOOTER ── */}
      <div style={{
        height: 44, padding: "0 28px",
        background: "rgba(59,130,246,0.06)",
        borderTop: "1px solid #1A2744",
        display: "flex", alignItems: "center", justifyContent: "space-between",
      }}>
        <span style={{ fontSize: 12, color: "#3B82F6", fontWeight: 700 }}>primarylens.app</span>
        <span style={{ fontSize: 10, color: "#475569" }}>独立研究 · 非投资建议</span>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  SHARE MODAL
// ══════════════════════════════════════════════════════
function ShareModal({ project, onClose, surfMindshare }) {
  const cardRef = useRef(null);
  const [capturing, setCapturing] = useState(false);

  const getCanvas = async () => {
    return html2canvas(cardRef.current, {
      useCORS: true,
      allowTaint: false,
      backgroundColor: null,
      scale: 2,
      logging: false,
    });
  };

  const handleDownload = async () => {
    setCapturing(true);
    try {
      const canvas = await getCanvas();
      const link = document.createElement("a");
      link.download = `PrimaryLens-${project.name}-${new Date().toISOString().slice(0,10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      track('share_screenshot', {
        project_name: project.name,
        action: 'download',
        composite: project.analysis?.composite ?? null,
      });
    } finally {
      setCapturing(false);
    }
  };

  const handleTwitter = async () => {
    setCapturing(true);
    try {
      const canvas = await getCanvas();
      const link = document.createElement("a");
      link.download = `PrimaryLens-${project.name}-${new Date().toISOString().slice(0,10)}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      track('share_screenshot', {
        project_name: project.name,
        action: 'twitter',
        composite: project.analysis?.composite ?? null,
      });
      const a = project.analysis;
      const token = project.details?.token_symbol;
      const r = rating(a?.composite || 0);
      const moat = a?.moat || (a?.summary || "").slice(0, 60);
      const tweetText = [
        `🔍 ${token ? "$" + token : project.name} 深度研究 | PrimaryLens`,
        ``,
        `综合评分 ${a?.composite}/100 · ${r.label}级`,
        moat ? `💡 ${moat}` : "",
        ``,
        `#Web3 #Crypto`,
        `primarylens.app`,
      ].filter(l => l !== undefined).join("\n");
      setTimeout(() => {
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(tweetText)}`, "_blank");
      }, 800);
    } finally {
      setCapturing(false);
    }
  };

  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: "fixed", inset: 0,
        background: "rgba(0,0,0,0.4)",
        zIndex: 9999,
        display: "flex", alignItems: "center", justifyContent: "center",
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        style={{
          position: "fixed", top: 20, right: 24,
          background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
          color: "#94A3B8", borderRadius: 8, width: 32, height: 32,
          cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center",
        }}
      >✕</button>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20 }}>
        {/* Card preview */}
        <div style={{ borderRadius: 20, overflow: "hidden", boxShadow: "0 8px 32px rgba(0,0,0,0.35)" }}>
          <ShareCard project={project} innerRef={cardRef} surfMindshare={surfMindshare} />
        </div>

        {/* Hint */}
        <div style={{ fontSize: 11, color: "#64748B", textAlign: "center", maxWidth: 480 }}>
          提示：分享到 Twitter 时，图片会先下载到本地，请在推文编辑框中手动附上图片
        </div>

        {/* Action buttons */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
            color: "#94A3B8", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontSize: 13,
          }}>
            ✕ 取消
          </button>
          <button onClick={handleDownload} disabled={capturing} style={{
            background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
            color: "#F1F5F9", borderRadius: 8, padding: "8px 18px", cursor: capturing ? "not-allowed" : "pointer",
            fontSize: 13, opacity: capturing ? 0.6 : 1,
          }}>
            ⬇ 下载图片
          </button>
          <button onClick={handleTwitter} disabled={capturing} style={{
            background: "#000", border: "1px solid #333",
            color: "#fff", borderRadius: 8, padding: "8px 18px",
            cursor: capturing ? "not-allowed" : "pointer", fontSize: 13, opacity: capturing ? 0.6 : 1,
          }}
            onMouseEnter={e => { if (!capturing) e.currentTarget.style.background = "#1a1a1a"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#000"; }}
          >
            𝕏 分享到 Twitter
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  PROJECT BADGE（项目认证徽章）
// ══════════════════════════════════════════════════════
function BadgeCard({ project, innerRef }) {
  const a = project.analysis;
  if (!a) return null;
  const r     = rating(a.composite || 0);
  const logo  = proxyLogo(project.details?.logo_url);
  const token = project.details?.token_symbol;
  return (
    <div ref={innerRef} style={{
      width: 340, height: 80,
      background: "linear-gradient(135deg, #060B18 0%, #0C1526 100%)",
      border: "1px solid #1A2744",
      borderRadius: 12,
      fontFamily: "'PingFang SC','Helvetica Neue','Arial',sans-serif",
      display: "flex", alignItems: "center",
      padding: "0 16px", gap: 12, overflow: "hidden",
    }}>
      {/* Logo */}
      {logo
        ? <img src={logo} alt="" crossOrigin="anonymous" style={{ width: 40, height: 40, borderRadius: 8, objectFit: "contain", background: "#1E293B", padding: 3, flexShrink: 0 }} />
        : <div style={{ width: 40, height: 40, borderRadius: 8, background: r.fg + "22", border: `1px solid ${r.fg}44`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <span style={{ fontSize: 18, fontWeight: 900, color: r.fg }}>{(project.name || "?")[0].toUpperCase()}</span>
          </div>
      }
      {/* Info */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#F8FAFC", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{project.name}</div>
        <div style={{ fontSize: 10, color: "#64748B", marginTop: 2 }}>
          {token ? `$${token} · ` : ""}
          <span style={{ color: "#3B82F6" }}>primarylens.app</span>
        </div>
      </div>
      {/* Score stamp */}
      <div style={{ flexShrink: 0, textAlign: "center" }}>
        <div style={{ fontSize: 22, fontWeight: 900, color: r.fg, lineHeight: 1 }}>{a.composite}</div>
        <div style={{
          marginTop: 3, fontSize: 11, fontWeight: 800, color: "#fff",
          background: r.fg, borderRadius: 5, padding: "1px 7px", letterSpacing: 1,
        }}>{r.label}</div>
      </div>
    </div>
  );
}

function BadgeModal({ project, onClose }) {
  const badgeRef = useRef(null);
  const [capturing, setCapturing] = useState(false);
  const [copied, setCopied] = useState(false);
  const { lang } = useLang();

  const embedCode = `<a href="https://primarylens.app/?q=${encodeURIComponent(project.name)}" target="_blank" rel="noopener">\n  <img src="primarylens-badge-${project.name.replace(/\s+/g,"-")}.png" alt="PrimaryLens: ${project.name} 综合评分 ${project.analysis?.composite}/100 ${rating(project.analysis?.composite||0).label}级" style="height:60px" />\n</a>`;

  const handleDownload = async () => {
    setCapturing(true);
    try {
      const canvas = await html2canvas(badgeRef.current, { useCORS: true, allowTaint: false, backgroundColor: null, scale: 3, logging: false });
      const link = document.createElement("a");
      link.download = `primarylens-badge-${project.name.replace(/\s+/g,"-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally { setCapturing(false); }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(embedCode).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };

  return (
    <div onClick={e => { if (e.target === e.currentTarget) onClose(); }} style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9999,
      display: "flex", alignItems: "center", justifyContent: "center",
    }}>
      <button onClick={onClose} style={{ position: "fixed", top: 20, right: 24, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", color: "#94A3B8", borderRadius: 8, width: 32, height: 32, cursor: "pointer", fontSize: 16, display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>

      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 20, maxWidth: 500, width: "100%", padding: 24 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14, fontWeight: 700, color: "#F1F5F9" }}><Award size={15} strokeWidth={2} /> {lang === 'en' ? 'Project Badge' : '项目认证徽章'}</div>

        {/* 徽章预览 */}
        <div style={{ padding: 24, background: "#1e293b", borderRadius: 12 }}>
          <BadgeCard project={project} innerRef={badgeRef} />
        </div>

        {/* 使用说明 */}
        <div style={{ fontSize: 11, color: "#64748B", textAlign: "center", lineHeight: 1.7 }}>
          {lang === 'en' ? 'Download badge PNG and embed in your website, whitepaper or README to showcase PrimaryLens research score' : '下载徽章 PNG，嵌入你的官网、白皮书或 README，展示 PrimaryLens 独立研究评分'}
        </div>

        {/* 嵌入代码 */}
        <div style={{ width: "100%", background: "#0f172a", border: "1px solid #1e293b", borderRadius: 8, padding: 12 }}>
          <div style={{ fontSize: 10, color: "#64748b", marginBottom: 6, textTransform: "uppercase", letterSpacing: 1 }}>Embed Code</div>
          <pre style={{ fontSize: 11, color: "#94a3b8", margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-all", lineHeight: 1.6 }}>{embedCode}</pre>
        </div>

        {/* 操作按钮 */}
        <div style={{ display: "flex", gap: 10 }}>
          <button onClick={onClose} style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "#94A3B8", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontSize: 13 }}>
            {lang === 'en' ? 'Cancel' : '取消'}
          </button>
          <button onClick={handleCopy} style={{ background: copied ? "#059669" : "rgba(255,255,255,0.06)", border: `1px solid ${copied ? "#059669" : "rgba(255,255,255,0.12)"}`, color: "#F1F5F9", borderRadius: 8, padding: "8px 18px", cursor: "pointer", fontSize: 13, transition: "all .2s" }}>
            {copied ? (lang === 'en' ? '✓ Copied' : '✓ 已复制') : (lang === 'en' ? '📋 Copy Code' : '📋 复制代码')}
          </button>
          <button onClick={handleDownload} disabled={capturing} style={{ background: "#2563eb", border: "none", color: "#fff", borderRadius: 8, padding: "8px 18px", cursor: capturing ? "not-allowed" : "pointer", fontSize: 13, opacity: capturing ? 0.6 : 1 }}>
            {capturing ? (lang === 'en' ? '⏳ Generating...' : '⏳ 生成中...') : (lang === 'en' ? '⬇ Download Badge' : '⬇ 下载徽章')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SentimentBar({ value, max = 100 }) {
  const pct = Math.min(100, Math.max(0, (value / max) * 100));
  const color = value >= 60 ? "#16a34a" : value >= 40 ? "#d97706" : "#dc2626";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
      <div style={{ flex: 1, height: 4, background: "#e4e4e7", borderRadius: 2, overflow: "hidden" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 2, transition: "width .3s" }} />
      </div>
      <span style={{ fontSize: 10, color, fontWeight: 700, minWidth: 28, textAlign: "right" }}>{Math.round(value)}</span>
    </div>
  );
}

// ── 叙事×链上四象限可视化组件 ──────────────────────────
function NarrativeChainMatrix({ project }) {
  const { lang } = useLang();
  const surfMindshare = useSurfMindshare(project?.name);
  const q = calcNarrativeChainQuadrant(project, surfMindshare);
  const GRID = [
    { key: "alpha",     label: "潜在 Alpha", label_en: "Alpha Signal", color: "#3b82f6" },
    { key: "resonance", label: "共振",       label_en: "Resonance",    color: "#10b981" },
    { key: "noise",     label: "无信号",     label_en: "No Signal",    color: "#a1a1aa" },
    { key: "hype",      label: "炒作预警",   label_en: "Hype Warning", color: "#f59e0b" },
  ];

  // 点的位置：x=叙事(0-100%), y从下往上=链上(0-100%)
  const dotX = `${q.narrativeScore}%`;
  const dotY = `${100 - q.chainScore}%`;

  return (
    <div style={{ ...S.card, padding: 14 }}>
      {/* 标题行 */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 1 }}>
          <Radio size={12} strokeWidth={2} /> {lang === 'en' ? 'Narrative × On-chain Matrix' : '叙事 × 链上 信号矩阵'}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6,
          background: q.bg, border: `1px solid ${q.color}44`,
          borderRadius: 20, padding: "3px 10px" }}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: q.color, flexShrink: 0 }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: q.color }}>{lang === 'en' ? q.label_en : q.label}</span>
        </div>
      </div>

      {/* 2×2 矩阵图 */}
      <div style={{ display: "flex", gap: 8 }}>
        {/* Y 轴标签 */}
        <div style={{ display: "flex", flexDirection: "column", justifyContent: "space-between", alignItems: "flex-end", paddingBottom: 18, gap: 0 }}>
          <span style={{ fontSize: 9, color: C.muted, writingMode: "vertical-rl", transform: "rotate(180deg)", letterSpacing: 1 }}>{lang === 'en' ? 'On-chain ↑' : '链上活跃度 ↑'}</span>
        </div>

        <div style={{ flex: 1 }}>
          {/* 四象限网格 */}
          <div style={{ position: "relative", display: "grid", gridTemplateColumns: "1fr 1fr", gridTemplateRows: "1fr 1fr", gap: 3, height: 160 }}>
            {GRID.map(cell => (
              <div key={cell.key} style={{
                background: cell.key === q.key ? cell.color + "14" : C.faint,
                border: cell.key === q.key ? `1px solid ${cell.color}44` : `1px solid ${C.border}`,
                borderRadius: 6,
                display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 5,
                transition: "all .2s",
              }}>
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: cell.key === q.key ? cell.color : "#d4d4d8" }} />
                <span style={{ fontSize: 9, color: cell.key === q.key ? cell.color : C.muted, fontWeight: cell.key === q.key ? 700 : 400 }}>
                  {lang === 'en' ? cell.label_en : cell.label}
                </span>
              </div>
            ))}

            {/* 项目定位点 */}
            <div style={{
              position: "absolute",
              left: dotX, top: dotY,
              transform: "translate(-50%, -50%)",
              width: 12, height: 12, borderRadius: "50%",
              background: q.color,
              boxShadow: `0 0 0 3px ${q.color}33`,
              border: "2px solid #fff",
              zIndex: 2,
            }} />
          </div>

          {/* X 轴标签 */}
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 4, paddingLeft: 2, paddingRight: 2 }}>
            <span style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'Low Narrative' : '叙事冷淡'}</span>
            <span style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'Narrative →' : '叙事热度 →'}</span>
            <span style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'High Narrative' : '叙事高涨'}</span>
          </div>
        </div>
      </div>

      {/* 得分条 */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginTop: 12 }}>
        {[
          { label: lang === 'en' ? "Narrative" : "叙事热度", value: q.narrativeScore, color: "#3b82f6" },
          { label: lang === 'en' ? "On-chain" : "链上活跃", value: q.chainScore,     color: "#10b981" },
        ].map(({ label, value, color }) => (
          <div key={label}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
              <span style={{ fontSize: 9, color: C.muted }}>{label}</span>
              <span style={{ fontSize: 9, fontWeight: 700, color }}>{value}</span>
            </div>
            <div style={{ height: 4, background: C.faint, borderRadius: 2 }}>
              <div style={{ width: `${value}%`, height: "100%", background: color, borderRadius: 2 }} />
            </div>
          </div>
        ))}
      </div>

      {/* Surf Mindshare 趋势 */}
      {surfMindshare?.data?.length > 1 && (() => {
        const ms = surfMindshare.data;
        const latest = ms[ms.length - 1].value;
        const prev7 = ms[Math.max(0, ms.length - 8)].value;
        const delta = prev7 > 0 ? ((latest - prev7) / prev7 * 100).toFixed(1) : null;
        const fmtMs = v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : String(Math.round(v));
        const isUp = delta !== null && Number(delta) >= 0;
        return (
          <div style={{ marginTop: 10, padding: "8px 10px", background: "rgba(139,92,246,0.06)", border: "1px solid rgba(139,92,246,0.18)", borderRadius: 6 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <span style={{ fontSize: 9, color: "#7c3aed", fontWeight: 700, letterSpacing: 0.5 }}>
                {lang === 'en' ? 'TWITTER MINDSHARE · Surf' : 'TWITTER 热度趋势 · Surf'}
              </span>
              <span style={{ fontSize: 10, color: isUp ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                {fmtMs(latest)} {delta !== null ? `${isUp ? '↑' : '↓'}${Math.abs(delta)}%` : ''}
              </span>
            </div>
            <MiniSparkline data={ms} color="#7c3aed" height={24} />
          </div>
        );
      })()}

      {/* 解读文字 */}
      <div style={{ marginTop: 10, fontSize: 11, color: C.muted, lineHeight: 1.5, padding: "8px 10px", background: C.faint, borderRadius: 6 }}>
        <span style={{ color: q.color, fontWeight: 700 }}>{lang === 'en' ? q.label_en : q.label}</span>
        {" — "}{lang === 'en' ? q.desc_en : q.desc}
      </div>
    </div>
  );
}

function DuneDashboardPanel({ dashboard, projectId, projectName }) {
  const { lang } = useLang();
  const lsKey = `dune_url_${projectId}`;
  const [embedInfo, setEmbedInfo] = useState(() => {
    try { return JSON.parse(localStorage.getItem(lsKey)) || null; } catch { return null; }
  });
  const [inputUrl, setInputUrl] = useState('');
  const [showInput, setShowInput] = useState(false);
  const [checking, setChecking] = useState(false);
  const [urlError, setUrlError] = useState('');

  const handleAddUrl = async () => {
    if (!inputUrl.trim()) return;
    setChecking(true);
    setUrlError('');
    const result = await parseDuneUrl(inputUrl);
    setChecking(false);
    if (result?.ok) {
      const info = { embedUrl: result.embedUrl, dashboardUrl: result.dashboardUrl, title: result.title };
      localStorage.setItem(lsKey, JSON.stringify(info));
      setEmbedInfo(info);
      setShowInput(false);
      setInputUrl('');
    } else {
      setUrlError('链接格式无效，需要 https://dune.com/用户名/看板名 格式');
    }
  };

  const handleRemove = () => {
    localStorage.removeItem(lsKey);
    setEmbedInfo(null);
  };

  // 已有手动绑定的看板 → 显示跳转卡片（iframe 被 Dune 安全策略拒绝，改为链接跳转）
  if (embedInfo) {
    return (
      <div style={{ marginTop: 12, background: "#0c1524", border: "1px solid #1e3a5f", borderRadius: 10, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
            <BarChart3 size={11} strokeWidth={2} /> Dune Analytics
          </div>
          <button onClick={handleRemove}
            style={{ background: "none", border: "none", color: "#475569", fontSize: 10, cursor: "pointer", padding: 0 }}>
            移除
          </button>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: "#f1f5f9", marginBottom: 4 }}>
          {embedInfo.title || 'Dune Dashboard'}
        </div>
        <div style={{ fontSize: 11, color: "#64748b", marginBottom: 12 }}>
          在 Dune 上查看完整链上数据看板
        </div>
        <a href={embedInfo.dashboardUrl} target="_blank" rel="noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "#1e3a5f", color: "#60a5fa", borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: "none", border: "1px solid #2563eb" }}>
          <span>在 Dune 查看看板</span>
          <span style={{ fontSize: 14 }}>↗</span>
        </a>
      </div>
    );
  }

  // 自动发现了官方看板（type:'dashboard'）→ 直接展示，带"自动发现"标记
  if (dashboard?.type === 'dashboard') {
    return (
      <div style={{ marginTop: 12, background: "#0c1524", border: "1px solid #1e3a5f", borderRadius: 10, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1 }}>
              <BarChart3 size={11} strokeWidth={2} /> Dune Analytics
            </span>
            <span style={{ fontSize: 9, background: "rgba(99,102,241,0.15)", color: "#818cf8", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 4, padding: "1px 5px", fontWeight: 600 }}>
              自动发现
            </span>
          </div>
          <button onClick={() => setShowInput(true)}
            style={{ background: "none", border: "none", color: "#475569", fontSize: 10, cursor: "pointer", padding: 0 }}>
            换绑
          </button>
        </div>
        <div style={{ fontSize: 12, color: "#94a3b8", marginBottom: 10 }}>
          已找到 <span style={{ color: "#e2e8f0", fontWeight: 600 }}>@{dashboard.owner}</span> 的链上看板
        </div>
        <a href={dashboard.dashboardUrl} target="_blank" rel="noreferrer"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "#1e3a5f", color: "#60a5fa", borderRadius: 6, fontSize: 12, fontWeight: 700, textDecoration: "none", border: "1px solid #2563eb" }}>
          <span>在 Dune 查看看板</span>
          <span style={{ fontSize: 14 }}>↗</span>
        </a>
        {showInput && (
          <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
            <input
              value={inputUrl}
              onChange={e => { setInputUrl(e.target.value); setUrlError(''); }}
              placeholder={lang === 'en' ? "https://dune.com/user/dashboard-name" : "https://dune.com/用户名/看板名"}
              style={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, padding: "7px 10px", color: "#f1f5f9", fontSize: 12, outline: "none" }}
              onKeyDown={e => e.key === 'Enter' && handleAddUrl()}
              autoFocus
            />
            {urlError && <div style={{ fontSize: 10, color: "#f87171" }}>{urlError}</div>}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={handleAddUrl} disabled={checking}
                style={{ padding: "5px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
                {checking ? '验证中…' : '确认'}
              </button>
              <button onClick={() => { setShowInput(false); setInputUrl(''); setUrlError(''); }}
                style={{ padding: "5px 10px", background: "none", border: "1px solid #334155", color: "#64748b", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
                取消
              </button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // 有合约地址 → 激活引导 + 绑定链接入口
  const showActivate = dashboard?.type === 'activate';
  const isHLDashboard = dashboard?.chain === 'hyperliquid';
  const candidates = dashboard?.candidates || [];

  /** 一键绑定候选看板（存 localStorage + 写入 Supabase 全局复用） */
  const handleBindCandidate = (c) => {
    const info = { embedUrl: c.embedUrl, dashboardUrl: c.dashboardUrl, title: c.name || c.slug };
    localStorage.setItem(lsKey, JSON.stringify(info));
    setEmbedInfo(info);
    // 异步写入 Supabase，不阻塞 UI
    const projectKey = projectName || '';
    if (projectKey && c.owner && c.slug) {
      fetch(`${RD_BASE}/dune/bind-dashboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectKey, owner: c.owner, slug: c.slug, name: c.name || c.slug }),
      }).catch(() => {});
    }
  };

  return (
    <div style={{ marginTop: 12 }}>
      {showActivate && (
        <div style={{ background: "#0c1524", border: "1px dashed #1e3a5f", borderRadius: 10, padding: "12px 14px", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: "#94a3b8", marginBottom: 4 }}>
            {isHLDashboard ? <BarChart3 size={12} strokeWidth={2} /> : <TrendingUp size={12} strokeWidth={2} />} {isHLDashboard ? 'Hyperliquid 原生链数据' : 'DEX 链上数据'}
          </div>
          <div style={{ fontSize: 11, color: "#64748b", marginBottom: 8, lineHeight: 1.6 }}>
            {isHLDashboard
              ? `已自动创建 Dune 查询（hyperliquid.market_data），点击在 Dune 查看 ${dashboard.coin || ''} 永续合约数据。`
              : '已自动创建 Dune 查询，点击在 Dune 运行一次激活数据。'}
            {!isHLDashboard && (
              <span style={{ display: "block", marginTop: 3, fontSize: 10, color: "#475569" }}>
                合约：{dashboard.contract?.slice(0, 10)}… · {dashboard.chain}
              </span>
            )}
          </div>
          <a href={dashboard.activateUrl} target="_blank" rel="noreferrer"
            style={{ display: "inline-block", padding: "6px 12px", background: "#1e3a5f", color: "#60a5fa", borderRadius: 6, fontSize: 11, fontWeight: 700, textDecoration: "none", border: "1px solid #2563eb" }}>
            {isHLDashboard ? '在 Dune 查看 →' : '在 Dune 激活 →'}
          </a>
        </div>
      )}

      {/* 候选看板列表 */}
      {candidates.length > 0 && (
        <div style={{ background: "#0c1524", border: "1px solid #1e293b", borderRadius: 10, padding: "10px 14px", marginBottom: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
            <Search size={11} strokeWidth={2} /> 找到 {candidates.length} 个相关看板，点击绑定
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {candidates.map((c, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 10px", background: "#0f172a", borderRadius: 7, border: "1px solid #1e293b" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: "#e2e8f0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {c.name}
                  </div>
                  <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>
                    @{c.owner}
                    <a href={c.dashboardUrl} target="_blank" rel="noreferrer"
                      style={{ color: "#334155", marginLeft: 6, textDecoration: "none" }}>
                      ↗
                    </a>
                  </div>
                </div>
                <button
                  onClick={() => handleBindCandidate(c)}
                  style={{ flexShrink: 0, padding: "4px 12px", background: "#1e3a5f", color: "#60a5fa", border: "1px solid #2563eb", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                  绑定
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* 手动粘贴链接入口 */}
      {!showInput ? (
        <button onClick={() => setShowInput(true)}
          style={{ background: "none", border: "1px dashed #334155", borderRadius: 7, padding: "6px 12px", color: "#64748b", fontSize: 11, cursor: "pointer", width: "100%", textAlign: "left" }}>
          + {lang === 'en' ? 'Manually bind Dune dashboard link' : '手动绑定 Dune 看板链接'}
        </button>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <input
            value={inputUrl}
            onChange={e => { setInputUrl(e.target.value); setUrlError(''); }}
            placeholder={lang === 'en' ? "https://dune.com/user/dashboard-name" : "https://dune.com/用户名/看板名"}
            style={{ background: "#0f172a", border: "1px solid #334155", borderRadius: 6, padding: "7px 10px", color: "#f1f5f9", fontSize: 12, outline: "none" }}
            onKeyDown={e => e.key === 'Enter' && handleAddUrl()}
            autoFocus
          />
          {urlError && <div style={{ fontSize: 10, color: "#f87171" }}>{urlError}</div>}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleAddUrl} disabled={checking}
              style={{ padding: "5px 14px", background: "#2563eb", color: "#fff", border: "none", borderRadius: 6, fontSize: 11, fontWeight: 700, cursor: "pointer" }}>
              {checking ? '验证中…' : '确认'}
            </button>
            <button onClick={() => { setShowInput(false); setInputUrl(''); setUrlError(''); }}
              style={{ padding: "5px 10px", background: "none", border: "1px solid #334155", color: "#64748b", borderRadius: 6, fontSize: 11, cursor: "pointer" }}>
              取消
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function ChainSignalsPanel({ project }) {
  const { lang } = useLang();
  const stageKey = project.scoreResult?.stage_key;
  const github   = project.githubSignal;
  const users    = project.defiData?.users;
  const dune        = project.duneTokenInfo;
  const duneDashboard = project.duneDashboard;
  const duneAnalytics = project.duneAnalytics;
  const cgSentUp    = project.cgData?.sentiment_votes_up_percentage ?? null;
  const cgComm      = project.cgData?.community_data ?? null;
  const cgWatchlist = project.cgData?.watchlist_portfolio_users ?? null;
  const hasSignal   = github || users || dune || duneDashboard?.type !== 'none' || cgSentUp != null || cgComm || duneAnalytics;

  if (!hasSignal && stageKey === "early") {
    return (
      <div style={{ ...S.card, display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: C.muted }}>
        <Microscope size={18} strokeWidth={1.75} color={C.muted} />
        <div>
          <div style={{ fontWeight: 600, color: C.text }}>{lang === 'en' ? 'Seed / Early Stage · No on-chain data' : '种子/早期阶段 · 链上数据暂无'}</div>
          <div style={{ marginTop: 2 }}>{lang === 'en' ? 'Project not yet launched — evaluation focuses on team background and narrative' : '项目尚未上线，以团队背景和叙事逻辑评估为主'}</div>
        </div>
      </div>
    );
  }

  if (!hasSignal) return null;

  const lastPushStr = github?.lastPushed
    ? lang === 'en'
      ? `${Math.floor((Date.now() - new Date(github.lastPushed)) / 86400000)}d ago`
      : `${Math.floor((Date.now() - new Date(github.lastPushed)) / 86400000)} 天前`
    : null;

  // 信号强弱色点：绿=达标 / 黄=一般 / 红=偏弱，取代原 ✅⚠️❌ emoji（与 Style E 其他徽章一致）
  const SIGNAL_COLOR = { good: "#16a34a", mid: "#d97706", low: "#dc2626" };
  const commitSignal = github?.commits4w != null
    ? github.commits4w >= 20 ? "good" : github.commits4w >= 5 ? "mid" : "low"
    : null;
  const SignalDot = ({ level }) => level
    ? <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: "50%", background: SIGNAL_COLOR[level], marginRight: 6 }} />
    : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* 四象限矩阵 — 始终显示 */}
      <NarrativeChainMatrix project={project} />

      {/* 明细信号 */}
      <div style={S.card}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 12, textTransform: "uppercase", letterSpacing: 1 }}>
        <Link2 size={12} strokeWidth={2} /> {lang === 'en' ? 'On-chain Signals' : '链上信号明细'}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

        {/* GitHub 开发活跃度 */}
        {github && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 11, color: C.muted }}>
              <span style={{ marginRight: 6, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <GitBranch size={11} strokeWidth={2} /> {lang === 'en' ? 'Dev Activity' : '开发活跃度'}
              </span>
              {github.repo && (
                <a href={github.url} target="_blank" rel="noreferrer"
                  style={{ color: C.muted, fontSize: 10, textDecoration: "none" }}>
                  {github.owner}/{github.repo} ↗
                </a>
              )}
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {github.weeklyCommits.length > 0 && <CommitSparkline weeks={github.weeklyCommits} />}
              <div style={{ textAlign: "right", fontSize: 11 }}>
                <SignalDot level={commitSignal} />
                {github.commits4w != null
                  ? <span style={{ color: C.text, fontWeight: 700 }}>{github.commits4w} commits</span>
                  : lastPushStr && <span style={{ color: C.muted }}>{lang === 'en' ? `Last push ${lastPushStr}` : `最近推送 ${lastPushStr}`}</span>
                }
                {github.commits4w != null && <span style={{ color: C.muted }}>{lang === 'en' ? '/4wk' : '/4周'}</span>}
                {github.stars != null && (
                  <div style={{ color: C.muted, fontSize: 10, marginTop: 2, display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 3 }}>
                    <Star size={10} strokeWidth={2} /> {github.stars.toLocaleString()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* DeFiLlama 活跃用户 */}
        {users && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ fontSize: 11, color: C.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
              <Users size={11} strokeWidth={2} /> {lang === 'en' ? 'Active Users · DeFiLlama' : '活跃用户 · DeFiLlama'}
            </div>
            <div style={{ textAlign: "right", fontSize: 11 }}>
              {users.daily && <span style={{ color: C.text, fontWeight: 700 }}>{users.daily.toLocaleString()} </span>}
              <span style={{ color: C.muted }}>/ 24h</span>
              {users.weekly && <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>{lang === 'en' ? '7d' : '7日'} {users.weekly.toLocaleString()}</div>}
            </div>
          </div>
        )}

        {/* Dune Sim 链上数据 */}
        {dune && (
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {dune.poolSize != null && (() => {
              const poolUsd = dune.poolSize;
              const poolSignal = poolUsd >= 5_000_000 ? "good" : poolUsd >= 500_000 ? "mid" : "low";
              return (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ fontSize: 11, color: C.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <Droplet size={11} strokeWidth={2} /> {lang === 'en' ? 'Liquidity Depth · Dune' : '流动性深度 · Dune Sim'}
                    {dune.chain && <span style={{ color: C.muted, fontSize: 10, marginLeft: 6 }}>({dune.chain})</span>}
                  </div>
                  <div style={{ textAlign: "right", fontSize: 11 }}>
                    <SignalDot level={poolSignal} />
                    <span style={{ color: C.text, fontWeight: 700 }}>${fmt(poolUsd)}</span>
                    <div style={{ color: C.muted, fontSize: 10, marginTop: 2 }}>
                      {dune.poolType || "DEX pool"}
                    </div>
                  </div>
                </div>
              );
            })()}
            {dune.priceUsd != null && (
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ fontSize: 11, color: C.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                  <DollarSign size={11} strokeWidth={2} /> {lang === 'en' ? 'On-chain Price · Dune' : '链上价格 · Dune Sim'}
                </div>
                <div style={{ fontSize: 11, color: C.text, fontWeight: 700 }}>
                  ${dune.priceUsd < 0.01 ? dune.priceUsd.toExponential(3) : dune.priceUsd.toFixed(4)}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Dune Analytics — 30天成交量（DEX 或 HL 原生链） */}
        {duneAnalytics && (() => {
          const vol30 = duneAnalytics.total_volume || 0;
          const trades30 = duneAnalytics.total_trades || 0;
          const days = duneAnalytics.days || [];
          const nDays = days.length || 30; // actual data days (HL data may be < 30d due to pipeline lag)
          const dailyAvg = nDays > 0 ? vol30 / nDays : 0;
          const isHL = duneAnalytics.source === 'hyperliquid_market_data';
          const volSignal = vol30 >= 50_000_000 ? "good" : vol30 >= 5_000_000 ? "mid" : "low";
          const dayLabel = nDays < 28 ? `${nDays}d` : '30d';
          const dayLabelZh = nDays < 28 ? `${nDays}日` : '30日';
          return (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                <div>
                  <div style={{ fontSize: 11, color: C.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                    <BarChart3 size={11} strokeWidth={2} /> {isHL
                      ? (lang === 'en' ? `${dayLabel} Perp Notional Vol · Dune` : `${dayLabelZh}合约名义成交量 · Dune`)
                      : (lang === 'en' ? `${dayLabel} DEX Volume · Dune` : `${dayLabelZh} DEX 交易量 · Dune`)}
                    <span style={{ color: "#6366f1", fontSize: 10, marginLeft: 6 }}>{duneAnalytics.chain}</span>
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                    {isHL
                      ? (lang === 'en' ? `avg $${fmt(dailyAvg)}/day · hyperliquid.market_data` : `日均 $${fmt(dailyAvg)} · 永续合约数据`)
                      : (lang === 'en' ? `${trades30.toLocaleString()} trades · avg $${fmt(dailyAvg)}/day` : `${trades30.toLocaleString()} 笔 · 日均 $${fmt(dailyAvg)}`)}
                  </div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <SignalDot level={volSignal} />
                  <span style={{ fontSize: 12, color: C.text, fontWeight: 700 }}>${fmt(vol30)}</span>
                </div>
              </div>
              {days.length > 0 && <DuneVolumeChart days={days} />}
            </div>
          );
        })()}

        {/* Dune 看板（iframe 嵌入 或 激活引导） */}
        <DuneDashboardPanel dashboard={duneDashboard} projectId={project.id} projectName={project.name} />

        {/* 叙事热度分隔线 */}
        {(cgSentUp != null || cgComm) && (github || users || dune || duneDashboard || duneAnalytics) && (
          <div style={{ borderTop: `1px solid ${C.border}`, margin: "4px 0" }} />
        )}

        {/* CoinGecko 社区情绪条 */}
        {cgSentUp != null && (
          <div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <div style={{ fontSize: 11, color: C.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                <MessageSquare size={11} strokeWidth={2} /> {lang === 'en' ? 'Community Sentiment · CoinGecko' : '社区情绪 · CoinGecko'}
              </div>
              <div style={{ fontSize: 10, color: C.muted }}>
                {lang === 'en' ? `Bullish ${Math.round(cgSentUp)}% · Bearish ${Math.round(100 - cgSentUp)}%` : `看涨 ${Math.round(cgSentUp)}% · 看跌 ${Math.round(100 - cgSentUp)}%`}
              </div>
            </div>
            <SentimentBar value={cgSentUp} />
          </div>
        )}

        {/* CoinGecko 社区数据 */}
        {(cgComm || cgWatchlist) && (() => {
          const tw  = cgComm?.twitter_followers ?? null;
          const rd  = cgComm?.reddit_subscribers ?? null;
          const rdA = cgComm?.reddit_active_accounts ?? null;
          const items = [
            tw   != null && { label: lang === 'en' ? "Twitter Followers" : "Twitter 关注", value: tw.toLocaleString(),  icon: "𝕏" },
            rd   != null && { label: lang === 'en' ? "Reddit Subscribers" : "Reddit 订阅",  value: rd.toLocaleString(),  Icon: MessageSquare },
            rdA  != null && { label: lang === 'en' ? "Reddit Active" : "Reddit 活跃",  value: rdA.toLocaleString(), Icon: Flame },
            cgWatchlist != null && { label: lang === 'en' ? "CG Watchlist" : "CG 关注",  value: cgWatchlist.toLocaleString(), Icon: Eye },
          ].filter(Boolean);
          if (!items.length) return null;
          return (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
              {items.map(({ label, value, icon, Icon }) => (
                <div key={label} style={{ background: C.faint, borderRadius: 6, padding: "7px 10px" }}>
                  <div style={{ fontSize: 9, color: C.muted, marginBottom: 2, display: "flex", alignItems: "center", gap: 4 }}>
                    {Icon ? <Icon size={10} strokeWidth={2} /> : icon} {label}
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{value}</div>
                </div>
              ))}
            </div>
          );
        })()}

      </div>
    </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  HISTORY + FEEDBACK PANEL
// ══════════════════════════════════════════════════════
const DIM_COLORS = {
  tech: "#6366F1", team: "#10B981", narrative: "#F59E0B",
  data_score: "#3B82F6", token: "#8B5CF6", compliance: "#EF4444",
};
const DIM_LABELS = {
  tech: "技术", team: "团队", narrative: "叙事",
  data_score: "数据", token: "代币", compliance: "合规",
};

// ──────────────────────────────────────────────
//  ResearchNotesPanel — 研报标签页
// ──────────────────────────────────────────────
function ResearchNotesPanel({ project }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const { lang } = useLang();

  useEffect(() => {
    setLoading(true);
    getResearchNotes(project.name).then(rows => {
      setNotes(rows);
      setLoading(false);
    });
  }, [project.name]);

  if (loading) {
    return <div style={{ color: C.muted, fontSize: 12, padding: 8 }}>{lang === 'en' ? 'Loading…' : '加载中…'}</div>;
  }

  if (notes.length === 0) {
    return (
      <div style={{ color: C.muted, fontSize: 12, padding: 8, textAlign: "center" }}>
        <div style={{ fontSize: 24, marginBottom: 8 }}>📄</div>
        <div>{lang === 'en' ? 'No research notes yet' : '暂无研报'}</div>
        <div style={{ marginTop: 4, fontSize: 11 }}>{lang === 'en' ? 'Add entries to the project_research_notes table in Supabase to display here' : '在 Supabase project_research_notes 表中添加数据后即可展示'}</div>
      </div>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {notes.map(note => {
        const isOpen = expanded === note.id;
        const dateStr = note.published_at
          ? new Date(note.published_at).toLocaleDateString(lang === 'en' ? "en-US" : "zh-CN")
          : "";
        return (
          <div key={note.id} style={{ ...S.card, padding: 12 }}>
            {/* Header row */}
            <div
              style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", cursor: "pointer", gap: 8 }}
              onClick={() => setExpanded(isOpen ? null : note.id)}
            >
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.4 }}>
                  {note.title || (lang === 'en' ? 'Untitled Note' : '无标题研报')}
                </div>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  {note.author && <span>{note.author}</span>}
                  {note.author && dateStr && <span style={{ margin: "0 4px" }}>·</span>}
                  {dateStr && <span>{dateStr}</span>}
                  {note.source && (
                    <span style={{ marginLeft: 6, background: C.faint, border: `1px solid ${C.border}`, borderRadius: 3, padding: "1px 5px", fontSize: 10 }}>
                      {note.source}
                    </span>
                  )}
                </div>
              </div>
              <span style={{ color: C.muted, fontSize: 14, flexShrink: 0 }}>{isOpen ? "▲" : "▼"}</span>
            </div>

            {/* Tags */}
            {note.tags && note.tags.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 6 }}>
                {note.tags.map(t => (
                  <span key={t} style={{ ...S.tag }}>{t}</span>
                ))}
              </div>
            )}

            {/* Expanded content — render markdown as plain text with line breaks */}
            {isOpen && note.content && (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 0 0",
                  borderTop: `1px solid ${C.border}`,
                  fontSize: 12,
                  lineHeight: 1.8,
                  color: C.text,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  maxHeight: 520,
                  overflowY: "auto",
                }}
              >
                {note.content}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function HistoryFeedbackPanel({ project, history = [] }) {
  const { lang } = useLang();
  const [historyRows, setHistoryRows]     = useState([]);
  const [histLoading, setHistLoading]     = useState(true);
  // feedback state
  const [accuracy, setAccuracy]           = useState(null);   // 'accurate'|'too_low'|'too_high'
  const [weakDim, setWeakDim]             = useState(null);
  const [userIntent, setUserIntent]       = useState(null);
  const [comment, setComment]             = useState("");
  const [fbSubmitted, setFbSubmitted]     = useState(false);
  const [fbLoading, setFbLoading]         = useState(false);

  useEffect(() => {
    setHistLoading(true);
    getScoreHistory(project.name).then(rows => {
      setHistoryRows(rows);
      setHistLoading(false);
    });
  }, [project.name]);

  const handleSubmitFeedback = async () => {
    if (!accuracy || fbLoading) return;
    setFbLoading(true);
    const rootdataId = project.details?.id || project.details?.project_id || null;
    await submitFeedback(project.name, rootdataId, {
      accuracy,
      weakDimension: weakDim,
      userIntent,
      comment: comment.trim() || undefined,
      composite: project.analysis?.composite,
    });
    setFbLoading(false);
    setFbSubmitted(true);
  };

  const scoreKeys = ["tech", "team", "narrative", "data_score", "token", "compliance"];
  const chartData = historyRows.map(row => ({
    date: new Date(row.scored_at).toLocaleDateString(lang === 'en' ? "en-US" : "zh-CN", { month: "numeric", day: "numeric" }),
    composite: row.composite,
    ...Object.fromEntries(scoreKeys.map(k => [k, row[k]])),
  }));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── 评分历史图表 ── */}
      <div style={{ ...S.card, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>
          <BarChart3 size={12} strokeWidth={2} /> {lang === 'en' ? 'Score History' : '评分变化历史'}
        </div>

        {histLoading ? (
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center", padding: "20px 0" }}>{lang === 'en' ? 'Loading…' : '加载中…'}</div>
        ) : chartData.length === 0 ? (
          <div style={{ fontSize: 11, color: C.muted, textAlign: "center", padding: "20px 0" }}>
            {lang === 'en' ? 'No history yet. Scores are recorded each time you re-analyze.' : '暂无历史记录。每次重新分析后，评分将自动记录在此。'}
          </div>
        ) : chartData.length === 1 ? (
          /* Single data point — show as a stat block, chart needs 2+ points */
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {[{ key: "composite", label: lang === 'en' ? "Overall" : "综合", color: C.text }, ...scoreKeys.map(k => ({ key: k, label: lang === 'en' ? (DIM_META[k]?.label_en || DIM_LABELS[k]) : DIM_LABELS[k], color: DIM_COLORS[k] }))].map(({ key, label, color }) => (
              <div key={key} style={{ flex: "1 1 60px", background: C.faint, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                <div style={{ fontSize: 9, color: C.muted, marginBottom: 3 }}>{label}</div>
                <div style={{ fontSize: 16, fontWeight: 800, color }}>{chartData[0][key] ?? "—"}</div>
              </div>
            ))}
            <div style={{ width: "100%", fontSize: 10, color: C.muted, marginTop: 4 }}>
              {lang === 'en' ? `First analysis: ${new Date(historyRows[0].scored_at).toLocaleString("en-US")}. Re-analyze to see trend.` : `首次分析：${new Date(historyRows[0].scored_at).toLocaleString("zh-CN")}。再次分析后可见变化趋势。`}
            </div>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={chartData} margin={{ top: 4, right: 8, bottom: 4, left: -20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
              <XAxis dataKey="date" tick={{ fill: C.muted, fontSize: 9 }} />
              <YAxis domain={[0, 100]} tick={{ fill: C.muted, fontSize: 9 }} />
              <Tooltip
                contentStyle={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, fontSize: 11 }}
                formatter={(v, name) => [`${v ?? "—"}`, lang === 'en' ? (DIM_META[name]?.label_en || (name === "composite" ? "Overall" : name)) : (DIM_LABELS[name] || (name === "composite" ? "综合" : name))]}
              />
              <Line type="monotone" dataKey="composite" stroke={C.text} strokeWidth={2} dot={{ r: 3 }} name="composite" />
              {scoreKeys.map(k => (
                <Line key={k} type="monotone" dataKey={k} stroke={DIM_COLORS[k]} strokeWidth={1} dot={false} name={k} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}

        {!histLoading && chartData.length > 0 && (
          <div style={{ marginTop: 8, display: "flex", flexWrap: "wrap", gap: 6 }}>
            <span style={{ fontSize: 10, color: C.muted, marginRight: 4 }}>{lang === 'en' ? 'Legend:' : '图例：'}</span>
            <span style={{ fontSize: 10, color: C.text, display: "inline-flex", alignItems: "center", gap: 3 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.text, display: "inline-block" }} /> {lang === 'en' ? 'Overall' : '综合'}
            </span>
            {scoreKeys.map(k => (
              <span key={k} style={{ fontSize: 10, color: DIM_COLORS[k], display: "inline-flex", alignItems: "center", gap: 3 }}>
                <span style={{ width: 6, height: 6, borderRadius: "50%", background: DIM_COLORS[k], display: "inline-block" }} /> {lang === 'en' ? (DIM_META[k]?.label_en || DIM_LABELS[k]) : DIM_LABELS[k]}
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── 分析版本历史 ── */}
      {history.length > 1 && (
        <div style={{ ...S.card, padding: "14px 16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>
            <Clock size={12} strokeWidth={2} /> {lang === 'en' ? 'Analysis Version History' : '分析版本历史'}
          </div>
          {history.map((h, i) => {
            const r = rating(h.composite || 0);
            const isLatest = i === history.length - 1;
            return (
              <div key={h.analyzedAt} style={{ display: "flex", justifyContent: "space-between", alignItems: "center",
                padding: "7px 0", borderBottom: i < history.length - 1 ? `1px solid ${C.faint}` : "none" }}>
                <div>
                  <span style={{ fontSize: 11, color: C.muted }}>
                    {new Date(h.analyzedAt).toLocaleDateString(lang === 'en' ? "en-US" : "zh-CN")}
                  </span>
                  {isLatest && <span style={{ marginLeft: 6, fontSize: 9, background: "#dbeafe",
                    color: "#2563eb", padding: "1px 5px", borderRadius: 3, fontWeight: 700 }}>{lang === 'en' ? 'Latest' : '最新'}</span>}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {i > 0 && (() => {
                    const prev = history[i - 1];
                    const diff = (h.composite || 0) - (prev.composite || 0);
                    if (diff === 0) return null;
                    const TrendIcon = diff > 0 ? TrendingUp : TrendingDown;
                    return (
                      <span style={{ display: "inline-flex", alignItems: "center", gap: 2, fontSize: 11, color: diff > 0 ? "#16a34a" : "#dc2626", fontWeight: 600 }}>
                        <TrendIcon size={11} strokeWidth={2.5} />{Math.abs(diff)}
                      </span>
                    );
                  })()}
                  <span style={S.rBadge(h.composite || 0)}>{r.label}</span>
                  <span style={{ fontSize: 18, fontWeight: 800, color: r.fg }}>{h.composite || 0}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── 反馈按钮 ── */}
      <div style={{ ...S.card, padding: "14px 16px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: C.muted, marginBottom: 12, textTransform: "uppercase" }}>
          <MessageSquare size={12} strokeWidth={2} /> {lang === 'en' ? 'Score Feedback' : '评分反馈'}
        </div>

        {fbSubmitted ? (
          <div style={{ textAlign: "center", padding: "16px 0" }}>
            <PartyPopper size={22} strokeWidth={1.75} color="#16a34a" style={{ marginBottom: 6 }} />
            <div style={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>{lang === 'en' ? 'Thanks for your feedback!' : '感谢你的反馈！'}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{lang === 'en' ? 'Your input helps improve scoring accuracy' : '你的意见将帮助我们持续改进评分准确性'}</div>
          </div>
        ) : (
          <>
            {/* Layer 1: accuracy (required) */}
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.text, marginBottom: 6 }}>{lang === 'en' ? 'How accurate is this score?' : '这次评分结果的准确性如何？'}<span style={{ color: "#dc2626" }}>*</span></div>
              <div style={{ display: "flex", gap: 6 }}>
                {[
                  { value: "accurate",  label: lang === 'en' ? "Accurate" : "准确",  Icon: CircleCheck, desc: lang === 'en' ? "Matches my judgment" : "基本符合我的判断" },
                  { value: "too_low",   label: lang === 'en' ? "Too Low" : "偏低",   Icon: TrendingUp,   desc: lang === 'en' ? "Project is better" : "我觉得这个项目更好" },
                  { value: "too_high",  label: lang === 'en' ? "Too High" : "偏高",  Icon: TrendingDown, desc: lang === 'en' ? "Score is too generous" : "我觉得评分给高了" },
                ].map(({ value, label, Icon, desc }) => (
                  <button
                    key={value}
                    onClick={() => setAccuracy(value)}
                    title={desc}
                    style={{
                      flex: 1, padding: "7px 6px", borderRadius: 8, fontSize: 11, fontWeight: 700,
                      cursor: "pointer", transition: "all 0.15s",
                      display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 4,
                      background: accuracy === value ? "#dbeafe" : C.faint,
                      border: `1.5px solid ${accuracy === value ? "#3b82f6" : C.border}`,
                      color: accuracy === value ? "#1d4ed8" : C.muted,
                    }}
                  ><Icon size={12} strokeWidth={2} />{label}</button>
                ))}
              </div>
            </div>

            {/* Layer 2: weak dimension (optional, appears after accuracy is chosen) */}
            {accuracy && accuracy !== "accurate" && (
              <div style={{ marginBottom: 12, animation: "fadeIn 0.2s" }}>
                <div style={{ fontSize: 11, color: C.text, marginBottom: 6 }}>{lang === 'en' ? 'Which dimension has the biggest gap? (optional)' : '哪个维度你觉得偏差最大？（可跳过）'}</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {scoreKeys.map(k => (
                    <button
                      key={k}
                      onClick={() => setWeakDim(prev => prev === k ? null : k)}
                      style={{
                        padding: "5px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                        cursor: "pointer",
                        background: weakDim === k ? `${DIM_COLORS[k]}18` : C.faint,
                        border: `1.5px solid ${weakDim === k ? DIM_COLORS[k] : C.border}`,
                        color: weakDim === k ? DIM_COLORS[k] : C.muted,
                      }}
                    >{lang === 'en' ? (DIM_META[k]?.label_en || DIM_LABELS[k]) : DIM_LABELS[k]}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Layer 3: user intent (optional) */}
            {accuracy && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, color: C.text, marginBottom: 6 }}>{lang === 'en' ? 'Your role? (optional)' : '你的角色是？（可跳过）'}</div>
                <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {[
                    { value: "investor",   label: lang === 'en' ? "Investor" : "投资人", Icon: Briefcase },
                    { value: "researcher", label: lang === 'en' ? "Researcher" : "研究员", Icon: Microscope },
                    { value: "founder",    label: lang === 'en' ? "Founder" : "创始人", Icon: Rocket },
                    { value: "other",      label: lang === 'en' ? "Other" : "其他", Icon: User },
                  ].map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      onClick={() => setUserIntent(prev => prev === value ? null : value)}
                      style={{
                        padding: "5px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700,
                        cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 4,
                        background: userIntent === value ? "#e0e7ff" : C.faint,
                        border: `1.5px solid ${userIntent === value ? "#6366f1" : C.border}`,
                        color: userIntent === value ? "#4338ca" : C.muted,
                      }}
                    ><Icon size={11} strokeWidth={2} />{label}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Submit */}
            {accuracy && (
              <button
                onClick={handleSubmitFeedback}
                disabled={fbLoading}
                style={{
                  width: "100%", padding: "9px 0", borderRadius: 8, fontSize: 12, fontWeight: 700,
                  cursor: fbLoading ? "default" : "pointer",
                  background: fbLoading ? "#93c5fd" : C.text,
                  border: `1.5px solid ${C.text}`,
                  color: "#fff",
                }}
              >
                {fbLoading ? (lang === 'en' ? 'Submitting…' : '提交中…') : (lang === 'en' ? 'Submit Feedback' : '提交反馈')}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  SURF NEWS PANEL
// ══════════════════════════════════════════════════════
function SurfNewsPanel({ project }) {
  const { lang } = useLang();
  const { trending, recent, loading } = useSurfNews(project?.name);

  const timeAgo = (unix) => {
    const diff = Date.now() / 1000 - unix;
    if (lang === 'en') {
      if (diff < 3600) return `${Math.round(diff / 60)}m ago`;
      if (diff < 86400) return `${Math.round(diff / 3600)}h ago`;
      return `${Math.round(diff / 86400)}d ago`;
    }
    if (diff < 3600) return `${Math.round(diff / 60)}分钟前`;
    if (diff < 86400) return `${Math.round(diff / 3600)}小时前`;
    return `${Math.round(diff / 86400)}天前`;
  };

  if (loading) return (
    <div style={{ marginTop: 12, padding: 14, background: "#0c1524", border: "1px solid #1e293b", borderRadius: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 10 }}>
        <Newspaper size={11} strokeWidth={2} /> {lang === 'en' ? 'Latest News · Surf' : '最新新闻 · Surf'}
      </div>
      {[1,2,3].map(i => (
        <div key={i} style={{ height: 12, background: "#1e293b", borderRadius: 4, marginBottom: 8, width: i === 2 ? '70%' : '100%',
          backgroundImage: 'linear-gradient(90deg,#1e293b 25%,#263548 50%,#1e293b 75%)',
          backgroundSize: '200% 100%', animation: 'shimmer 1.2s infinite' }} />
      ))}
    </div>
  );

  const allEmpty = !trending.length && !recent.length;
  if (allEmpty) return null;

  const NewsItem = ({ item }) => (
    <div style={{ borderBottom: "1px solid #1e293b", paddingBottom: 8, marginBottom: 8 }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: "#a78bfa", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 2 }}>
        {item.source || 'News'}
      </div>
      <a href={item.url} target="_blank" rel="noopener noreferrer"
        style={{ fontSize: 12, color: "#e2e8f0", textDecoration: "none", lineHeight: 1.4, display: "block" }}
        onMouseEnter={e => e.target.style.color = "#a78bfa"}
        onMouseLeave={e => e.target.style.color = "#e2e8f0"}>
        {item.title}
      </a>
      <div style={{ fontSize: 10, color: "#475569", marginTop: 2 }}>{timeAgo(item.published_at)}</div>
    </div>
  );

  return (
    <div style={{ marginTop: 12, background: "#0c1524", border: "1px solid #1e293b", borderRadius: 10, padding: "14px 16px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: 1, marginBottom: 12 }}>
        <Newspaper size={11} strokeWidth={2} /> {lang === 'en' ? 'Latest News · Surf' : '最新新闻 · Surf'}
      </div>

      {trending.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: "#f59e0b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            <Flame size={10} strokeWidth={2} /> {lang === 'en' ? 'Trending' : '热门话题'}
          </div>
          {trending.map((item, i) => <NewsItem key={i} item={item} />)}
        </div>
      )}

      {recent.length > 0 && (
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 3, fontSize: 9, color: "#64748b", fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>
            <Clock size={10} strokeWidth={2} /> {lang === 'en' ? 'Recent' : '最新动态'}
          </div>
          {recent.map((item, i) => <NewsItem key={i} item={item} />)}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  BP 核查面板
// ══════════════════════════════════════════════════════
function BPVerifyPanel({ projectName }) {
  const { lang } = useLang();
  const [docText, setDocText] = useState('');
  const [fileName, setFileName] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const fileRef = useRef(null);

  const handleFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setExtracting(true);
    setError('');
    setResult(null);
    setDocText('');
    try {
      const text = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
          const base64 = e.target.result.split(',')[1];
          try {
            const res = await fetch(`${RD_BASE}/api/doc-extract`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ filename: file.name, base64 }),
            });
            const data = await res.json();
            if (data.error) reject(new Error(data.error));
            else resolve(data.extractedText || '');
          } catch (err) { reject(err); }
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      setDocText(text);
    } catch (e) {
      setError(lang === 'en' ? `Extract failed: ${e.message}` : `文档提取失败：${e.message}`);
    } finally {
      setExtracting(false);
    }
  };

  const handleVerify = async () => {
    if (!docText.trim()) return;
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const res = await fetch(`${RD_BASE}/api/bp-verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectName, docText }),
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResult(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const statusMeta = {
    verified:     { Icon: CircleCheck,   label: lang === 'en' ? 'Verified' : '已核实', color: '#059669', bg: 'rgba(5,150,105,0.08)', border: 'rgba(5,150,105,0.25)' },
    disputed:     { Icon: AlertTriangle, label: lang === 'en' ? 'Disputed' : '存疑',   color: '#d97706', bg: 'rgba(217,119,6,0.08)', border: 'rgba(217,119,6,0.25)' },
    unverifiable: { Icon: HelpCircle,    label: lang === 'en' ? 'No Data' : '无法核实', color: '#6b7280', bg: 'rgba(107,114,128,0.08)', border: 'rgba(107,114,128,0.20)' },
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* 上传区域 */}
      <div style={S.card}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: 'uppercase' }}>
          {lang === 'en' ? 'Upload Business Plan (BP)' : '上传商业计划书（BP）'}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.txt,.md,.docx"
          style={{ display: 'none' }}
          onChange={(e) => handleFile(e.target.files?.[0])}
        />
        <button
          onClick={() => fileRef.current?.click()}
          style={{ ...S.btn(), width: '100%', textAlign: 'center', marginBottom: 8, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
          disabled={extracting}
        >
          {extracting
            ? (lang === 'en' ? 'Extracting...' : '提取中...')
            : fileName
              ? (<><FileText size={13} strokeWidth={2} /> {fileName}</>)
              : (<><Paperclip size={13} strokeWidth={2} /> {lang === 'en' ? 'Choose File' : '选择文件'}</>)}
        </button>
        {docText && (
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 8 }}>
            {lang === 'en' ? `✓ Extracted ${docText.length.toLocaleString()} chars` : `✓ 已提取 ${docText.length.toLocaleString()} 字符`}
          </div>
        )}
        <button
          onClick={handleVerify}
          disabled={!docText.trim() || loading}
          style={{ ...S.btn('primary'), width: '100%', textAlign: 'center', opacity: (!docText.trim() || loading) ? 0.5 : 1, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
        >
          {loading ? (lang === 'en' ? 'Verifying...' : '核查中...') : (<><Search size={13} strokeWidth={2} /> {lang === 'en' ? 'Verify Claims' : '开始核查'}</>)}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6, padding: '8px 12px' }}>
          {error}
        </div>
      )}

      {result && (
        <>
          {/* 摘要栏 */}
          <div style={{ ...S.card, display: 'flex', gap: 8, justifyContent: 'space-around', textAlign: 'center' }}>
            {[
              { key: 'verified',     color: '#059669' },
              { key: 'disputed',     color: '#d97706' },
              { key: 'unverifiable', color: '#6b7280' },
            ].map(({ key, color }) => (
              <div key={key}>
                <div style={{ fontSize: 20, fontWeight: 700, color }}>{result.summary?.[key] ?? 0}</div>
                <div style={{ fontSize: 10, color: C.muted }}>{statusMeta[key].label}</div>
              </div>
            ))}
          </div>

          {!result.hasCache && (
            <div style={{ fontSize: 11, color: '#d97706', background: 'rgba(217,119,6,0.08)', border: '1px solid rgba(217,119,6,0.25)', borderRadius: 6, padding: '8px 12px' }}>
              {lang === 'en'
                ? '⚠ No cached analysis found. Please analyze this project first to enable cross-referencing.'
                : '⚠ 未找到缓存分析数据。请先分析该项目，以便核查时进行交叉比对。'}
            </div>
          )}

          {/* 声明列表 */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {(result.claims || []).map((claim) => {
              const meta = statusMeta[claim.status] || statusMeta.unverifiable;
              return (
                <div key={claim.id} style={{ ...S.card, background: meta.bg, border: `1px solid ${meta.border}`, padding: '10px 12px' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: claim.evidence ? 6 : 0 }}>
                    <span style={{ flexShrink: 0, color: meta.color, display: "flex" }}><meta.Icon size={14} strokeWidth={2} /></span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: C.text, lineHeight: 1.5 }}>{claim.text}</div>
                      {claim.extracted_value && (
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                          {lang === 'en' ? 'Claimed: ' : '声明值：'}<strong>{claim.extracted_value}</strong>
                        </div>
                      )}
                    </div>
                    <span style={{ fontSize: 10, fontWeight: 600, color: meta.color, flexShrink: 0, padding: '1px 5px', border: `1px solid ${meta.border}`, borderRadius: 4 }}>
                      {meta.label}
                    </span>
                  </div>
                  {claim.evidence && (
                    <div style={{ fontSize: 11, color: claim.status === 'disputed' ? '#d97706' : C.muted, marginTop: 4, paddingLeft: 22, lineHeight: 1.5 }}>
                      {claim.source && <span style={{ fontWeight: 600 }}>[{claim.source}] </span>}
                      {claim.evidence}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  DETAIL PANEL
// ══════════════════════════════════════════════════════
// 🔍 数据源诊断面板 — 折叠卡片, 展开后显示所有 fetch 的 ok/ms/sample/error
function DataSourceHealthPanel({ health, S, C, lang }) {
  const [open, setOpen] = useState(false);
  const sources = Object.entries(health || {});
  if (!sources.length) return null;
  const okCount = sources.filter(([, v]) => v.ok).length;
  const totalMs = sources.reduce((a, [, v]) => a + (v.ms || 0), 0);
  return (
    <div style={{ ...S.card, marginTop: 12, fontSize: 11 }}>
      <div onClick={() => setOpen(!open)} style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', userSelect: 'none' }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, color: C.muted, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5 }}>
          <Search size={11} strokeWidth={2} /> {lang === 'en' ? 'Data Source Diagnostics' : '数据源诊断'} · {okCount}/{sources.length} {lang === 'en' ? 'ok' : '正常'} · {(totalMs / 1000).toFixed(1)}s
        </span>
        <span style={{ color: C.muted }}>{open ? '▴' : '▾'}</span>
      </div>
      {open && (
        <table style={{ width: '100%', marginTop: 10, fontSize: 10, borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ color: C.muted, borderBottom: `1px solid ${C.faint}` }}>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>{lang === 'en' ? 'Source' : '数据源'}</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>{lang === 'en' ? 'Status' : '状态'}</th>
              <th style={{ textAlign: 'right', padding: '4px 6px' }}>ms</th>
              <th style={{ textAlign: 'left', padding: '4px 6px' }}>{lang === 'en' ? 'Sample / Error' : '样本 / 错误'}</th>
            </tr>
          </thead>
          <tbody>
            {sources.sort(([a], [b]) => a.localeCompare(b)).map(([name, v]) => (
              <tr key={name} style={{ borderBottom: `1px solid ${C.faint}` }}>
                <td style={{ padding: '4px 6px', fontWeight: 600 }}>{name}</td>
                <td style={{ padding: '4px 6px', color: v.ok ? '#059669' : '#dc2626' }}>{v.ok ? '✓' : '✗'}</td>
                <td style={{ padding: '4px 6px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: v.ms > 5000 ? '#d97706' : C.muted }}>{v.ms || '—'}</td>
                <td style={{ padding: '4px 6px', color: v.error ? '#dc2626' : C.muted, fontFamily: 'monospace', fontSize: 9, maxWidth: 320, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {v.error || (v.sample ? Object.entries(v.sample).map(([k, val]) => `${k}=${val}`).join(' ') : '—')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function DetailPanel({ project, onClose, onReanalyze, notes = [], onAddNote, history = [], ffMap = {}, projects = [], onCompareWith = null, isInWatchlist = () => false, onAddWatchlist = () => {}, onRemoveWatchlist = () => {}, onRefreshCompliance = async () => {}, isMobile = false, weights = null }) {
  const t = useT();
  const { lang } = useLang();
  const [tab, setTab] = useState("overview");
  const [showShareModal, setShowShareModal] = useState(false);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const [showReportMenu, setShowReportMenu] = useState(false);
  const [reportToast, setReportToast]       = useState('');
  const [compLoadingMap, setCompLoadingMap] = useState({}); // { compName: true } 分析中
  const [complianceRefreshing, setComplianceRefreshing] = useState(false);
  const handleComplianceRefresh = async () => {
    if (complianceRefreshing) return;
    setComplianceRefreshing(true);
    try {
      await onRefreshCompliance();
    } catch (e) {
      console.warn('[compliance-refresh] failed:', e?.message);
    } finally {
      setComplianceRefreshing(false);
    }
  };

  const [panelWidth, setPanelWidth] = useState(() => {
    const saved = Number(localStorage.getItem("alpha_os_detail_width"));
    return Number.isFinite(saved) && saved > 0 ? saved : 340;
  });
  const dragRef = useRef(null);
  const a = project.analysis;
  // 企业定制评分维度：有账户级自定义权重时统一使用，否则回退到该项目分析时的
  // 阶段自适应权重（a.stage_weights）—— 保证未设置自定义权重的用户看到的数字
  // 跟之前完全一致。整个面板（评分徽章/权重分布条/verdict/VC Memo 导出）统一用
  // effComposite/effWeights，避免"看到的数字"和"导出/verdict 用的数字"不一致。
  const effWeights = weights || a?.stage_weights || DEFAULT_WEIGHTS;
  const effComposite = calcComposite(a?.scores, effWeights);
  const r = rating(effComposite || 0);

  // Verdict 计算已迁入 data-views/extractors/verdict.js + <VerdictCard />

  const clampPanelWidth = (width) => {
    const viewport = typeof window !== "undefined" ? window.innerWidth : 1200;
    const max = Math.max(420, Math.min(920, viewport - 280));
    return Math.min(max, Math.max(340, width));
  };

  const startPanelResize = (e) => {
    e.preventDefault();
    const pointerId = e.pointerId;
    dragRef.current = {
      startX: e.clientX,
      startWidth: panelWidth,
    };
    e.currentTarget.setPointerCapture?.(pointerId);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  const resizePanel = (e) => {
    if (!dragRef.current) return;
    const nextWidth = clampPanelWidth(dragRef.current.startWidth + dragRef.current.startX - e.clientX);
    setPanelWidth(nextWidth);
  };

  const stopPanelResize = () => {
    if (!dragRef.current) return;
    dragRef.current = null;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  };

  useEffect(() => {
    localStorage.setItem("alpha_os_detail_width", String(Math.round(panelWidth)));
  }, [panelWidth]);

  useEffect(() => () => {
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
  }, []);

  // ── Surf API 数据 ──────────────────────────────────────────────────
  const surfMindshare = useSurfMindshare(project?.name);
  const surfNews = useSurfNews(project?.name);
  const contract0 = project.details?.contracts?.[0];
  const surfHolders = useSurfTokenHolders(
    contract0?.address,
    (contract0?.blockchain_name || 'ethereum').toLowerCase()
  );

  // ── 合规事件流（Brave Search 实时） ─────────────────────────────────
  const [complianceEvents, setComplianceEvents] = useState(null);
  const [eventsLoading, setEventsLoading] = useState(false);
  useEffect(() => {
    if (!project?.name) return;
    setEventsLoading(true);
    import('./services/compliance').then(({ fetchComplianceEvents }) =>
      fetchComplianceEvents(project.name)
    ).then(data => {
      setComplianceEvents(data);
    }).catch(() => {}).finally(() => setEventsLoading(false));
  }, [project?.id, project?.name]);

  // ── 研究报告生成（VC Memo 格式 v2，面向投资决策）─────────
  const buildReportHtml = () => {
    // HTML 转义：团队/融资/竞品数据来自 RootData 等外部源，插入前需转义防止存储型 XSS
    const escapeHtml = (str) => String(str ?? "").replace(/[&<>"']/g, (ch) => ({
      "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
    }[ch]));

    // ── 基础数据 ──────────────────────────────────────────
    const q        = calcNarrativeChainQuadrant(project);
    const tvl      = project.defiData?.protocol?.tvl;
    const users    = project.defiData?.users;
    const github   = project.githubSignal;
    const dune     = project.duneTokenInfo;
    const cgComm   = project.cgData?.community_data;
    const cgSent   = project.cgData?.sentiment_votes_up_percentage;
    const traffic  = project.webIntel?.techIntel?.traffic;
    const techIntel = project.webIntel?.techIntel;   // 完整技术产品情报
    const cgVol    = project.cgData?.market_data?.total_volume?.usd;
    const cgPrice  = project.cgData?.market_data?.current_price?.usd;
    const watchlist = project.cgData?.watchlist_portfolio_users;
    // 社区数据（CoinGecko community_data）
    const cgTelegram = cgComm?.telegram_channel_user_count;
    const cgReddit   = cgComm?.reddit_subscribers;
    const cgTwitter  = cgComm?.twitter_followers;

    // ── IRR 计算（同 IRRPanel 逻辑）────────────────────────
    const cgFdvRaw   = project.cgData?.market_data?.fully_diluted_valuation?.usd;
    const totalFund  = project.details?.total_funding || 0;
    const cgFdvValid = cgFdvRaw && cgFdvRaw > totalFund * 0.5;
    const cgFdv      = cgFdvValid ? cgFdvRaw : null;
    const lastRound  = project.fundingRounds?.[0];
    const roundVal   = lastRound?.valuation || 0;
    const roundAmt   = lastRound?.amount || 0;
    const impliedVal = roundVal > 0 ? roundVal : roundAmt > 0 ? roundAmt / 0.25 : totalFund;
    const entryVal   = cgFdv || impliedVal;
    const entryLabel = cgFdv ? "当前 FDV (CoinGecko)" : roundVal > 0 ? "最新轮次估值" : "隐含估值";
    const fairVal    = a?.fair_value_usd;
    const irrData    = calcIRR(entryVal, fairVal);
    const alpha      = alphaScore(effComposite || 0, irrData, a?.risk_penalty || 0);

    // ── 投资结论 ─────────────────────────────────────────
    // 用 computeVerdict 拿 finalVerdict（含红绿旗 overlay），保证与详情页"投资建议"卡一致
    // 之前硬编码 base 公式（仅 alpha + composite）会与详情页结果分叉
    // 传入 { ...a, composite: effComposite } 而非原始 a：computeVerdict 内部
    // computeSignals() 会再读一次 a.composite 算 signals.composite，覆盖掉这个
    // 字段才能让红绿旗 verdict 也跟着账户自定义权重走，不用改 services/verdict.js。
    const _v = computeVerdict(project, { ...a, composite: effComposite }, alpha, 'zh');
    const verdict = _v?.finalVerdict
                  || (alpha > 150 && (effComposite || 0) >= 78 ? "建议参与"
                    : alpha > 70  && (effComposite || 0) >= 62 ? "保持观察"
                    : "暂不参与");
    const verdictColor = verdict === "建议参与" ? "#059669"
                       : verdict === "保持观察" ? "#d97706" : "#dc2626";
    const verdictBg    = verdict === "建议参与" ? "#ecfdf5"
                       : verdict === "保持观察" ? "#fffbeb" : "#fef2f2";

    // ── 最优入场 FDV ─────────────────────────────────────
    const bestTier = (a?.fdv_tiers || []).find(t => t.rating === "S") || (a?.fdv_tiers || [])[0];

    // ── 四象限颜色 ───────────────────────────────────────
    const quadColor = q.key === "resonance" ? "#059669" : q.key === "alpha" ? "#2563eb"
                    : q.key === "hype" ? "#d97706" : "#6b7280";

    // ── 公共 HTML 组件 ────────────────────────────────────
    // 维度评分徽章
    const scoreBadge = (score, color) => {
      const bg = score >= 80 ? color + "18" : score >= 60 ? color + "10" : "#f9fafb";
      return `<span style="display:inline-flex;align-items:center;justify-content:center;width:44px;height:44px;border-radius:50%;border:2.5px solid ${color};background:${bg};font-size:18px;font-weight:900;color:${color};flex-shrink:0">${score}</span>`;
    };

    // 维度卡片容器
    const dimCard = (key, title, score, body) => {
      const d     = DIMS.find(x => x.key === key) || {};
      const color = d.color || "#6b7280";
      const noteZh = a?.notes?.[key]    || "";
      const noteEn = a?.notes_en?.[key] || "";
      // 分析文字块：中文 + 英文（若两者相同则只显示一条）
      const noteBlock = noteZh ? `
        <div style="margin-bottom:${body ? 14 : 0}px">
          <div style="font-size:11px;color:#374151;line-height:1.75;padding:12px 14px;background:#f9fafb;border-radius:7px;border-left:3px solid ${color}">
            ${noteZh}
          </div>
          ${noteEn && noteEn !== noteZh ? `
          <div style="font-size:10.5px;color:#6b7280;line-height:1.7;padding:10px 14px;margin-top:6px;background:#f0f9ff;border-radius:7px;border-left:3px solid #93c5fd">
            <span style="font-size:9px;font-weight:700;color:#93c5fd;letter-spacing:0.5px;text-transform:uppercase;display:block;margin-bottom:3px">EN</span>
            ${noteEn}
          </div>` : ""}
        </div>` : "";
      return `
      <div style="border:1px solid #e5e7eb;border-radius:10px;overflow:hidden;margin-bottom:14px">
        <div style="display:flex;align-items:center;gap:14px;padding:13px 16px;border-bottom:1px solid #f3f4f6;background:#fafafa">
          ${scoreBadge(score || 0, color)}
          <div style="flex:1;min-width:0">
            <div style="font-size:13px;font-weight:800;color:#111827">${title}</div>
          </div>
        </div>
        <div style="padding:14px 16px">
          ${noteBlock}
          ${body || ""}
        </div>
      </div>`;
    };

    // 数据卡 mini
    const miniCard = (label, value, sub, color = "#374151") =>
      value ? `<div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:7px;padding:10px 14px;flex:1;min-width:120px">
        <div style="font-size:10px;color:#6b7280;margin-bottom:3px">${label}</div>
        <div style="font-size:17px;font-weight:800;color:${color}">${value}</div>
        ${sub ? `<div style="font-size:10px;color:#9ca3af;margin-top:1px">${sub}</div>` : ""}
      </div>` : "";

    // ── 团队成员 ─────────────────────────────────────────
    // raw_data.teamMembers 是 RootData get_member 接口返回的完整成员数组
    const members = project.teamMembers || project.details?.members || project.details?.team || [];
    const memberCards = members.slice(0, 10).map(m => {
      const name  = escapeHtml(m.name  || m.full_name  || "—");
      const title = escapeHtml(m.title || m.role       || m.position || "");
      const desc  = escapeHtml(m.description || m.bio  || "");
      const rdUrl = m.rootdataUrl || m.rootdata_url || "";
      const links = [
        m.twitter  && `<a href="${m.twitter}"  target="_blank" style="color:#1d9bf0;font-size:10px;text-decoration:none">𝕏 Twitter</a>`,
        m.linkedIn && `<a href="${m.linkedIn}" target="_blank" style="color:#0a66c2;font-size:10px;text-decoration:none">LinkedIn</a>`,
        m.linkedin && `<a href="${m.linkedin}" target="_blank" style="color:#0a66c2;font-size:10px;text-decoration:none">LinkedIn</a>`,
        m.github   && `<a href="${m.github}"   target="_blank" style="color:#374151;font-size:10px;text-decoration:none">GitHub</a>`,
        rdUrl      && `<a href="${rdUrl}"       target="_blank" style="color:#6b7280;font-size:10px;text-decoration:none">RD ↗</a>`,
      ].filter(Boolean).join("&nbsp;·&nbsp;");
      return `
      <div style="padding:12px 0;border-bottom:1px solid #f3f4f6">
        <div style="display:flex;align-items:baseline;gap:10px;margin-bottom:${desc ? 5 : 0}px;flex-wrap:wrap">
          <span style="font-size:13px;font-weight:700;color:#111827">${name}</span>
          ${title ? `<span style="font-size:11px;color:#6b7280">${title}</span>` : ""}
          ${links ? `<span style="margin-left:auto;white-space:nowrap">${links}</span>` : ""}
        </div>
        ${desc ? `<div style="font-size:11.5px;color:#374151;line-height:1.65">${desc}</div>` : ""}
      </div>`;
    }).join("");

    // 兼容旧版 memberRows（融资表格不受影响）
    const memberRows = members.slice(0, 10).map(m => {
      const name  = escapeHtml(m.name || m.full_name || "—");
      const title = escapeHtml(m.title || m.role || m.position || "");
      return `<tr>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-weight:600">${name}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280">${title}</td>
      </tr>`;
    }).join("");

    // ── 融资历史 ─────────────────────────────────────────
    const fundingRows = (project.fundingRounds || []).slice(0, 5).map(rd => {
      const leadNames   = (rd.invests || []).filter(i => i.lead_investor).map(i => escapeHtml(i.name));
      const followNames = (rd.invests || []).filter(i => !i.lead_investor).map(i => escapeHtml(i.name));
      const investors = [
        leadNames.length > 0 ? `<b>${lang === 'en' ? 'Lead' : '领投'}</b> ${leadNames.join(", ")}` : "",
        followNames.length > 0 ? `${leadNames.length > 0 ? ' · ' : ''}${lang === 'en' ? 'Follow' : '跟投'} ${followNames.join(", ")}` : "",
      ].join("") || "—";
      const dateStr   = rd.date ? new Date(rd.date * 1000).toLocaleDateString("zh-CN") : escapeHtml(rd.published_time || "—");
      return `<tr>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-weight:600">${escapeHtml(rd.rounds || "—")}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#2563eb;font-weight:600">${rd.amount ? fmt(rd.amount) : (lang === 'en' ? "Undisclosed" : "未披露")}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280;font-size:11px">${investors}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#9ca3af;font-size:11px">${dateStr}</td>
      </tr>`;
    }).join("");

    // ── 解锁时间轴 ───────────────────────────────────────
    const unlockSchedule = a?.unlock_schedule || [];
    const unlockPressure = a?.unlock_pressure || "";
    const unlockColor = unlockPressure === "Green" ? "#059669"
                      : unlockPressure === "Yellow" ? "#d97706" : "#dc2626";
    const unlockLabel = lang === 'en'
      ? (unlockPressure === "Green" ? "Low Pressure" : unlockPressure === "Yellow" ? "Medium Pressure" : "High Pressure")
      : (unlockPressure === "Green" ? "解锁压力低" : unlockPressure === "Yellow" ? "解锁压力中等" : "解锁压力高");

    const unlockTimeline = unlockSchedule.length > 0 ? `
      <div style="margin-top:10px">
        <div style="font-size:10px;font-weight:700;color:#374151;margin-bottom:8px">
          ${lang === 'en' ? 'Unlock Timeline' : '解锁时间表'}
          <span style="margin-left:8px;padding:2px 8px;border-radius:10px;background:${unlockColor}18;color:${unlockColor};font-size:10px;font-weight:700">${unlockLabel}</span>
        </div>
        <div style="position:relative;padding-left:16px">
          <div style="position:absolute;left:5px;top:4px;bottom:4px;width:1.5px;background:#e5e7eb"></div>
          ${unlockSchedule.map(ev => {
            const pc = ev.pressure === "low" ? "#059669" : ev.pressure === "medium" ? "#d97706" : "#dc2626";
            const evtT = lang === 'en' ? (ev.event_en || ev.event) : ev.event;
            const pLabel = lang === 'en' ? ev.pressure : (ev.pressure === "low" ? "低" : ev.pressure === "medium" ? "中" : "高");
            return `<div style="position:relative;margin-bottom:10px;padding-left:12px">
              <div style="position:absolute;left:-11px;top:4px;width:8px;height:8px;border-radius:50%;background:${pc}"></div>
              <div style="font-size:11px;color:#374151">${evtT}</div>
              <div style="font-size:10px;color:#9ca3af">${lang === 'en' ? `Month ${ev.month}` : `${ev.month} 个月后`} · ${lang === 'en' ? 'Pressure' : '压力'}: <span style="color:${pc};font-weight:600">${pLabel}</span></div>
            </div>`;
          }).join("")}
        </div>
      </div>` : `<div style="font-size:11px;color:#9ca3af;margin-top:8px">${lang === 'en' ? 'No unlock data available. Check official docs.' : '暂无解锁数据，建议查阅官方文档'}</div>`;

    // ── 竞品对标 ─────────────────────────────────────────
    const compRows = (a?.comparable_projects || []).map(c => {
      const vsT2 = lang === 'en' ? (c.vs_en || c.vs || "") : (c.vs || "");
      const simT2 = lang === 'en' ? (c.similarity_en || c.similarity || "") : (c.similarity || "");
      const vc = (c.vs||"").includes("折价") || (c.vs_en||"").toLowerCase().includes("discount") ? "#059669" : (c.vs||"").includes("溢价") || (c.vs_en||"").toLowerCase().includes("premium") ? "#dc2626" : "#6b7280";
      return `<tr>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-weight:600">${escapeHtml(c.name)}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#2563eb;font-weight:600">${escapeHtml(c.fdv)}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280;font-size:11px">${escapeHtml(simT2)}</td>
        <td style="padding:7px 12px;border-bottom:1px solid #f3f4f6;font-weight:600;color:${vc}">${escapeHtml(vsT2)}</td>
      </tr>`;
    }).join("");

    // ── Sybil 风险检测 ─────────────────────────────────
    const dataScore = a?.scores?.data || 0;
    const notesData = (a?.notes?.data || "").toLowerCase();
    const hasSybilRisk = dataScore < 50 ||
      ["测试网", "testnet", "水分", "sybil", "刷量", "空投刷"].some(kw => notesData.includes(kw));

    // ── 退出路径 ─────────────────────────────────────────
    const exitPaths = a?.exit_paths || [];

    // ── Fundraise.fun 数据 ────────────────────────────
    const ff = ffMap[project.name] || null;
    const ffSection = ff
      ? ff.found
        ? `<div style="margin-top:10px;padding:8px 12px;background:#fffbeb;border-left:3px solid #f59e0b;border-radius:0 6px 6px 0;font-size:11px;color:#92400e">
             <strong>fundraise.fun 融资记录：</strong>${escapeHtml(ff.label || ff.round || "有融资记录")}
             ${ff.amount ? `<span style="margin-left:6px;font-weight:700">${escapeHtml(ff.amount)}</span>` : ""}
             ${ff.investors ? `<span style="color:#78350f;margin-left:6px">· ${escapeHtml(ff.investors)}</span>` : ""}
             <span style="color:#9ca3af;margin-left:6px">（西方 VC 数据库）</span>
           </div>`
        : `<div style="margin-top:10px;font-size:11px;color:#059669;background:#f0fdf4;border-left:3px solid #86efac;border-radius:0 6px 6px 0;padding:6px 12px">
             <strong>双源零融资确认：</strong>RootData 无融资记录，fundraise.fun（9,700+ 西方 VC 数据）亦无记录
           </div>`
      : "";

    // ── 评分趋势 ─────────────────────────────────────
    const scoreTrend = (() => {
      if (history.length < 2) return null;
      const sorted = [...history].sort((a, b) => new Date(a.scored_at) - new Date(b.scored_at));
      const prev = sorted[sorted.length - 2];
      const curr = sorted[sorted.length - 1];
      const delta = (curr.composite || 0) - (prev.composite || 0);
      if (delta === 0) return { text: "与上次持平", color: "#6b7280", icon: "→" };
      if (delta > 0)  return { text: `较上次 +${delta} 分`, color: "#059669", icon: "↑" };
      return { text: `较上次 ${delta} 分`, color: "#dc2626", icon: "↓" };
    })();

    // ── 数据来源 ─────────────────────────────────────────
    const today = new Date().toLocaleDateString("zh-CN");
    const srcRows = [
      { field: "项目信息、融资记录、团队、投资人", src: "RootData (rootdata.com)" },
      ff                         && { field: "西方 VC 融资核查（9,700+ 条记录）", src: "fundraise.fun" },
      project.cgData             && { field: "代币价格、FDV、24h交易量、社区情绪、Watchlist", src: "CoinGecko API (coingecko.com)" },
      cgComm?.twitter_followers  && { field: "Twitter 粉丝数、社区看涨情绪", src: "CoinGecko Community Data" },
      project.defiData?.protocol && { field: "协议 TVL、收益、费用", src: "DeFiLlama (defillama.com)" },
      users?.daily               && { field: "日活/周活用户数", src: "DeFiLlama Users API" },
      github                     && { field: "近4周 Commits、Stars、Forks", src: `GitHub API · ${github.owner||""}/${github.repo||""}` },
      dune                       && { field: "链上流动性深度、DEX 实时价格", src: `Dune Sim API · ${dune.chain||""}` },
      traffic                    && { field: "网站月访问量估算、流量规模、增长趋势、地区分布", src: "AI 分析估算（WebIntel）· 仅供参考" },
    ].filter(Boolean).map(row => `
      <tr>
        <td style="padding:6px 12px;border-bottom:1px solid #f3f4f6">${row.field}</td>
        <td style="padding:6px 12px;border-bottom:1px solid #f3f4f6;color:#2563eb">${row.src}</td>
        <td style="padding:6px 12px;border-bottom:1px solid #f3f4f6;color:#9ca3af;font-size:11px">${today}</td>
      </tr>`).join("");

    // ── HTML ──────────────────────────────────────────────
    const html = `<!DOCTYPE html>
<html lang="zh-CN">
<head>
<meta charset="UTF-8">
<title>${project.name} — 投资分析简报 · PrimaryLens</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:'Helvetica Neue','PingFang SC',sans-serif;color:#111827;font-size:13px;line-height:1.75;background:#fff;padding:48px;max-width:960px;margin:0 auto}
  h1{font-size:26px;font-weight:900;letter-spacing:-0.5px}
  h2{font-size:10px;font-weight:700;color:#9ca3af;letter-spacing:1.5px;text-transform:uppercase;margin:32px 0 14px;padding-bottom:8px;border-bottom:1.5px solid #e5e7eb}
  table{width:100%;border-collapse:collapse;font-size:12px}
  th{padding:8px 12px;background:#f9fafb;text-align:left;font-size:10px;color:#6b7280;font-weight:700;text-transform:uppercase;letter-spacing:0.5px}
  ul{padding-left:18px;margin:0}
  li{margin-bottom:4px;font-size:12px;line-height:1.6}
  a{color:#2563eb;text-decoration:none}
  .section{margin-bottom:4px}
  .src-tag{display:inline-block;font-size:10px;background:#eff6ff;color:#2563eb;border:1px solid #bfdbfe;padding:1px 5px;border-radius:3px;margin-left:4px;vertical-align:middle}
  @media print{body{padding:24px}h2{break-after:avoid}.pl-toolbar{display:none!important}}
</style>
</head>
<body>

<!-- ═══ TOOLBAR ═══ -->
<div class="pl-toolbar" style="position:sticky;top:0;z-index:100;background:#fff;border-bottom:1px solid #e5e7eb;padding:10px 48px;margin:-48px -48px 32px;display:flex;align-items:center;justify-content:space-between;gap:12px">
  <span style="font-size:12px;font-weight:700;color:#1e293b;letter-spacing:0.01em">PrimaryLens · 投资简报</span>
  <div style="display:flex;align-items:center;gap:6px">
    <button onclick="copyRichText()" id="btn-copy" style="display:flex;align-items:center;gap:6px;padding:6px 14px;border:1px solid #e5e7eb;border-radius:6px;background:#fff;cursor:pointer;font-size:12px;font-weight:500;color:#374151;transition:background 0.15s" onmouseover="this.style.background='#f9fafb'" onmouseout="this.style.background='#fff'">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/></svg>
      复制
    </button>
    <button onclick="exportPDF()" style="display:flex;align-items:center;gap:6px;padding:6px 14px;border:1px solid #e5e7eb;border-radius:6px;background:#fff;cursor:pointer;font-size:12px;font-weight:500;color:#374151;transition:background 0.15s" onmouseover="this.style.background='#f9fafb'" onmouseout="this.style.background='#fff'">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="12" y1="18" x2="12" y2="12"/><line x1="9" y1="15" x2="15" y2="15"/></svg>
      导出 PDF
    </button>
    <button onclick="exportNotion()" style="display:flex;align-items:center;gap:6px;padding:6px 14px;border:1px solid #e5e7eb;border-radius:6px;background:#fff;cursor:pointer;font-size:12px;font-weight:500;color:#374151;transition:background 0.15s" onmouseover="this.style.background='#f9fafb'" onmouseout="this.style.background='#fff'">
      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM17 14l-3 6h6l-3-6z"/></svg>
      导出 Notion
    </button>
    <div id="pl-toast" style="display:none;margin-left:8px;font-size:11px;color:#059669;font-weight:500"></div>
  </div>
</div>
<script>
  const PROXY = '${RD_BASE}';
  const PROJECT = '${(project?.name || "").replace(/'/g, "\\'")}';
  function track(ev) {
    try { fetch(PROXY + '/api/track', { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ event: ev, project: PROJECT, ts: new Date().toISOString() }) }).catch(()=>{}); } catch(_){}
  }
  function showToast(msg) {
    const t = document.getElementById('pl-toast');
    t.textContent = msg; t.style.display = 'block';
    setTimeout(() => { t.style.display = 'none'; }, 2500);
  }
  function exportPDF() {
    track('memo_pdf');
    window.print();
  }
  async function copyRichText() {
    track('memo_copy');
    const btn = document.getElementById('btn-copy');
    try {
      const html = document.documentElement.outerHTML;
      await navigator.clipboard.write([new ClipboardItem({ 'text/html': new Blob([html], { type:'text/html' }) })]);
      showToast('✓ 已复制，可粘贴到 Notion / Google Docs');
    } catch(e) {
      showToast('浏览器限制，请使用 Ctrl+A 全选后复制');
    }
  }
  function exportNotion() {
    track('memo_notion');
    const text = document.body.innerText;
    navigator.clipboard.writeText(text).then(() => {
      showToast('✓ 已复制文本，粘贴到 Notion 页面即可');
    }).catch(() => {
      showToast('请手动 Ctrl+A 全选复制');
    });
  }
</script>

<!-- ═══ HEADER ═══ -->
<div style="display:flex;justify-content:space-between;align-items:flex-start;padding-bottom:24px;border-bottom:2.5px solid #111827;margin-bottom:28px">
  <div>
    <div style="font-size:10px;color:#9ca3af;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:10px">
      Investment Analysis Brief · PrimaryLens · ${new Date(project.analyzedAt || Date.now()).toLocaleDateString("zh-CN")}
    </div>
    <h1>${project.name}</h1>
    <div style="margin-top:8px;display:flex;gap:8px;align-items:center;flex-wrap:wrap">
      ${a?.track ? `<span style="font-size:11px;font-weight:600;background:#f3f4f6;padding:2px 9px;border-radius:12px;color:#374151">${lang === 'en' ? (a.track_en || a.track) : a.track}</span>` : ""}
      ${a?.stage_label ? `<span style="font-size:11px;font-weight:600;background:#eff6ff;padding:2px 9px;border-radius:12px;color:#2563eb">${lang === 'en' ? (STAGE_WEIGHTS[a.stage_key]?.label_en || a.stage_label) : a.stage_label}</span>` : ""}
      ${a?.tge_status ? `<span style="font-size:11px;color:#6b7280">TGE: ${lang === 'en' ? (a.tge_status_en || enify(a.tge_status)) : a.tge_status}</span>` : ""}
      ${project.details?.token_symbol ? `<span style="font-size:11px;color:#6b7280">$${project.details.token_symbol}</span>` : ""}
    </div>
  </div>
  <div style="text-align:right;flex-shrink:0;padding-left:24px">
    <div style="font-size:10px;color:#9ca3af;margin-bottom:4px">综合评分</div>
    <div style="font-size:52px;font-weight:900;color:${r.fg};line-height:1">${effComposite || 0}</div>
    <div style="font-size:14px;font-weight:800;color:${r.fg}">${r.label} 级</div>
    ${scoreTrend ? `<div style="font-size:11px;color:${scoreTrend.color};margin-top:4px;font-weight:600">${scoreTrend.icon} ${scoreTrend.text}</div>` : ""}
    ${history.length > 0 ? `<div style="font-size:10px;color:#9ca3af;margin-top:2px">共分析 ${history.length} 次</div>` : ""}
  </div>
</div>

<!-- ═══ 一、执行摘要 ═══ -->
<div class="section">
  <h2>${lang === 'en' ? '1. Executive Summary' : '一、执行摘要'}</h2>

  <!-- 投资结论主框 -->
  <div style="border:1.5px solid ${verdictColor}44;border-radius:12px;overflow:hidden;margin-bottom:16px">
    <div style="background:${verdictBg};padding:18px 22px;display:flex;justify-content:space-between;align-items:center;gap:16px">
      <div>
        <div style="font-size:9px;color:#6b7280;font-weight:700;letter-spacing:1.2px;text-transform:uppercase;margin-bottom:4px">${lang === 'en' ? 'INVESTMENT VERDICT' : '投资结论'}</div>
        <div style="font-size:24px;font-weight:900;color:${verdictColor};line-height:1">${verdict}</div>
        ${bestTier ? `<div style="font-size:11px;color:#6b7280;margin-top:6px">${lang === 'en' ? 'Suggested entry range:' : '建议入场区间：'}<strong style="color:${verdictColor}">${bestTier.fdv_range}</strong></div>` : ""}
      </div>
      ${irrData ? `<div style="text-align:right">
        <div style="font-size:9px;color:#6b7280;font-weight:700;letter-spacing:1px;text-transform:uppercase;margin-bottom:2px">ALPHA SCORE</div>
        <div style="font-size:40px;font-weight:900;color:${alpha>150?"#059669":alpha>70?"#2563eb":"#6b7280"};line-height:1.1">${alpha}</div>
        <div style="font-size:11px;color:#6b7280">${lang === 'en' ? 'Expected' : '预期'} <strong>${irrData.expectedMult.toFixed(2)}x</strong> · IRR <strong>${(irrData.expectedIRR*100).toFixed(1)}%</strong></div>
      </div>` : ""}
    </div>
    <!-- 一句话核心逻辑 -->
    ${a?.recommendation ? `<div style="padding:12px 22px;background:#fff;border-top:1px solid ${verdictColor}22;font-size:12px;color:#374151;line-height:1.7">
      <span style="font-weight:700;color:${verdictColor}">${lang === 'en' ? 'Core Thesis:' : '核心逻辑：'}</span>${lang === 'en' ? (a.recommendation_en || a.recommendation) : a.recommendation}
    </div>` : a?.summary ? `<div style="padding:12px 22px;background:#fff;border-top:1px solid ${verdictColor}22;font-size:12px;color:#374151;line-height:1.7">
      <span style="font-weight:700;color:#374151">${lang === 'en' ? 'Core Thesis:' : '核心逻辑：'}</span>${lang === 'en' ? (a.summary_en || a.summary) : a.summary}
    </div>` : ""}
  </div>

  <!-- 护城河 -->
  ${a?.moat ? `<div style="border-left:3.5px solid #f59e0b;padding:10px 14px;background:#fffbeb;border-radius:0 6px 6px 0;margin-bottom:14px;font-size:12px;color:#92400e"><strong>${lang === 'en' ? 'Core Moat:' : '核心护城河：'}</strong>${lang === 'en' ? (a.moat_en || a.moat) : a.moat}</div>` : ""}

  <!-- 核心优势 -->
  ${(a?.strengths||[]).length > 0 ? `<div style="margin-bottom:14px">
    <div style="font-size:10px;font-weight:700;color:#374151;letter-spacing:0.8px;margin-bottom:8px">${lang === 'en' ? 'Core Strengths' : '核心优势'}</div>
    <div style="display:flex;flex-direction:column;gap:6px">
      ${a.strengths.map((s,i) => `<div style="display:flex;align-items:flex-start;gap:10px;font-size:12px;color:#374151">
        <span style="width:6px;height:6px;margin-top:6px;border-radius:50%;background:#059669;flex-shrink:0"></span>
        <span style="line-height:1.6">${s}</span>
      </div>`).join("")}
    </div>
  </div>` : ""}

  ${(a?.strengths_en||[]).length > 0 && a.strengths_en[0] !== (a?.strengths||[])[0] ? `
  <div style="margin-bottom:14px;padding:10px 14px;background:#f0f9ff;border-radius:7px;border-left:3px solid #93c5fd">
    <div style="font-size:9px;font-weight:700;color:#93c5fd;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:6px">EN · Core Strengths</div>
    ${a.strengths_en.map(s => `<div style="font-size:11px;color:#374151;margin-bottom:5px;display:flex;gap:6px;align-items:flex-start"><span style="width:5px;height:5px;margin-top:5px;border-radius:50%;background:#93c5fd;flex-shrink:0"></span><span>${s}</span></div>`).join("")}
  </div>` : ""}

  <!-- 退出路径 -->
  ${exitPaths.length > 0 ? `<div style="margin-bottom:14px">
    <div style="font-size:10px;font-weight:700;color:#374151;letter-spacing:0.8px;margin-bottom:8px">${lang === 'en' ? 'Exit Paths' : '退出路径'}</div>
    <div style="display:flex;flex-direction:column;gap:6px">
      ${exitPaths.map((ep, i) => `<div style="display:flex;align-items:flex-start;gap:10px;font-size:12px;color:#374151">
        <span style="min-width:20px;height:20px;border-radius:50%;background:#e0e7ff;color:#4f46e5;font-size:10px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px">${i+1}</span>
        <span style="line-height:1.6">${typeof ep === "string" ? ep : (ep.trigger ? `${ep.trigger}${ep.scenario ? "：" + ep.scenario : ""}${ep.target_multiple ? "（" + ep.target_multiple + "）" : ""}` : JSON.stringify(ep))}</span>
      </div>`).join("")}
    </div>
  </div>` : ""}
</div>

<!-- ═══ 二、IRR 回报预测 ═══ -->
${irrData ? `
<div class="section">
  <h2>${lang === 'en' ? `2. Return Forecast (3-Scenario IRR, Hold ${HOLD_YEARS} Yrs)` : `二、回报预测（三情景 IRR · 持有 ${HOLD_YEARS} 年）`}</h2>
  <div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:12px;margin-bottom:14px">
    <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;text-align:center">
      <div style="font-size:10px;color:#6b7280;font-weight:700;letter-spacing:0.8px;margin-bottom:4px">${entryLabel}</div>
      <div style="font-size:22px;font-weight:800">${fmt(entryVal)}</div>
    </div>
    <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;text-align:center">
      <div style="font-size:10px;color:#6b7280;font-weight:700;letter-spacing:0.8px;margin-bottom:4px">${lang === 'en' ? 'AI Fair Value' : 'AI 公允价值'}</div>
      <div style="font-size:22px;font-weight:800">${fmt(fairVal)}</div>
      <div style="font-size:10px;color:#6b7280;margin-top:2px">${(() => { const vb = lang === 'en' ? (a?.valuation_basis_en || a?.valuation_basis || "") : (a?.valuation_basis || ""); return vb.slice(0, 60) + (vb.length > 60 ? "…" : ""); })()}</div>
    </div>
    <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:12px;text-align:center">
      <div style="font-size:10px;color:#6b7280;font-weight:700;letter-spacing:0.8px;margin-bottom:4px">${lang === 'en' ? 'Margin of Safety' : '安全边际'}</div>
      <div style="font-size:22px;font-weight:800;color:${irrData.underMult>=1?"#059669":"#dc2626"}">${irrData.underMult.toFixed(2)}x</div>
      <div style="font-size:10px;color:#6b7280;margin-top:2px">${irrData.underMult>=1?(lang === 'en' ? 'Trading below fair value' : '估值折价（低于公允）'):(lang === 'en' ? 'Trading above fair value' : '估值溢价（高于公允）')}</div>
    </div>
  </div>
  <table>
    <thead><tr><th>${lang === 'en' ? 'Scenario' : '情景'}</th><th>${lang === 'en' ? 'Probability' : '概率'}</th><th>${lang === 'en' ? 'Exit Return' : '退出回报'}</th><th>${lang === 'en' ? 'Annualized IRR' : '年化 IRR'}</th></tr></thead>
    <tbody>${irrData.scenarios.map(s => {
      const sc = s.label === "牛市" ? "#059669" : s.label === "中性" ? "#d97706" : "#dc2626";
      const scenarioLabelEn = { '牛市': 'Bull', '中性': 'Neutral', '熊市': 'Bear' }[s.label] || s.label;
      return `<tr>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;font-weight:700;color:${sc}">${lang === 'en' ? scenarioLabelEn : s.label}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280;font-size:11px">概率 ${Math.round(s.prob*100)}%</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;font-weight:700;color:${sc}">${s.exitMult.toFixed(1)}x</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;font-weight:700;color:${sc}">${(s.irr*100).toFixed(0)}% / yr</td>
      </tr>`;
    }).join("")}</tbody>
    <tfoot>
      <tr style="background:#f9fafb">
        <td colspan="2" style="padding:9px 12px;font-weight:700;color:#374151">加权预期</td>
        <td style="padding:9px 12px;font-weight:800;color:#1f2937">${irrData.expectedMult.toFixed(2)}x</td>
        <td style="padding:9px 12px;font-weight:800;color:${irrData.expectedIRR>0.3?"#059669":"#d97706"}">${(irrData.expectedIRR*100).toFixed(1)}% / yr</td>
      </tr>
    </tfoot>
  </table>

  ${(a?.comparable_projects||[]).length > 0 ? `
  <div style="margin-top:20px">
    <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:10px">竞品估值对标</div>
    <table>
      <thead><tr><th>竞品</th><th>当前 FDV</th><th>相似度</th><th>相对定价</th></tr></thead>
      <tbody>${compRows}</tbody>
    </table>
  </div>` : ""}
</div>` : ""}

<!-- ═══ 三、六维详细分析 ═══ -->
<!-- 注：原"叙事 × 链上信号矩阵"已并入下方 4.1 叙事与愿景（叙事热度）+ 4.5 数据表现（链上活跃），
     避免上方矩阵卡与六维详细分析重复呈现同一组指标。详情页保留矩阵卡作为快速视觉总览。 -->
<div class="section">
  <h2>${lang === 'en' ? '3. Six-Dimension Analysis' : '三、六维详细分析'}${a?.stage_label ? `&nbsp;&nbsp;<span style="font-size:10px;font-weight:600;background:#eff6ff;color:#2563eb;padding:2px 8px;border-radius:10px;letter-spacing:0">${lang === 'en' ? (STAGE_WEIGHTS[a.stage_key]?.label_en || a.stage_label) + ' Weights' : a.stage_label + '权重'}</span>` : ""}</h2>

  <!-- 4.1 叙事与愿景 -->
  ${dimCard("narrative", lang === 'en' ? "Narrative & Vision" : "叙事与愿景", a?.scores?.narrative, `
    <div style="display:flex;flex-wrap:wrap;gap:10px">
      ${miniCard(lang === 'en' ? "Narrative Heat" : "叙事热度", q?.narrativeScore != null ? `${q.narrativeScore}/100` : null, "CoinGecko", q?.narrativeScore >= 60 ? "#3b82f6" : "#6b7280")}
      ${miniCard(lang === 'en' ? "Twitter Followers" : "Twitter 粉丝", cgComm?.twitter_followers ? (cgComm.twitter_followers/1000).toFixed(0)+"K" : null, "CoinGecko", "#1d9bf0")}
      ${miniCard(lang === 'en' ? "Bullish Sentiment" : "社区看涨情绪", cgSent != null ? Math.round(cgSent)+"%" : null, "CoinGecko", cgSent>=60?"#059669":cgSent>=40?"#d97706":"#dc2626")}
      ${miniCard(lang === 'en' ? "Watchlist Users" : "Watchlist 用户", project.cgData?.watchlist_portfolio_users ? (project.cgData.watchlist_portfolio_users/1000).toFixed(0)+"K" : null, "CoinGecko")}
      ${miniCard(lang === 'en' ? "Track" : "赛道", lang === 'en' ? (a?.track_en || a?.track || null) : (a?.track || null))}
    </div>
  `)}

  <!-- 4.2 技术与产品 -->
  ${dimCard("tech", lang === 'en' ? "Tech & Product" : "技术与产品", a?.scores?.tech, `
    <!-- GitHub 数据卡 -->
    <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:${techIntel ? 14 : 0}px">
      ${miniCard(lang === 'en' ? "Commits (4w)" : "近4周 Commits", github?.commits4w != null ? String(github.commits4w) : null, "GitHub", github?.commits4w > 20 ? "#059669" : "#374151")}
      ${miniCard("GitHub Stars", github?.stars ? github.stars.toLocaleString() : null, "GitHub")}
      ${miniCard("Forks", github?.forks ? github.forks.toLocaleString() : null, "GitHub")}
      ${miniCard(lang === 'en' ? "Product Stage" : "产品阶段", lang === 'en' ? (techIntel?.product_stage_en || techIntel?.product_stage || STAGE_WEIGHTS[a?.stage_key]?.label_en || null) : (techIntel?.product_stage || a?.stage_label || null))}
    </div>
    ${!github && !techIntel ? `<div style="font-size:11px;color:#9ca3af;margin-top:4px">${lang === 'en' ? 'No GitHub data available' : '暂无 GitHub 数据'}</div>` : ""}

    ${techIntel ? `
    <!-- 技术产品情报（AI 深度分析） -->
    <div style="border-top:1px solid #f3f4f6;padding-top:14px;margin-top:${github ? 0 : -10}px">
      <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:10px">
        ${lang === 'en' ? 'Tech &amp; Product Intel' : '技术产品情报'} <span style="font-size:9px;font-weight:400;background:#fef3c7;color:#92400e;padding:1px 6px;border-radius:4px;text-transform:none">${lang === 'en' ? 'AI Deep Analysis' : 'AI 深度分析'}</span>
      </div>

      <!-- 产品阶段 + GitHub 活跃度 -->
      <div style="display:flex;gap:8px;flex-wrap:wrap;margin-bottom:12px">
        ${techIntel.github_activity ? `<span style="font-size:11px;font-weight:600;padding:3px 10px;border-radius:20px;${techIntel.github_activity.includes("活跃") ? "background:#ecfdf5;color:#059669" : "background:#f3f4f6;color:#6b7280"}">GitHub: ${techIntel.github_activity}</span>` : ""}
      </div>

      <div style="display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:12px">
        ${(lang === 'en' ? (techIntel.product_features_en || techIntel.product_features) : techIntel.product_features||[]).length > 0 ? `
        <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px 12px">
          <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:6px">${lang === 'en' ? 'Core Features' : '核心功能'}</div>
          ${(lang === 'en' ? (techIntel.product_features_en || techIntel.product_features) : techIntel.product_features).map(f => `<div style="font-size:11px;color:#374151;margin-bottom:4px;display:flex;align-items:flex-start;gap:6px"><span style="width:5px;height:5px;margin-top:5px;border-radius:50%;background:#10b981;flex-shrink:0"></span><span>${f}</span></div>`).join("")}
        </div>` : ""}

        ${(lang === 'en' ? (techIntel.differentiators_en || techIntel.differentiators) : techIntel.differentiators||[]).length > 0 ? `
        <div style="background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:10px 12px">
          <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:6px">${lang === 'en' ? 'Tech Differentiation' : '技术差异化'}</div>
          ${(lang === 'en' ? (techIntel.differentiators_en || techIntel.differentiators) : techIntel.differentiators).map(d => `<div style="font-size:11px;color:#374151;margin-bottom:4px;display:flex;align-items:flex-start;gap:6px"><span style="width:5px;height:5px;margin-top:5px;border-radius:50%;background:#f59e0b;flex-shrink:0"></span><span>${d}</span></div>`).join("")}
        </div>` : ""}
      </div>

      ${(techIntel.tech_stack||[]).length > 0 ? `
      <div style="margin-bottom:10px">
        <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:5px">${lang === 'en' ? 'Tech Stack' : '技术栈'}</div>
        <div style="display:flex;gap:5px;flex-wrap:wrap">
          ${techIntel.tech_stack.map(t => `<span style="font-size:10px;background:#f3f4f6;color:#374151;padding:2px 8px;border-radius:20px;border:1px solid #e5e7eb">${t}</span>`).join("")}
        </div>
      </div>` : ""}

      ${(lang === 'en' ? (techIntel.data_evidence_en || techIntel.data_evidence) : techIntel.data_evidence||[]).filter(Boolean).length > 0 ? `
      <div style="margin-bottom:10px">
        <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.5px;text-transform:uppercase;margin-bottom:5px">${lang === 'en' ? 'Quantifiable Data' : '可量化数据'}</div>
        <div style="display:flex;gap:5px;flex-wrap:wrap">
          ${(lang === 'en' ? (techIntel.data_evidence_en || techIntel.data_evidence) : techIntel.data_evidence).filter(Boolean).map(d => `<span style="font-size:10px;background:#ecfdf5;color:#059669;padding:2px 8px;border-radius:20px;border:1px solid #bbf7d0">${d}</span>`).join("")}
        </div>
      </div>` : ""}

      ${(lang === 'en' ? (techIntel.competitor_comparison_en || techIntel.competitor_comparison) : techIntel.competitor_comparison) ? `
      <div style="padding:8px 12px;background:#fffbeb;border-left:3px solid #f59e0b;border-radius:0 5px 5px 0;margin-bottom:8px">
        <div style="font-size:10px;font-weight:700;color:#92400e;margin-bottom:3px">${lang === 'en' ? 'Competitor Comparison' : '竞品对比'}</div>
        <div style="font-size:11px;color:#78350f;line-height:1.65">${lang === 'en' ? (techIntel.competitor_comparison_en || techIntel.competitor_comparison) : techIntel.competitor_comparison}</div>
      </div>` : ""}

      ${(lang === 'en' ? (techIntel.tech_risks_en || techIntel.tech_risks) : techIntel.tech_risks||[]).length > 0 ? `
      <div style="padding:8px 12px;background:#fef2f2;border-left:3px solid #fca5a5;border-radius:0 5px 5px 0;margin-bottom:8px">
        <div style="font-size:10px;font-weight:700;color:#b91c1c;margin-bottom:4px">${lang === 'en' ? 'Tech Risks' : '技术风险'}</div>
        ${(lang === 'en' ? (techIntel.tech_risks_en || techIntel.tech_risks) : techIntel.tech_risks).map(r => `<div style="font-size:11px;color:#7f1d1d;margin-bottom:3px;display:flex;gap:5px"><span>·</span><span>${r}</span></div>`).join("")}
      </div>` : ""}

      ${(lang === 'en' ? (techIntel.tech_score_basis_en || techIntel.tech_score_basis) : techIntel.tech_score_basis) ? `
      <div style="font-size:10px;color:#6b7280;padding:5px 8px;background:#f9fafb;border-radius:5px;border-left:2px solid #d1d5db">
        ${lang === 'en' ? 'Score basis: ' : '评分依据：'}${lang === 'en' ? (techIntel.tech_score_basis_en || techIntel.tech_score_basis) : techIntel.tech_score_basis}
      </div>` : ""}

      <div style="font-size:10px;color:#9ca3af;margin-top:8px">${lang === 'en' ? 'Based on AI knowledge base analysis; verify against the official site and latest announcements' : '基于 AI 知识库分析，建议结合官网与最新公告核实'}</div>
    </div>` : ""}
  `)}

  <!-- 4.3 团队背景 -->
  ${dimCard("team", lang === 'en' ? "Team" : "团队背景", a?.scores?.team, `
    ${memberCards
      ? `<div style="margin-bottom:${fundingRows ? 16 : 0}px">
           <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:2px">${lang === 'en' ? 'Core Members' : '核心成员'} <span class="src-tag">RootData</span></div>
           ${memberCards}
         </div>`
      : `<div style="font-size:11px;color:#9ca3af;margin-bottom:10px">${lang === 'en' ? 'Member data not yet available (RootData)' : '成员数据暂未获取（RootData）'}</div>`}
    ${fundingRows ? `<div style="font-size:10px;font-weight:700;color:#374151;margin-bottom:6px;margin-top:4px">${lang === 'en' ? 'Funding History' : '融资历史'} <span class="src-tag">RootData</span></div>
    <table>
      <thead><tr><th>${lang === 'en' ? 'Round' : '轮次'}</th><th>${lang === 'en' ? 'Amount' : '金额'}</th><th>${lang === 'en' ? 'Lead Investors' : '主要投资方'}</th><th>${lang === 'en' ? 'Date' : '日期'}</th></tr></thead>
      <tbody>${fundingRows}</tbody>
    </table>
    ${totalFund > 0 ? `<div style="font-size:11px;color:#6b7280;margin-top:6px;text-align:right">${lang === 'en' ? 'Total raised' : '累计融资'} <strong style="color:#1f2937">${fmt(totalFund)}</strong></div>` : ""}` : ""}
    ${ffSection}
  `)}

  <!-- 4.4 代币经济学 -->
  ${dimCard("tokenomics", lang === 'en' ? "Tokenomics" : "代币经济学", a?.scores?.tokenomics, `
    ${(() => {
      // 全局市场背景横幅（仅有数据时显示）
      const mc = project.marketContext;
      if (!mc) return "";
      const fg = mc.fearGreed;
      const as = mc.altSeason;
      const fgColor = fg ? (fg.value >= 70 ? "#059669" : fg.value >= 50 ? "#d97706" : fg.value >= 30 ? "#d97706" : "#dc2626") : "#6b7280";
      return `<div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:14px;padding:10px 12px;background:#f8fafc;border:1px solid #e2e8f0;border-radius:8px;align-items:center">
        <span style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-right:4px">${lang === 'en' ? 'Market Context' : '市场背景'}</span>
        ${fg ? `<span style="font-size:11px;padding:2px 8px;border-radius:12px;background:${fgColor}20;color:${fgColor};border:1px solid ${fgColor}40;font-weight:600">
          ${lang === 'en' ? 'F&G' : '恐慌贪婪'} ${fg.value} · ${lang === 'en' ? fg.label : fg.label}
        </span>` : ""}
        ${as ? `<span style="font-size:11px;padding:2px 8px;border-radius:12px;background:${as.color}20;color:${as.color};border:1px solid ${as.color}40;font-weight:600">
          ${lang === 'en' ? as.label_en : as.label} · BTC ${as.btcDom}%
        </span>` : ""}
        ${mc.totalMarketCap ? `<span style="font-size:10px;color:#6b7280">
          ${lang === 'en' ? 'Mkt Cap' : '总市值'} ${fmt(mc.totalMarketCap)}${mc.marketCapChange24h != null ? ` <span style="color:${mc.marketCapChange24h >= 0 ? '#059669' : '#dc2626'}">${mc.marketCapChange24h >= 0 ? '+' : ''}${mc.marketCapChange24h}%</span>` : ""}
        </span>` : ""}
        <span style="font-size:9px;color:#9ca3af;margin-left:auto">alternative.me · CoinGecko</span>
      </div>`;
    })()}
    <!-- Artemis 风格估值指标表（CoinGecko + DefiLlama 计算，无额外 API） -->
    ${(() => {
      const mc   = project.cgData?.market_data?.market_cap?.usd;
      const fdmc = project.cgData?.market_data?.fully_diluted_valuation?.usd;
      // ── 估值 & 核心倍数：迁移自 data-views/html/valuationHtml.js ──
      // 后续 PR 把"活动指标"（DAU, daily fees, 协议分成率）迁入 protocol_fundamentals view
      const valuationHtml = buildValuationHtml(project, a, lang);

      // ── 活动指标（暂保留，待 protocol_fundamentals view 迁移）──
      const am = project.artemisMetrics;
      const fees24h = (am?.ok && am.perpFees24h) ? am.perpFees24h : project.defiData?.fees?.daily || null;
      const rev24h  = (am?.ok && am.revenue24h)  ? am.revenue24h  : project.defiData?.revenue?.daily || null;
      const fees30dRate = (am?.ok && am.perpFees30d) ? am.perpFees30d : project.defiData?.fees?.monthly || null;
      const rev30dRate  = (am?.ok && am.revenue30d)  ? am.revenue30d  : project.defiData?.revenue?.monthly || null;
      const duneDays = project.duneAnalytics?.days || [];
      const duneIsHL = project.duneAnalytics?.source === 'hyperliquid_market_data';
      const duneAvgTraders = (!duneIsHL && duneDays.length > 0)
        ? Math.round(duneDays.reduce((s, d) => s + (d.unique_traders || 0), 0) / duneDays.length)
        : 0;
      const dau = (am?.ok && am.perpDauLatest) ? am.perpDauLatest
                : duneAvgTraders > 0                ? duneAvgTraders
                : project.defiData?.users?.daily    || null;
      const dauSrc = (am?.ok && am.perpDauLatest) ? 'Hyperliquid'
                   : duneAvgTraders > 0             ? 'Dune (30d avg)'
                   : 'DeFiLlama';
      const fmtV = v => v == null ? "—" : v >= 1e9 ? "$"+(v/1e9).toFixed(2)+"B" : v >= 1e6 ? "$"+(v/1e6).toFixed(1)+"M" : v >= 1e3 ? "$"+(v/1e3).toFixed(0)+"K" : "$"+v.toFixed(0);
      const activityRows = [
        ...(fees24h ? [{ label: lang === 'en' ? "Fees (Daily)" : "手续费（日）", value: fmtV(fees24h) }] : []),
        ...(rev24h  ? [{ label: lang === 'en' ? "Revenue (Daily)" : "收入（日）",  value: fmtV(rev24h)  }] : []),
        ...(dau     ? [{ label: lang === 'en' ? `Daily Active Users (${dauSrc})` : `日活用户（${dauSrc}）`, value: dau >= 1e6 ? (dau/1e6).toFixed(2)+"M" : dau >= 1e3 ? (dau/1e3).toFixed(0)+"K" : dau.toLocaleString(), color: "#2563eb" }] : []),
        ...(fees30dRate && rev30dRate ? [{ label: lang === 'en' ? "Revenue/Fee Rate" : "协议分成率", value: (rev30dRate/fees30dRate*100).toFixed(1)+"%", color: "#8b5cf6" }] : []),
      ];
      const activityHtml = activityRows.length > 0 ? `
      <div style="margin-bottom:14px">
        <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:8px">
          ${lang === 'en' ? 'Activity Metrics' : '活动指标'}
          <span class="src-tag" style="margin-left:4px">DefiLlama</span>
        </div>
        <div style="display:grid;grid-template-columns:1fr 1fr;gap:0;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;font-size:12px">
          ${activityRows.map((row, i) => `
            <div style="display:flex;justify-content:space-between;align-items:center;padding:7px 12px;background:${i%2===0?'#ffffff':'#f9fafb'};border-bottom:1px solid #f3f4f6">
              <span style="color:#6b7280;font-size:11px">${row.label}</span>
              <span style="font-weight:600;color:${row.color||'#111827'};font-size:12px">${row.value}</span>
            </div>
          `).join("")}
        </div>
      </div>` : '';

      return valuationHtml + activityHtml;
    })()}

    <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:12px">
      ${miniCard(lang === 'en' ? "Current FDV" : "当前 FDV", fmt(cgFdvRaw || null), "CoinGecko", "#2563eb")}
      ${(() => {
        const circ = project.cgData?.market_data?.circulating_supply;
        const total = project.cgData?.market_data?.total_supply;
        const pct = circ && total ? ((circ / total) * 100).toFixed(0) + "%" : null;
        return miniCard(lang === 'en' ? "Circ. Ratio" : "流通率", pct, lang === 'en' ? "Circ./Total Supply" : "流通/总供应量", pct && parseInt(pct) > 60 ? "#059669" : "#d97706");
      })()}
      ${miniCard(lang === 'en' ? "Token" : "代币符号", project.details?.token_symbol ? "$" + project.details.token_symbol : null)}
      ${(() => {
        const athChg = project.cgData?.market_data?.ath_change_percentage?.usd;
        if (athChg == null) return "";
        const display = `${athChg.toFixed(1)}%`;
        const color = athChg > -20 ? "#059669" : athChg > -50 ? "#d97706" : "#dc2626";
        return miniCard(lang === 'en' ? "vs ATH" : "距历史高点", display, lang === 'en' ? "Price vs All-Time-High" : "当前价格距ATH", color);
      })()}
      ${(() => {
        const p200 = project.cgData?.market_data?.price_change_percentage_200d;
        if (p200 == null) return "";
        const display = `${p200 > 0 ? "+" : ""}${p200.toFixed(1)}%`;
        const color = p200 > 0 ? "#059669" : "#dc2626";
        return miniCard(lang === 'en' ? "200d Change" : "200日涨跌", display, "CoinGecko", color);
      })()}
      ${(() => {
        const tf = project.tokenFutures;
        if (!tf?.hasFutures) return "";
        const fr = tf.fundingRate;
        const frColor = fr > 0.05 ? "#dc2626" : fr > 0 ? "#d97706" : fr < -0.01 ? "#059669" : "#374151";
        return miniCard(lang === 'en' ? "Funding Rate" : "资金费率",
          `${fr >= 0 ? "+" : ""}${fr}%`,
          lang === 'en' ? "Binance Futures (8h)" : "Binance 期货(8h)", frColor);
      })()}
      ${(() => {
        const tf = project.tokenFutures;
        if (!tf?.hasFutures || !tf.openInterestUsd) return "";
        return miniCard(lang === 'en' ? "Open Interest" : "未平仓合约", fmt(tf.openInterestUsd), "Binance Futures", "#2563eb");
      })()}
      ${(() => {
        const tf = project.tokenFutures;
        if (!tf?.hasFutures || !tf.longShortRatio) return "";
        const ls = tf.longShortRatio;
        const lsColor = ls > 2 ? "#059669" : ls > 1 ? "#374151" : "#dc2626";
        return miniCard(lang === 'en' ? "L/S Ratio" : "多空比",
          ls.toFixed(2),
          lang === 'en' ? "Long/Short (Binance)" : "做多/做空(Binance)", lsColor);
      })()}
    </div>

    <!-- 协议链上基本面（Hyperliquid 官方 API 免费 / Artemis Enterprise 付费） -->
    ${(() => {
      const am = project.artemisMetrics;
      if (!am?.ok) return "";
      const fmtV = v => v == null ? null : v >= 1e9 ? "$"+(v/1e9).toFixed(2)+"B" : v >= 1e6 ? "$"+(v/1e6).toFixed(1)+"M" : v >= 1e3 ? "$"+(v/1e3).toFixed(0)+"K" : "$"+v.toFixed(0);
      const fmtN = v => v == null ? null : v >= 1e6 ? (v/1e6).toFixed(2)+"M" : v >= 1e3 ? (v/1e3).toFixed(0)+"K" : String(Math.round(v));
      const isNative = am.source === 'hyperliquid_native';
      const srcLabel = isNative ? 'Hyperliquid /info' : 'Artemis';

      // native 路径：全协议实时 + DefiLlama 历史 + 质押数据
      if (isNative) {
        const top = am.topAssets || [];
        const hasAny = am.perpVolume24h || am.totalOI || am.perpFees30d || am.stakedHype;
        if (!hasAny) return "";

        // 质押占比（HYPE 总供应 10 亿）
        const stakedPct = am.stakedHype ? (am.stakedHype / 1e9 * 100).toFixed(1) : null;
        // 手续费/收入转换率（payout ratio）
        const feeRevenueRatio = am.perpFees30d && am.revenue30d
          ? (am.revenue30d / am.perpFees30d * 100).toFixed(1) : null;

        return `
        <div style="margin-bottom:16px">
          <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:8px">
            ${lang === 'en' ? 'Protocol Fundamentals' : '协议基本面'}
            <span class="src-tag">Hyperliquid API</span>
            <span class="src-tag" style="margin-left:4px">DefiLlama</span>
          </div>

          <!-- 实时数据行 -->
          <div style="font-size:10px;color:#9ca3af;margin-bottom:6px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px">
            ${lang === 'en' ? 'Live — All Perp Markets' : '实时 — 全协议永续市场'}
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:12px">
            ${am.perpVolume24h ? miniCard(lang === 'en' ? "Total Vol 24h" : "全协议24h量", fmtV(am.perpVolume24h), lang === 'en' ? "230 perp markets" : "230个永续市场", "#6366f1") : ""}
            ${am.totalOI ? miniCard(lang === 'en' ? "Total Open Interest" : "全协议未平仓", fmtV(am.totalOI), lang === 'en' ? "All markets" : "全市场", "#2563eb") : ""}
            ${am.activeMarkets ? miniCard(lang === 'en' ? "Active Markets" : "活跃市场", String(am.activeMarkets), lang === 'en' ? "Perp pairs w/ volume" : "有交易量的永续对", "#0891b2") : ""}
          </div>

          <!-- 历史数据行（DefiLlama）-->
          <div style="font-size:10px;color:#9ca3af;margin-bottom:6px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px">
            ${lang === 'en' ? 'Historical — DefiLlama' : '历史数据 — DefiLlama'}
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:12px">
            ${am.perpFees30d ? miniCard(lang === 'en' ? "Perp Fees 30d" : "永续手续费30d", fmtV(am.perpFees30d), "DefiLlama", "#d97706") : ""}
            ${am.perpFees7d  ? miniCard(lang === 'en' ? "Perp Fees 7d"  : "永续手续费7d",  fmtV(am.perpFees7d),  "DefiLlama", "#d97706") : ""}
            ${am.perpFees24h ? miniCard(lang === 'en' ? "Perp Fees 24h" : "永续手续费24h", fmtV(am.perpFees24h), "DefiLlama", "#d97706") : ""}
            ${am.revenue30d  ? miniCard(lang === 'en' ? "Revenue 30d"   : "协议收入30d",   fmtV(am.revenue30d),  "DefiLlama", "#059669") : ""}
            ${am.revenue7d   ? miniCard(lang === 'en' ? "Revenue 7d"    : "协议收入7d",    fmtV(am.revenue7d),   "DefiLlama", "#059669") : ""}
            ${feeRevenueRatio ? miniCard(lang === 'en' ? "Revenue/Fee%" : "收入/费用率", feeRevenueRatio+"%", lang === 'en' ? "Protocol take rate" : "协议分成比例", "#8b5cf6") : ""}
            ${am.perpFeeAllTime ? miniCard(lang === 'en' ? "All-Time Fees" : "历史总手续费", fmtV(am.perpFeeAllTime), "DefiLlama", "#6b7280") : ""}
          </div>

          <!-- 质押数据行（Hyperliquid validatorSummaries）-->
          ${am.stakedHype ? `
          <div style="font-size:10px;color:#9ca3af;margin-bottom:6px;font-weight:600;text-transform:uppercase;letter-spacing:0.5px">
            ${lang === 'en' ? 'Staking — Hyperliquid Validators' : '质押 — Hyperliquid 验证者'}
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:12px">
            ${miniCard(lang === 'en' ? "Staked HYPE" : "质押 HYPE", fmtN(am.stakedHype), lang === 'en' ? "validator staking" : "验证者质押", "#8b5cf6")}
            ${stakedPct ? miniCard(lang === 'en' ? "Staked %" : "质押占比", stakedPct+"%", lang === 'en' ? "of 1B total supply" : "占总供应10亿", stakedPct > 30 ? "#059669" : "#d97706") : ""}
            ${am.activeValidators ? miniCard(lang === 'en' ? "Validators" : "活跃验证者", `${am.activeValidators}/${am.totalValidators}`, lang === 'en' ? "active/total" : "活跃/总数", "#0891b2") : ""}
          </div>` : ""}

          <!-- Top Markets 表格 -->
          ${top.length > 0 ? `
          <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:6px">
            ${lang === 'en' ? 'Top 8 Markets by 24h Volume' : 'Top 8 市场（24h 交易量）'}
          </div>
          <div style="overflow-x:auto;border-radius:6px;border:1px solid #e5e7eb">
          <table style="width:100%;font-size:11px;border-collapse:collapse">
            <thead>
              <tr style="background:#f9fafb;color:#6b7280;font-size:10px;font-weight:700;text-transform:uppercase">
                <th style="text-align:left;padding:6px 10px">${lang === 'en' ? 'Asset' : '资产'}</th>
                <th style="text-align:right;padding:6px 10px">24h Vol</th>
                <th style="text-align:right;padding:6px 10px">OI</th>
                <th style="text-align:right;padding:6px 10px">${lang === 'en' ? 'Funding (8h)' : '资金费率(8h)'}</th>
              </tr>
            </thead>
            <tbody>
              ${top.slice(0, 8).map((t, i) => {
                const frColor = t.fundingRate > 0.05 ? "#dc2626" : t.fundingRate < -0.01 ? "#059669" : "#374151";
                return `<tr style="border-top:1px solid #f3f4f6">
                  <td style="padding:6px 10px;font-weight:700">${t.name}</td>
                  <td style="padding:6px 10px;text-align:right;color:#374151">${fmtV(t.vol24h)}</td>
                  <td style="padding:6px 10px;text-align:right;color:#6b7280">${fmtV(t.oi)}</td>
                  <td style="padding:6px 10px;text-align:right;font-weight:700;color:${frColor}">${t.fundingRate >= 0 ? '+' : ''}${t.fundingRate}%</td>
                </tr>`;
              }).join("")}
            </tbody>
          </table>
          </div>` : ""}
        </div>`;
      }

      // Artemis Enterprise 路径
      const hasPerp = am.perpVolume30d > 0;
      const hasRevenue = am.revenue30d > 0;
      if (!hasPerp && !hasRevenue) return "";
      return `
      <div style="margin-bottom:12px">
        <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:8px">
          ${lang === 'en' ? 'Protocol Fundamentals' : '协议基本面'} <span class="src-tag">Artemis</span>
        </div>
        <div style="display:flex;flex-wrap:wrap;gap:10px">
          ${hasPerp ? miniCard(lang === 'en' ? "Perp Vol 30d" : "永续量30d", fmtV(am.perpVolume30d), srcLabel, "#6366f1") : ""}
          ${am.perpVolume24h ? miniCard(lang === 'en' ? "Perp Vol 24h" : "永续量24h", fmtV(am.perpVolume24h), srcLabel, "#6366f1") : ""}
          ${am.perpDauLatest > 0 ? miniCard(lang === 'en' ? "Daily Traders" : "日活交易者", fmtN(am.perpDauLatest), srcLabel, "#0891b2") : ""}
          ${hasRevenue ? miniCard(lang === 'en' ? "Revenue 30d" : "协议收入30d", fmtV(am.revenue30d), srcLabel, "#059669") : ""}
          ${am.perpFees30d > 0 ? miniCard(lang === 'en' ? "Fees 30d" : "手续费30d", fmtV(am.perpFees30d), srcLabel, "#d97706") : ""}
          ${am.burnedFee30d > 0 ? miniCard(lang === 'en' ? "Burned 30d" : "销毁30d", fmtV(am.burnedFee30d), srcLabel, "#dc2626") : ""}
          ${am.stakedHype > 0 ? miniCard(lang === 'en' ? "Staked HYPE" : "质押HYPE", fmtN(am.stakedHype), srcLabel, "#8b5cf6") : ""}
        </div>
      </div>`;
    })()}
    ${(() => {
      // 交易所分布 Top 5（仅已发币项目有 tickers 数据时显示）
      const tickers = project.cgData?.tickers;
      if (!tickers?.length) return "";
      const volMap = {};
      tickers.forEach(t => {
        const name = t.market?.name || "Unknown";
        volMap[name] = (volMap[name] || 0) + (t.converted_volume?.usd || 0);
      });
      const top5 = Object.entries(volMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
      const totalVol = top5.reduce((s, [, v]) => s + v, 0);
      if (!totalVol) return "";
      return `
        <div style="margin-bottom:12px">
          <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:8px">
            ${lang === 'en' ? 'Exchange Volume Distribution' : '交易所成交量分布'} <span class="src-tag">CoinGecko</span>
          </div>
          ${top5.map(([name, vol]) => {
            const pct = (vol / totalVol * 100).toFixed(1);
            const barW = Math.max(3, Math.round(vol / totalVol * 100));
            return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:5px">
              <div style="font-size:11px;color:#374151;width:130px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${name}</div>
              <div style="flex:1;background:#f3f4f6;border-radius:3px;height:6px;overflow:hidden">
                <div style="width:${barW}%;background:#3b82f6;height:100%;border-radius:3px"></div>
              </div>
              <div style="font-size:10px;color:#6b7280;min-width:60px;text-align:right">${pct}% · ${fmt(vol)}</div>
            </div>`;
          }).join("")}
        </div>`;
    })()}
    <!-- 真实代币解锁时间表（Surf /token/tokenomics，优先于 AI 生成） -->
    <!-- 未发币项目 gate：EDGE Markets 事件后增加。token_symbol 空 + 无 CG 价格 = 没发币，
         即使 Surf 误返其他 token 的 tokenomics（如 edge → edgeX）也不渲染。 -->
    ${(() => {
      const _hasToken = !!(project.details?.token_symbol || project.cgData?.market_data?.current_price?.usd);
      if (!_hasToken) return "";
      const tu = project.tokenUnlock;
      if (!tu?.ok || !tu.events?.length) return unlockTimeline; // 降级到 AI 版本

      const fmtN = v => v >= 1e9 ? (v/1e9).toFixed(2)+"B" : v >= 1e6 ? (v/1e6).toFixed(1)+"M" : v >= 1e3 ? (v/1e3).toFixed(0)+"K" : v.toLocaleString();

      // 分配类型颜色（按名称关键词判断）
      const allocColor = (name) => {
        const n = (name || "").toLowerCase();
        if (n.includes("team") || n.includes("contributor") || n.includes("advisor") || n.includes("founder")) return "#dc2626"; // 团队=红
        if (n.includes("investor") || n.includes("vc") || n.includes("strategic")) return "#f97316"; // 投资方=橙
        if (n.includes("community") || n.includes("grant") || n.includes("airdrop") || n.includes("public")) return "#059669"; // 社区=绿
        if (n.includes("liquidity") || n.includes("ecosystem") || n.includes("reserve") || n.includes("treasury")) return "#2563eb"; // 生态=蓝
        if (n.includes("foundation") || n.includes("budget")) return "#8b5cf6"; // 基金会=紫
        return "#6b7280"; // 其他=灰
      };

      // 近期解锁压力信号
      const pressureColor = tu.next30dAmount > 1e8 ? "#dc2626" : tu.next30dAmount > 1e7 ? "#d97706" : "#059669";
      const pressureLabel = lang === 'en'
        ? (tu.next30dAmount > 1e8 ? "High Pressure" : tu.next30dAmount > 1e7 ? "Medium" : "Low")
        : (tu.next30dAmount > 1e8 ? "高压力" : tu.next30dAmount > 1e7 ? "中等压力" : "低压力");

      // 分配总量汇总（全周期）
      const allocSummary = (tu.allocationTotals || []).slice(0, 8);
      const totalAlloc = allocSummary.reduce((s, a) => s + a.amount, 0);

      // 总供应量（用于计算解锁%）：从 CoinGecko 或全周期解锁总量估算
      const cgTotalSupply = project.cgData?.market_data?.total_supply;
      const supplyRef = cgTotalSupply || totalAlloc || 0;

      // 将事件按月分组（用于月度柱状图）
      const monthGroups = {};
      (tu.events || []).forEach(ev => {
        const ym = ev.date.slice(0, 7); // "2026-06"
        monthGroups[ym] = (monthGroups[ym] || 0) + ev.totalAmount;
      });
      const months = Object.entries(monthGroups).sort((a, b) => a[0].localeCompare(b[0])).slice(0, 12);
      const maxMonthAmt = Math.max(...months.map(([, v]) => v), 1);

      // 高风险警告：团队/投资方解锁量 > 30% 总分配
      const vestingRisk = allocSummary
        .filter(a => {
          const n = a.name.toLowerCase();
          return n.includes("team") || n.includes("contributor") || n.includes("investor") || n.includes("advisor");
        })
        .reduce((s, a) => s + a.amount, 0);
      const vestingRiskPct = totalAlloc > 0 ? vestingRisk / totalAlloc * 100 : 0;

      return `
      <div style="margin-top:12px">
        <!-- 标题行 -->
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px">
          <div style="font-size:10px;font-weight:700;color:#374151;letter-spacing:0.8px;text-transform:uppercase">
            ${lang === 'en' ? 'Token Unlock Schedule' : '代币解锁时间表'}
            <span class="src-tag" style="margin-left:6px">Surf</span>
          </div>
          ${tu.next30dAmount > 0 ? `<span style="font-size:10px;padding:2px 8px;border-radius:8px;background:${pressureColor}18;color:${pressureColor};font-weight:700;border:1px solid ${pressureColor}30">
            30d ${fmtN(tu.next30dAmount)}${supplyRef > 0 ? ' · '+((tu.next30dAmount/supplyRef)*100).toFixed(1)+'%' : ''} · ${pressureLabel}
          </span>` : ""}
        </div>

        ${vestingRiskPct > 30 ? `
        <!-- 高风险警告 -->
        <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:8px 12px;margin-bottom:10px;font-size:11px;color:#dc2626">
          <strong>${lang === 'en' ? 'High Team/Investor Unlock:' : '团队/投资方解锁比例较高：'}</strong>
          ${lang === 'en' ? `${vestingRiskPct.toFixed(0)}% of vesting supply is allocated to team/investors/advisors — monitor sell pressure.` : `${vestingRiskPct.toFixed(0)}% 分配给团队/投资方/顾问，注意解锁抛压。`}
        </div>` : ""}

        ${allocSummary.length > 0 ? `
        <!-- 全周期分配结构 -->
        <div style="margin-bottom:12px">
          <div style="font-size:10px;color:#6b7280;margin-bottom:6px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px">
            ${lang === 'en' ? 'Allocation Structure' : '全周期分配结构'}
            ${supplyRef > 0 ? `<span style="font-weight:400;color:#9ca3af;text-transform:none;letter-spacing:0"> (${lang === 'en' ? 'of total' : '占总供应'})</span>` : ''}
          </div>
          <!-- 彩色占比条 -->
          <div style="display:flex;height:14px;border-radius:7px;overflow:hidden;gap:1px;margin-bottom:6px">
            ${allocSummary.map(a => {
              const pct = totalAlloc > 0 ? (a.amount / totalAlloc * 100) : 0;
              const color = allocColor(a.name);
              return `<div style="width:${pct.toFixed(1)}%;background:${color}" title="${a.name}: ${fmtN(a.amount)} (${pct.toFixed(1)}%)"></div>`;
            }).join("")}
          </div>
          <!-- 图例 + 数量 -->
          <div style="display:flex;flex-direction:column;gap:3px">
            ${allocSummary.map(a => {
              const pct = totalAlloc > 0 ? (a.amount / totalAlloc * 100) : 0;
              const supplyPct = supplyRef > 0 ? (a.amount / supplyRef * 100) : 0;
              const color = allocColor(a.name);
              const barW = Math.round(pct);
              return `<div style="display:flex;align-items:center;gap:6px">
                <span style="width:8px;height:8px;border-radius:2px;background:${color};flex-shrink:0"></span>
                <span style="font-size:10px;color:#374151;flex:1;min-width:0;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${a.name}</span>
                <div style="width:80px;height:4px;background:#f3f4f6;border-radius:2px;overflow:hidden;flex-shrink:0">
                  <div style="width:${barW}%;height:100%;background:${color};border-radius:2px"></div>
                </div>
                <span style="font-size:10px;color:#6b7280;min-width:40px;text-align:right">${pct.toFixed(1)}%</span>
                <span style="font-size:10px;color:#9ca3af;min-width:50px;text-align:right">${fmtN(a.amount)}</span>
              </div>`;
            }).join("")}
          </div>
        </div>` : ""}

        ${months.length > 0 ? `
        <!-- 月度解锁柱状图 -->
        <div style="margin-bottom:12px">
          <div style="font-size:10px;color:#6b7280;margin-bottom:6px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px">
            ${lang === 'en' ? 'Monthly Unlock Volume' : '月度解锁量'}
          </div>
          <div style="display:flex;gap:3px;align-items:flex-end;height:40px">
            ${months.map(([ym, amt]) => {
              const now = new Date().toISOString().slice(0,7);
              const isPast = ym < now; const isCurrent = ym === now;
              const h = Math.round((amt / maxMonthAmt) * 36) + 4;
              const barColor = isPast ? "#d1d5db" : isCurrent ? "#f97316" : (amt / maxMonthAmt > 0.5 ? "#dc2626" : "#6b7280");
              return `<div style="flex:1;display:flex;flex-direction:column;align-items:center;gap:1px" title="${ym}: ${fmtN(amt)}${supplyRef > 0 ? ' ('+(amt/supplyRef*100).toFixed(1)+'%)' : ''}">
                <div style="width:100%;height:${h}px;background:${barColor};border-radius:2px 2px 0 0;min-height:3px"></div>
                <span style="font-size:8px;color:#9ca3af;transform:rotate(-45deg);transform-origin:top;white-space:nowrap">${ym.slice(5)}</span>
              </div>`;
            }).join("")}
          </div>
        </div>` : ""}

        <!-- 详细解锁事件列表 -->
        <div style="font-size:10px;color:#6b7280;margin-bottom:6px;font-weight:700;text-transform:uppercase;letter-spacing:0.5px">
          ${lang === 'en' ? 'Unlock Events' : '解锁事件详情'} (${tu.totalEvents})
        </div>
        <div style="border:1px solid #e5e7eb;border-radius:8px;overflow:hidden">
          ${tu.events.slice(0, 8).map((ev, idx) => {
            const isPast = ev.daysFromNow < 0;
            const isSoon = ev.daysFromNow >= 0 && ev.daysFromNow <= 30;
            const rowBg = idx % 2 === 0 ? "#ffffff" : "#f9fafb";
            const dateColor = isPast ? "#9ca3af" : isSoon ? "#dc2626" : "#111827";
            const supplyPctStr = supplyRef > 0 ? ((ev.totalAmount / supplyRef) * 100).toFixed(1) + "%" : "";
            return `<div style="display:flex;align-items:flex-start;gap:8px;padding:8px 10px;background:${rowBg};border-bottom:1px solid #f3f4f6">
              <!-- 日期列 -->
              <div style="min-width:80px">
                <div style="font-size:11px;font-weight:700;color:${dateColor}">${ev.date}</div>
                ${isSoon ? `<div style="font-size:10px;background:#fef2f2;color:#dc2626;border-radius:3px;padding:1px 5px;display:inline-block;margin-top:2px;font-weight:600">
                  ${ev.daysFromNow === 0 ? '今日' : `${ev.daysFromNow}d`}
                </div>` : isPast ? `<div style="font-size:9px;color:#9ca3af">${lang === 'en' ? 'passed' : '已过'}</div>` : ''}
              </div>
              <!-- 总量 + 占比 -->
              <div style="min-width:80px;text-align:right">
                <div style="font-size:12px;font-weight:700;color:${isPast?'#9ca3af':'#111827'}">${fmtN(ev.totalAmount)}</div>
                ${supplyPctStr ? `<div style="font-size:9px;color:#6b7280">${supplyPctStr} supply</div>` : ''}
              </div>
              <!-- 分配明细 -->
              <div style="flex:1;display:flex;flex-direction:column;gap:3px">
                ${ev.allocations.slice(0, 4).map(a => {
                  const color = allocColor(a.name);
                  const pct = ev.totalAmount > 0 ? (a.amount / ev.totalAmount * 100).toFixed(0) : 0;
                  return `<div style="display:flex;align-items:center;gap:4px">
                    <span style="width:6px;height:6px;border-radius:1px;background:${color};flex-shrink:0"></span>
                    <span style="font-size:10px;color:#374151;flex:1">${a.name}</span>
                    <span style="font-size:10px;color:#6b7280">${fmtN(a.amount)}</span>
                    <span style="font-size:9px;color:#9ca3af">${pct}%</span>
                  </div>`;
                }).join("")}
              </div>
            </div>`;
          }).join("")}
        </div>
        ${tu.totalEvents > 8 ? `<div style="font-size:10px;color:#9ca3af;margin-top:6px;text-align:center">
          ${lang === 'en' ? `+${tu.totalEvents - 8} more unlock events` : `另有 ${tu.totalEvents - 8} 个解锁事件`}
        </div>` : ""}
      </div>`;
    })()}
  `)}

  <!-- 4.5 数据表现 -->
  ${dimCard("data", lang === 'en' ? "On-chain Data" : "数据表现", a?.scores?.data, `
    ${hasSybilRisk ? `<div style="background:#fef3c7;border:1px solid #fcd34d;border-radius:6px;padding:8px 12px;margin-bottom:12px;font-size:11px;color:#92400e">
      <strong>${lang === 'en' ? 'Sybil Risk Warning:' : 'Sybil 风险提示：'}</strong>${lang === 'en' ? 'On-chain data score is low or contains testnet keywords. Independent verification recommended to filter potential wash activity.' : '链上数据得分偏低或描述含测试网关键词，建议对用户数据进行独立核查，过滤潜在刷量行为。'}
    </div>` : ""}

    <!-- 链上活跃综合分（取代原"叙事 × 链上信号矩阵"中的链上维度） -->
    <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:14px">
      ${miniCard(lang === 'en' ? "On-chain Activity" : "链上活跃", q?.chainScore != null ? `${q.chainScore}/100` : null, "DeFiLlama · Dune", q?.chainScore >= 60 ? "#10b981" : "#6b7280")}
    </div>

    <!-- 社区 & 市场 -->
    <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:8px">${lang === 'en' ? 'Community & Market Data' : '社区 & 市场数据'}</div>
    <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:16px">
      ${miniCard(lang === 'en' ? "Twitter Followers" : "Twitter 粉丝", cgTwitter ? (cgTwitter >= 1e6 ? (cgTwitter/1e6).toFixed(1)+"M" : (cgTwitter/1000).toFixed(0)+"K") : null, "CoinGecko · @X", "#1d9bf0")}
      ${miniCard(lang === 'en' ? "Telegram Members" : "Telegram 成员", cgTelegram ? (cgTelegram >= 1e6 ? (cgTelegram/1e6).toFixed(1)+"M" : cgTelegram >= 1000 ? (cgTelegram/1000).toFixed(1)+"K" : String(cgTelegram)) : null, "CoinGecko", "#0088cc")}
      ${miniCard(lang === 'en' ? "Reddit Subs" : "Reddit 订阅", cgReddit ? (cgReddit >= 1000 ? (cgReddit/1000).toFixed(1)+"K" : String(cgReddit)) : null, "CoinGecko", "#ff4500")}
      ${miniCard(lang === 'en' ? "Bullish Sentiment" : "社区看涨情绪", cgSent != null ? Math.round(cgSent)+"%" : null, lang === 'en' ? "CoinGecko Votes" : "CoinGecko 投票", cgSent>=60?"#059669":cgSent>=40?"#d97706":"#dc2626")}
      ${miniCard(lang === 'en' ? "24h Volume" : "24h 交易量", fmt(cgVol || null), "CoinGecko", "#2563eb")}
      ${miniCard(lang === 'en' ? "Token Price" : "代币价格", cgPrice ? "$"+cgPrice.toPrecision(4) : null, "CoinGecko")}
      ${miniCard(lang === 'en' ? "Watchlist Users" : "Watchlist 用户", watchlist ? (watchlist >= 1e6 ? (watchlist/1e6).toFixed(1)+"M" : (watchlist/1000).toFixed(0)+"K") : null, "CoinGecko")}
      ${(() => {
        const athChg = project.cgData?.market_data?.ath_change_percentage?.usd;
        if (athChg == null) return "";
        const color = athChg > -20 ? "#059669" : athChg > -50 ? "#d97706" : "#dc2626";
        return miniCard(lang === 'en' ? "vs ATH" : "距历史高点", `${athChg.toFixed(1)}%`, "CoinGecko", color);
      })()}
      ${(() => {
        const p200 = project.cgData?.market_data?.price_change_percentage_200d;
        if (p200 == null) return "";
        const color = p200 > 0 ? "#059669" : "#dc2626";
        return miniCard(lang === 'en' ? "200d Return" : "200日涨跌", `${p200 > 0 ? "+" : ""}${p200.toFixed(1)}%`, "CoinGecko", color);
      })()}
    </div>

    <!-- 持有者前10浓度（EVM 代币）-->
    ${(() => {
      const th = project.topHolders;
      if (!th?.holders?.length) return "";
      const top10 = th.top10Concentration;
      const concColor = top10 > 80 ? "#dc2626" : top10 > 60 ? "#d97706" : "#059669";
      // 持有者类型颜色映射
      const typeColor = { cex: "#2563eb", defi: "#8b5cf6", "liquid-staking": "#059669", protocol: "#0891b2", wallet: "#6b7280" };
      return `<div style="margin-bottom:16px">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px">
          <span style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase">${lang === 'en' ? 'Top 10 Holders' : '前10持有集中度'}</span>
          <span class="src-tag">Surf</span>
          <span style="font-size:12px;font-weight:700;color:${concColor};margin-left:auto">${top10.toFixed(1)}%</span>
          <span style="font-size:10px;color:#9ca3af">${lang === 'en' ? 'concentration' : '集中度'}</span>
        </div>
        ${th.holders.slice(0, 8).map(h => {
          const pct = h.percentage || 0;
          const barW = Math.max(2, Math.round(pct * 1.5));
          const color = typeColor[h.entityType] || "#6b7280";
          return `<div style="display:flex;align-items:center;gap:8px;margin-bottom:4px">
            <div style="font-size:10px;color:#374151;width:130px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">
              ${h.entityName ? `<span style="color:${color};font-weight:600">${h.entityName}</span>` : `<span style="color:#9ca3af;font-family:monospace">${h.address.slice(0,6)}…${h.address.slice(-4)}</span>`}
            </div>
            <div style="flex:1;background:#f3f4f6;border-radius:3px;height:5px;overflow:hidden">
              <div style="width:${barW}%;background:${color};height:100%;border-radius:3px;opacity:0.7"></div>
            </div>
            <div style="font-size:10px;color:#6b7280;min-width:40px;text-align:right">${pct.toFixed(1)}%</div>
          </div>`;
        }).join("")}
      </div>`;
    })()}

    <!-- 网站流量 -->
    ${traffic ? `
    <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:8px">${lang === 'en' ? 'Website Traffic' : '网站流量'} <span style="font-size:9px;font-weight:400;color:#9ca3af;text-transform:none">${lang === 'en' ? '(AI Estimate)' : '（AI 估算）'}</span></div>
    <div style="display:flex;flex-wrap:wrap;gap:10px;margin-bottom:16px">
      ${miniCard(lang === 'en' ? "Monthly Visits" : "月访问量", traffic.monthly_visits_estimate || null, lang === 'en' ? "AI Estimate" : "AI 估算")}
      ${miniCard(lang === 'en' ? "Traffic Scale" : "流量规模", lang === 'en' ? (traffic.traffic_tier_en || traffic.traffic_tier || null) : (traffic.traffic_tier || null))}
      ${miniCard(lang === 'en' ? "Growth Trend" : "增长趋势", lang === 'en' ? (traffic.growth_trend_en || traffic.growth_trend || null) : (traffic.growth_trend || null), null, (traffic.growth_trend?.includes("增长") || traffic.growth_trend_en?.toLowerCase().includes("growth")) ? "#059669" : (traffic.growth_trend?.includes("下降") || traffic.growth_trend_en?.toLowerCase().includes("declin")) ? "#dc2626" : "#374151")}
      ${traffic.primary_audience ? miniCard(lang === 'en' ? "Primary Audience" : "主要受众", lang === 'en' ? (traffic.primary_audience_en || traffic.primary_audience) : traffic.primary_audience) : ""}
    </div>
    ${traffic.top_regions?.length > 0 ? `<div style="margin-bottom:14px">
      <span style="font-size:10px;color:#6b7280;">${lang === 'en' ? 'Top Regions:' : '主要流量地区：'}</span>
      ${traffic.top_regions.map(r => `<span style="display:inline-block;font-size:10px;background:#eff6ff;color:#2563eb;border:1px solid #bfdbfe;padding:1px 7px;border-radius:10px;margin:2px 4px 2px 0">${r}</span>`).join("")}
    </div>` : ""}` : ""}

    <!-- 链上数据 -->
    <div style="font-size:10px;font-weight:700;color:#6b7280;letter-spacing:0.8px;text-transform:uppercase;margin-bottom:8px">${lang === 'en' ? 'On-chain Data' : '链上数据'}</div>
    <div style="display:flex;flex-wrap:wrap;gap:10px">
      ${miniCard(lang === 'en' ? "Protocol TVL" : "协议 TVL", fmt(tvl || null), "DeFiLlama", "#2563eb")}
      ${miniCard(lang === 'en' ? "Daily Users" : "日活用户", users?.daily ? users.daily.toLocaleString() : null, "DeFiLlama", "#059669")}
      ${miniCard(lang === 'en' ? "7d Active" : "7日活跃", users?.weekly ? users.weekly.toLocaleString() : null, "DeFiLlama")}
      ${miniCard(lang === 'en' ? "On-chain Liquidity" : "链上流动性", fmt(dune?.poolSize || null), "Dune", "#8b5cf6")}
      ${dune?.price ? miniCard(lang === 'en' ? "DEX Price" : "DEX 实时价格", "$"+Number(dune.price).toPrecision(4), "Dune") : ""}
    </div>
    ${!tvl && !users?.daily && !dune?.poolSize && !cgVol ? `<div style="font-size:11px;color:#9ca3af;margin-top:8px">${lang === 'en' ? 'No on-chain data yet (project may not have launched mainnet)' : '暂无链上数据（项目可能尚未上线主网）'}</div>` : ""}
  `)}

  <!-- 4.6 合规风险 -->
  ${dimCard("compliance", lang === 'en' ? "Compliance" : "合规风险", a?.scores?.compliance, `
    ${a?.risk_penalty > 0 ? `<div style="background:#fef2f2;border:1px solid #fecaca;border-radius:6px;padding:8px 12px;margin-bottom:8px;font-size:11px;color:#7f1d1d">
      综合评分已因合规风险扣分 <strong>${Math.round(a.risk_penalty * 100)}%</strong>
    </div>` : `<div style="font-size:11px;color:#059669;margin-bottom:8px">未触发合规扣分机制</div>`}
  `)}
</div>

<!-- ═══ 四、风险 & 催化剂 ═══ -->
<div class="section">
  <h2>${lang === 'en' ? '4. Risks & Catalysts' : '四、风险 & 催化剂'}</h2>
  <div style="display:grid;grid-template-columns:1fr 1fr;gap:14px">
    <div style="background:#fef2f2;border:1px solid #fecaca;border-radius:10px;padding:14px 16px">
      <div style="font-size:10px;font-weight:700;color:#b91c1c;letter-spacing:0.8px;margin-bottom:10px">主要风险</div>
      ${(a?.risks||[]).map((s,i) => `<div style="display:flex;gap:10px;margin-bottom:8px">
        <span style="min-width:20px;height:20px;border-radius:50%;background:#fee2e2;color:#b91c1c;font-size:10px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px">${i+1}</span>
        <span style="font-size:12px;color:#7f1d1d;line-height:1.6">${s}</span>
      </div>`).join("") || `<p style="font-size:12px;color:#9ca3af">—</p>`}
    </div>
    <div style="background:#f0fdf4;border:1px solid #bbf7d0;border-radius:10px;padding:14px 16px">
      <div style="font-size:10px;font-weight:700;color:#15803d;letter-spacing:0.8px;margin-bottom:10px">催化剂</div>
      ${(a?.catalysts||[]).map((s,i) => `<div style="display:flex;gap:10px;margin-bottom:8px">
        <span style="min-width:20px;height:20px;border-radius:50%;background:#dcfce7;color:#15803d;font-size:10px;font-weight:700;display:inline-flex;align-items:center;justify-content:center;flex-shrink:0;margin-top:1px">${i+1}</span>
        <span style="font-size:12px;color:#166534;line-height:1.6">${s}</span>
      </div>`).join("") || `<p style="font-size:12px;color:#9ca3af">—</p>`}
    </div>
  </div>
</div>

<!-- ═══ 五、FDV 分层入场建议 ═══ -->
${(a?.fdv_tiers||[]).length > 0 ? `
<div class="section">
  <h2>${lang === 'en' ? '5. FDV Tier Guide' : '五、FDV 分层入场建议'}</h2>
  <table>
    <thead><tr><th>FDV 区间</th><th>评级</th><th>建议仓位</th><th>预期回报</th></tr></thead>
    <tbody>${(a.fdv_tiers||[]).map(t=>{
      const tc=t.rating==="S"?"#059669":t.rating==="A"?"#2563eb":"#d97706";
      return `<tr>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;font-weight:600">${t.fdv_range}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;text-align:center"><span style="color:${tc};font-weight:900;font-size:16px">${t.rating}</span></td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;color:#6b7280">${t.position}</td>
        <td style="padding:9px 12px;border-bottom:1px solid #f3f4f6;font-weight:700;color:${tc}">${t.expected_return}</td>
      </tr>`;
    }).join("")}</tbody>
  </table>
</div>` : ""}

<!-- ═══ 六、数据来源 ═══ -->
<div class="section">
  <h2>${lang === 'en' ? '6. Data Sources' : '六、数据来源'}</h2>
  <table>
    <thead><tr><th>数据字段</th><th>来源平台</th><th>获取日期</th></tr></thead>
    <tbody>${srcRows}</tbody>
  </table>
</div>

<!-- ═══ 七、扩展数据视图（data-views 自动渲染） ═══ -->
${(() => {
  // 每个 buildXxxHtml 自我决定渲染或返回 '' — manifest 保证 import 已挂
  const blocks = [
    buildVerdictHtml(project, a, lang),
    buildUnlockHtml(project, a, lang),
    buildProtocolHtml(project, a, lang),
    buildHoldersHtml(project, a, lang),
    buildNewsSignalsHtml(project, a, lang),
  ].filter(Boolean);
  if (!blocks.length) return '';
  return `<div class="section">
    <h2>${lang === 'en' ? '7. Extended Data Views' : '七、 扩展数据视图'}</h2>
    ${blocks.join('\n')}
  </div>`;
})()}

<!-- ═══ FOOTER ═══ -->
<div style="margin-top:40px;padding-top:16px;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;align-items:center;gap:16px">
  <a href="https://primarylens.app?utm_source=memo&utm_medium=report&p=${encodeURIComponent(project?.name || '')}" style="display:inline-flex;align-items:center;gap:6px;text-decoration:none;opacity:0.7;transition:opacity 0.2s" target="_blank" rel="noopener">
    <span style="font-size:11px;font-weight:700;color:#1e293b;letter-spacing:0.02em">PrimaryLens</span>
    <span style="font-size:10px;color:#94a3b8">AI 投研 · primarylens.app</span>
  </a>
  <span style="font-size:10px;color:#cbd5e1">AI 辅助生成，仅供研究参考，不构成投资建议。数据截止 ${today}</span>
</div>

</body>
</html>`;

    return html;
  };

  const trackMemoEvent = (eventType) => {
    try {
      fetch(`${RD_BASE}/api/track`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ event: eventType, project: project?.name, ts: new Date().toISOString() }),
      }).catch(() => {});
    } catch (_) {}
  };

  const handleExportPDF = () => {
    setShowReportMenu(false);
    trackMemoEvent('memo_pdf');
    const html = buildReportHtml();
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url  = URL.createObjectURL(blob);
    const win  = window.open(url, '_blank');
    setTimeout(() => { win?.print(); }, 900);
  };

  const handleCopyReport = async () => {
    setShowReportMenu(false);
    trackMemoEvent('memo_copy');
    const html = buildReportHtml();
    try {
      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': new Blob([html], { type: 'text/html' }) }),
      ]);
      setReportToast('已复制富文本，可直接粘贴到 Notion / Google Docs');
    } catch {
      const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
      window.open(URL.createObjectURL(blob), '_blank');
      setReportToast('浏览器不支持直接复制，已在新标签页打开');
    }
    setTimeout(() => setReportToast(''), 3000);
  };

  const handleShareReport = () => {
    setShowReportMenu(false);
    trackMemoEvent('memo_share');
    const slug = encodeURIComponent(project?.name || '');
    const shareUrl = `https://primarylens.app?utm_source=memo&utm_medium=share&p=${slug}`;
    navigator.clipboard.writeText(shareUrl).catch(() => {});
    setReportToast('链接已复制');
    setTimeout(() => setReportToast(''), 2500);
  };

  return (
    <aside style={isMobile ? { ...S.panel, width: "100%", borderLeft: "none" } : { ...S.panel, width: panelWidth, position: "relative" }}>
      {/* Resize handle — desktop only */}
      {!isMobile && (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="调整详情页宽度"
          title="拖拽调整详情页宽度"
          onPointerDown={startPanelResize}
          onPointerMove={resizePanel}
          onPointerUp={stopPanelResize}
          onPointerCancel={stopPanelResize}
          style={{
            position: "absolute",
            left: -5, top: 0, bottom: 0, width: 10,
            cursor: "ew-resize", zIndex: 5, touchAction: "none",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <div style={{ width: 2, height: "100%", background: "transparent" }} />
        </div>
      )}
      <div style={{ padding: "16px 16px 12px", borderBottom: `1px solid ${C.border}`, display: "flex", gap: 12, alignItems: "flex-start" }}>
        <div style={{ width: 40, height: 40, borderRadius: 8, overflow: "hidden", background: C.faint, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {project.details?.logo
            ? <img src={proxyLogo(project.details.logo)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} onError={e => e.target.style.display = "none"} />
            : <span style={{ fontSize: 16, color: C.muted }}>{project.name?.[0]}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>{project.name}</div>
          <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? (a?.track_en || a?.track) : a?.track} · {lang === 'en' ? (a?.stage_en || a?.stage) : a?.stage}</div>
          <div style={{ marginTop: 4, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
            <span style={S.rBadge(effComposite || 0)}>{r.label}</span>
            <span style={{ fontSize: 22, fontWeight: 900, color: r.fg }}>{effComposite || 0}</span>
            {a?.stage_label && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                background: a.stage_key === "early" ? "#eff6ff" : a.stage_key === "growth" ? "#ecfdf5" : "#fef3c7",
                color:      a.stage_key === "early" ? "#2563eb" : a.stage_key === "growth" ? "#059669" : "#d97706",
              }} title={lang === 'en' ? `Score Framework: ${STAGE_WEIGHTS[a.stage_key]?.focus_en || a.stage_focus}` : `评分框架: ${a.stage_focus}`}>
                {lang === 'en' ? (STAGE_WEIGHTS[a.stage_key]?.label_en || a.stage_label) : a.stage_label}
              </span>
            )}
            {a?.unlock_pressure && <UnlockBadge level={a.unlock_pressure} />}
            {project.details?._lightweight && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10,
                background: "#fef3c7", color: "#92400e",
              }} title={`AI 信心度: ${project.details._confidence || 'medium'}`}>
                <Brain size={10} strokeWidth={2} style={{ verticalAlign: -1, marginRight: 3 }} />{lang === 'en' ? 'AI Lite' : '轻量分析'}
              </span>
            )}
          </div>
        </div>
        {/* Close button — desktop only (mobile uses back button in wrapper) */}
        {!isMobile && <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 18 }}>×</button>}
      </div>
      {/* 导出按钮栏 */}
      <div style={{ padding: "8px 14px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 6 }}>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => { onClose(); onReanalyze(project.name, project.details?._matchedId || project.details?.id || project.rootdataId || null); }}
            style={{ ...S.btn("default"), fontSize: 11, padding: "4px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <RotateCw size={12} strokeWidth={2} /> {t('btn.reanalyze')}
          </button>
          <button
            onClick={() => isInWatchlist(project.name) ? onRemoveWatchlist(project.name) : onAddWatchlist(project)}
            style={{ ...S.btn(isInWatchlist(project.name) ? "primary" : "default"), fontSize: 11, padding: "4px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Star size={12} strokeWidth={2} fill={isInWatchlist(project.name) ? "currentColor" : "none"} /> {t('btn.watchlist')}
          </button>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          <button onClick={() => setShowShareModal(true)} style={{ ...S.btn("default"), fontSize: 11, padding: "4px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Share2 size={12} strokeWidth={2} /> {t('btn.share')}
          </button>
          <button onClick={() => setShowBadgeModal(true)} style={{ ...S.btn("default"), fontSize: 11, padding: "4px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Award size={12} strokeWidth={2} /> {t('btn.badge')}
          </button>
          <button
            onClick={() => { trackMemoEvent('memo_opened'); const html = buildReportHtml(); const blob = new Blob([html], { type: 'text/html;charset=utf-8' }); window.open(URL.createObjectURL(blob), '_blank'); }}
            style={{ ...S.btn("primary"), fontSize: 11, padding: "4px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <FileText size={12} strokeWidth={2} /> {t('btn.report')}
          </button>
        </div>
      </div>

      {/* 方案A：分享横幅 — 评分存在时常驻显示 */}
      {effComposite > 0 && (
        <div
          onClick={() => setShowShareModal(true)}
          style={{
            margin: "0 14px 10px",
            marginTop: 8,
            background: C.faint,
            border: `1px solid ${C.border}`,
            borderRadius: 10,
            padding: "10px 14px",
            display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
            cursor: "pointer",
          }}
        >
          <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.4 }}>
            <span style={{ color: C.text, fontWeight: 600 }}>{lang === 'en' ? 'Share this analysis' : '分享这份分析'}</span>
            <span style={{ marginLeft: 6, fontSize: 11 }}>{lang === 'en' ? 'Generate a card for X / Twitter' : '生成分析卡片，发布到 X'}</span>
          </div>
          <div style={{
            background: C.accent,
            borderRadius: 8, padding: "5px 14px",
            color: "#fff", fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", flexShrink: 0,
            display: "flex", alignItems: "center", gap: 5,
          }}>
            <Share2 size={12} strokeWidth={2} /> {lang === 'en' ? 'Share' : '分享'}
          </div>
        </div>
      )}

      <div style={{ display: "flex", borderBottom: `1px solid ${C.border}`, padding: "0 8px", overflowX: "auto", WebkitOverflowScrolling: "touch", scrollbarWidth: "none" }}>
        {[["overview","tab.overview"],["scores","tab.scores"],["irr","tab.irr"],["comp","tab.comp"],["unlock","tab.unlock"],["data","tab.data"],["intel","tab.intel"],["history","tab.history"],["research","tab.research"],["notes","tab.notes"],["bp_verify","tab.bp_verify"]].map(([id, key]) => (
          <button key={id} onClick={() => setTab(id)} style={S.tabBtn(tab === id)}>
            {t(key)}
            {id === "notes" && notes.length > 0 && (
              <span style={{ marginLeft: 3, fontSize: 9, background: "#2563eb", color: "#fff", borderRadius: 8, padding: "1px 4px" }}>{notes.length}</span>
            )}
          </button>
        ))}
      </div>

      <div style={{ padding: 14, display: "flex", flexDirection: "column", gap: 12, overflowY: "auto" }}>

        {tab === "overview" && a && (
          <>
            {(a._scores_missing || a._scores_recovered) && (
              <div style={{
                marginBottom: 10,
                padding: "10px 12px",
                background: a._scores_missing ? "#fef2f2" : "#fffbeb",
                border: `1px solid ${a._scores_missing ? "#fecaca" : "#fde68a"}`,
                borderRadius: 6,
                fontSize: 12,
                color: a._scores_missing ? "#991b1b" : "#92400e",
                display: "flex",
                alignItems: "center",
                gap: 8,
              }}>
                <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  {a._scores_missing
                    ? (lang === 'en'
                        ? 'AI did not output scores this run. Composite shown as 0 is a placeholder, not a real score. Click Refresh to re-analyze.'
                        : 'AI 本次未输出评分对象，下方综合分 0 为占位，不代表项目真实评分。请点右侧「刷新」重新分析。')
                    : (lang === 'en'
                        ? 'AI returned scores in non-standard shape. Recovered from flat fields — click Refresh if anything looks off.'
                        : 'AI 输出评分格式异常，已从扁平字段恢复。如显示异常请点右侧「刷新」重新分析。')}
                </div>
                <button
                  onClick={onReanalyze}
                  style={{
                    fontSize: 11, padding: "4px 10px", border: "1px solid currentColor",
                    background: "transparent", color: "inherit", borderRadius: 4, cursor: "pointer",
                    display: "inline-flex", alignItems: "center", gap: 5,
                  }}
                ><RotateCw size={11} strokeWidth={2} /> {lang === 'en' ? 'Refresh' : '刷新'}</button>
              </div>
            )}

            {/* ── Q36-C：入场价可疑警告 banner ─────────────────────────
                shape function 检测 recommendation 中入场价 < fair_value × 0.3
                且 prompt 没 hedge 语，即被标记 _entry_price_suspicious。
                Hyperliquid 案例：fair_value $59.4B + 建议 $500M-1B 建仓（99% 折扣）。
                不挡 recommendation 显示，加 caveat banner 让用户带怀疑读。 */}
            {a._entry_price_suspicious && (
              <div style={{
                marginBottom: 10,
                padding: "10px 12px",
                background: "#fff7ed",
                border: "1px solid #fdba74",
                borderRadius: 6,
                fontSize: 12,
                color: "#9a3412",
                display: "flex",
                alignItems: "flex-start",
                gap: 8,
              }}>
                <AlertTriangle size={14} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                <div style={{ flex: 1, lineHeight: 1.5 }}>
                  {lang === 'en'
                    ? <>AI's <b>entry price suggestion</b> in the recommendation below is <b>~{Math.round((a._entry_price_ratio || 0) * 100)}%</b> of fair value (${(a.fair_value_usd / 1e9).toFixed(1)}B). This may be a fabrication when ground-truth Revenue is missing — treat the price band as illustrative, not as a target.</>
                    : <>下方"建议"中的<b>入场价</b>仅为公允估值（${(a.fair_value_usd / 1e9).toFixed(1)}B）的 <b>{Math.round((a._entry_price_ratio || 0) * 100)}%</b>。在缺少实测 Revenue 时 AI 可能凭空给出该价位。<b>请只当作示意，不要作为入场目标</b>。</>}
                </div>
              </div>
            )}
            <ResponsiveContainer width="100%" height={180}>
              <RadarChart data={DIMS.map(d => ({ dim: lang === 'en' ? d.short_en : d.short, v: a.scores?.[d.key] || 0 }))}>
                <PolarGrid stroke={C.faint} />
                <PolarAngleAxis dataKey="dim" tick={{ fill: C.muted, fontSize: 10 }} />
                <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                <Radar dataKey="v" stroke={C.text} fill="rgba(0,0,0,0.06)" strokeWidth={1.5} />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 6 }} formatter={v => [`${v}分`]} />
              </RadarChart>
            </ResponsiveContainer>

            {/* ── Project Lifecycle Timeline ── */}
            {(project.timeline?.length > 0 || project.details?._lightweight) && (
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span>{lang === 'en' ? 'Project Timeline' : '项目生命周期'}</span>
                  {project.details?._lightweight && (
                    <span style={{ fontSize: 9, color: "#d97706", background: "#fef3c7", padding: "1px 6px", borderRadius: 8, fontWeight: 600 }}>
                      {lang === 'en' ? 'AI Lite' : '轻量数据'}
                    </span>
                  )}
                </div>
                {!project.timeline?.length && (
                  <div style={{ fontSize: 11, color: C.muted, padding: "8px 0", lineHeight: 1.6, display: "flex", gap: 6 }}>
                    <AlertTriangle size={13} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2 }} />
                    <span>{lang === 'en'
                      ? 'Limited timeline data — RootData has no entry, and AI knowledge / GitHub / Wayback Machine returned no anchor dates. Try re-analyzing with the project\'s official website or GitHub URL.'
                      : '暂无可靠时间锚点 — RootData 无收录，AI 知识库 / GitHub / Wayback 也未返回日期。可重新分析时手动提供官网或 GitHub 链接以补全数据。'}</span>
                  </div>
                )}
                {/* 自动发现的链接（轻量模式专属） */}
                {project.details?._autoDiscovery && (project.details.social_media?.website || project.details.social_media?.github) && (
                  <div style={{ fontSize: 11, color: C.muted, padding: "6px 0 10px", lineHeight: 1.7, borderBottom: `1px dashed ${C.border}`, marginBottom: 10 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#059669", marginBottom: 4, display: "flex", alignItems: "center", gap: 4 }}>
                      <Search size={11} strokeWidth={2} /> {lang === 'en' ? 'Auto-discovered links' : '自动发现的链接'}
                    </div>
                    {project.details.social_media.website && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <Globe size={12} strokeWidth={2} style={{ flexShrink: 0, color: C.muted }} /> <a href={project.details.social_media.website} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>{project.details.social_media.website.replace(/^https?:\/\//, '').replace(/\/$/, '')}</a>
                        {project.details._autoDiscovery.website && (
                          <span style={{ marginLeft: 6, fontSize: 9, color: C.muted }}>via {project.details._autoDiscovery.website}</span>
                        )}
                      </div>
                    )}
                    {project.details.social_media.github && (
                      <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                        <Code2 size={12} strokeWidth={2} style={{ flexShrink: 0, color: C.muted }} /> <a href={project.details.social_media.github} target="_blank" rel="noopener noreferrer" style={{ color: "#2563eb" }}>{project.details.social_media.github.replace(/^https?:\/\/github\.com\//, '')}</a>
                        {project.details._autoDiscovery.github && (
                          <span style={{ marginLeft: 6, fontSize: 9, color: C.muted }}>via {project.details._autoDiscovery.github}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative', paddingLeft: 16 }}>
                  {/* vertical line */}
                  <div style={{ position: 'absolute', left: 5, top: 4, bottom: 4, width: 2, background: C.border, borderRadius: 1 }} />
                  {project.timeline.map((t, i) => {
                    const isFirst   = i === 0;
                    const isFunding = t.key === 'firstFunding';
                    const dotColor  = isFunding ? '#10b981' : isFirst ? '#3b82f6' : C.muted;
                    return (
                      <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', position: 'relative' }}>
                        <div style={{ position: 'absolute', left: -14, width: 10, height: 10, borderRadius: '50%', background: dotColor, border: `2px solid ${C.surface}`, zIndex: 1 }} />
                        <span style={{ fontSize: 11, color: C.muted, fontFamily: 'monospace', minWidth: 78 }}>{t.date}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{lang === 'en' ? t.labelEn : t.label}</span>
                        <span style={{ fontSize: 9, color: C.muted, background: C.faint || '#f3f4f6', padding: '1px 5px', borderRadius: 3 }}>{t.source}</span>
                        {t.url && <a href={t.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 9, color: '#3b82f6' }}>↗</a>}
                      </div>
                    );
                  })}
                  {!project.timeline.some(t => t.key === 'firstFunding') && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '5px 0', position: 'relative' }}>
                      <div style={{ position: 'absolute', left: -14, width: 10, height: 10, borderRadius: '50%', background: project.details?.total_funding ? '#3b82f6' : '#f59e0b', border: `2px solid ${C.surface}`, zIndex: 1 }} />
                      {project.details?.total_funding ? (
                        <span style={{ fontSize: 11, color: '#3b82f6', fontWeight: 600 }}>
                          {lang === 'en'
                            ? `Raised ${fmt(project.details.total_funding)} (round details undisclosed)`
                            : `已融资 ${fmt(project.details.total_funding)}（轮次详情未披露）`}
                        </span>
                      ) : (
                        <span style={{ fontSize: 11, color: '#c2410c', fontWeight: 600 }}>{lang === 'en' ? 'No funding record' : '无融资记录'} — {lang === 'en' ? 'Early Signal' : '早期信号'}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── 团队信号速览（概览简版）── */}
            {(() => {
              const tms = project.teamMembers || [];
              const gs  = project.githubSignal;
              const ts  = project.teamSignals;
              const signals = [];

              // 实名情况
              const doxxed = tms.filter(m => m.name && m.name !== '—').length;
              if (tms.length > 0) signals.push({
                level: doxxed === 0 ? 'warn' : 'ok',
                label: lang === 'en' ? 'Team' : '团队',
                val: doxxed === 0
                  ? (lang === 'en' ? 'Fully anonymous' : '完全匿名')
                  : `${doxxed} ${lang === 'en' ? 'doxxed members' : '位实名成员'}`,
                warn: doxxed === 0,
              });

              // GitHub 活跃度
              if (gs) {
                const isGood = !gs.isFork && (gs.contributorCount == null || gs.contributorCount > 1) && gs.commits4w > 0;
                const isWarn = gs.isFork || gs.contributorCount === 1 || gs.commits4w === 0;
                signals.push({
                  level: isWarn ? 'warn' : gs.commits4w > 10 ? 'ok' : 'mid',
                  label: 'GitHub',
                  val: gs.isFork
                    ? (lang === 'en' ? 'Forked repo' : 'Fork 仓库，代码来源存疑')
                    : gs.contributorCount === 1
                      ? (lang === 'en' ? `Solo dev · ${gs.commits4w} commits/4w` : `单人开发 · ${gs.commits4w} 次提交/4周`)
                      : gs.contributorCount != null
                        ? `${gs.contributorCount} ${lang === 'en' ? 'contributors' : '位贡献者'} · ${gs.commits4w} ${lang === 'en' ? 'commits/4w' : '次提交/4周'}`
                        : `${gs.commits4w} ${lang === 'en' ? `commits/4w · ${gs.stars?.toLocaleString() || 0} stars` : `次提交/4周 · ${gs.stars?.toLocaleString() || 0} stars`}`,
                  warn: isWarn,
                });
              }

              // 孵化器背书
              if (ts?.pitchRecord?.accelerators?.length > 0) {
                signals.push({
                  level: 'ok',
                  label: lang === 'en' ? 'Backed' : '孵化器',
                  val: ts.pitchRecord.accelerators.slice(0, 2).join(' · '),
                  warn: false,
                });
              }

              // Twitter 账号年龄异常（只在有新账号时显示警告）
              if (ts?.twitterAges) {
                const ages = Object.values(ts.twitterAges).map(a => a.ageYears).filter(Boolean);
                const newAccounts = ages.filter(a => a < 1);
                if (newAccounts.length > 0) {
                  signals.push({
                    level: 'warn',
                    label: lang === 'en' ? 'Twitter Age' : '推特年龄',
                    val: lang === 'en'
                      ? `${newAccounts.length} handle(s) < 1yr old`
                      : `${newAccounts.length} 个账号不足 1 年`,
                    warn: true,
                  });
                }
              }

              if (signals.length === 0) return null;
              return (
                <div style={{ ...S.card, padding: "10px 14px" }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 5 }}>
                    <Users size={12} strokeWidth={2} /> {lang === 'en' ? 'Team Signals' : '团队信号速览'}
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                    {signals.map((sig, i) => {
                      const dotColor = sig.level === 'warn' ? '#dc2626' : sig.level === 'mid' ? '#d97706' : '#16a34a';
                      return (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
                          <span style={{
                            minWidth: 52, fontSize: 9, fontWeight: 700, textAlign: "center",
                            padding: "1px 5px", borderRadius: 3,
                            background: sig.warn ? "#fef2f2" : "#f0fdf4",
                            color: sig.warn ? "#dc2626" : "#16a34a",
                          }}>{sig.label}</span>
                          <span style={{ color: sig.warn ? "#dc2626" : C.text, display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <span style={{ width: 5, height: 5, borderRadius: "50%", background: dotColor, flexShrink: 0 }} /> {sig.val}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{ marginTop: 8, fontSize: 9, color: C.muted }}>
                    {lang === 'en' ? '→ Full signals in Intelligence tab' : '→ 完整团队信号见情报 Tab'}
                  </div>
                </div>
              );
            })()}

            {/* ── 投资建议综合判断（迁移至 data-views/react/VerdictCard）── */}
            <VerdictCard project={project} a={a} lang={lang} S={S} C={C} mode="full" />

            <div style={S.card}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>{t('overview.thesis')}</div>
              <p style={{ margin: 0, fontSize: 12, lineHeight: 1.7, color: "#374151" }}>
                {lang === 'en' ? (a.summary_en || a.summary) : a.summary}
              </p>
              {(lang === 'en' ? (a.moat_en || a.moat) : a.moat) && (
                <div style={{ marginTop: 8, padding: "6px 10px", background: "#fffbeb", borderLeft: "3px solid #f59e0b", borderRadius: "0 4px 4px 0", fontSize: 11, color: "#92400e", display: "flex", alignItems: "flex-start", gap: 6 }}>
                  <Castle size={12} strokeWidth={2} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span><strong>{t('overview.moat')}：</strong>{lang === 'en' ? (a.moat_en || a.moat) : a.moat}</span>
                </div>
              )}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>{t('overview.strengths')}</div>
                {(() => {
                  const arr = lang === 'en' ? (a.strengths_en || a.strengths) : a.strengths;
                  if (!Array.isArray(arr) || arr.length === 0) {
                    return <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', display: "flex", alignItems: "center", gap: 5 }}>
                      <AlertTriangle size={11} strokeWidth={2} style={{ flexShrink: 0 }} /> {lang === 'en' ? 'Not generated this run' : 'AI 本次未生成，可重新分析后查看'}
                    </div>;
                  }
                  return arr.map((s, i) => (
                    <div key={i} style={{ fontSize: 11, marginBottom: 3, display: "flex", gap: 6 }}>
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#16a34a", marginTop: 6, flexShrink: 0 }} />
                      <span>{safeText(s)}</span>
                    </div>
                  ));
                })()}
              </div>
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>{t('overview.risks')}</div>
                {(() => {
                  const arr = lang === 'en' ? (a.risks_en || a.risks) : a.risks;
                  if (!Array.isArray(arr) || arr.length === 0) {
                    return <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', display: "flex", alignItems: "center", gap: 5 }}>
                      <AlertTriangle size={11} strokeWidth={2} style={{ flexShrink: 0 }} /> {lang === 'en' ? 'Not generated this run' : 'AI 本次未生成，可重新分析后查看'}
                    </div>;
                  }
                  return arr.map((s, i) => (
                    <div key={i} style={{ fontSize: 11, marginBottom: 3, display: "flex", gap: 6 }}>
                      <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#c2410c", marginTop: 6, flexShrink: 0 }} />
                      <span>{safeText(s)}</span>
                    </div>
                  ));
                })()}
              </div>
            </div>
            {/* 催化剂卡：永远渲染，空时显示 placeholder（不再依赖 length>0 判断）*/}
            <div style={S.card}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase" }}>{t('overview.catalysts')}</div>
              {(() => {
                const arr = lang === 'en' ? (a.catalysts_en || a.catalysts) : a.catalysts;
                if (!Array.isArray(arr) || arr.length === 0) {
                  return <div style={{ fontSize: 11, color: C.muted, fontStyle: 'italic', display: "flex", alignItems: "center", gap: 5 }}>
                    <AlertTriangle size={11} strokeWidth={2} style={{ flexShrink: 0 }} /> {lang === 'en' ? 'Not generated this run — refresh to retry' : 'AI 本次未生成，可重新分析后查看'}
                  </div>;
                }
                return arr.map((c, i) => (
                  <div key={i} style={{ fontSize: 11, marginBottom: 3, display: "flex", gap: 6 }}>
                    <span style={{ width: 4, height: 4, borderRadius: "50%", background: "#d97706", marginTop: 6, flexShrink: 0 }} />
                    <span>{safeText(c)}</span>
                  </div>
                ));
              })()}
            </div>

            {/* Comparable valuations */}
            {a.comparable_projects?.length > 0 && (
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>{t('overview.comps')}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: C.faint }}>
                      {['comp.project','comp.fdv','comp.stage','comp.similarity','comp.pricing'].map(h => (
                        <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: C.muted, fontSize: 10 }}>{t(h)}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {a.comparable_projects.map((comp, i) => {
                      const isDiscount = (comp.vs || "").includes("折价") || (comp.vs_en || "").toLowerCase().includes("discount");
                      return (
                        <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                          <td style={{ padding: "6px 8px", fontWeight: 600 }}>{lang === 'en' ? enify(comp.name) : comp.name}</td>
                          <td style={{ padding: "6px 8px", color: "#059669", fontWeight: 600 }}>{lang === 'en' ? enify(comp.fdv) : comp.fdv}</td>
                          <td style={{ padding: "6px 8px" }}><span style={S.tag}>{lang === 'en' ? enify(comp.stage_en || comp.stage) : comp.stage}</span></td>
                          <td style={{ padding: "6px 8px", color: C.muted, fontSize: 10 }}>{lang === 'en' ? (comp.similarity_en || comp.similarity) : comp.similarity}</td>
                          <td style={{ padding: "6px 8px", fontWeight: 700, color: isDiscount ? "#059669" : "#d97706" }}>{lang === 'en' ? (comp.vs_en || enify(comp.vs)) : comp.vs}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 8, fontSize: 10, color: C.muted, borderTop: `1px solid ${C.faint}`, paddingTop: 7 }}>
                  {lang === 'en' ? 'Relative Pricing: Current project fair value vs comparable traded valuations' : '相对定价：当前项目公允价值 vs 竞品已交易估值对比'}
                </div>
              </div>
            )}
          </>
        )}

        {tab === "scores" && a && (() => {
          // 权重分布条按有效权重展示：账户级自定义权重覆盖时优先，否则用该项目分析时
          // 的阶段权重（若无则用默认）—— 跟上面 effComposite 用的是同一套权重，
          // 保证"权重分布条"和"综合分数"视觉上永远一致，不会出现对不上的情况。
          const stageDims = buildDims(effWeights);
          const dimLabelKey = { narrative:'score.narrative', tech:'score.tech', team:'score.team', tokenomics:'score.token', data:'score.data', compliance:'score.compliance' };
          const dimShortKey = { narrative:'dim.narrative', tech:'dim.tech', team:'dim.team', tokenomics:'dim.token', data:'dim.data', compliance:'dim.compliance' };
          return (
          <>
            {/* 阶段说明条 */}
            {a.stage_label && (
              <div style={{
                ...S.card, padding: "10px 14px",
                background: a.stage_key === "early" ? "#eff6ff" : a.stage_key === "growth" ? "#ecfdf5" : "#fef3c7",
                border: `1px solid ${a.stage_key === "early" ? "#bfdbfe" : a.stage_key === "growth" ? "#a7f3d0" : "#fde68a"}`,
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 12, fontWeight: 800,
                    color: a.stage_key === "early" ? "#1e40af" : a.stage_key === "growth" ? "#047857" : "#92400e" }}>
                    {lang === 'en' ? (STAGE_WEIGHTS[a.stage_key]?.label_en || a.stage_label) : a.stage_label}
                  </span>
                  <span style={{ fontSize: 10, color: C.muted }}>{t('score.framework')}{lang === 'en' ? ': ' : '：'}{lang === 'en' ? (STAGE_WEIGHTS[a.stage_key]?.focus_en || a.stage_focus) : a.stage_focus}</span>
                </div>
                <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.6, marginBottom: 6 }}>
                  {a.stage_key === "early"    && (lang === 'en' ? STAGE_WEIGHTS.early.desc_en    : "项目尚无可验证的产品或链上数据，data 维度给中性分，重点评估叙事和团队")}
                  {a.stage_key === "growth"   && (lang === 'en' ? STAGE_WEIGHTS.growth.desc_en   : "项目已有可验证的产品信号，进入全维度评估，技术产品和数据权重上升")}
                  {a.stage_key === "tge_post" && (lang === 'en' ? STAGE_WEIGHTS.tge_post.desc_en : "代币已上市，重点关注代币经济和市场数据，叙事和团队权重下调")}
                </div>
                {/* 判断依据 */}
                {a.stage_signals?.length > 0 && (
                  <div style={{ borderTop: `1px solid ${a.stage_key === "early" ? "#bfdbfe" : a.stage_key === "growth" ? "#a7f3d0" : "#fde68a"}`, paddingTop: 6 }}>
                    <div style={{ fontSize: 10, color: C.muted, fontWeight: 700, marginBottom: 4 }}>{t('score.basis')}</div>
                    {(lang === 'en' && a.stage_signals_en?.length ? a.stage_signals_en : a.stage_signals).map((s, i) => (
                      <div key={i} style={{ fontSize: 11, color: "#374151", display: "flex", gap: 5, marginBottom: 2 }}>
                        <span style={{ color: a.stage_key === "early" ? "#2563eb" : a.stage_key === "growth" ? "#059669" : "#d97706" }}>✓</span>
                        {s}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* 综合得分 + 权重分布条 */}
            <div style={{ ...S.card, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>{t('score.composite')}</div>
                <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                  <span style={{ fontSize: 32, fontWeight: 900, color: rating(effComposite || 0).fg }}>{effComposite || 0}</span>
                  <span style={S.rBadge(effComposite || 0)}>{rating(effComposite || 0).label}</span>
                </div>
              </div>
              {/* 权重分布条（按阶段权重） */}
              <div style={{ height: 6, display: "flex", borderRadius: 4, overflow: "hidden", gap: 1 }}>
                {stageDims.map(d => (
                  <div key={d.key} title={`${d.label} ${Math.round(d.w * 100)}%`}
                    style={{ flex: d.w, background: d.color, opacity: 0.85 }} />
                ))}
              </div>
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                {stageDims.map(d => (
                  <span key={d.key} style={{ fontSize: 10, color: C.muted, display: "flex", alignItems: "center", gap: 3 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: d.color, display: "inline-block" }} />
                    {t(dimShortKey[d.key]) || d.short} {Math.round(d.w * 100)}%
                  </span>
                ))}
              </div>
            </div>

            {/* Dune 数据表现快速看板 */}
            {project.duneAnalytics && (() => {
              const da = project.duneAnalytics;
              const isHL = da.source === 'hyperliquid_market_data';
              const vol30 = da.total_volume || 0;
              const trades30 = da.total_trades || 0;
              const duneDays = da.days || [];
              const nDays = duneDays.length || 30;
              const dayLabel = nDays < 28 ? `${nDays}d` : '30d';
              const dayLabelZh = nDays < 28 ? `${nDays}日` : '30日';
              const avgTraders = duneDays.length > 0
                ? Math.round(duneDays.reduce((s, d) => s + (d.unique_traders || 0), 0) / duneDays.length)
                : 0;
              const avgTrades = duneDays.length > 0
                ? Math.round(trades30 / duneDays.length)
                : 0;
              const fmtV = (v) =>
                v >= 1e9 ? `$${(v/1e9).toFixed(1)}B`
                : v >= 1e6 ? `$${(v/1e6).toFixed(1)}M`
                : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K`
                : `$${v.toFixed(0)}`;
              // HL 原生链无逐笔数据，显示两格（成交量 + OI 或均价）
              const cells = isHL ? [
                { label: lang === 'en' ? `${dayLabel} Perp Vol` : `${dayLabelZh}合约名义量`, value: fmtV(vol30) },
                { label: lang === 'en' ? 'Avg Daily Vol' : '日均名义量', value: fmtV(vol30 / Math.max(1, nDays)) },
                { label: lang === 'en' ? 'Avg Mark Price' : '均标记价格', value: `$${fmtV(duneDays.reduce((s, d) => s + (d.mark_price || 0), 0) / Math.max(1, duneDays.filter(d => d.mark_price > 0).length))}` },
              ] : [
                { label: lang === 'en' ? `${dayLabel} DEX Volume` : `${dayLabelZh} DEX 成交量`, value: fmtV(vol30) },
                { label: lang === 'en' ? 'Avg Daily Trades' : '日均交易笔数', value: avgTrades.toLocaleString() },
                { label: lang === 'en' ? 'Avg Daily Traders' : '日均交易者', value: avgTraders.toLocaleString() },
              ];
              return (
                <div style={{ ...S.card, padding: "10px 12px" }}>
                  <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: 0.6, marginBottom: 8 }}>
                    Dune Analytics · {da.chain}
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                    {cells.map(({ label, value }) => (
                      <div key={label} style={{ background: C.faint, borderRadius: 6, padding: "8px 10px", textAlign: "center" }}>
                        <div style={{ fontSize: 9, color: C.muted, marginBottom: 3 }}>{label}</div>
                        <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* 各维度溯源卡片 */}
            {stageDims.map(d => {
              const score = a.scores?.[d.key] || 0;
              const note  = (lang === 'en' ? (a.notes_en?.[d.key] || a.notes?.[d.key]) : a.notes?.[d.key]) || "";
              const r = rating(effComposite || 0);

              // 主张级来源：构建各数据源的跳转链接
              const srcUrl = (src) => {
                switch (src) {
                  case 'RootData':  return project.details?.rootdataurl || null;
                  case 'CoinGecko': return project.cgData?.id ? `https://www.coingecko.com/en/coins/${project.cgData.id}` : null;
                  case 'DeFiLlama': {
                    const _llamaSlug = project.defiData?.protocol?.slug;
                    if (!_llamaSlug) return null;
                    const _llamaIsChain = project.defiData?.protocol?.type === 'chain';
                    return `https://defillama.com/${_llamaIsChain ? 'chain' : 'protocol'}/${_llamaSlug}`;
                  }
                  case 'GitHub':    return project.details?.github || project.githubSignal?.url || null;
                  case 'Dune':      return project.duneDashboard?.url || null;
                  default:          return null;
                }
              };

              // 数据来源 badge 颜色
              const SRC_COLORS = {
                RootData:   { bg: "#f4f4f5", text: "#3f3f46" },
                CoinGecko:  { bg: "#f0fdf4", text: "#15803d" },
                DeFiLlama:  { bg: "#eff6ff", text: "#1d4ed8" },
                Dune:       { bg: "#fff7ed", text: "#c2410c" },
                GitHub:     { bg: "#f5f3ff", text: "#6d28d9" },
                AI:         { bg: "#eef2ff", text: "#4338ca" },
                Surf:       { bg: "#f5f3ff", text: "#7c3aed" },
              };

              // 生成溯源证据列表（每项含 source 字段）
              const evidence = [];
              if (d.key === "narrative") {
                if (project.details?.one_liner)
                  evidence.push({ label: t('ev.positioning'), val: project.details.one_liner.slice(0, 60), source: "RootData" });
                if (project.details?.tags?.length) {
                  const trackTags = localizeRootDataTags(project.details.tags, lang).slice(0, 3);
                  if (trackTags.length)
                    evidence.push({ label: t('ev.track'), val: trackTags.join(" · "), source: "RootData" });
                }
                const tw = project.cgData?.community_data?.twitter_followers;
                if (tw > 0)
                  evidence.push({ label: lang === 'en' ? "Twitter Followers" : "Twitter 关注", val: tw >= 1000 ? `${(tw/1000).toFixed(1)}K` : tw, source: "CoinGecko", positive: tw > 10000 });
                const sent = project.cgData?.sentiment_votes_up_percentage;
                if (sent != null)
                  evidence.push({ label: lang === 'en' ? "Sentiment ↑" : "社区情绪看涨", val: `${sent.toFixed(0)}%`, source: "CoinGecko", positive: sent >= 60 });
                const wl = project.cgData?.watchlist_portfolio_users;
                if (wl > 0)
                  evidence.push({ label: lang === 'en' ? "Watchlist Users" : "CG Watchlist", val: wl >= 1000 ? `${(wl/1000).toFixed(1)}K` : wl, source: "CoinGecko" });
                // Surf Mindshare
                if (surfMindshare.data?.length) {
                  const ms = surfMindshare.data;
                  const latest = ms[ms.length - 1].value;
                  const prev7 = ms[Math.max(0, ms.length - 8)].value;
                  const delta = prev7 > 0 ? ((latest - prev7) / prev7 * 100).toFixed(1) : null;
                  const fmtMs = v => v >= 1e6 ? `${(v/1e6).toFixed(1)}M` : v >= 1e3 ? `${(v/1e3).toFixed(0)}K` : String(Math.round(v));
                  const arrow = delta !== null ? (delta >= 0 ? '↑' : '↓') : '';
                  const val = delta !== null
                    ? `${fmtMs(latest)} · 7日 ${arrow}${Math.abs(delta)}%`
                    : fmtMs(latest);
                  evidence.push({ label: lang === 'en' ? 'Mindshare' : 'Twitter 热度', val, source: 'Surf', positive: delta !== null && Number(delta) >= 0 });
                }
                if (a.track)
                  evidence.push({ label: t('ev.ai_track'), val: lang === 'en' ? (a.track_en || a.track) : a.track, source: "AI" });
              }
              if (d.key === "tech") {
                const contracts = project.details?.contracts || [];
                if (contracts.length > 0)
                  evidence.push({ label: lang === 'en' ? "Contracts Deployed" : "已部署合约", val: `${contracts.length}`, source: "RootData", positive: true });
                const techStage = project.webIntel?.techIntel?.product_stage;
                if (techStage)
                  evidence.push({ label: lang === 'en' ? "Product Stage" : "产品阶段", val: lang === 'en' ? (project.webIntel?.techIntel?.product_stage_en || techStage) : techStage, source: "AI" });
                if (project.defiData?.protocol)
                  evidence.push({ label: lang === 'en' ? "DeFiLlama Listed" : "DeFiLlama 已收录", val: project.defiData.protocol.name || "✓", source: "DeFiLlama", positive: true });
                if (project.defiData?.tvl?.current)
                  evidence.push({ label: "TVL", val: fmt(project.defiData.tvl.current), source: "DeFiLlama", positive: true });
                if (project.defiData?.volume?.daily)
                  evidence.push({ label: lang === 'en' ? "24h Volume" : "24h 交易量", val: fmt(project.defiData.volume.daily), source: "DeFiLlama", positive: true });
                const ghCommits = project.githubSignal?.commit_count_4_weeks;
                if (ghCommits != null)
                  evidence.push({ label: lang === 'en' ? "GitHub Commits (4w)" : "GitHub 提交 (4周)", val: ghCommits, source: "GitHub", positive: ghCommits > 10 });
                if (!project.defiData?.protocol && !contracts.length)
                  evidence.push({ label: t('ev.on_chain'), val: t('ev.not_launched'), source: "DeFiLlama", positive: false });
              }
              if (d.key === "team") {
                const members = project.details?.team_members || [];
                const teamMembers = project.teamMembers || [];
                const ts = project.teamSignals || null;

                // 团队规模
                if (members.length > 0)
                  evidence.push({ label: lang === 'en' ? "Team Size" : "团队人数", val: `${members.length}${lang === 'en' ? ' members' : ' 人'}`, source: "RootData" });

                // 实名 vs 匿名判断
                const doxxedCount = teamMembers.filter(m => m.name && m.name !== '—').length;
                if (teamMembers.length > 0) {
                  evidence.push({
                    label: lang === 'en' ? "Doxxed" : "实名情况",
                    val: doxxedCount === 0
                      ? (lang === 'en' ? 'Fully anonymous' : '完全匿名')
                      : `${doxxedCount}${lang === 'en' ? ' named members' : ' 位实名成员'}`,
                    source: "RootData",
                    positive: doxxedCount > 0,
                  });
                }

                // Twitter 账号年龄
                if (ts?.twitterAges && Object.keys(ts.twitterAges).length > 0) {
                  const ages = Object.values(ts.twitterAges).map(a => a.ageYears).filter(Boolean);
                  if (ages.length > 0) {
                    const minAge = Math.min(...ages);
                    const ageLabel = minAge < 1
                      ? (lang === 'en' ? `Newest: ${(minAge * 12).toFixed(0)}mo old` : `最新账号仅 ${(minAge * 12).toFixed(0)} 个月`)
                      : (lang === 'en' ? `Oldest: ${Math.max(...ages).toFixed(1)}y` : `最长 ${Math.max(...ages).toFixed(1)} 年`);
                    evidence.push({
                      label: lang === 'en' ? "Twitter Age" : "推特账号年龄",
                      val: ageLabel,
                      source: "Twitter",
                      positive: minAge >= 1,
                    });
                  }
                }

                // GitHub 真实活跃度
                const gs = project.githubSignal;
                if (gs) {
                  if (gs.isFork)
                    evidence.push({ label: lang === 'en' ? "GitHub" : "GitHub", val: lang === 'en' ? 'Forked repo' : 'Fork 仓库', source: "GitHub", positive: false });
                  if (gs.contributorCount != null)
                    evidence.push({
                      label: lang === 'en' ? "Contributors" : "代码贡献者",
                      val: gs.contributorCount === 1
                        ? (lang === 'en' ? 'Solo dev' : '单人开发')
                        : `${gs.contributorCount}${lang === 'en' ? ' devs' : ' 位开发者'}`,
                      source: "GitHub",
                      positive: gs.contributorCount > 1,
                    });
                }

                // 路演/孵化器记录
                if (ts?.pitchRecord?.hasPitchRecord) {
                  const accs = ts.pitchRecord.accelerators || [];
                  const surfMentions = ts.pitchRecord.surfMentions || [];
                  if (accs.length > 0)
                    evidence.push({ label: lang === 'en' ? "Accelerator" : "孵化器背书", val: accs.slice(0, 2).join(' · '), source: "RootData", positive: true });
                  else if (surfMentions.length > 0)
                    evidence.push({ label: lang === 'en' ? "Pitch Record" : "路演记录", val: lang === 'en' ? 'Found in news' : '新闻有提及', source: "Surf", positive: true });
                }

                // LinkedIn — RootData 已有 / fallback 搜索链接
                const membersWithLinkedIn = teamMembers.filter(m => m.linkedIn);
                if (membersWithLinkedIn.length > 0) {
                  evidence.push({ label: "LinkedIn", val: `${membersWithLinkedIn.length}${lang === 'en' ? ' profiles' : ' 位有资料'}`, source: "RootData", positive: true });
                } else if (teamMembers.length > 0) {
                  const firstMember = teamMembers[0];
                  const linkedinSearchUrl = `https://www.linkedin.com/search/results/people/?keywords=${encodeURIComponent((firstMember.name || '') + ' ' + (project.details?.project_name || ''))}`;
                  evidence.push({ label: "LinkedIn", val: lang === 'en' ? 'Search' : '搜索', source: "RootData", positive: null, url: linkedinSearchUrl });
                }

                // 创始人过往项目（从 description 和 Twitter bio 提取）
                const founderDescs = teamMembers
                  .filter(m => ['founder', 'ceo', 'cto', 'co-founder'].some(k => (m.title || '').toLowerCase().includes(k)))
                  .map(m => m.description).filter(Boolean);
                if (founderDescs.length > 0) {
                  const hasExperience = founderDescs.some(d => /ex-|former|previously|co-founded|built|created|launched/i.test(d));
                  evidence.push({ label: lang === 'en' ? "Founder Track" : "创始人经历", val: hasExperience ? (lang === 'en' ? 'Prior experience' : '有过往项目记录') : (lang === 'en' ? 'No prior info' : '无过往记录'), source: "RootData", positive: hasExperience });
                }

                // 原有：投资方 / 融资 / 成立日期
                const leads = (project.details?.investors || []).filter(i => i.lead_investor).map(i => i.name);
                if (leads.length)
                  evidence.push({ label: t('ev.lead_investors'), val: leads.slice(0, 3).join(", "), source: "RootData", positive: true });
                const allInvs = (project.details?.investors || []).map(i => i.name);
                if (allInvs.length)
                  evidence.push({ label: t('ev.investor_count'), val: `${allInvs.length}${t('ev.firms_count')}`, source: "RootData" });
                if (project.details?.total_funding)
                  evidence.push({ label: lang === 'en' ? "Total Raised" : "总融资", val: fmt(project.details.total_funding), source: "RootData", positive: true });
                if (project.details?.establishment_date)
                  evidence.push({ label: t('ev.founded'), val: project.details.establishment_date, source: "RootData" });
              }
              if (d.key === "tokenomics") {
                const sym = project.details?.token_symbol;
                evidence.push({ label: t('ev.token_status'), val: sym ? `${t('ev.token_issued')} $${sym}` : t('ev.token_not_issued'), source: "RootData", positive: !!sym });
                const fdv = project.cgData?.market_data?.fully_diluted_valuation?.usd;
                if (fdv)
                  evidence.push({ label: "FDV", val: fmt(fdv), source: "CoinGecko" });
                const circ = project.cgData?.market_data?.circulating_supply;
                const total = project.cgData?.market_data?.total_supply;
                if (circ && total)
                  evidence.push({ label: lang === 'en' ? "Circ. Ratio" : "流通比例", val: `${((circ / total) * 100).toFixed(0)}%`, source: "CoinGecko", positive: (circ / total) > 0.3 });
                if (project.duneTokenInfo?.poolSize)
                  evidence.push({ label: lang === 'en' ? "DEX Pool Size" : "DEX 池子深度", val: fmt(project.duneTokenInfo.poolSize), source: "Dune" });
                if (a.valuation_basis)
                  evidence.push({ label: t('ev.valuation_basis'), val: lang === 'en' ? (a.valuation_basis_en || a.valuation_basis) : a.valuation_basis, source: "AI" });
                if (a.unlock_pressure)
                  evidence.push({ label: t('ev.unlock_pressure'), val: a.unlock_pressure === "Green" ? t('ev.pressure_low') : a.unlock_pressure === "Yellow" ? t('ev.pressure_mid') : t('ev.pressure_high'), source: "AI", positive: a.unlock_pressure === "Green" });
                // ── 解锁日程 evidence（迁移自 data-views/react/UnlockCard）──
                getUnlockEvidence(project, a, lang).forEach(ev => evidence.push(ev));
                // Protocol Economics — P/S = FDV ÷ 年化手续费（代币估值核心锚点）
                // DeFiLlama 月手续费 = 用户支付的全部手续费，是 P/S 的标准分母
                // 仅对已发币且 DeFiLlama 有收费数据的协议显示，早期项目跳过
                const fees30d = project.defiData?.fees?.monthly;
                const fdvPE   = project.cgData?.market_data?.fully_diluted_valuation?.usd;
                if (fees30d && fdvPE) {
                  const ps = fdvPE / (fees30d * 12);
                  evidence.push({
                    label: "P/S",
                    val: `${ps.toFixed(1)}x`,
                    source: "DeFiLlama",
                    positive: ps < 20,
                  });
                }
                if (fees30d) {
                  evidence.push({
                    label: lang === 'en' ? "Monthly Fees" : "月手续费",
                    val: fmt(fees30d),
                    source: "DeFiLlama",
                    positive: true,
                  });
                }
                // 距ATH跌幅 — 价格位置信号
                const athChgEv = project.cgData?.market_data?.ath_change_percentage?.usd;
                if (athChgEv != null)
                  evidence.push({ label: lang === 'en' ? "vs ATH" : "距历史高点", val: `${athChgEv.toFixed(1)}%`, source: "CoinGecko", positive: athChgEv > -30 });
                // 200日涨跌 — 中期趋势
                const p200Ev = project.cgData?.market_data?.price_change_percentage_200d;
                if (p200Ev != null)
                  evidence.push({ label: lang === 'en' ? "200d Return" : "200日涨跌", val: `${p200Ev > 0 ? "+" : ""}${p200Ev.toFixed(1)}%`, source: "CoinGecko", positive: p200Ev > 0 });
                // 社区看涨情绪
                const sentEv = project.cgData?.sentiment_votes_up_percentage;
                if (sentEv != null)
                  evidence.push({ label: lang === 'en' ? "Bullish Votes" : "看涨情绪", val: `${Math.round(sentEv)}%`, source: "CoinGecko", positive: sentEv >= 55 });
                // Surf Token Holders
                if (surfHolders.data?.length) {
                  const labeled = surfHolders.data.filter(h => h.label && h.label !== 'unknown');
                  const exchanges = labeled.filter(h => h.label === 'exchange').length;
                  const funds = labeled.filter(h => h.label === 'fund').length;
                  const whales = labeled.filter(h => h.label === 'whale').length;
                  const top1Pct = surfHolders.data[0]?.percentage;
                  if (top1Pct != null)
                    evidence.push({ label: lang === 'en' ? 'Top Holder %' : '第一持仓占比', val: `${top1Pct.toFixed(1)}%`, source: 'Surf', positive: top1Pct < 15 });
                  if (exchanges + funds > 0)
                    evidence.push({ label: lang === 'en' ? 'Inst. Holders' : '机构持仓数', val: lang === 'en' ? `${exchanges} exchanges · ${funds} funds` : `${exchanges}交易所 · ${funds}基金`, source: 'Surf' });
                  if (whales > 0)
                    evidence.push({ label: lang === 'en' ? 'Whale Holders' : '鲸鱼地址', val: `${whales}`, source: 'Surf', positive: whales < 5 });
                }
              }
              if (d.key === "data") {
                if (project.defiData?.tvl?.current)
                  evidence.push({ label: "TVL", val: `${fmt(project.defiData.tvl.current)}${project.defiData.tvl.weekChange != null ? ` (7d${project.defiData.tvl.weekChange >= 0 ? "+" : ""}${project.defiData.tvl.weekChange}%)` : ""}`, source: "DeFiLlama", positive: (project.defiData.tvl.weekChange || 0) >= 0 });
                if (project.defiData?.fees?.daily)
                  evidence.push({ label: lang === 'en' ? "Daily Fees" : "日费用", val: fmt(project.defiData.fees.daily), source: "DeFiLlama", positive: true });
                if (project.defiData?.users?.daily)
                  evidence.push({ label: lang === 'en' ? "Daily Users" : "日活用户", val: project.defiData.users.daily.toLocaleString(), source: "DeFiLlama", positive: true });
                if (project.cgData?.market_data?.total_volume?.usd)
                  evidence.push({ label: lang === 'en' ? "24h Volume" : "24h 交易量", val: fmt(project.cgData.market_data.total_volume.usd), source: "CoinGecko" });
                if (project.duneTokenInfo?.poolSize)
                  evidence.push({ label: lang === 'en' ? "DEX Pool" : "DEX 池子", val: fmt(project.duneTokenInfo.poolSize), source: "Dune", positive: true });
                // ── data-views 维度证据汇总（valuation/protocol/holders/social/news） ──
                getValuationEvidence(project, a, lang).forEach(ev => evidence.push(ev));
                getProtocolEvidence(project, a, lang).forEach(ev => evidence.push(ev));
                getHoldersEvidence(project, a, lang).forEach(ev => evidence.push(ev));
                getSocialEvidence(project, a, lang).forEach(ev => evidence.push(ev));
                if (!project.defiData?.protocol && !project.cgData?.market_data?.total_volume?.usd && !project.artemisMetrics?.ok)
                  evidence.push({ label: t('ev.data_status'), val: t('ev.no_data'), source: "DeFiLlama", positive: false });
              }
              if (d.key === "compliance") {
                const tge = a.tge_status || "";
                evidence.push({ label: t('ev.tge_status'), val: lang === 'en' ? (a.tge_status_en || tge || 'TBD') : (tge || '未定'), source: "AI" });
                const sensitiveTags = localizeRootDataTags(project.details?.tags || [], lang).filter(tag => ["RWA","DeFi","Payments","Gambling","Derivatives"].includes(tag));
                if (sensitiveTags.length)
                  evidence.push({ label: lang === 'en' ? "Sensitive Sectors" : "敏感赛道", val: sensitiveTags.join(", "), source: "RootData", positive: false });
                const regMention = project.webIntel?.techIntel?.regulatory_notes;
                if (regMention)
                  evidence.push({ label: lang === 'en' ? "Regulatory Notes" : "监管备注", val: regMention.slice(0, 50), source: "AI" });
                if (a.unlock_pressure && d.key !== "tokenomics")
                  evidence.push({ label: t('ev.unlock_pressure'), val: a.unlock_pressure === "Green" ? t('ev.pressure_low') : a.unlock_pressure === "Yellow" ? t('ev.pressure_mid') : t('ev.pressure_high'), source: "AI", positive: a.unlock_pressure === "Green" });
              }

              // 数据源汇总（去重）
              const usedSources = [...new Set(evidence.map(ev => ev.source).filter(Boolean))];

              return (
                <div key={d.key} style={S.card}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                    <div>
                      <span style={{ fontWeight: 700, fontSize: 13 }}>{lang === 'en' ? d.label_en : (t(dimLabelKey[d.key]) || d.label)}</span>
                      <span style={{ fontSize: 10, color: C.muted, marginLeft: 6 }}>×{Math.round(d.w * 100)}%</span>
                    </div>
                    <span style={{ fontSize: 26, fontWeight: 900, color: d.color }}>{score}</span>
                  </div>
                  <ScoreBar value={score} color={d.color} height={4} />

                  {/* AI 评语 + 主张级引用 */}
                  {note && (
                    <div style={{ marginTop: 8, background: C.faint, borderRadius: 4, overflow: "hidden" }}>
                      <div style={{ fontSize: 11, color: "#374151", padding: "6px 8px", lineHeight: 1.6, display: "flex", gap: 6 }}>
                        <MessageSquare size={12} strokeWidth={2} style={{ flexShrink: 0, marginTop: 2, color: C.muted }} />
                        <span>{note}</span>
                      </div>
                      {(() => {
                        const cits = a?.note_citations?.[d.key];
                        if (!cits?.length) return null;
                        return (
                          <div style={{ borderTop: `1px solid ${C.border}`, padding: "4px 8px", display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: 9, color: C.muted, marginRight: 2, display: "inline-flex", alignItems: "center", gap: 3 }}><Paperclip size={10} strokeWidth={2} /> {lang === 'en' ? 'Based on:' : '引用依据：'}</span>
                            {cits.map((c, ci) => {
                              const sc2 = SRC_COLORS[c.src] || { bg: "#f4f4f5", text: "#3f3f46" };
                              const url2 = srcUrl(c.src);
                              const chipStyle = { display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, padding: "2px 6px", borderRadius: 3, background: sc2.bg, color: sc2.text, textDecoration: "none", fontWeight: 600 };
                              const factText = lang === 'en' ? (c.fact_en || enify(c.fact)) : c.fact;
                              return url2 ? (
                                <a key={ci} href={url2} target="_blank" rel="noreferrer" style={chipStyle} title={factText}>
                                  <span>{c.src}</span>
                                  <span style={{ opacity: 0.8, fontWeight: 400 }}>· {factText}</span>
                                  <span style={{ opacity: 0.6 }}>↗</span>
                                </a>
                              ) : (
                                <span key={ci} style={chipStyle} title={factText}>
                                  <span>{c.src}</span>
                                  <span style={{ opacity: 0.8, fontWeight: 400 }}>· {factText}</span>
                                </span>
                              );
                            })}
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* 合规数据库查询结果面板 */}
                  {d.key === "compliance" && (() => {
                    const cc = project.complianceCheck;
                    if (!cc) return null;
                    // 检查 AI 是否给了风险评估，与数据库未命中可能冲突
                    const aiNote = (a?.notes?.compliance || '').toLowerCase();
                    const aiFlagsRisk = /risk|warning|sanction|enforcement|investigation|未注册|警告|制裁|监管|违规|执法|起诉/.test(aiNote);
                    const allClear = cc.ofac?.status !== 'hit' && cc.ofsi?.status !== 'hit'
                                   && cc.sec?.status !== 'hit' && cc.fca?.status !== 'warning';
                    const showConflictWarning = aiFlagsRisk && allClear;

                    const rows = [
                      {
                        db: "OFAC",
                        label: lang === 'en' ? "US Sanctions (OFAC)" : "美国制裁名单 (OFAC)",
                        status: cc.ofac?.status,
                        hit: cc.ofac?.status === 'hit',
                        detail: cc.ofac?.hits?.slice(0, 2).join("、") || cc.ofac?.matches?.slice(0, 2).join("、") || "",
                        link: "https://sanctionssearch.ofac.treas.gov/",
                        hitColor: "#dc2626",
                        // 中性 ⚪ 代替 ✅，避免视觉上「全绿 = 安全」的误导
                        okIcon: "⚪", hitIcon: "⚠️",
                        okLabel: lang === 'en' ? "No match" : "无字面匹配",
                        hitLabel: lang === 'en' ? "Match found" : "发现匹配",
                      },
                      {
                        db: "OFSI",
                        label: lang === 'en' ? "UK Sanctions (OFSI)" : "英国制裁名单 (OFSI)",
                        status: cc.ofsi?.status,
                        hit: cc.ofsi?.status === 'hit',
                        detail: cc.ofsi?.hits?.slice(0, 2).join("、") || "",
                        link: "https://www.gov.uk/government/publications/financial-sanctions-consolidated-list-of-targets",
                        hitColor: "#dc2626",
                        okIcon: "⚪", hitIcon: "⚠️",
                        okLabel: lang === 'en' ? "No match" : "无字面匹配",
                        hitLabel: lang === 'en' ? "Match found" : "发现匹配",
                      },
                      {
                        db: "SEC",
                        label: lang === 'en' ? "Enforcement Actions" : "SEC 执法记录",
                        status: cc.sec?.status,
                        hit: cc.sec?.status === 'hit',
                        detail: `${cc.sec?.count || 0}${lang === 'en' ? ' record(s)' : ' 条记录'}`,
                        link: `https://efts.sec.gov/LATEST/search-index?q="${encodeURIComponent(project.name)}"&forms=LR`,
                        hitColor: "#d97706",
                        okIcon: "⚪", hitIcon: "⚠️",
                        okLabel: lang === 'en' ? "No record" : "未发现记录",
                        hitLabel: lang === 'en' ? "Records found" : "发现执法记录",
                      },
                      {
                        db: "FCA",
                        label: lang === 'en' ? "FCA Registration" : "FCA 注册状态",
                        status: cc.fca?.status,
                        hit: cc.fca?.status === 'warning',
                        detail: cc.fca?.firms?.slice(0, 2).join("、") || "",
                        link: `https://register.fca.org.uk/s/search?predefined=ALL&q=${encodeURIComponent(project.name)}`,
                        hitColor: "#d97706",
                        okIcon: cc.fca?.status === 'registered' ? "🟢" : "—",
                        hitIcon: "⚠️",
                        okLabel: cc.fca?.status === 'registered'
                          ? (lang === 'en' ? "Registered" : "已注册")
                          : (lang === 'en' ? "Not found" : "未找到"),
                        hitLabel: lang === 'en' ? "Warning listed" : "在警告名单中",
                      },
                      {
                        db: "FinCEN",
                        label: lang === 'en' ? "MSB Registration" : "FinCEN MSB 注册",
                        status: cc.fincen?.status,
                        hit: false,
                        detail: cc.fincen?.registrants?.slice(0, 2).join("、") || "",
                        link: `https://www.fincen.gov/msb-state-selector`,
                        hitColor: "#059669",
                        okIcon: cc.fincen?.status === 'registered' ? "🟢" : "—",
                        hitIcon: "🟢",
                        okLabel: cc.fincen?.status === 'registered'
                          ? (lang === 'en' ? "Registered" : "已注册")
                          : (lang === 'en' ? "Not found" : "未找到"),
                        hitLabel: lang === 'en' ? "Registered" : "已注册",
                      },
                    ];
                    const ts = (cc.checkedAt || cc.queriedAt) ? new Date(cc.checkedAt || cc.queriedAt).toLocaleDateString(lang === 'en' ? 'en-US' : 'zh-CN') : null;
                    return (
                      <div style={{ marginTop: 10, border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                        <div style={{ background: "#0f172a", padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                            <Search size={11} strokeWidth={2} />{lang === 'en' ? 'Live Database Checks' : '合规数据库实时查询'}
                          </span>
                          {ts && <span style={{ fontSize: 9, color: "#475569" }}>{ts}</span>}
                        </div>
                        {/* AI 判断与数据库结果冲突警告 */}
                        {showConflictWarning && (
                          <div style={{ padding: "8px 10px", background: "rgba(220,38,38,0.08)", borderBottom: `1px solid rgba(220,38,38,0.3)`, fontSize: 10, color: "#fca5a5", lineHeight: 1.5 }}>
                            <strong style={{ color: "#dc2626" }}>{lang === 'en' ? 'Data source gap detected' : '检测到数据源盲区'}：</strong>
                            {lang === 'en'
                              ? ' AI flags compliance risk based on public knowledge, but databases below show no match. This often means: (a) sanctions are from jurisdictions not covered here (e.g. EU/HK/SG), (b) related entities (parent/subsidiary) are listed under different legal names, or (c) recent events (last 30d) not yet indexed. Trust the AI signal and verify manually.'
                              : ' AI 基于公开知识判定存在合规风险，但下方数据库未发现字面匹配。可能原因：① 制裁来自我们未覆盖的司法管辖区（如欧盟、香港、新加坡）；② 关联实体（母/子公司）以不同法人名登记在册；③ 30 天内的新事件尚未被索引。**应以 AI 判断为准，并手动核查**。'}
                          </div>
                        )}
                        {rows.map((row, ri) => (
                          <div key={row.db} style={{ display: "flex", alignItems: "center", padding: "6px 10px", borderBottom: ri < rows.length - 1 ? `1px solid ${C.faint}` : "none", gap: 8 }}>
                            <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: "#1e293b", color: "#94a3b8", minWidth: 42, textAlign: "center" }}>{row.db}</span>
                            <span style={{ fontSize: 10, color: C.muted, flex: 1 }}>{row.label}</span>
                            <span style={{ fontSize: 10, fontWeight: 600, color: row.hit ? row.hitColor : (row.status === 'registered' ? "#059669" : C.muted), display: "flex", alignItems: "center", gap: 3 }}>
                              <span>{row.hit ? row.hitIcon : row.okIcon}</span>
                              <span>{row.hit ? row.hitLabel : row.okLabel}</span>
                              {row.detail && <span style={{ fontSize: 9, color: C.muted, marginLeft: 3 }}>({row.detail})</span>}
                            </span>
                            <a href={row.link} target="_blank" rel="noreferrer" style={{ fontSize: 9, color: "#3b82f6", textDecoration: "none" }}>↗</a>
                          </div>
                        ))}
                        <div style={{ padding: "6px 10px", background: C.faint, fontSize: 9, color: C.muted, lineHeight: 1.5 }}>
                          <div style={{ fontWeight: 700, marginBottom: 2 }}>{lang === 'en' ? 'Coverage notes' : '覆盖范围说明'}</div>
                          {lang === 'en'
                            ? '· "No match" = no exact name match in listed sources, NOT a compliance certification. · Coverage: US (OFAC/SEC/FinCEN) + UK (OFSI/FCA). EU, HK, SG, JP sanctions NOT included. · String-based matching may miss related entities (parent/subsidiary).'
                            : '·「无字面匹配」= 在以下数据源中没有名称精确命中，**不代表合规认定**。· 当前覆盖：美国 (OFAC/SEC/FinCEN) + 英国 (OFSI/FCA)，未包含欧盟/香港/新加坡/日本制裁。· 字面字符串匹配可能漏掉关联实体（母公司/子公司不同法人名）。'}
                        </div>

                        {/* ── 合规事件流（实时 Brave Search，覆盖名单盲区）── */}
                        {(eventsLoading || (complianceEvents && complianceEvents.events?.length > 0)) && (
                          <div style={{ borderTop: `1px solid ${C.border}`, background: "#0a0f1c" }}>
                            <div style={{ padding: "6px 10px", display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: `1px solid ${C.faint}` }}>
                              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: 0.5 }}>
                                <FileText size={11} strokeWidth={2} />{lang === 'en' ? 'Compliance Events (90d)' : '合规事件流（近 90 天）'}
                              </span>
                              {complianceEvents && (
                                <span style={{ fontSize: 9, display: "flex", gap: 5 }}>
                                  {complianceEvents.highCount > 0 && <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: "#dc2626", fontWeight: 700 }}><AlertTriangle size={9} strokeWidth={2} />{complianceEvents.highCount} {lang === 'en' ? 'high' : '高'}</span>}
                                  {complianceEvents.mediumCount > 0 && <span style={{ color: "#d97706" }}>● {complianceEvents.mediumCount} {lang === 'en' ? 'med' : '中'}</span>}
                                  {complianceEvents.lowCount > 0 && <span style={{ color: "#94a3b8" }}>○ {complianceEvents.lowCount} {lang === 'en' ? 'low' : '低'}</span>}
                                </span>
                              )}
                            </div>
                            {eventsLoading && (
                              <div style={{ padding: "10px", fontSize: 10, color: C.muted, textAlign: "center" }}>
                                {lang === 'en' ? 'Searching live compliance events...' : '正在搜索实时合规事件...'}
                              </div>
                            )}
                            {!eventsLoading && complianceEvents?.events?.map((ev, ei) => {
                              const sevColor = ev.severity === 'high' ? "#dc2626" : ev.severity === 'medium' ? "#d97706" : "#94a3b8";
                              const sevBg = ev.severity === 'high' ? "rgba(220,38,38,0.08)" : "transparent";
                              const sevIcon = ev.severity === 'medium' ? "●" : ev.severity === 'low' ? "○" : null;
                              const sevLabel = ev.severity === 'high' ? (lang === 'en' ? 'HIGH' : '高')
                                            : ev.severity === 'medium' ? (lang === 'en' ? 'MED' : '中')
                                            : (lang === 'en' ? 'LOW' : '低');
                              return (
                                <a key={ei} href={ev.url} target="_blank" rel="noreferrer"
                                  style={{ display: "block", textDecoration: "none", padding: "8px 10px",
                                    borderBottom: ei < complianceEvents.events.length - 1 ? `1px solid ${C.faint}` : "none",
                                    background: sevBg,
                                    transition: "background 0.15s",
                                  }}
                                  onMouseEnter={e => e.currentTarget.style.background = ev.severity === 'high' ? 'rgba(220,38,38,0.15)' : '#0f172a'}
                                  onMouseLeave={e => e.currentTarget.style.background = sevBg}>
                                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 3 }}>
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: sevColor + "22", color: sevColor }}>
                                      {ev.severity === 'high' ? <AlertTriangle size={9} strokeWidth={2} /> : sevIcon} {sevLabel}
                                    </span>
                                    <span style={{ fontSize: 9, color: "#64748b" }}>{ev.date || '—'}</span>
                                    <span style={{ fontSize: 9, color: "#64748b", marginLeft: "auto" }}>{ev.source}</span>
                                  </div>
                                  <div style={{ fontSize: 11, fontWeight: 600, color: C.text, lineHeight: 1.4, marginBottom: 2 }}>
                                    {ev.title}
                                  </div>
                                  {ev.description && (
                                    <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.45, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                      {ev.description}
                                    </div>
                                  )}
                                </a>
                              );
                            })}
                          </div>
                        )}
                        {!eventsLoading && complianceEvents && complianceEvents.events?.length === 0 && (
                          <div style={{ borderTop: `1px solid ${C.border}`, padding: "6px 10px", fontSize: 9, color: "#475569", background: "#0a0f1c" }}>
                            {lang === 'en' ? 'No compliance events found in last 90 days (via news search)' : '近 90 天未发现合规事件（实时新闻搜索）'}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* 监管合规尽调 — _error 状态时的友好 banner
                      不再"静默隐藏"，让用户知道审查跑过但失败了 */}
                  {d.key === "compliance"
                    && project.complianceRisk
                    && project.complianceRisk._error && (() => {
                    const cr = project.complianceRisk;
                    const topicsCount = cr.topics_considered?.length || 0;
                    return (
                      <div style={{ marginTop: 10, padding: "10px 12px", border: `1px solid #FAC775`, background: "#FFFBEB", borderRadius: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 700, color: "#854F0B", display: "inline-flex", alignItems: "center", gap: 6 }}>
                            <Scale size={13} strokeWidth={2} /> {lang === 'en' ? 'Compliance review temporarily unavailable' : '合规审查暂时不可用'}
                          </span>
                          <span style={{ fontSize: 9, padding: "1px 6px", background: "#FAEEDA", color: "#633806", borderRadius: 3, fontFamily: "monospace" }}>
                            {cr._error}
                          </span>
                        </div>
                        <div style={{ fontSize: 11, color: "#633806", lineHeight: 1.55, marginBottom: 6 }}>
                          {lang === 'en'
                            ? `AI compliance synthesis failed mid-analysis. ${topicsCount > 0 ? `${topicsCount} topic(s) were matched but findings could not be generated.` : ''} The check was attempted, not skipped.`
                            : `本次分析中 AI 合规合成失败${topicsCount > 0 ? `（已匹配 ${topicsCount} 个监管 topic，但无法生成 finding）` : ''}。审查已尝试，并非跳过。`}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                          <div style={{ fontSize: 10, color: "#854F0B" }}>
                            {lang === 'en'
                              ? 'Most often resolves on a second attempt (AI provider transient errors).'
                              : '绝大多数是 AI 服务方临时抽风，重试一次基本能成功。'}
                          </div>
                          <button
                            onClick={handleComplianceRefresh}
                            disabled={complianceRefreshing}
                            style={{
                              fontSize: 10, padding: "3px 9px",
                              border: "1px solid #E8B559",
                              borderRadius: 4, background: "transparent",
                              cursor: complianceRefreshing ? "default" : "pointer",
                              color: "#854F0B",
                              display: "inline-flex", alignItems: "center", gap: 3,
                              flexShrink: 0,
                            }}>
                            <RotateCw size={11} strokeWidth={2}
                              style={complianceRefreshing ? { animation: "compRefreshSpin .8s linear infinite" } : undefined} />
                            {complianceRefreshing ? (lang === 'en' ? 'Refreshing' : '刷新中') : (lang === 'en' ? 'Retry' : '重试')}
                          </button>
                        </div>
                      </div>
                    );
                  })()}

                  {/* 监管合规尽调（A3 — 机构尽调工具）
                      产品定位：辖区机构投资/合作前的合规缺口扫描，独立于合规维度评分
                      位置：合规维度卡内、OFAC 全球速查之后，但通过标题 + 顶部 disclaimer 与维度评分明确切割 */}
                  {d.key === "compliance"
                    && project.complianceRisk
                    && !project.complianceRisk._error
                    && Array.isArray(project.complianceRisk.findings) && (() => {
                    const cr = project.complianceRisk;
                    const findings = cr.findings;
                    const hasFindings = findings.length > 0;
                    const topicsCount = cr.topics_considered?.length || 0;
                    const sev = cr.overall_severity;
                    const sevBg = { critical: "#FCEBEB", high: "#FAEEDA", medium: "#FFFBEB", low: "#ECFDF5" }[sev] || "#f3f4f6";
                    const sevColor = { critical: "#791F1F", high: "#633806", medium: "#854F0B", low: "#065F46" }[sev] || "#6b7280";
                    const critCount = findings.filter(f => f.severity === 'critical').length;
                    const highCount = findings.filter(f => f.severity === 'high').length;
                    const fmtSeverity = s => (s || '').toUpperCase();
                    // finding.topic 只是 slug（如 "mica-whitepaper-classification"），不带 jurisdiction——
                    // 从 topics_considered 建一份 slug → jurisdiction 的映射，这样每条 finding、
                    // 每个辖区徽章才能标对辖区，而不是把所有内容都当成 HK 的（Q66 之前的 bug：
                    // onlyJurisdictions 加了 eu 之后，findings 可能全部来自 eu，但横幅/徽章硬编码写死"HK"）
                    const topicToJur = {};
                    (cr.topics_considered || []).forEach(t => { topicToJur[t.topic] = t.jurisdiction; });
                    const JUR_META = { hk: { flag: '🇭🇰', label: 'HK' }, sg: { flag: '🇸🇬', label: 'SG' }, eu: { flag: '🇪🇺', label: 'EU' }, us: { flag: '🇺🇸', label: 'US' } };
                    const presentJurs = [...new Set((cr.topics_considered || []).map(t => t.jurisdiction))];
                    // 哪些辖区已经有 topic 内容库——静态事实，跟"这个项目有没有触发该辖区"是两回事。
                    // Q68 bug：之前把 enabled 直接绑到 presentJurs（本项目是否触发），导致 Qubit
                    // 这种只触发 HK topic 的项目，EU 标签也显示"即将"——但 EU topic 库明明已经建好了，
                    // 只是这个项目没撞上，「即将」应该只用在"这个辖区还没建库"，不是"建了但没触发"。
                    const JUR_WITH_CONTENT = new Set(['hk', 'eu']);
                    // 司法辖区切换器：有内容库的辖区始终可见，countLabel 才是"这个项目实际触发了多少条"
                    const jurisdictions = ['hk', 'sg', 'eu', 'us'].map(id => {
                      const hasContent = JUR_WITH_CONTENT.has(id);
                      const jurCritCount = hasContent
                        ? findings.filter(f => f.severity === 'critical' && topicToJur[f.topic] === id).length
                        : 0;
                      return { id, ...JUR_META[id], enabled: hasContent, countLabel: hasContent && jurCritCount > 0 ? String(jurCritCount) : null };
                    });
                    const activeJur = presentJurs[0] || 'hk';
                    // 横幅/摘要标题用的辖区文案——本次实际触发了哪些辖区就显示哪些，不再写死"HK"
                    const jurLabel = presentJurs.length
                      ? presentJurs.map(id => `${JUR_META[id]?.flag || ''} ${JUR_META[id]?.label || id.toUpperCase()}`).join(' + ')
                      : `${JUR_META.hk.flag} ${JUR_META.hk.label}`;
                    return (
                      <div style={{ marginTop: 10, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12, background: "#fff" }}>
                        {/* 标题行 + 机构视角徽章 + 导出按钮 */}
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, marginBottom: 4 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ fontSize: 14, fontWeight: 700, color: C.text, display: "inline-flex", alignItems: "center", gap: 6 }}>
                              <Scale size={14} strokeWidth={2} /> {lang === 'en' ? 'Regulatory Compliance Diligence' : '监管合规尽调'}
                            </span>
                            <span style={{ fontSize: 9, padding: "2px 7px", background: "#EEEDFE", color: "#3C3489", borderRadius: 4, fontWeight: 700 }}>
                              {lang === 'en' ? 'Institutional view' : '机构视角'}
                            </span>
                          </div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <button
                              onClick={handleComplianceRefresh}
                              disabled={complianceRefreshing}
                              title={lang === 'en' ? 'Re-run compliance check only (other dimensions unaffected)' : '仅重新跑合规检查，不影响其它维度'}
                              style={{
                                fontSize: 10, padding: "3px 9px",
                                border: `1px solid ${C.border}`,
                                borderRadius: 4, background: "transparent",
                                cursor: complianceRefreshing ? "default" : "pointer",
                                color: complianceRefreshing ? C.muted : C.text,
                                display: "inline-flex", alignItems: "center", gap: 3,
                              }}>
                              <RotateCw size={11} strokeWidth={2}
                                style={complianceRefreshing ? { animation: "compRefreshSpin .8s linear infinite" } : undefined} />
                              {complianceRefreshing ? (lang === 'en' ? 'Refreshing' : '刷新中') : (lang === 'en' ? 'Refresh' : '刷新')}
                            </button>
                            <button
                              onClick={() => downloadComplianceMarkdown({
                                projectName: project.name || project.details?.project_name,
                                complianceRisk: cr,
                                jurisdiction: activeJur,
                                lang,
                              })}
                              disabled={!hasFindings}
                              title={lang === 'en' ? 'Export findings as Markdown' : '导出为 Markdown 文档'}
                              style={{
                                fontSize: 10, padding: "3px 9px",
                                border: `1px solid ${hasFindings ? C.border : C.faint}`,
                                borderRadius: 4, background: "transparent",
                                cursor: hasFindings ? "pointer" : "not-allowed",
                                color: hasFindings ? C.text : C.muted,
                                display: "inline-flex", alignItems: "center", gap: 3,
                              }}>
                              ⬇ {lang === 'en' ? 'Export' : '导出'}
                            </button>
                          </div>
                          <style>{`@keyframes compRefreshSpin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                        {/* 副标题：定位说明 */}
                        <div style={{ fontSize: 10, color: C.muted, marginBottom: 12, lineHeight: 1.5 }}>
                          {lang === 'en'
                            ? 'Per-jurisdiction regulatory gap scan for pre-investment / partnership review. Does not represent the project\'s overall compliance posture.'
                            : '辖区机构投资/合作前的合规缺口扫描，不代表项目整体合规姿态'}
                        </div>

                        {/* Jurisdiction 切换器 */}
                        <div style={{ display: "flex", gap: 4, padding: 3, background: C.faint, borderRadius: 6, marginBottom: 12 }}>
                          {jurisdictions.map(j => (
                            <div key={j.id} title={j.enabled ? '' : (lang === 'en' ? 'Coming soon' : '即将上线')}
                              style={{
                                flex: 1, textAlign: "center", padding: "5px 8px", fontSize: 11,
                                background: j.id === activeJur ? "#fff" : "transparent",
                                border: j.id === activeJur ? `1px solid ${C.border}` : "1px solid transparent",
                                borderRadius: 4,
                                color: j.enabled ? C.text : C.muted,
                                cursor: j.enabled && j.id !== activeJur ? "pointer" : "default",
                                fontWeight: j.id === activeJur ? 700 : 400,
                              }}>
                              {j.flag} {j.label}
                              {j.enabled && j.countLabel
                                ? <span style={{ fontSize: 9, color: "#A32D2D", marginLeft: 3 }}>{j.countLabel}</span>
                                : !j.enabled
                                  ? <span style={{ fontSize: 9, color: C.muted, marginLeft: 3 }}>{lang === 'en' ? 'soon' : '即将'}</span>
                                  : null}
                            </div>
                          ))}
                        </div>

                        {/* 黄色 disclaimer：明确审查口径 */}
                        <div style={{ background: "#FFFBEB", borderLeft: "3px solid #BA7517", padding: "8px 10px", borderRadius: "0 4px 4px 0", marginBottom: 12 }}>
                          <div style={{ fontSize: 11, color: "#633806", lineHeight: 1.55 }}>
                            <strong>{lang === 'en' ? `Under ${jurLabel} regulation: ` : `${jurLabel} 监管下：`}</strong>
                            {lang === 'en' ? (
                              'Gaps below are open items an institution should clarify with the project before investing or partnering. They do not affect the project\'s overall score.'
                            ) : (
                              <>以下缺口为机构投资/合作前应向项目方澄清的开放问题，<strong>不影响项目全球评分</strong>。</>
                            )}
                          </div>
                        </div>

                        {hasFindings ? (
                          <>
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 12 }}>
                              <div style={{ background: C.faint, padding: "8px 10px", borderRadius: 6 }}>
                                <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>{lang === 'en' ? 'Findings' : '总缺口'}</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{findings.length}</div>
                              </div>
                              <div style={{ background: critCount > 0 ? "#FCEBEB" : C.faint, padding: "8px 10px", borderRadius: 6 }}>
                                <div style={{ fontSize: 10, color: critCount > 0 ? "#791F1F" : C.muted, marginBottom: 2 }}>Critical</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: critCount > 0 ? "#A32D2D" : C.muted }}>{critCount}</div>
                              </div>
                              <div style={{ background: highCount > 0 ? "#FAEEDA" : C.faint, padding: "8px 10px", borderRadius: 6 }}>
                                <div style={{ fontSize: 10, color: highCount > 0 ? "#633806" : C.muted, marginBottom: 2 }}>High</div>
                                <div style={{ fontSize: 16, fontWeight: 700, color: highCount > 0 ? "#854F0B" : C.muted }}>{highCount}</div>
                              </div>
                            </div>
                            {cr.summary && (
                              <div style={{ fontSize: 12, color: C.text, marginBottom: 12, padding: "8px 10px", background: C.faint, borderRadius: 6, lineHeight: 1.6 }}>
                                <strong>{lang === 'en' ? `${jurLabel} posture: ` : `${jurLabel} 合规姿态：`}</strong>
                                {lang === 'en' ? (cr.summary_en || cr.summary) : cr.summary}
                                {lang === 'en' && !cr.summary_en && t('misc.ai_content_note') && (
                                  <span style={{ color: C.muted, fontSize: 10, marginLeft: 6 }}>{t('misc.ai_content_note')}</span>
                                )}
                              </div>
                            )}
                            <div>
                              {findings.map((f, i) => {
                                const fBg = { critical: "#FCEBEB", high: "#FAEEDA", medium: "#FFFBEB", low: "#ECFDF5" }[f.severity] || "#f3f4f6";
                                const fColor = { critical: "#791F1F", high: "#633806", medium: "#854F0B", low: "#065F46" }[f.severity] || "#6b7280";
                                const fBar = { critical: "#E24B4A", high: "#EF9F27", medium: "#FACC15", low: "#22C55E" }[f.severity] || "#9ca3af";
                                return (
                                  <div key={i} style={{ borderLeft: `3px solid ${fBar}`, paddingLeft: 10, marginBottom: 10 }}>
                                    <div style={{ display: "flex", gap: 6, marginBottom: 4, flexWrap: "wrap" }}>
                                      <span style={{ background: fBg, color: fColor, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>
                                        {fmtSeverity(f.severity)}
                                      </span>
                                      <span style={{ background: C.faint, color: C.muted, fontSize: 9, padding: "1px 6px", borderRadius: 8 }}>
                                        {JUR_META[topicToJur[f.topic]]?.flag} {f.topic}
                                      </span>
                                    </div>
                                    <div style={{ fontSize: 12, color: C.text, fontWeight: 600, marginBottom: 3, lineHeight: 1.5 }}>
                                      {lang === 'en' ? (f.requirement_en || f.requirement) : f.requirement}
                                      {lang === 'en' && !f.requirement_en && t('misc.ai_content_note') && (
                                        <span style={{ color: C.muted, fontSize: 10, fontWeight: 400, marginLeft: 6 }}>{t('misc.ai_content_note')}</span>
                                      )}
                                    </div>
                                    {f.gap && (
                                      <div style={{ fontSize: 11, color: C.muted, marginBottom: 4, lineHeight: 1.5 }}>
                                        {lang === 'en' ? (f.gap_en || f.gap) : f.gap}
                                      </div>
                                    )}
                                    {f.citation?.section && (
                                      f.citation?.source_url ? (
                                        <a href={f.citation.source_url} target="_blank" rel="noreferrer"
                                          style={{ fontSize: 10, color: "#2563eb", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 3 }}>
                                          {f.citation.section} ↗
                                        </a>
                                      ) : (
                                        <span style={{ fontSize: 10, color: C.muted }}>{f.citation.section}</span>
                                      )
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        ) : (
                          <div style={{ padding: "10px 12px", background: C.faint, borderRadius: 6, fontSize: 11, color: C.muted, lineHeight: 1.5, textAlign: "center" }}>
                            {topicsCount > 0
                              ? (lang === 'en'
                                ? `Reviewed ${topicsCount} ${jurLabel} topic${topicsCount === 1 ? '' : 's'} — no critical / high gaps surfaced.`
                                : `已审视 ${topicsCount} 个 ${jurLabel} topic — 未发现 critical / high 缺口`)
                              : (lang === 'en'
                                ? 'Project did not trigger any covered regulatory topics (HK / EU).'
                                : '该项目未触发任何已收录辖区（HK / EU）的监管 topic')}
                          </div>
                        )}

                        {/* 底部：与合规维度评分独立性提示 + 时间戳 */}
                        <div style={{ marginTop: 12, paddingTop: 8, borderTop: `1px solid ${C.faint}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 9, color: C.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
                            <ShieldCheck size={11} strokeWidth={2} /> {lang === 'en'
                              ? 'Independent from the compliance dim score above'
                              : '与上方合规维度评分相互独立'}
                          </span>
                          {cr._meta?.generated_at && (
                            <span style={{ fontSize: 9, color: C.muted }}>
                              {lang === 'en' ? 'AI synthesized: ' : 'AI 合成于：'}{cr._meta.generated_at.slice(0, 10)}
                            </span>
                          )}
                        </div>

                        {/* 关联信号 — 引用其他维度已有数据，不重复采集 */}
                        <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.faint}` }}>
                          <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>
                            {lang === 'en' ? 'Related signals' : '关联信号'}
                          </div>
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {project.topHolders?.top10Concentration != null && (
                              <button onClick={() => setTab("data")}
                                style={{ flex: "1 1 160px", textAlign: "left", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: "transparent", cursor: "pointer" }}>
                                <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>{lang === 'en' ? 'Holder concentration' : '持仓集中度'}</div>
                                <div style={{ fontSize: 15, fontWeight: 700, color: project.topHolders.top10Concentration > 80 ? "#dc2626" : project.topHolders.top10Concentration > 60 ? "#d97706" : "#059669" }}>
                                  {project.topHolders.top10Concentration}%
                                </div>
                                <div style={{ fontSize: 9, color: C.muted }}>{lang === 'en' ? 'top10 · from tokenomics ↗' : '前十地址占比 · 来自代币经济 ↗'}</div>
                              </button>
                            )}
                            <button onClick={() => setTab("bp_verify")}
                              style={{ flex: "1 1 160px", textAlign: "left", padding: "8px 10px", border: `1px solid ${C.border}`, borderRadius: 6, background: "transparent", cursor: "pointer" }}>
                              <div style={{ fontSize: 10, color: C.muted, marginBottom: 2 }}>{lang === 'en' ? 'Disclosure check' : '披露核查'}</div>
                              <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{lang === 'en' ? 'View BP verification →' : '查看 BP 核查 →'}</div>
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })()}

                  {/* 溯源证据 */}
                  {evidence.length > 0 && (
                    <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 3 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 2 }}>{t('score.data_basis')}</div>
                      {evidence.map((ev, i) => {
                        const sc = SRC_COLORS[ev.source] || { bg: "#f4f4f5", text: "#3f3f46" };
                        return (
                          <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, padding: "3px 0", borderBottom: i < evidence.length - 1 ? `1px solid ${C.faint}` : "none" }}>
                            <span style={{ display: "flex", alignItems: "center", gap: 5, color: C.muted }}>
                              {ev.source && (() => {
                                const url = srcUrl(ev.source);
                                const badgeStyle = { fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: sc.bg, color: sc.text, letterSpacing: 0.3, flexShrink: 0, textDecoration: "none" };
                                return url ? (
                                  <a href={url} target="_blank" rel="noreferrer" style={badgeStyle} title={`查看 ${ev.source} 来源`}>
                                    {ev.source} ↗
                                  </a>
                                ) : (
                                  <span style={badgeStyle}>{ev.source}</span>
                                );
                              })()}
                              {ev.label}
                            </span>
                            <span style={{ fontWeight: 600, color: ev.positive === true ? "#059669" : ev.positive === false ? "#dc2626" : C.text, textAlign: "right", maxWidth: "55%", wordBreak: "break-word" }}>
                              {ev.val}
                            </span>
                          </div>
                        );
                      })}
                      {/* 数据源汇总 */}
                      {usedSources.length > 0 && (
                        <div style={{ marginTop: 6, paddingTop: 5, borderTop: `1px solid ${C.faint}`, display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontSize: 9, color: C.muted, marginRight: 2 }}>{lang === 'en' ? 'Sources:' : '数据源：'}</span>
                          {usedSources.map(src => {
                            const sc = SRC_COLORS[src] || { bg: "#f4f4f5", text: "#3f3f46" };
                            return (
                              <span key={src} style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 3, background: sc.bg, color: sc.text }}>
                                {src}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
          );
        })()}

        {tab === "irr" && <IRRPanel project={project} />}

        {tab === "comp" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* 竞品对标表 */}
            {a?.comparable_projects?.length > 0 ? (
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>{t('comp.header')}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: C.faint }}>
                      {[t('comp.project'), "FDV", t('comp.stage'), t('comp.similarity'), t('comp.pricing'), ""].map((h, hi) => (
                        <th key={hi} style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: C.muted, fontSize: 10 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {a.comparable_projects.map((c, i) => {
                      const vsText = lang === 'en' ? (c.vs_en || c.vs || "") : (c.vs || "");
                      const isPremium  = (c.vs || "").includes("溢价") || (c.vs_en || "").toLowerCase().includes("premium");
                      const isDiscount = (c.vs || "").includes("折价") || (c.vs_en || "").toLowerCase().includes("discount");
                      // 竞品是否已在本地分析过
                      const existing   = projects.find(p => p.name.toLowerCase() === (c.name || "").toLowerCase());
                      const isLoading  = compLoadingMap[c.name];

                      const handleCompare = async () => {
                        if (!onCompareWith || isLoading) return;
                        // 已有缓存 → 直接跳转
                        if (existing) {
                          onCompareWith(project, existing);
                          return;
                        }
                        // 需要先分析
                        setCompLoadingMap(prev => ({ ...prev, [c.name]: true }));
                        try {
                          const cached = await getCached(c.name);
                          let result;
                          if (cached?.analysis && cached?.raw_data) {
                            const r = cached.raw_data;
                            result = { id: Date.now(), name: r.details?.project_name || c.name, details: r.details, fundingRounds: r.fundingRounds, cgData: r.cgData, defiData: r.defiData, webIntel: r.webIntel, githubSignal: r.githubSignal || null, socialSignals: r.socialSignals || null, duneTokenInfo: r.duneTokenInfo || null, duneDashboard: r.duneDashboard || null, duneAnalytics: r.duneAnalytics || null, waybackSnapshot: r.waybackSnapshot || null, timeline: r.timeline || [], teamMembers: r.teamMembers || [], marketContext: r.marketContext || null, tokenFutures: r.tokenFutures || null, artemisMetrics: r.artemisMetrics || null, tokenUnlock: r.tokenUnlock || null, topHolders: r.topHolders || null, complianceRisk: r.complianceRisk || null, analysis: cached.analysis, analyzedAt: cached.scored_at, fromCache: true };
                          } else {
                            const raw      = await fetchFullProject(c.name);
                            const analysis = await scoreProject(raw, () => {});
                            result = { id: Date.now(), name: raw.details.project_name, details: raw.details, fundingRounds: raw.fundingRounds, cgData: raw.cgData, defiData: raw.defiData, webIntel: raw.webIntel, githubSignal: raw.githubSignal || null, socialSignals: raw.socialSignals || null, duneTokenInfo: raw.duneTokenInfo || null, duneDashboard: raw.duneDashboard || null, duneAnalytics: raw.duneAnalytics || null, waybackSnapshot: raw.waybackSnapshot || null, timeline: raw.timeline || [], teamMembers: raw.teamMembers || [], marketContext: raw.marketContext || null, tokenFutures: raw.tokenFutures || null, artemisMetrics: raw.artemisMetrics || null, tokenUnlock: raw.tokenUnlock || null, topHolders: raw.topHolders || null, analysis, analyzedAt: new Date().toISOString() };
                          }
                          onCompareWith(project, result);
                        } catch (err) {
                          console.error("Compare analyze error:", err);
                          setCompLoadingMap(prev => ({ ...prev, [c.name]: false }));
                        }
                      };

                      const btnLabel = isLoading ? (lang === 'en' ? "Analyzing…" : "分析中…")
                                     : existing  ? (lang === 'en' ? "Compare" : "直接对比")
                                     : (lang === 'en' ? "Compare" : "对比");
                      const btnColor = existing ? "#2563eb" : "#6b7280";

                      return (
                        <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                          <td style={{ padding: "6px 8px", fontWeight: 600 }}>{lang === 'en' ? enify(c.name) : c.name}</td>
                          <td style={{ padding: "6px 8px", color: "#2563eb", fontWeight: 600 }}>{lang === 'en' ? enify(c.fdv) : c.fdv}</td>
                          <td style={{ padding: "6px 8px" }}><span style={S.tag}>{lang === 'en' ? enify(c.stage_en || c.stage) : c.stage}</span></td>
                          <td style={{ padding: "6px 8px", color: C.muted, fontSize: 10 }}>{lang === 'en' ? (c.similarity_en || c.similarity) : c.similarity}</td>
                          <td style={{ padding: "6px 8px", fontWeight: 600, color: isPremium ? "#dc2626" : isDiscount ? "#059669" : C.muted }}>{vsText}</td>
                          <td style={{ padding: "6px 8px" }}>
                            {onCompareWith && (
                              <button
                                onClick={handleCompare}
                                disabled={isLoading}
                                title={existing ? (lang === 'en' ? `Compare with ${c.name}` : `直接与 ${c.name} 对比`) : (lang === 'en' ? `Analyze ${c.name} then compare` : `分析 ${c.name} 后跳转对比`)}
                                style={{ fontSize: 10, padding: "2px 8px", borderRadius: 4, border: `1px solid ${btnColor}30`, background: existing ? "#eff6ff" : C.faint, color: btnColor, cursor: isLoading ? "wait" : "pointer", fontWeight: 600, whiteSpace: "nowrap", opacity: isLoading ? 0.6 : 1 }}
                              >
                                {btnLabel}
                              </button>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div style={{ marginTop: 8, fontSize: 10, color: C.muted, borderTop: `1px solid ${C.faint}`, paddingTop: 7 }}>
                  {t('comp.pricing_note')}
                </div>
              </div>
            ) : (
              <div style={{ ...S.card, color: C.muted, textAlign: "center", padding: 24 }}>
                {lang === 'en' ? 'No comparable data — re-analyze to fetch' : '暂无竞品对标数据，重新分析可获取'}
              </div>
            )}

            {/* FDV 分层投资建议（也放在竞品页） */}
            {a?.fdv_tiers?.length > 0 && (
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>{t('irr.fdv_tiers')}</div>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                  <thead>
                    <tr style={{ background: C.faint }}>
                      {[t('irr.tier_range'), t('irr.tier_rating'), t('irr.tier_position'), lang === 'en' ? 'Entry Thesis' : '入场逻辑', t('irr.tier_return')].map(h => (
                        <th key={h} style={{ padding: "5px 8px", textAlign: "left", fontWeight: 600, color: C.muted, fontSize: 10 }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {a.fdv_tiers.map((tier, i) => {
                      const rColor = tier.rating === "S" ? "#059669" : tier.rating === "A" ? "#2563eb" : "#d97706";
                      const rBg   = tier.rating === "S" ? "#ecfdf5" : tier.rating === "A" ? "#eff6ff" : "#fffbeb";
                      return (
                        <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                          <td style={{ padding: "6px 8px", fontWeight: 600 }}>{lang === 'en' ? enify(tier.fdv_range) : tier.fdv_range}</td>
                          <td style={{ padding: "6px 8px" }}>
                            <span style={{ background: rBg, color: rColor, padding: "1px 7px", borderRadius: 4, fontWeight: 700 }}>{tier.rating}</span>
                          </td>
                          <td style={{ padding: "6px 8px", color: C.muted }}>{lang === 'en' ? (tier.position_en || tier.position) : tier.position}</td>
                          <td style={{ padding: "6px 8px", color: C.muted, fontSize: 10 }}>{lang === 'en' ? (tier.thesis_en || tier.thesis) : tier.thesis}</td>
                          <td style={{ padding: "6px 8px", fontWeight: 700, color: rColor }}>{tier.expected_return}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* 退出路径 */}
            {a?.exit_paths?.length > 0 && (
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>{t('irr.exit_paths')}</div>
                {(lang === 'en' && a.exit_paths_en?.length ? a.exit_paths_en : a.exit_paths).map((p, i) => (
                  <div key={i} style={{ fontSize: 11, marginBottom: 6, display: "flex", gap: 8, alignItems: "flex-start", padding: "6px 0", borderBottom: i < (lang === 'en' && a.exit_paths_en?.length ? a.exit_paths_en : a.exit_paths).length - 1 ? `1px solid ${C.faint}` : "none" }}>
                    <span style={{ background: "#eff6ff", color: "#2563eb", padding: "1px 6px", borderRadius: 4, fontWeight: 700, flexShrink: 0, fontSize: 11 }}>{["①","②","③"][i]}</span>
                    <span style={{ color: "#374151", lineHeight: 1.6 }}>{typeof p === "string" ? p : (p.trigger ? `${p.trigger}${p.scenario ? (lang === 'en' ? ": " : "：") + p.scenario : ""}${p.target_multiple ? ` (${p.target_multiple})` : ""}` : JSON.stringify(p))}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === "unlock" && (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {/* 压力标签 */}
            <div style={{ ...S.card, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 4 }}>{t('unlock.overall')}</div>
                {a?.unlock_pressure ? <UnlockBadge level={a.unlock_pressure} /> : <span style={{ color: C.muted }}>—</span>}
              </div>
              <div style={{ textAlign: "right", fontSize: 11, color: C.muted }}>
                <div>{t('unlock.tge_status')}</div>
                <div style={{ fontWeight: 600, color: C.text, marginTop: 2 }}>{lang === 'en' ? (a?.tge_status_en || a?.tge_status || 'Unknown') : (a?.tge_status || '未知')}</div>
              </div>
            </div>

            {/* 时间轴 */}
            {a?.unlock_schedule?.length > 0 ? (
              <div style={S.card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 14 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>{t('unlock.timeline')}</div>
                  {/* Q32 source badge：surf = 实测；ai_inference = AI 推断 */}
                  {a._unlock_schedule_source === 'surf' ? (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: '#059669', background: '#ecfdf5', padding: '2px 7px', borderRadius: 4, border: '1px solid #a7f3d0' }}>
                      <CircleCheck size={11} strokeWidth={2} />{lang === 'en' ? 'Surf real data' : 'Surf 实测数据'}
                    </span>
                  ) : (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, fontWeight: 600, color: '#d97706', background: '#fffbeb', padding: '2px 7px', borderRadius: 4, border: '1px solid #fde68a' }} title={lang === 'en' ? 'No verified tokenomics — generated by AI inference' : '无 Surf 实测数据，由 AI 推断生成'}>
                      <Bot size={11} strokeWidth={2} />{lang === 'en' ? 'AI inference (no real data)' : 'AI 推断（无真实数据）'}
                    </span>
                  )}
                </div>
                <div style={{ position: "relative", paddingLeft: 24 }}>
                  {/* 竖线 */}
                  <div style={{ position: "absolute", left: 7, top: 6, bottom: 6, width: 2, background: C.faint, borderRadius: 1 }} />
                  {a.unlock_schedule.map((item, i) => {
                    const pressureColor = item.pressure === "high" ? "#dc2626" : item.pressure === "medium" ? "#d97706" : "#059669";
                    const pressureBg   = item.pressure === "high" ? "#fef2f2" : item.pressure === "medium" ? "#fffbeb" : "#ecfdf5";
                    return (
                      <div key={i} style={{ position: "relative", marginBottom: i < a.unlock_schedule.length - 1 ? 18 : 0 }}>
                        {/* 圆点 */}
                        <div style={{ position: "absolute", left: -20, top: 3, width: 10, height: 10, borderRadius: "50%", background: pressureColor, border: `2px solid ${C.surface}`, boxShadow: `0 0 0 2px ${pressureColor}33` }} />
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: 11, fontWeight: 600, color: C.text, marginBottom: 2 }}>
                              {lang === 'en' ? `Month ${item.month}` : `第 ${item.month} 个月`}
                              {item.month === 1 && <span style={{ marginLeft: 6, fontSize: 10, background: "#eff6ff", color: "#2563eb", padding: "1px 5px", borderRadius: 3 }}>TGE</span>}
                            </div>
                            <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5 }}>{lang === 'en' ? (item.event_en || item.event) : item.event}</div>
                          </div>
                          <span style={{ fontSize: 10, fontWeight: 600, color: pressureColor, background: pressureBg, padding: "2px 7px", borderRadius: 4, marginLeft: 8, flexShrink: 0 }}>
                            {item.pressure === "high" ? t('unlock.pressure_high') : item.pressure === "medium" ? t('unlock.pressure_medium') : t('unlock.pressure_low')}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <div style={{ ...S.card, color: C.muted, textAlign: "center", padding: 24, fontSize: 12 }}>
                {t('unlock.no_timeline')}
              </div>
            )}

            {/* ── 详细解锁日程（迁移自 data-views/react/UnlockCard）── */}
            <UnlockCard project={project} a={a} lang={lang} S={S} C={C} />

            {/* 操作建议 */}
            {a?.unlock_schedule?.length > 0 && (
              <div style={{ ...S.card, background: a._unlock_schedule_source === 'surf' ? '#f0fdf4' : '#fffbeb', border: a._unlock_schedule_source === 'surf' ? "1px solid #6ee7b7" : "1px solid #fde68a" }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: a._unlock_schedule_source === 'surf' ? "#065f46" : "#92400e", textTransform: "uppercase" }}>{t('unlock.position_guide')}</div>
                  {a._unlock_schedule_source !== 'surf' && (
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10, color: '#92400e', fontWeight: 500 }}>
                      <AlertTriangle size={11} strokeWidth={2} />{lang === 'en' ? 'Based on AI inference — not investment advice' : '基于 AI 推断 · 不构成投资建议'}
                    </span>
                  )}
                </div>
                {(() => {
                  const highPressureMonths = (a.unlock_schedule || []).filter(x => x.pressure === "high").map(x => lang === 'en' ? `Month ${x.month}` : `第${x.month}月`);
                  const firstHigh = (a.unlock_schedule || []).find(x => x.pressure === "high");
                  return (
                    <div style={{ fontSize: 11, color: "#065f46", lineHeight: 1.8 }}>
                      {highPressureMonths.length > 0
                        ? lang === 'en'
                          ? `High-pressure windows: ${highPressureMonths.join(", ")} — reduce position 1-2 months before`
                          : `高抛压窗口：${highPressureMonths.join("、")}，建议提前 1-2 个月减仓`
                        : t('unlock.no_high')}
                      {firstHigh && <div style={{ marginTop: 4 }}>
                        {lang === 'en'
                          ? `Max risk event: Month ${firstHigh.month} — ${firstHigh.event_en || firstHigh.event}`
                          : `最大风险节点：第 ${firstHigh.month} 个月 — ${firstHigh.event}`}
                      </div>}
                      <div style={{ marginTop: 4 }}>{t('unlock.exit_tip')}</div>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        )}

        {tab === "data" && (
          <>
            {/* 基本信息 */}
            <div style={{ ...S.card, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <StatBlock label={t('data.founded')} value={project.details?.establishment_date} />
              <StatBlock label={t('data.token')} value={project.details?.token_symbol ? `$${project.details.token_symbol}` : t('data.not_issued')} />
              <StatBlock label={t('data.total_funding')} value={fmt(project.details?.total_funding)} />
              <StatBlock label={t('table.track')} value={localizeRootDataTags(project.details?.tags || [], lang).slice(0, 2).join(", ")} />
              {project.cgData && <>
                <StatBlock label="FDV" value={fmt(project.cgData.market_data?.fully_diluted_valuation?.usd)} sub="CoinGecko" />
                <StatBlock label={t('data.24h_trades')} value={fmt(project.cgData.market_data?.total_volume?.usd)} sub="CoinGecko" />
              </>}
            </div>

            {/* ── 全市场 24h 交易（CEX/DEX + Binance + top 5 venues, CoinGecko 聚合）放在 DeFiLlama 链上数据上方 ── */}
            <MarketVolumeCard project={project} a={a} lang={lang} S={S} C={C} />

            {/* DeFiLlama 链上数据 */}
            {project.defiData?.protocol && (
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}><Globe size={12} strokeWidth={2} />{t('data.onchain2')}</span>
                  <span style={{ fontSize: 10, color: C.muted, fontWeight: 400 }}>
                    {project.defiData.protocol.category} · {(project.defiData.protocol.chains || []).join(", ")}
                  </span>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 10, marginBottom: project.defiData.tvl ? 10 : 0 }}>
                  {project.defiData.tvl?.current > 0 && (
                    <div style={{ background: C.faint, borderRadius: 6, padding: 10, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>TVL</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#059669" }}>{fmt(project.defiData.tvl.current)}</div>
                      {project.defiData.tvl.weekChange && (
                        <div style={{ fontSize: 10, color: Number(project.defiData.tvl.weekChange) >= 0 ? "#059669" : "#dc2626", marginTop: 2 }}>
                          {lang === 'en' ? '7d' : '7日'} {Number(project.defiData.tvl.weekChange) >= 0 ? "▲" : "▼"}{Math.abs(project.defiData.tvl.weekChange)}%
                        </div>
                      )}
                    </div>
                  )}
                  {project.defiData.volume?.daily > 0 && (
                    <div style={{ background: C.faint, borderRadius: 6, padding: 10, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{t('data.24h_volume')}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#2563eb" }}>{fmt(project.defiData.volume.daily)}</div>
                      {project.defiData.volume.weekly && (
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{t('data.7d')} {fmt(project.defiData.volume.weekly)}</div>
                      )}
                    </div>
                  )}
                  {project.defiData.fees?.daily > 0 && (
                    <div style={{ background: C.faint, borderRadius: 6, padding: 10, textAlign: "center" }}>
                      <div style={{ fontSize: 10, color: C.muted, marginBottom: 4 }}>{t('data.24h_fees')}</div>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#8b5cf6" }}>{fmt(project.defiData.fees.daily)}</div>
                      {project.defiData.fees.weekly && (
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{t('data.7d')} {fmt(project.defiData.fees.weekly)}</div>
                      )}
                    </div>
                  )}
                </div>
                {!project.defiData.tvl?.current && !project.defiData.volume?.daily && (
                  <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? `Protocol "${project.defiData.protocol.name}" matched — no TVL/volume data yet` : `已匹配协议「${project.defiData.protocol.name}」，暂无 TVL/交易量数据`}</div>
                )}
              </div>
            )}
            {!project.defiData?.protocol && (
              <div style={{ ...S.card, fontSize: 11, color: C.muted, display: "flex", alignItems: "center", gap: 8 }}>
                <Globe size={14} strokeWidth={2} />
                <span>{lang === 'en' ? 'No matching protocol on DeFiLlama (normal for early-stage projects)' : '未在 DeFiLlama 找到匹配协议（早期未上线项目属正常）'}</span>
              </div>
            )}

            {/* Dune 30日成交量图表卡片（DEX 或 HL 原生合约） */}
            {project.duneAnalytics?.days?.length > 0 && (() => {
              const da = project.duneAnalytics;
              const isHL = da.source === 'hyperliquid_market_data';
              const vol30 = da.total_volume || 0;
              const trades30 = da.total_trades || 0;
              const duneDays = da.days || [];
              const avgTraders = duneDays.length > 0
                ? Math.round(duneDays.reduce((s, d) => s + (d.unique_traders || 0), 0) / duneDays.length)
                : 0;
              // 趋势：前15天 vs 后15天
              const half = Math.floor(duneDays.length / 2);
              const prevHalf = duneDays.slice(0, half).reduce((s, d) => s + (d.volume_usd || 0), 0);
              const recentHalf = duneDays.slice(half).reduce((s, d) => s + (d.volume_usd || 0), 0);
              const trend = prevHalf > 0 ? ((recentHalf - prevHalf) / prevHalf * 100).toFixed(0) : null;
              const trendColor = trend > 0 ? "#059669" : trend < 0 ? "#dc2626" : C.muted;
              const fmtVol = (v) =>
                v >= 1e9 ? `$${(v/1e9).toFixed(1)}B`
                : v >= 1e6 ? `$${(v/1e6).toFixed(1)}M`
                : v >= 1e3 ? `$${(v/1e3).toFixed(0)}K`
                : `$${v.toFixed(0)}`;
              return (
                <div style={S.card}>
                  {/* 标题行 */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 6 }}>
                        <BarChart3 size={11} strokeWidth={2} />{isHL
                          ? (lang === 'en' ? '30d Perp Notional Vol · Dune' : '30日合约名义成交量 · Dune')
                          : (lang === 'en' ? '30d DEX Volume · Dune' : '30日 DEX 交易量 · Dune')}
                        <span style={{ color: "#818cf8", fontWeight: 400, fontSize: 10 }}>{da.chain}</span>
                      </div>
                      <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                        {isHL
                          ? (lang === 'en' ? `avg $${fmtVol(vol30 / Math.max(1, duneDays.length))}/day · perp market data` : `日均 $${fmtVol(vol30 / Math.max(1, duneDays.length))} · 永续合约市场数据`)
                          : (lang === 'en'
                            ? `${trades30.toLocaleString()} trades · avg ${avgTraders.toLocaleString()} traders/day`
                            : `${trades30.toLocaleString()} 笔交易 · 日均 ${avgTraders.toLocaleString()} 交易者`)}
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 15, fontWeight: 800, color: "#818cf8" }}>{fmtVol(vol30)}</div>
                      {trend !== null && (
                        <div style={{ fontSize: 10, color: trendColor, marginTop: 2 }}>
                          {trend > 0 ? "▲ +" : "▼ "}{trend}% {lang === 'en' ? '(2nd half)' : '（后半程）'}
                        </div>
                      )}
                    </div>
                  </div>
                  {/* 图表：量柱（HL 原生无交易者折线） */}
                  <DuneVolumeChart days={duneDays} />
                  {/* 图例 */}
                  <div style={{ display: "flex", gap: 12, marginTop: 4, fontSize: 9, color: C.muted, justifyContent: "flex-end" }}>
                    <span><span style={{ display: "inline-block", width: 8, height: 8, background: "#6366f1", borderRadius: 1, marginRight: 3 }} />{lang === 'en' ? (isHL ? 'Notional Vol' : 'Volume') : (isHL ? '名义成交量' : '成交量')}</span>
                    {!isHL && <span><span style={{ display: "inline-block", width: 16, height: 1.5, background: "#22d3ee", borderRadius: 1, marginRight: 3, verticalAlign: "middle" }} />{lang === 'en' ? 'Unique Traders' : '独立交易者'}</span>}
                  </div>
                </div>
              );
            })()}

            <ChainSignalsPanel project={project} />

            {project.details?.investors?.length > 0 && (() => {
              const leads = project.details.investors.filter(inv => inv.lead_investor);
              const follows = project.details.investors.filter(inv => !inv.lead_investor);
              return (
                <div style={S.card}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>{lang === 'en' ? 'Investors' : '投资方'} ({project.details.investors.length})</div>
                  {leads.length > 0 && (
                    <div style={{ marginBottom: follows.length > 0 ? 8 : 0 }}>
                      <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: "uppercase" }}>{lang === 'en' ? `Lead · ${leads.length}` : `领投方 · ${leads.length}`}</div>
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {leads.slice(0, 20).map(inv => (
                          <span key={inv.name} style={{ ...S.tag, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", fontWeight: 600 }}>{inv.name}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {follows.length > 0 && (
                    <div>
                      {leads.length > 0 && <div style={{ fontSize: 9, fontWeight: 700, color: C.muted, marginBottom: 4, textTransform: "uppercase" }}>{lang === 'en' ? `Follow · ${follows.length}` : `跟投方 · ${follows.length}`}</div>}
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {follows.slice(0, 20).map(inv => (
                          <span key={inv.name} style={S.tag}>{inv.name}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            {project.fundingRounds?.length > 0 && (
              <div style={S.card}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>{lang === 'en' ? 'Funding History' : '融资历史'}</div>
                {project.fundingRounds.map((r, i) => (
                  <div key={i} style={{ padding: "5px 0", borderBottom: i < project.fundingRounds.length - 1 ? `1px solid ${C.faint}` : "none" }}>
                    <div style={{ display: "flex", justifyContent: "space-between" }}>
                      <div>
                        <span style={{ fontWeight: 600, fontSize: 12 }}>{r.rounds}</span>
                        <span style={{ fontSize: 10, color: C.muted, marginLeft: 6 }}>{r.published_time}</span>
                      </div>
                      <div style={{ textAlign: "right", fontSize: 11 }}>
                        <span style={{ fontWeight: 600 }}>{r.amount ? fmt(r.amount) : (lang === 'en' ? 'Undisclosed' : '未披露')}</span>
                        {r.valuation > 0 && <div style={{ fontSize: 10, color: C.muted }}>{lang === 'en' ? 'Val.' : '估值'} {fmt(r.valuation)}</div>}
                      </div>
                    </div>
                    {r.invests?.length > 0 && (
                      <div style={{ display: "flex", flexWrap: "wrap", gap: 3, marginTop: 4 }}>
                        {r.invests.slice(0, 12).map((inv, j) => (
                          <span key={j} style={inv.lead_investor
                            ? { ...S.tag, fontSize: 9, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", fontWeight: 600 }
                            : { ...S.tag, fontSize: 9 }}>{inv.name}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* ── 估值 & 核心倍数（迁移自 data-views/react/ValuationCard） ── */}
            <ValuationCard project={project} a={a} lang={lang} S={S} C={C} />

            {/* ── 协议基本面 + 期货信号（迁移自 data-views/react/ProtocolFundamentalsCard）── */}
            <ProtocolFundamentalsCard project={project} a={a} lang={lang} S={S} C={C} />

            {/* ── 前 10 持有者集中度（迁移自 data-views/react/HoldersCard）── */}
            <HoldersCard project={project} a={a} lang={lang} S={S} C={C} />

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {project.details?.rootdataurl && <a href={project.details.rootdataurl} target="_blank" rel="noopener noreferrer" style={{ ...S.btn(), textDecoration: "none" }}>RootData ↗</a>}
              {project.details?.social_media?.website && <a href={project.details.social_media.website} target="_blank" rel="noopener noreferrer" style={{ ...S.btn(), textDecoration: "none" }}>{lang === 'en' ? 'Website' : '官网'} ↗</a>}
              {project.details?.social_media?.twitter && <a href={project.details.social_media.twitter} target="_blank" rel="noopener noreferrer" style={{ ...S.btn(), textDecoration: "none" }}>Twitter ↗</a>}
            </div>
          </>
        )}

        {/* ── 笔记 Tab ── */}
        {/* ── 情报 Tab ── */}
        {tab === "intel" && (
          <>
            <WebIntelPanel project={project} />
            <SurfNewsPanel project={project} />
          </>
        )}

        {tab === "history" && (
          <HistoryFeedbackPanel project={project} history={history} />
        )}

        {tab === "research" && (
          <ResearchNotesPanel project={project} />
        )}

        {tab === "notes" && (
          <NotesDiary notes={notes} onAdd={onAddNote} projectName={project.name} history={history} />
        )}

        {tab === "bp_verify" && (
          <BPVerifyPanel projectName={project.name} />
        )}

        {/* 数据源诊断 — 所有 tab 都可点开，靠 _debug.dataSourceHealth 渲染 */}
        {project._debug?.dataSourceHealth && (
          <DataSourceHealthPanel health={project._debug.dataSourceHealth} S={S} C={C} lang={lang} />
        )}

      </div>
      {showShareModal && <ShareModal project={project} onClose={() => setShowShareModal(false)} surfMindshare={surfMindshare} />}
      {showBadgeModal && <BadgeModal project={project} onClose={() => setShowBadgeModal(false)} />}
    </aside>
  );
}

// ══════════════════════════════════════════════════════
//  PIPELINE PAGE
// ══════════════════════════════════════════════════════
function PipelinePage({ onImport, onBatchAnalyze, onFfMapUpdate = () => {} }) {
  const t = useT();
  const { lang } = useLang();
  const isMobile = useIsMobile();
  // 四个子 tab 各自可深链/可分享：#pipeline/earlybird、#pipeline/kol、#pipeline/hot、
  // #pipeline/deals。#earlybird/{id}（具体某个 Early Bird 项目的分享链接，
  // EarlyBirdTab.jsx 自己维护）落到 earlybird 子 tab，不需要额外解析项目 id——
  // EarlyBirdTab 挂载时自己会再读一遍 hash 找到具体项目。
  const SUBTABS = ["earlybird", "kol", "hot", "deals"];
  const [activeTab, setActiveTab] = useState(() => {
    if (parseEarlyBirdShareId(window.location.hash)) return "earlybird";
    const parts = window.location.hash.slice(1).split("/");
    return parts[0] === "pipeline" && SUBTABS.includes(parts[1]) ? parts[1] : "earlybird";
  }); // "earlybird" | "kol" | "hot" | "deals"
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [cachedScores, setCachedScores] = useState({}); // { projectName: { composite, rating } }
  const [heatMap, setHeatMap] = useState({}); // { projectName: 分析次数 } — 见 getAnalysisHeatMap
  const [pipelineDocMap, setPipelineDocMap] = useState({}); // { projectName: { querying, firstDoc, probed } }
  // 筛选状态
  const [rFilter, setRFilter] = useState("all");
  const [investorSearch, setInvestorSearch] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [trackFilter, setTrackFilter] = useState("all"); // 赛道筛选
  const [pipelineSort, setPipelineSort] = useState("date"); // "date" | "score"
  const [pipelineMinScore, setPipelineMinScore] = useState(""); // "" | "60" | "70" | "80"

  const pull = async () => {
    setLoading(true); setStatus(lang === 'en' ? "Fetching pipeline (cached)..." : "读取融资列表（缓存）...");
    try {
      // 走服务端列表缓存端点，避免每次冷打 RootData get_fac（warm-trending 每日预热）
      const res = await fetch('/api/pipeline?minAmount=1000000');
      const json = await res.json();
      if (!res.ok) throw new Error(json.error || 'pipeline fetch failed');
      const data = json.data;
      const items = data?.items || [];
      const cached = json.cached;
      setDeals(items);
      setStatus(
        lang === 'en'
          ? `${items.length} recent deals${cached ? ' (cached)' : ''}`
          : `获取 ${items.length} 条近期融资${cached ? '（缓存）' : ''}`
      );
      // 批量检查哪些项目已有预评分缓存，然后对无缓存项目后台探测 firstDoc
      if (items.length > 0) {
        const cachedMap = await fetchCachedScores(items.map(d => d.name));
        probeUncachedDocs(items, cachedMap || {});
        getAnalysisHeatMap(items.map(d => d.name)).then(setHeatMap);
      }
    } catch (e) { setStatus("⚠ " + e.message); }
    finally { setLoading(false); }
  };

  const fetchCachedScores = async (names) => {
    try {
      const { supabase } = await import('./lib/supabase');
      const now = new Date().toISOString();
      const { data } = await supabase
        .from('project_cache')
        .select('project_name, analysis, structured_labels, raw_data, scored_at')
        .in('project_name', names)
        .gt('expires_at', now);
      if (!data) return {};
      // 用共享的 cacheIsDisplayable 判定 — 与 getCached 同一套标准。
      // 只看"能否渲染"（composite + scores 实存、非 _warmed / _lightweight）；
      // 缺 complianceRisk / news_signals 等新字段时 getCached 会返回数据 +
      // _missingFeatures 让 UI 标银 banner，不再触发全量重跑。
      const map = {};
      data.forEach(row => {
        if (!cacheIsDisplayable(row)) return;
        const composite = row.analysis?.composite;
        const track = row.structured_labels?.track || row.analysis?.track || null;
        const trackEn = row.analysis?.track_en || null;
        const tl = row.raw_data?.timeline || [];
        const firstDoc = tl.find(t => t.key === 'firstDoc');
        map[row.project_name] = {
          composite,
          isCron: !!row.analysis?._cron,
          track,
          trackEn,
          summary_zh: row.analysis?.summary || null,
          summary_en: row.analysis?.summary_en || null,
          scoredAt: row.scored_at || null,
          firstDoc: firstDoc ? {
            date: firstDoc.date,
            source: firstDoc.source,
            sourceType: firstDoc.sourceType,
            url: firstDoc.url,
          } : null,
        };
      });
      setCachedScores(map);
      return map;
    } catch { return {}; }
  };

  // localStorage 持久化：探测结果缓存 24h，避免每次打开 Pipeline 重复探测
  // v2 起统一为 src/data-views/firstDocCache.js 全局缓存（跨页面共享）
  const loadProbeCache = () => {
    const cache = loadFirstDocCache();
    const out = {};
    Object.entries(cache).forEach(([name, entry]) => {
      out[name] = { querying: false, firstDoc: entry.firstDoc ?? null, probed: true };
    });
    return out;
  };

  const saveProbeResult = (name, firstDoc) => {
    saveFirstDocCacheBatch({ [name]: firstDoc });
  };

  // 后台探测：对 Pipeline 中没有缓存的项目做 Wayback firstDoc 查询
  // 优先从 localStorage 读取（24h 内有效），跳过已探测的项目
  const probeUncachedDocs = async (allDeals, cachedMap) => {
    const lsCache = loadProbeCache();
    // 已在 localStorage 的直接合并进 pipelineDocMap，无需重新探测
    if (Object.keys(lsCache).length > 0) {
      setPipelineDocMap(prev => ({ ...lsCache, ...prev }));
    }
    // 探测条件：项目从未在任何缓存中出现（前端不主动重探已知 N/A，避免压垮 archive.org）
    //   - 完全未在 project_cache（warm-pipeline cron 未覆盖到的新项目）
    //   - 完全未在 localStorage（之前未浏览过的项目）
    // 已存在的 N/A 由 project_cache 的 3 天 TTL 触发 cron 自动重探
    const toProbe = allDeals.filter(d =>
      d.project_id
      && !lsCache[d.name]
      && !cachedMap[d.name]
    ).slice(0, 30);
    if (toProbe.length === 0) return;

    // 标记待探测项目为 querying
    const docMap = { ...lsCache };
    toProbe.forEach(d => { docMap[d.name] = { querying: true, firstDoc: null, probed: false }; });
    setPipelineDocMap({ ...docMap });

    // 使用服务端 /api/firstdoc-batch（10个/批，更多 URL 模式，无 CORS 问题）
    for (let i = 0; i < toProbe.length; i += 10) {
      const batch = toProbe.slice(i, i + 10);
      try {
        const res = await fetch(`${RD_BASE}/api/firstdoc-batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ projects: batch.map(d => ({ project_id: d.project_id, project_name: d.name })) }),
        });
        if (res.ok) {
          const { results = {} } = await res.json();
          batch.forEach(d => {
            const raw = results[d.name];
            // 新 error 形状：{ error } → 不缓存，标记 probed=false 让下次重试
            if (raw && typeof raw === 'object' && raw.error) {
              docMap[d.name] = { querying: false, firstDoc: null, probed: false };
              return;
            }
            // 有效 hit
            if (raw && raw.date) {
              const firstDoc = { date: raw.date, source: raw.source, sourceType: raw.sourceType, url: raw.url };
              docMap[d.name] = { querying: false, firstDoc, probed: true };
              saveProbeResult(d.name, firstDoc);
              return;
            }
            // 真负（API 正常但没归档）
            docMap[d.name] = { querying: false, firstDoc: null, probed: true };
            saveProbeResult(d.name, null);
          });
        } else {
          // ok=false：不缓存，下次重试
          batch.forEach(d => { docMap[d.name] = { querying: false, firstDoc: null, probed: false }; });
        }
      } catch {
        // 网络失败：不缓存，下次重试
        batch.forEach(d => { docMap[d.name] = { querying: false, firstDoc: null, probed: false }; });
      }
      setPipelineDocMap({ ...docMap });
      if (i + 10 < toProbe.length) await new Promise(r => setTimeout(r, 600));
    }
  };

  useEffect(() => { pull(); }, []);

  // 切换子 tab 时把 hash 同步成 #pipeline/{tab}，让四个子 tab 各自可分享/可深链。
  // 具体某个 Early Bird 项目的分享链接（#earlybird/{id}）打开中时不要覆盖——那个
  // 由 EarlyBirdTab.jsx 自己管，这里跟 App.jsx 顶层的 hash-sync effect 用的是同一条准则。
  useEffect(() => {
    if (parseEarlyBirdShareId(window.location.hash)) return;
    window.location.hash = `pipeline/${activeTab}`;
  }, [activeTab]);

  const ROUNDS = ["all", "Pre-Seed", "Seed", "Series A", "Series B", "Strategic"];

  // 从 deals 中提取所有出现过的领投机构，用于下拉提示
  const allLeadInvestors = [...new Set(
    deals.flatMap(d => (d.invests || []).filter(i => i.lead_investor).map(i => i.name))
  )].sort();

  // 从已分析项目的 cachedScores 中提取赛道（get_fac 不返回 tags，用 Supabase structured_labels 补充）
  const allTracks = [...new Set(
    Object.values(cachedScores).map(s => s.track).filter(Boolean)
  )].sort();
  // track 是 AI 自由生成的文本，没有固定分类表可查——直接从已有数据里找同一个 track
  // 对应的 trackEn，筛选下拉的显示用英文，实际筛选 value 仍传中文原值（与 cachedScores 一致）
  const trackEnByTrack = {};
  Object.values(cachedScores).forEach(s => {
    if (s.track && s.trackEn && !trackEnByTrack[s.track]) trackEnByTrack[s.track] = s.trackEn;
  });

  // 复合筛选逻辑
  const pipelineFiltered = deals.filter(d => {
    // 轮次
    if (rFilter !== "all" && d.rounds !== rFilter) return false;
    // 赛道（从 cachedScores 取，因为 get_fac 不返回 tags）
    if (trackFilter !== "all" && cachedScores[d.name]?.track !== trackFilter) return false;
    // 领投机构（模糊匹配）
    if (investorSearch.trim()) {
      const kw = investorSearch.trim().toLowerCase();
      const leadNames = (d.invests || []).filter(i => i.lead_investor).map(i => i.name.toLowerCase());
      if (!leadNames.some(n => n.includes(kw))) return false;
    }
    // 最低融资金额（输入单位：万美元，内部单位：美元）
    if (minAmount && d.amount < Number(minAmount) * 10000) return false;
    // 日期范围
    if (dateFrom && d.published_time && d.published_time < dateFrom) return false;
    if (dateTo   && d.published_time && d.published_time > dateTo)   return false;
    // 最低 AI 评分
    if (pipelineMinScore !== "") {
      const cs = cachedScores[d.name]?.composite;
      if (!cs || cs < Number(pipelineMinScore)) return false;
    }
    return true;
  });
  const filtered = [...pipelineFiltered].sort((a, b) => {
    if (pipelineSort === "score") {
      const sa = cachedScores[a.name]?.composite ?? -1;
      const sb = cachedScores[b.name]?.composite ?? -1;
      return sb - sa;
    }
    // 默认：日期降序（原 API 顺序）
    return 0;
  });

  const toggleSelect = (name) => {
    setSelected(prev => { const next = new Set(prev); next.has(name) ? next.delete(name) : next.add(name); return next; });
  };
  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map(d => d.name)));
  };

  const activeFilterCount = [
    rFilter !== "all",
    trackFilter !== "all",
    !!investorSearch.trim(),
    !!minAmount,
    !!dateFrom || !!dateTo,
    pipelineMinScore !== "",
  ].filter(Boolean).length;

  // 四个子 tab 拉平成一排（原来"近期融资/早期雷达"两层嵌套，"早期雷达"下面才是
  // RootData热榜/KOL雷达/Early Bird 三个子 tab）——顺序按信号确定性从低到高：
  // Early Bird（最原始，无人验证）→ KOL雷达（社交软信号）→ 零融资热榜（站内分析次数
  // 从众信号）→ 近期融资（最硬，钱已到账）。2026-07-10 产品讨论定的顺序和命名。
  const TABS = [
    { key: "earlybird", label: lang === 'en' ? 'Early Bird' : 'Early Bird 发现', Icon: Search },
    { key: "kol",        label: lang === 'en' ? 'KOL Radar' : 'KOL雷达', Icon: Radio },
    { key: "hot",        label: lang === 'en' ? 'Zero-funding Hot' : '零融资热榜', Icon: Flame },
    { key: "deals",      label: lang === 'en' ? 'Recent Deals' : '近期融资', Icon: GitBranch },
  ];

  return (
    <div>
      {/* Page header + tab switcher */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TABS.map(({ key, label, Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)}
              style={{ ...S.btn(activeTab === key ? "primary" : "default"), fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Icon size={13} strokeWidth={2} /> {label}
            </button>
          ))}
        </div>
        {activeTab === "deals" && (
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setShowFilters(v => !v)}
              style={{ ...S.btn(showFilters ? "primary" : "default"), position: "relative", display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Filter size={13} strokeWidth={2} /> {lang === 'en' ? 'Filter' : '筛选'}{activeFilterCount > 0 ? ` (${activeFilterCount})` : ""}
            </button>
            <button onClick={pull} disabled={loading} style={{ ...S.btn(), display: "inline-flex", alignItems: "center", gap: 6 }}>
              <RefreshCw size={13} strokeWidth={2} /> {loading ? (lang === 'en' ? 'Updating...' : '更新中...') : (lang === 'en' ? 'Refresh' : '刷新')}
            </button>
            {selected.size > 0 && (
              <button onClick={() => { onBatchAnalyze([...selected]); setSelected(new Set()); }}
                style={{ ...S.btn("primary"), display: "inline-flex", alignItems: "center", gap: 6 }}>
                <Rocket size={13} strokeWidth={2} /> {lang === 'en' ? `Batch Analyze (${selected.size})` : `批量分析 (${selected.size})`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Early Bird / KOL雷达 / 零融资热榜 */}
      {activeTab !== "deals" && <EarlyRadarPanel activeSubTab={activeTab} onImport={onImport} onFfMapUpdate={onFfMapUpdate} />}

      {/* Deal flow tab */}
      {activeTab === "deals" && <div>
      {/* Sub-header: count（首次加载时不显示 0/0，避免误以为没有数据） */}
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted, marginBottom: 10 }}>
        {loading && deals.length === 0 ? (
          <>
            <span style={{ width: 11, height: 11, borderRadius: "50%", border: `2px solid ${C.border}`, borderTopColor: C.muted, display: "inline-block", animation: "plSpin .8s linear infinite" }} />
            {lang === 'en' ? 'Loading fundraising data…' : '正在加载融资数据…'}
          </>
        ) : (
          lang === 'en' ? `Showing ${filtered.length} / ${deals.length} · Last 30 days` : `显示 ${filtered.length} / ${deals.length} 条 · 近30天新融资`
        )}
      </div>
      <style>{`@keyframes plSpin { to { transform: rotate(360deg); } }`}</style>

      {/* 筛选面板 */}
      {showFilters && (
        <div style={{ ...S.card, marginBottom: 12, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12 }}>
          {/* 融资轮次 */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5, textTransform: "uppercase" }}>{lang === 'en' ? 'Round' : '融资轮次'}</div>
            <select value={rFilter} onChange={e => setRFilter(e.target.value)} style={S.input}>
              {ROUNDS.map(r => <option key={r} value={r}>{r === "all" ? (lang === 'en' ? 'All' : '全部') : r}</option>)}
            </select>
          </div>

          {/* 赛道筛选 */}
          {allTracks.length > 0 && (
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5, textTransform: "uppercase" }}>{lang === 'en' ? 'Track' : '赛道'}</div>
              <select value={trackFilter} onChange={e => setTrackFilter(e.target.value)} style={S.input}>
                <option value="all">{lang === 'en' ? 'All Tracks' : '全部赛道'}</option>
                {allTracks.map(t => <option key={t} value={t}>{lang === 'en' ? (trackEnByTrack[t] || t) : t}</option>)}
              </select>
            </div>
          )}

          {/* 领投机构 */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5, textTransform: "uppercase" }}>{lang === 'en' ? 'Lead Investor' : '领投机构'}</div>
            <input
              list="investor-list"
              value={investorSearch}
              onChange={e => setInvestorSearch(e.target.value)}
              placeholder={lang === 'en' ? 'e.g. a16z, Binance Labs...' : '如：a16z, Binance Labs...'}
              style={S.input}
            />
            <datalist id="investor-list">
              {allLeadInvestors.map(n => <option key={n} value={n} />)}
            </datalist>
          </div>

          {/* 最低融资金额 */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5, textTransform: "uppercase" }}>{lang === 'en' ? 'Min Raise (USD)' : '最低融资金额（万美元）'}</div>
            <div style={{ display: "flex", gap: 4 }}>
              {["", "100", "500", "1000", "5000"].map(v => (
                <button key={v} onClick={() => setMinAmount(v)}
                  style={{ ...S.btn(minAmount === v ? "primary" : "default"), padding: "4px 8px", fontSize: 11, flex: 1 }}>
                  {v === "" ? (lang === 'en' ? 'Any' : '不限') : `$${v === "100" ? "1M+" : v === "500" ? "5M+" : v === "1000" ? "10M+" : "50M+"}`}
                </button>
              ))}
            </div>
          </div>

          {/* 日期范围 */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5, textTransform: "uppercase" }}>{lang === 'en' ? 'Date Range' : '日期范围'}</div>
            <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                style={{ ...S.input, flex: 1, fontSize: 11 }} />
              <span style={{ color: C.muted, fontSize: 11 }}>—</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                style={{ ...S.input, flex: 1, fontSize: 11 }} />
            </div>
          </div>

          {/* 最低 AI 评分 */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5, textTransform: "uppercase" }}>{lang === 'en' ? 'Min AI Score' : '最低AI评分'}</div>
            <div style={{ display: "flex", gap: 4 }}>
              {["", "60", "70", "80"].map(v => (
                <button key={v} onClick={() => setPipelineMinScore(v)}
                  style={{ ...S.btn(pipelineMinScore === v ? "primary" : "default"), padding: "4px 8px", fontSize: 11, flex: 1 }}>
                  {v === "" ? (lang === 'en' ? 'All' : '不限') : `${v}+`}
                </button>
              ))}
            </div>
          </div>

          {/* 排序 */}
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5, textTransform: "uppercase" }}>{lang === 'en' ? 'Sort By' : '排序'}</div>
            <div style={{ display: "flex", gap: 4 }}>
              {[
                { val: "date",  label: lang === 'en' ? 'Date ↓' : '日期↓' },
                { val: "score", label: lang === 'en' ? 'Score ↓' : '评分↓' },
              ].map(({ val, label }) => (
                <button key={val} onClick={() => setPipelineSort(val)}
                  style={{ ...S.btn(pipelineSort === val ? "primary" : "default"), padding: "4px 8px", fontSize: 11, flex: 1 }}>
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* 重置按钮 */}
          {activeFilterCount > 0 && (
            <div style={{ gridColumn: "1 / -1", textAlign: "right" }}>
              <button onClick={() => { setRFilter("all"); setTrackFilter("all"); setInvestorSearch(""); setMinAmount(""); setDateFrom(""); setDateTo(""); setPipelineMinScore(""); }}
                style={{ ...S.btn("danger"), fontSize: 11, padding: "3px 12px", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <X size={12} strokeWidth={2} /> {lang === 'en' ? 'Clear All Filters' : '清除所有筛选'}
              </button>
            </div>
          )}
        </div>
      )}

      {status && <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>{status}</div>}

      {/* 表格（移动端换成卡片列表，避免宽表格横向滚动） */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.slice(0, 50).map((d, i) => {
            const cs = cachedScores[d.name] || {};
            const aiZh = cs.summary_zh ?? (typeof cs.summary === 'string' && /[一-龥]/.test(cs.summary) ? cs.summary : null);
            const aiEn = cs.summary_en ?? (typeof cs.summary === 'string' && !/[一-龥]/.test(cs.summary) ? cs.summary : null);
            const aiSummary = lang === 'en' ? (aiEn || aiZh) : (aiZh || aiEn);
            // 双向语言过滤：见 TrendingPage 同款注释——反过来"中文界面显示英文 one_liner"
            // 原来完全没挡。
            const oneLinerIsZh = typeof d.one_liner === 'string' && /[一-龥]/.test(d.one_liner);
            const oneLinerLangMismatch = typeof d.one_liner === 'string' && (lang === 'en' ? oneLinerIsZh : !oneLinerIsZh);
            const brief = aiSummary || (oneLinerLangMismatch ? null : d.one_liner);
            const briefUntranslated = lang === 'en' && !!aiSummary && !aiEn;
            const leadInvestors = (d.invests || []).filter(inv => inv.lead_investor).slice(0, 2);
            const followInvestors = (d.invests || []).filter(inv => !inv.lead_investor).slice(0, 2);
            const fresh = freshnessLabel(cs.scoredAt, lang);
            const heat = heatMap[d.name];
            return (
              <MobileListCard
                key={i}
                header={
                  <div style={{ fontSize: 15, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{d.name}</div>
                }
                badge={cs.composite != null && (() => {
                  const r = rating(cs.composite);
                  return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, background: r.bg, border: `1px solid ${r.border}`, borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>
                      <span style={{ color: r.fg }}>{cs.composite}</span>
                      <span style={{ color: r.fg, fontSize: 10 }}>{r.label}</span>
                    </span>
                  );
                })()}
                tags={
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 12, background: C.faint, color: C.muted, padding: "2px 8px", borderRadius: 6 }}>{d.rounds || "—"}</span>
                    {(d.tags || []).slice(0, 2).map(tg => (
                      <span key={tg} style={{ fontSize: 12, background: C.faint, color: C.muted, padding: "2px 8px", borderRadius: 6 }}>{tg}</span>
                    ))}
                  </div>
                }
                summary={
                  <>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 8 }}>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'Raise' : '融资金额'}</div>
                        <div style={{ fontSize: 13, color: C.text, marginTop: 2 }}>{d.amount ? fmt(d.amount) : (lang === 'en' ? 'Undisclosed' : '未披露')}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'Valuation' : '估值'}</div>
                        <div style={{ fontSize: 13, color: C.text, marginTop: 2 }}>
                          {d.valuation > 0 ? fmt(d.valuation) : d.amount > 0 ? <span style={{ fontStyle: "italic" }}>~{fmt(d.amount / 0.25)} est.</span> : (lang === 'en' ? 'Undisclosed' : '未披露')}
                        </div>
                      </div>
                    </div>
                    {brief && (
                      <div style={{ marginTop: 8 }}>
                        <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                          {aiSummary && <span style={{ color: "#a1a1aa", fontWeight: 700, marginRight: 4, fontSize: 11 }}>AI</span>}
                          {brief}
                        </div>
                        {briefUntranslated && t('misc.ai_content_note') && (
                          <div style={{ color: "#a1a1aa", fontSize: 10, marginTop: 1 }}>{t('misc.ai_content_note')}</div>
                        )}
                      </div>
                    )}
                  </>
                }
                footerLeft={
                  <div style={{ fontSize: 12, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {leadInvestors.length > 0 ? (
                      <>
                        <span style={{ color: "#92400e", fontWeight: 600 }}>{lang === 'en' ? 'Lead' : '领投'} {leadInvestors.map(inv => inv.name).join(', ')}</span>
                        {followInvestors.length > 0 && <span> · {lang === 'en' ? 'Follow' : '跟投'} {followInvestors.map(inv => inv.name).join(', ')}</span>}
                      </>
                    ) : (d.published_time || '—')}
                    {(fresh || heat >= 3) && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, fontSize: 11, color: fresh?.stale ? "#c2410c" : "#a1a1aa" }}>
                        {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={11} strokeWidth={2} />{fresh.text}</span>}
                        {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={11} strokeWidth={2} />{lang === 'en' ? `${heat} analyses` : `分析 ${heat} 次`}</span>}
                      </div>
                    )}
                  </div>
                }
                footerRight={
                  <button onClick={() => onImport(d.name)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 4 }}>
                    {cs.composite != null ? (lang === 'en' ? 'View' : '查看') : (lang === 'en' ? 'Analyze' : '分析')}
                    <ChevronRight size={14} strokeWidth={2} />
                  </button>
                }
              />
            );
          })}
          {loading && filtered.length === 0 && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ height: 13, width: 140, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.25 }} />
            </div>
          ))}
          {filtered.length === 0 && !loading && (
            <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              {lang === 'en' ? 'No matching deals' : '暂无符合条件的数据'}
            </div>
          )}
        </div>
      ) : (
      <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.faint, fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
              <th style={{ padding: "8px 12px" }}>
                <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} />
              </th>
              {(lang === 'en' ? ["Project", "Round", "Track", "Raise", "Valuation", "Lead Investor", "Date", "First Docs", "Action"] : ["项目", "轮次", "赛道", "融资金额", "估值", "领投机构", "日期", "首次文档", "操作"]).map(h => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left" }} title={h.includes('Docs') || h.includes('文档') ? (lang === 'en' ? 'First whitepaper/docs release date' : '白皮书/技术文档首次公开日期') : undefined}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 50).map((d, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${C.faint}`, background: selected.has(d.name) ? C.faint : "transparent" }}
                onMouseEnter={e => { if (!selected.has(d.name)) e.currentTarget.style.background = C.faint; }}
                onMouseLeave={e => { e.currentTarget.style.background = selected.has(d.name) ? C.faint : "transparent"; }}>
                <td style={{ padding: "8px 12px" }}>
                  <input type="checkbox" checked={selected.has(d.name)} onChange={() => toggleSelect(d.name)} />
                </td>
                <td style={{ padding: "8px 12px", maxWidth: 320 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {d.logo && <img src={proxyLogo(d.logo)} alt="" style={{ width: 20, height: 20, borderRadius: 4 }} onError={e => e.target.style.display = "none"} />}
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{d.name}</span>
                    {cachedScores[d.name] && (() => {
                      const { composite, isCron } = cachedScores[d.name];
                      const r = rating(composite);
                      return (
                        <span title={isCron ? (lang === 'en' ? 'Pre-scored overnight' : '隔夜已预评分') : (lang === 'en' ? 'Analysis cached' : '已缓存分析')}
                          style={{ display: "flex", alignItems: "center", gap: 3, background: r.bg, border: `1px solid ${r.border}`, borderRadius: 4, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>
                          <span style={{ color: r.fg, fontFamily: "monospace", fontVariantNumeric: "tabular-nums" }}>{composite}</span>
                          <span style={{ color: r.fg, fontSize: 9 }}>{r.label}</span>
                          {isCron && <span style={{ width: 4, height: 4, borderRadius: "50%", background: r.fg, opacity: 0.6 }} />}
                        </span>
                      );
                    })()}
                  </div>
                  {/* AI 简介 / RootData one_liner fallback */}
                  {(() => {
                    const cs = cachedScores[d.name] || {};
                    const aiZh = cs.summary_zh ?? (typeof cs.summary === 'string' && /[一-龥]/.test(cs.summary) ? cs.summary : null);
                    const aiEn = cs.summary_en ?? (typeof cs.summary === 'string' && !/[一-龥]/.test(cs.summary) ? cs.summary : null);
                    const aiSummary = lang === 'en' ? (aiEn || aiZh) : (aiZh || aiEn);
                    const oneLinerIsZh = typeof d.one_liner === 'string' && /[一-龥]/.test(d.one_liner);
                    const oneLinerLangMismatch = typeof d.one_liner === 'string' && (lang === 'en' ? oneLinerIsZh : !oneLinerIsZh);
                    const brief = aiSummary || (oneLinerLangMismatch ? null : d.one_liner);
                    if (!brief) return null;
                    const untranslated = lang === 'en' && !!aiSummary && !aiEn;
                    const MAX = lang === 'en' ? 110 : 60;
                    const truncated = brief.length > MAX ? brief.slice(0, MAX).trim() + '…' : brief;
                    // 未翻译提示放在 WebkitLineClamp 容器外面单独一行——放在里面会被
                    // clamp 直接裁掉（中文字符更宽，2 行经常被正文占满，提示不可见）
                    return (
                      <div>
                        <div style={{ fontSize: 10, color: C.muted, marginTop: 3, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}
                          title={brief}>
                          {aiSummary && <span style={{ color: C.muted, fontWeight: 700, marginRight: 4, fontSize: 9, letterSpacing: 0.4 }}>AI</span>}
                          {truncated}
                        </div>
                        {untranslated && t('misc.ai_content_note') && (
                          <div style={{ color: C.muted, fontSize: 9, marginTop: 1 }}>{t('misc.ai_content_note')}</div>
                        )}
                      </div>
                    );
                  })()}
                  {/* 数据时效 + 分析热度 — 用真实分析次数代替社交关注数，冷启动阶段不暴露空数字 */}
                  {(() => {
                    const fresh = freshnessLabel(cachedScores[d.name]?.scoredAt, lang);
                    const heat = heatMap[d.name];
                    if (!fresh && !heat) return null;
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, fontSize: 9.5, color: fresh?.stale ? "#c2410c" : C.muted }}>
                        {fresh && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <Clock size={10} strokeWidth={2} />{fresh.text}
                          </span>
                        )}
                        {heat >= 3 && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3, color: C.muted }} title={lang === 'en' ? 'Analyzed by real users' : '真实分析次数，非社交关注数'}>
                            <Flame size={10} strokeWidth={2} />{lang === 'en' ? `${heat} analyses` : `分析 ${heat} 次`}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td style={{ padding: "8px 12px" }}><span style={S.tag}>{d.rounds || "—"}</span></td>
                <td style={{ padding: "8px 12px" }}>
                  {(() => {
                    const aiTrack = cachedScores[d.name]?.track;
                    const aiTrackEn = cachedScores[d.name]?.trackEn;
                    const rdTags = (d.tags || []).slice(0, 2);
                    // 真实赛道数据（RootData）优先；AI 分类只在没有真实数据时兜底，不再挂显眼的 "AI" 标签
                    if (rdTags.length > 0) {
                      return (
                        <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                          {rdTags.map(t => {
                            const { display, untranslated } = localizeRootDataTag(t, lang);
                            return (
                              <span key={t} style={{ ...S.tag, fontSize: 9, ...(untranslated ? { opacity: 0.55 } : {}) }}
                                title={untranslated ? 'RootData category label (no English translation available)' : (lang === 'en' ? 'From RootData' : '来自 RootData')}>
                                {display}
                              </span>
                            );
                          })}
                        </div>
                      );
                    }
                    if (aiTrack) {
                      return (
                        <span
                          onClick={() => { setTrackFilter(aiTrack); setShowFilters(true); }}
                          style={{ ...S.tag, fontSize: 9, cursor: "pointer" }}
                          title={lang === 'en' ? 'AI-classified track (RootData has no tags) · click to filter' : 'AI 分类赛道（RootData 无标签数据）· 点击筛选'}>
                          {lang === 'en' ? (aiTrackEn || aiTrack) : aiTrack}
                        </span>
                      );
                    }
                    return <span style={{ color: C.muted, fontSize: 11 }}>—</span>;
                  })()}
                </td>
                <td style={{ padding: "8px 12px", fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{d.amount ? fmt(d.amount) : <span style={{ color: C.muted }}>{lang === 'en' ? 'Undisclosed' : '未披露'}</span>}</td>
                <td style={{ padding: "8px 12px", color: C.muted, fontSize: 11 }}>
                  {d.valuation > 0 ? (
                    <span style={{ fontSize: 12, fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{fmt(d.valuation)}</span>
                  ) : d.amount > 0 ? (
                    <span title={lang === 'en' ? 'Implied valuation (amount / 25% dilution)' : '隐含估值（融资额 / 25% 稀释推算）'}
                      style={{ color: C.muted, fontStyle: "italic", fontVariantNumeric: "tabular-nums" }}>
                      ~{fmt(d.amount / 0.25)}<span style={{ fontSize: 9, opacity: 0.7, marginLeft: 2 }}>est.</span>
                    </span>
                  ) : (
                    <span title={lang === 'en' ? 'Not disclosed by VC' : 'VC 未披露'}>未披露</span>
                  )}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  <div style={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                    {(d.invests || []).filter(i => i.lead_investor).slice(0, 2).map(inv => (
                      <span key={inv.name}
                        onClick={() => { setInvestorSearch(inv.name); setShowFilters(true); }}
                        style={{ ...S.tag, fontSize: 9, fontWeight: 600, cursor: "pointer", background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}
                        title={lang === 'en' ? 'Lead investor · click to filter' : '领投方 · 点击筛选该机构'}>
                        {inv.name}
                      </span>
                    ))}
                    {(d.invests || []).filter(i => !i.lead_investor).slice(0, 1).map(inv => (
                      <span key={inv.name} style={{ ...S.tag, fontSize: 9 }} title={lang === 'en' ? 'Follow investor' : '跟投方'}>{inv.name}</span>
                    ))}
                  </div>
                </td>
                <td style={{ padding: "8px 12px", color: C.muted, fontSize: 11 }}>{d.published_time}</td>
                <td style={{ padding: "8px 12px" }}>
                  <FirstDocCell
                    entry={cachedScores[d.name]?.firstDoc ?? pipelineDocMap[d.name]?.firstDoc}
                    querying={!!pipelineDocMap[d.name]?.querying}
                    probed={!!pipelineDocMap[d.name]?.probed}
                    lang={lang}
                    mutedColor={C.muted}
                  />
                </td>
                <td style={{ padding: "8px 12px" }}>
                  {cachedScores[d.name] ? (
                    <button onClick={() => onImport(d.name)} style={{ ...S.btn("primary"), fontSize: 11, padding: "3px 8px" }}>
                      {lang === 'en' ? 'View' : '查看'}
                    </button>
                  ) : (
                    <button onClick={() => onImport(d.name)} style={{ ...S.btn(), fontSize: 11, padding: "3px 8px" }}>
                      {lang === 'en' ? 'Analyze' : '分析'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
            {loading && filtered.length === 0 && Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                {[16, 140, 60, 80, 70, 70, 100, 60, 100, 60].map((w, j) => (
                  <td key={j} style={{ padding: "10px 12px" }}>
                    <div style={{ height: 13, width: w, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.1 }} />
                  </td>
                ))}
              </tr>
            ))}
            {filtered.length === 0 && !loading && (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: C.muted }}>
                {lang === 'en' ? 'No matching deals' : '暂无符合条件的数据'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      </div>}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  KOL RADAR — 展开行内的私人笔记（云端/本地双写，见 useNotes；key 加 kol: 前缀
//  避免跟「项目跟进笔记」共用同一个 project_name 命名空间）
// ══════════════════════════════════════════════════════
function KolNotesBox({ noteKey, notes, onAdd, lang }) {
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const handleSave = async () => {
    if (!text.trim()) return;
    setSaving(true);
    try {
      await onAdd(noteKey, { text: text.trim(), type: 'other' });
      setText("");
    } finally {
      setSaving(false);
    }
  };
  return (
    <div onClick={e => e.stopPropagation()}>
      {notes.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 8 }}>
          {notes.map(n => (
            <div key={n.id} style={{ fontSize: 11, color: C.text, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 8px" }}>
              <div>{n.text}</div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 2 }}>{new Date(n.createdAt).toLocaleString()}</div>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 6 }}>
        <textarea value={text} onChange={e => setText(e.target.value)} rows={2}
          placeholder={lang === 'en' ? 'Private notes on this account — only you can see these.' : '关于这个账号的私人笔记 — 只有你能看到。'}
          style={{ flex: 1, resize: "vertical", fontSize: 11, padding: 8, borderRadius: 6, border: `1px solid ${C.border}`, background: C.surface, color: C.text, fontFamily: "inherit" }} />
        <button onClick={handleSave} disabled={saving || !text.trim()}
          style={{ ...S.btn(text.trim() ? "primary" : "default"), fontSize: 11, padding: "4px 10px", alignSelf: "flex-end", opacity: saving || !text.trim() ? 0.5 : 1 }}>
          {lang === 'en' ? 'Save' : '保存'}
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  EARLY RADAR PANEL
// ══════════════════════════════════════════════════════
// activeSubTab（"hot" | "kol" | "earlybird"）现在由父组件 PipelinePage 的统一四
// tab 切换器控制（拉平之前这里自己管理一份 subTab 状态、自己渲染切换按钮，跟"近期
// 融资"不在同一层级），这里只按 prop 渲染对应内容，不再自己管路由
function EarlyRadarPanel({ activeSubTab, onImport, onFfMapUpdate = () => {} }) {
  const { lang } = useLang();
  const { user } = useAuth();
  const { notes: kolNotesMap, addNote: addKolNote } = useNotes(user);
  const isMobile = useIsMobile();
  const subTab = activeSubTab; // "hot" | "kol" | "earlybird"
  const [hotItems, setHotItems]     = useState([]);
  const [hotInvestorSignals, setHotInvestorSignals] = useState({}); // twitter_handle(小写) → 机构互动信号数组
  const [recentInvestorSignals, setRecentInvestorSignals] = useState([]); // KOL雷达机构互动动向面板用，按时间倒序
  const [fundingMap, setFundingMap] = useState({}); // { name: { totalFunding, cached } }
  const [ffMap, setFfMap]           = useState({}); // fundraise.fun 结果 { name: { found, totalFunding, matchBy } }
  const [heatMap, setHeatMap]       = useState({}); // RootData 热榜：{ name: 分析次数 } — 见 getAnalysisHeatMap
  const [kolHeatMap, setKolHeatMap] = useState({}); // KOL 雷达：同上，独立 map
  const [loading, setLoading]       = useState(false);
  const [status, setStatus]         = useState("");
  const [fundFilter, setFundFilter] = useState("all");       // "all" | "low" | "unfunded" | "dual_clean"
  const [tokenFilter, setTokenFilter] = useState("no_token"); // "all" | "no_token"
  const [trackFilter, setTrackFilter] = useState("all");     // "all" | specific track
  const [ageFilter, setAgeFilter] = useState("all");         // "all" | "<90d" | "<1y" | "1y+"
  const [sortBy, setSortBy] = useState("funding");           // "funding" | "score" | "earliest_signal" | "earliest_signal_desc"
  const [minScoreFilter, setMinScoreFilter] = useState("");  // "" = 不限；数字 = 最低 composite
  // KOL Radar state
  const [kolItems, setKolItems]         = useState([]);
  const [kolSignalMap, setKolSignalMap] = useState({}); // { name: { earliestSignal } } from project_cache
  const [kolCacheMap, setKolCacheMap]   = useState({}); // { name: { cached, composite, scoredAt } } — 查看按钮状态 + 时效/热度
  const [kolLoading, setKolLoading]     = useState(false);
  const [kolStatus, setKolStatus]       = useState("");
  const [kolTypeFilter, setKolTypeFilter] = useState("all"); // "all" | "project" | "personal"
  const [kolCryptoOnly, setKolCryptoOnly] = useState(true);  // 仅显示加密相关
  const [kolCachedAt, setKolCachedAt]   = useState(null);
  const [kolUserTags, setKolUserTags]   = useState(() => {   // 用户手动标注 { username: category }
    try { return JSON.parse(localStorage.getItem('kol_user_tags') || '{}'); } catch { return {}; }
  });
  // KOL sort
  const [kolSortCol, setKolSortCol]     = useState("newKols");
  const [kolSortDir, setKolSortDir]     = useState("desc");
  // KOL range filters
  const [kolAgeFilter, setKolAgeFilter]         = useState("all");   // "all"|"<30"|"30-90"|"90-365"|">365"
  const [kolFollowersMin, setKolFollowersMin]   = useState("");
  const [kolFollowersMax, setKolFollowersMax]   = useState("");
  const [kolVcMin, setKolVcMin]                 = useState("");
  const [kolVcMax, setKolVcMax]                 = useState("");
  const [kolDegenMin, setKolDegenMin]           = useState("");
  const [kolDegenMax, setKolDegenMax]           = useState("");
  const [kolCorpMin, setKolCorpMin]             = useState("");
  const [kolCorpMax, setKolCorpMax]             = useState("");
  const [kolNewKolsMin, setKolNewKolsMin]       = useState("");
  const [kolNewKolsMax, setKolNewKolsMax]       = useState("");
  const [kolCatFilter, setKolCatFilter]         = useState("all");   // "all" | specific category
  const [kolTimeframe, setKolTimeframe]         = useState("24h");   // time window
  const [kolIdentityFilter, setKolIdentityFilter] = useState("all"); // account identity type
  const [kolWatchlist, setKolWatchlist]         = useState(() => {
    try { return JSON.parse(localStorage.getItem('kol_watchlist') || '[]'); } catch { return []; }
  });
  const [kolShowWatchlist, setKolShowWatchlist] = useState(false);
  const [kolExpandedRows, setKolExpandedRows]   = useState(() => new Set()); // 展开详情的账号 username 集合
  const [kolBios, setKolBios]                   = useState(() => {
    try { return JSON.parse(localStorage.getItem('kol_bios') || '{}'); } catch { return {}; }
  });
  // Twitter 原始 bio + url（用于 firstDoc 探测的 website 兜底）
  const [kolTwitterBios, setKolTwitterBios]     = useState(() => {
    try { return JSON.parse(localStorage.getItem('kol_twitter_bios') || '{}'); } catch { return {}; }
  });
  const [kolBiosLoading, setKolBiosLoading]     = useState(false);
  // Layer 2 AI 分类结果（仅对 Layer 1 unsure 账号触发，结果 7d 缓存）
  const [kolLayer2Map, setKolLayer2Map]         = useState(() => loadLayer2Cache());
  const [kolLayer2Loading, setKolLayer2Loading] = useState(false);
  // Layer 3 视觉头像分析（仅对 Layer 1+2 后仍低信心账号触发，30d 缓存）
  const [kolLayer3Map, setKolLayer3Map]         = useState(() => loadLayer3Cache());
  const [kolLayer3Loading, setKolLayer3Loading] = useState(false);
  const [kolNotablePopup, setKolNotablePopup]   = useState(null); // { name, kols[] }
  const [kolNotableLoading, setKolNotableLoading] = useState(false);
  const [kolNotableInline, setKolNotableInline] = useState({}); // { [username]: { kols, images, loading, error, quotaExceeded, hasDetailData } } — 展开行内联版，独立于弹窗

  const pull = async () => {
    setLoading(true);
    setStatus(lang === 'en' ? "🔍 Fetching hot projects..." : "🔍 拉取热榜...");
    try {
      // 1. 今日 + 本周 + 本月热榜合并去重，扩大候选池
      const [today, week, month] = await Promise.all([
        rdSkill("hot_index", { days: 1 }).catch(() => []),
        rdSkill("hot_index", { days: 7 }).catch(() => []),
        rdSkill("hot_index", { days: 30 }).catch(() => []),
      ]);
      const seen = new Set();
      const unique = [...(today || []), ...(week || []), ...(month || [])].filter(p => {
        if (seen.has(p.project_id)) return false;
        seen.add(p.project_id);
        return true;
      }).slice(0, 80);
      setHotItems(unique);
      setStatus(`✅ ${unique.length} ${lang === 'en' ? 'hot projects (1d+7d+30d)' : '个热门项目（日/周/月榜合并）'}`);
      // 机构互动信号——数据由 scripts/match-investor-signals.mjs 批量写入，这里只读
      const hotHandles = unique.map(p => (p.X || "").split("/").filter(Boolean).pop()).filter(Boolean);
      fetchSignalsForHandles(hotHandles).then(setHotInvestorSignals);

      // 2. 批量查 project_cache 获取融资数据（已分析过的项目）
      const names = unique.map(p => p.project_name).filter(Boolean);
      if (names.length === 0) return;
      const { supabase } = await import('./lib/supabase');
      const { data } = await supabase
        .from('project_cache')
        .select('project_name, raw_data, analysis, scored_at')
        .in('project_name', names)
        .gt('expires_at', new Date().toISOString());
      const map = {};
      if (data) {
        data.forEach(row => {
          // 与 Pipeline/Trending 对齐：非 displayable（_warmed/_lightweight 等半成品）不算「已缓存」，
          // 让它走下面的 uncached 分支重新探测，避免"查看"按钮打开一个空评分。
          if (!cacheIsDisplayable(row)) return;
          const tl = row.raw_data?.timeline || [];
          const firstDoc = tl.find(t => t.key === 'firstDoc');
          map[row.project_name] = {
            totalFunding: row.raw_data?.details?.total_funding ?? null,
            cached: true,
            firstDoc: firstDoc ? { date: firstDoc.date, source: firstDoc.source, sourceType: firstDoc.sourceType, url: firstDoc.url } : null,
            // 双语简介：分别存中英文，渲染时按 lang 选
            summary_zh: row.analysis?.summary || null,
            summary_en: row.analysis?.summary_en || null,
            // AI 评分（已分析过的项目）
            composite: row.analysis?.composite ?? null,
            scores: row.analysis?.scores ?? null,
            isCron: !!row.analysis?._cron,
            scoredAt: row.scored_at || null,
            // 五分类卡片用：团队 + GitHub 活跃度，只有完整分析过的项目才有
            teamMembers: row.raw_data?.teamMembers || [],
            githubSignal: row.raw_data?.githubSignal || null,
          };
        });
        setFundingMap({ ...map });
        getAnalysisHeatMap(names).then(setHeatMap);
      }

      // 3. 对未在缓存里的项目，用 get_item 批量查融资（补全「查询中」）
      //    融资数据：最多前 24 个（RootData API 限速）
      //    首次文档：全部覆盖（使用服务端 /api/firstdoc-batch，见步骤 3b）
      const fdCache = loadFirstDocCache(); // 24h 首次文档缓存
      const uncached = unique.filter(p => p.project_id && !(p.project_name in map));
      // 先把未缓存项目标记为「查询中」；已有 24h firstDoc 缓存的直接预填
      uncached.forEach(p => {
        const cached24h = fdCache[p.project_name];
        map[p.project_name] = {
          totalFunding: undefined,
          querying: true,
          firstDoc: cached24h?.firstDoc ?? null,
          firstDocQuerying: !cached24h?.probed, // 未在 24h 缓存中的需要探测
        };
      });
      setFundingMap({ ...map });

      // 步骤 3a: 融资数据探测（前 24 个，同时顺带查 firstDoc 存入 24h 缓存）
      for (let i = 0; i < Math.min(uncached.length, 24); i += 4) {
        const batch = uncached.slice(i, i + 4);
        await Promise.all(batch.map(async (p) => {
          try {
            const detail = await rdSkill("get_item", { project_id: p.project_id });
            const details = detail?.data || detail || {};
            const totalFunding = details.total_funding ?? null;
            const tokenSym = details.token_symbol || p.token_symbol || null;

            // 若 24h 缓存已有 firstDoc，跳过 Wayback 查询（节省 API 调用）
            let firstDoc = fdCache[p.project_name]?.probed
              ? (fdCache[p.project_name]?.firstDoc ?? null)
              : null;
            const probeFirstDoc = !fdCache[p.project_name]?.probed
              ? fetchFirstDocSnapshot(details.social_media, details.project_name).catch(() => null)
              : Promise.resolve(null);
            // 免费 GitHub 信号（纯 GitHub API，不花钱）：未分析项目也能提前看团队规模/活跃度，
            // 不用等付费全量分析——同一批次顺手查，见早期雷达 Early Bird 卡片同款设计
            const [docSnap, githubSignal] = await Promise.all([
              probeFirstDoc,
              fetchGithubSignal(details).catch(() => null),
            ]);
            if (!fdCache[p.project_name]?.probed) {
              firstDoc = docSnap?.firstDocDate
                ? { date: docSnap.firstDocDate, source: docSnap.sourceLabel || 'Wayback Machine', sourceType: docSnap.sourceType, url: docSnap.firstDocUrl }
                : null;
              fdCache[p.project_name] = { firstDoc, probed: true, ts: Date.now() };
            }

            map[p.project_name] = { totalFunding, tokenSym, querying: false, cached: false, firstDoc, firstDocQuerying: false, githubSignal };
          } catch {
            map[p.project_name] = { totalFunding: null, querying: false, cached: false, firstDoc: map[p.project_name]?.firstDoc ?? null, firstDocQuerying: false };
          }
        }));
        setFundingMap({ ...map });
        if (i + 4 < Math.min(uncached.length, 24)) await new Promise(r => setTimeout(r, 800));
      }
      // 融资探测上限之外的项目：结束融资查询中状态（firstDoc 在步骤 3b 处理）
      uncached.slice(24).forEach(p => {
        if (map[p.project_name]?.querying) {
          map[p.project_name] = { ...map[p.project_name], totalFunding: null, querying: false, cached: false };
        }
      });
      setFundingMap({ ...map });

      // 步骤 3b: 首次文档批量探测 — 覆盖所有未在 24h 缓存中的项目（含第 25-80 名）
      // 使用服务端 /api/firstdoc-batch（10 个/批，更多 URL 模式，无 CORS 问题）
      const needsFirstDoc = uncached.filter(p => !fdCache[p.project_name]?.probed);
      if (needsFirstDoc.length > 0) {
        setStatus(lang === 'en'
          ? `🔍 Probing docs for ${needsFirstDoc.length} more projects...`
          : `🔍 批量检索 ${needsFirstDoc.length} 个项目的首次文档...`);

        for (let i = 0; i < needsFirstDoc.length; i += 10) {
          const batch = needsFirstDoc.slice(i, i + 10);
          try {
            const res = await fetch(`${RD_BASE}/api/firstdoc-batch`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ projects: batch.map(p => ({ project_id: p.project_id, project_name: p.project_name })) }),
            });
            if (res.ok) {
              const { results = {} } = await res.json();
              batch.forEach(p => {
                const raw = results[p.project_name];
                // 新 error 形状：不缓存，下次重试
                if (raw && typeof raw === 'object' && raw.error) {
                  map[p.project_name] = { ...(map[p.project_name] || {}), firstDoc: null, firstDocQuerying: false };
                  return;
                }
                const firstDoc = (raw && raw.date)
                  ? { date: raw.date, source: raw.source, sourceType: raw.sourceType, url: raw.url }
                  : null;
                map[p.project_name] = { ...(map[p.project_name] || {}), firstDoc, firstDocQuerying: false };
                fdCache[p.project_name] = { firstDoc, probed: true, ts: Date.now() };
              });
              setFundingMap({ ...map });
            }
          } catch {
            batch.forEach(p => {
              if (map[p.project_name]) map[p.project_name].firstDocQuerying = false;
              fdCache[p.project_name] = { firstDoc: null, probed: true, ts: Date.now() };
            });
          }
          if (i + 10 < needsFirstDoc.length) await new Promise(r => setTimeout(r, 600));
        }
        saveFirstDocCache(fdCache); // 持久化 24h 缓存
        setFundingMap({ ...map });
      }

      // 4. 批量查询 fundraise.fun（西方 VC 数据）
      //    分批发请求，每批 40 个，避免 URL 过长
      setStatus(lang === 'en' ? "🌐 Cross-checking fundraise.fun..." : "🌐 交叉核查 fundraise.fun 西方 VC 数据...");
      const allNames  = unique.map(p => p.project_name).filter(Boolean);
      const allTokens = unique.map(p => fundingMap[p.project_name]?.tokenSym || p.token_symbol || '').filter(Boolean);
      const ffResult  = {};

      for (let i = 0; i < allNames.length; i += 40) {
        const batchNames  = allNames.slice(i, i + 40);
        const batchTokens = allTokens.slice(i, i + 40);
        try {
          const qs  = `names=${encodeURIComponent(batchNames.join(','))}&tokens=${encodeURIComponent(batchTokens.join(','))}`;
          const res = await fetch(`${RD_BASE}/api/fundraise-check?${qs}`);
          if (res.ok) {
            const json = await res.json();
            Object.assign(ffResult, json.data || {});
          }
        } catch (_) {}
      }
      setFfMap(ffResult);
      onFfMapUpdate(ffResult); // 同步到 App 级别，供 DetailPanel Memo 使用

      // 保存到 localStorage 缓存（30 分钟内 mount 时复用，避免重复拉取）
      try {
        localStorage.setItem('early_radar_cache_v2', JSON.stringify({
          ts: Date.now(),
          hotItems: unique,
          fundingMap: map,
          ffMap: ffResult,
        }));
      } catch { /* localStorage 满或不可用，静默忽略 */ }

      setStatus(`✅ ${unique.length} ${lang === 'en' ? 'projects loaded · fundraise.fun cross-checked' : '个项目已加载 · fundraise.fun 双源核查完毕'}`);
    } catch (e) {
      setStatus("⚠ " + e.message);
    } finally {
      setLoading(false);
    }
  };

  // ── 早期雷达缓存（30 分钟，避免切 tab 来回时重复拉取） ──
  const RADAR_CACHE_KEY = 'early_radar_cache_v2';
  const RADAR_CACHE_TTL_MS = 30 * 60 * 1000;

  // ── 首次文档 24h 独立缓存（生命周期比 30min 主缓存长）──
  // firstDoc 日期不会变，24h 内无需重新探测
  const FIRSTDOC_CACHE_KEY = 'radar_firstdoc_v1';
  const FIRSTDOC_TTL_MS    = 24 * 60 * 60 * 1000;

  const loadFirstDocCache = () => {
    try {
      const raw = localStorage.getItem(FIRSTDOC_CACHE_KEY);
      if (!raw) return {};
      const parsed = JSON.parse(raw);
      const now = Date.now();
      const valid = {};
      Object.entries(parsed).forEach(([name, entry]) => {
        if (entry?.ts && (now - entry.ts) < FIRSTDOC_TTL_MS) valid[name] = entry;
      });
      return valid;
    } catch { return {}; }
  };

  const saveFirstDocCache = (fdCache) => {
    try { localStorage.setItem(FIRSTDOC_CACHE_KEY, JSON.stringify(fdCache)); } catch {}
  };

  useEffect(() => {
    // mount 时先看 localStorage 缓存
    try {
      const raw = localStorage.getItem(RADAR_CACHE_KEY);
      if (raw) {
        const cached = JSON.parse(raw);
        if (cached?.ts && Date.now() - cached.ts < RADAR_CACHE_TTL_MS) {
          const hotItemsCached = cached.hotItems || [];
          setHotItems(hotItemsCached);
          // 合并 24h firstDoc 缓存到 fundingMap（覆盖 30min 缓存中缺失的 firstDoc）
          const restoredMap = { ...(cached.fundingMap || {}) };
          const fdCache = loadFirstDocCache();
          Object.entries(fdCache).forEach(([name, entry]) => {
            if (!restoredMap[name]?.firstDoc) {
              restoredMap[name] = { ...(restoredMap[name] || {}), firstDoc: entry.firstDoc ?? null };
            }
          });
          setFundingMap(restoredMap);
          if (cached.ffMap) onFfMapUpdate(cached.ffMap);
          const minAgo = Math.round((Date.now() - cached.ts) / 60000);
          setStatus(
            lang === 'en'
              ? `✅ ${hotItemsCached.length} hot projects (cached ${minAgo}m ago, click ↻ to refresh)`
              : `✅ ${hotItemsCached.length} 个热门项目（${minAgo} 分钟前缓存，点 ↻ 强制刷新）`
          );

          // ── 后台补探测：从缓存恢复后，对仍缺 firstDoc 且不在 24h 缓存的项目补查 ──
          // 场景：① 部署新版本后旧缓存无步骤3b数据 ② 24h 缓存首次初始化为空
          const needsProbeFromCache = hotItemsCached.filter(p =>
            p.project_id &&
            !restoredMap[p.project_name]?.firstDoc &&
            !fdCache[p.project_name]?.probed
          );
          if (needsProbeFromCache.length > 0) {
            // 异步后台运行，不阻塞 UI 渲染
            (async () => {
              const liveMap = { ...restoredMap };
              const liveFdCache = { ...fdCache };
              // 标记 querying
              needsProbeFromCache.forEach(p => {
                liveMap[p.project_name] = { ...(liveMap[p.project_name] || {}), firstDocQuerying: true };
              });
              setFundingMap({ ...liveMap });

              for (let i = 0; i < needsProbeFromCache.length; i += 10) {
                const batch = needsProbeFromCache.slice(i, i + 10);
                try {
                  const res = await fetch(`${RD_BASE}/api/firstdoc-batch`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ projects: batch.map(p => ({ project_id: p.project_id, project_name: p.project_name })) }),
                  });
                  if (res.ok) {
                    const { results = {} } = await res.json();
                    batch.forEach(p => {
                      const raw = results[p.project_name];
                      // 新 error 形状：不缓存
                      if (raw && typeof raw === 'object' && raw.error) {
                        liveMap[p.project_name] = { ...(liveMap[p.project_name] || {}), firstDoc: null, firstDocQuerying: false };
                        return;
                      }
                      const firstDoc = (raw && raw.date)
                        ? { date: raw.date, source: raw.source, sourceType: raw.sourceType, url: raw.url }
                        : null;
                      liveMap[p.project_name] = { ...(liveMap[p.project_name] || {}), firstDoc, firstDocQuerying: false };
                      liveFdCache[p.project_name] = { firstDoc, probed: true, ts: Date.now() };
                    });
                    setFundingMap({ ...liveMap });
                  }
                } catch {
                  batch.forEach(p => {
                    if (liveMap[p.project_name]) liveMap[p.project_name].firstDocQuerying = false;
                    liveFdCache[p.project_name] = { firstDoc: null, probed: true, ts: Date.now() };
                  });
                }
                if (i + 10 < needsProbeFromCache.length) await new Promise(r => setTimeout(r, 600));
              }
              saveFirstDocCache(liveFdCache);
              setFundingMap({ ...liveMap });
            })();
          }
          return;
        }
      }
    } catch {}
    pull();
  }, []);

  // ── KOL Radar helpers ────────────────────────────────────────────
  // 将 "580D" / "12H" / "30W" 等转换为天数
  const parseAgeDays = (ageStr) => {
    if (!ageStr) return null;
    const m = String(ageStr).match(/^(\d+)([DWMYH]?)$/i);
    if (!m) return null;
    const n = parseInt(m[1]);
    const unit = (m[2] || 'D').toUpperCase();
    if (unit === 'D') return n;
    if (unit === 'W') return n * 7;
    if (unit === 'M') return n * 30;
    if (unit === 'Y') return n * 365;
    if (unit === 'H') return Math.round(n / 24);
    return n;
  };

  // 将 "45.9K" / "2.6M" / "341" 转换为数字
  const parseFollowers = (s) => {
    if (!s) return 0;
    const str = String(s).replace(/,/g, '').trim().toUpperCase();
    if (str.endsWith('M')) return parseFloat(str) * 1e6;
    if (str.endsWith('K')) return parseFloat(str) * 1e3;
    return parseFloat(str) || 0;
  };

  // 关键词自动分类（基于 username + name）
  const inferKolCat = (username, name) => {
    const t = ((username || '') + ' ' + (name || '')).toLowerCase();
    if (/\bai\b|gpt|llm|machine.?learn|intelligent|neural/.test(t)) return 'AI';
    if (/depin|physical.?infra|wireless|sensor|mining.?device/.test(t)) return 'DePIN';
    if (/rwa|real.?world|tokenize|real.?estate|treasury/.test(t)) return 'RWA';
    if (/game|gaming|guild|play.?earn|metaverse|rpg/.test(t)) return 'GameFi';
    if (/nft|collectible|art|pfp|ordinal/.test(t)) return 'NFT';
    if (/defi|swap|dex|lending|yield|vault|amm|lp|liquidity|staking/.test(t)) return 'DeFi';
    if (/layer.?2|l2|rollup|zk|optimistic|arbitr|base\b|scroll|blast/.test(t)) return 'L2';
    if (/layer.?1|l1|chain|cosmos|substrate|aptos|sui|solana|near/.test(t)) return 'L1';
    if (/infra|node|validator|oracle|bridge|rpc|index/.test(t)) return 'Infra';
    if (/social|media|creator|dao|govern/.test(t)) return 'Social';
    if (/exchange|cex|wallet|payment|trade/.test(t)) return 'CeFi';
    return 'Other';
  };

  // 判断是否"加密相关"（VC关注 OR Degen关注 > 阈值）
  const isCrypto = (p) => (p.vcCount > 0) || (p.degenCount > 3) || (p.corpCount > 5);

  // 账号身份分层（正交于赛道分类）
  const KOL_IDENTITY_LABELS = {
    'Project':    '🏗️ Project',
    'Trader':     '💰 Trader',
    'TradFi':     '🏦 TradFi',
    'Researcher': '🔬 Researcher',
    'Builder':    '👷 Builder',
    'Newcomer':   '🌱 Newcomer',
    'Influencer': '📢 Influencer',
    'Person':     '👤 Person',
  };
  const inferKolIdentity = (p) => {
    const u = (p.username || '').toLowerCase();
    const n = (p.name || '').toLowerCase();
    const t = u + ' ' + n;
    const days = parseAgeDays(p.age) || 9999;
    const fol = parseFollowers(p.followers) || 0;

    // ── 层1：ffMatch（fundraise.fun 融资记录交叉 — 最高置信度）───────────────
    // 有融资记录的 Twitter handle 几乎 100% 是项目账号
    if (p.ffMatch) return 'Project';

    // ── 层2：DeepSeek bio 分类（AI 分析 Twitter 原文简介 — 次高置信度）───────
    const bioData = kolBios[p.username] || kolBios['@' + p.username];
    if (bioData && typeof bioData === 'object') {
      if (bioData.isProject === true)  return 'Project';
      if (bioData.isProject === false) return inferPersonalKolIdentity(p);  // 确认是个人，走个人分类
    }

    // ── 层3：用户名规则（免费，快速，覆盖常见项目名词）─────────────────────
    const hasProjectSlug = /protocol|\.xyz$|labs|network|\.fi$|defi|dao$|bridge|swap|dex|vault|restake|layer|chain\b/.test(u);
    if (hasProjectSlug && p.accountType === 'project') return 'Project';

    // ── 层4：个人账号负向信号（原有逻辑保留）──────────────────────────────
    const hasPersonalSignal =
      /\.(eth|bnb|sol|lens|base|arb|btc)$/i.test(u) ||          // ENS/chain handles
      (/^[a-z]+\d{1,4}$/.test(u) && fol > 5000) ||             // short name + big following
      /vitalik|buterin|hayden|sbf|cz_|do.?kwon|naval|punk/.test(t) || // known people
      /^\w{1,15}\.(eth|sol|base)/.test(u) ||                    // dotname handle
      (p.accountType === 'project' && fol > 50000 && !hasProjectSlug);  // huge "project" w/o slug = likely personal

    if (p.accountType === 'project' && !hasPersonalSignal) return 'Project';
    if (/research|analyst|analysis|report|insight|study|thread/.test(t)) return 'Researcher';
    if (/trad.?fi|bank|equit|fund|asset.?mgmt|hedge|portfolio|family.?office/.test(t)) return 'TradFi';
    if (/build|dev|engineer|code|hack|protocol|founder|cto|engineer/.test(t)) return 'Builder';
    if (/trade|trader|trading|chart|signal|alpha|scalp|swing|options|calls/.test(t)) return 'Trader';
    if (days < 90) return 'Newcomer';
    if (fol > 20000) return 'Influencer';
    return 'Trader';
  };

  // Derived: 项目/个人分类（Layer 1 多信号评分 + Layer 2 AI 复核 merge）
  // 返回 { type, displayType, confidence, signals, refined? }
  //   - type: 'project'|'person'|'unsure'（用于 Layer 2/3 触发判断）
  //   - displayType: 'project'|'person'（UI 强制 2 态，unsure 按 score 符号偏倚归类）
  const classifyAccount = (p) => {
    const bioData = kolBios[p.username] || kolBios['@' + p.username];
    const aiBio = bioData && typeof bioData === 'object' ? (bioData.bio || bioData.zh || bioData.en) : '';
    const aiIsProject = bioData && typeof bioData === 'object' ? bioData.isProject : undefined;
    const aiRole = bioData && typeof bioData === 'object' ? bioData.role : undefined;
    const layer1 = classifyKolAccount({
      username: p.username,
      name: p.name,
      bio: p.twitterBio || '',
      aiBio,
      aiIsProject,
      aiRole,
      accountType: p.accountType,
      ffMatch: p.ffMatch,
      followers: parseFollowers(p.followers) || 0,
      following: p.following ? parseFollowers(p.following) : 0,
      tags: p.tags,
      userTag: kolUserTags[p.username],
    });
    // Layer 2 仅在 Layer 1 信心不够（< 0.7）时生效
    const layer2 = kolLayer2Map[p.username];
    let merged = mergeWithLayer2(layer1, layer2);
    // Layer 3 视觉分析（仅在 Layer 1+2 后仍低信心时合入）
    const layer3 = kolLayer3Map[p.username];
    merged = mergeWithLayer3(merged, layer3);
    // displayType：UI 强制 2 态，unsure 按 score 符号偏倚（score>=0 → project）
    // 错把项目当个人代价更大（漏掉新项目入场信号）
    const displayType = merged.type === 'unsure'
      ? ((layer1.score ?? 0) >= 0 ? 'project' : 'person')
      : merged.type;
    return { ...merged, displayType };
  };

  const isEffectiveProject = (p) => classifyAccount(p).type === 'project';

  // When user manually tagged as Person, infer personal identity (skip Project classification)
  const inferPersonalKolIdentity = (p) => {
    const u = (p.username || '').toLowerCase();
    const n = (p.name || '').toLowerCase();
    const t = u + ' ' + n;
    const days = parseAgeDays(p.age) || 9999;
    const fol = parseFollowers(p.followers) || 0;
    if (/research|analyst|analysis|report|insight|study|thread/.test(t)) return 'Researcher';
    if (/trad.?fi|bank|equit|fund|asset.?mgmt|hedge|portfolio|family.?office/.test(t)) return 'TradFi';
    if (/build|dev|engineer|code|hack|protocol|founder|cto/.test(t)) return 'Builder';
    if (/trade|trader|trading|chart|signal|alpha|scalp|swing|options|calls/.test(t)) return 'Trader';
    if (days < 90) return 'Newcomer';
    if (fol > 20000) return 'Influencer';
    return 'Trader';
  };

  // Watchlist helpers (localStorage-backed)
  const toggleKolWatchlist = (username) => {
    const next = kolWatchlist.includes(username)
      ? kolWatchlist.filter(u => u !== username)
      : [...kolWatchlist, username];
    setKolWatchlist(next);
    try { localStorage.setItem('kol_watchlist', JSON.stringify(next)); } catch {}
  };

  // Batch generate AI bios — processes ALL items in chunks of 50
  // Pre-fetches Twitter profile bios to enrich DeepSeek context when available
  const batchGenerateBios = async (items) => {
    const missing = items.filter(p => !kolBios[p.username]);
    if (missing.length === 0) return;
    setKolBiosLoading(true);
    const CHUNK = 50;
    let accumulated = { ...kolBios };
    try {
      for (let i = 0; i < missing.length; i += CHUNK) {
        const chunk = missing.slice(i, i + CHUNK);

        // Step 1: Try to fetch real Twitter bios for this chunk
        let twitterBios = {};
        try {
          const usernames = chunk.map(p => p.username).join(',');
          const tbRes = await fetch(`${RD_BASE}/api/twitter-bios?usernames=${encodeURIComponent(usernames)}`);
          const tbJson = await tbRes.json();
          if (tbJson.ok && tbJson.bios) {
            twitterBios = tbJson.bios;
            // 持久化到 state，让 firstdoc-batch 路径能拿到 url + bioUrls
            setKolTwitterBios(prev => {
              const next = { ...prev };
              Object.entries(twitterBios).forEach(([k, v]) => {
                const u = k.startsWith('@') ? k.slice(1) : k;
                next[u] = v;
              });
              try { localStorage.setItem('kol_twitter_bios', JSON.stringify(next)); } catch {}
              return next;
            });
          }
        } catch (tbErr) {
          console.warn('[kol-bios] twitter-bios prefetch failed:', tbErr.message);
        }

        // Step 2: Build account list, attaching Twitter bio when available
        const accounts = chunk.map(p => {
          const tw = twitterBios['@' + p.username] || twitterBios[p.username] || '';
          const twBioText = typeof tw === 'object' ? tw.bio : tw; // 兼容旧 string 格式
          return {
            username: p.username, name: p.name, accountType: p.accountType,
            followers: p.followers, tags: p.tags,
            ...(twBioText ? { twitterBio: twBioText } : {}),
          };
        });

        // Step 3: Generate AI bios via DeepSeek
        try {
          const r = await fetch(`${RD_BASE}/api/kol-bios`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ accounts }),
          });
          const json = await r.json();
          if (json.ok && json.bios) {
            for (const [k, v] of Object.entries(json.bios)) {
              const u = k.startsWith('@') ? k.slice(1) : k;
              accumulated[u] = v;
              accumulated['@' + u] = v;
            }
            // Update state after each chunk so bios appear progressively
            const snapshot = { ...accumulated };
            setKolBios(snapshot);
            try { localStorage.setItem('kol_bios', JSON.stringify(snapshot)); } catch {}
          }
        } catch (chunkErr) {
          console.warn('[kol-bios] chunk error:', chunkErr.message);
        }
      }
    } finally {
      setKolBiosLoading(false);
    }
  };

  // ── Layer 2 AI 复核：对 Layer 1 unsure 的账号调 /api/kol-classify ──
  const batchTriggerLayer2 = async (items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    // 用最新 bio 数据重算 Layer 1，挑出 unsure / 低置信
    const cache = loadLayer2Cache();
    const unsureAccounts = items.filter(p => {
      if (!p.username) return false;
      if (cache[p.username]) return false; // 已有 Layer 2 结果，跳过
      const bioData = kolBios[p.username] || kolBios['@' + p.username];
      const layer1 = classifyKolAccount({
        username: p.username,
        name: p.name,
        bio: p.twitterBio || '',
        aiBio: bioData && typeof bioData === 'object' ? (bioData.bio || bioData.zh || bioData.en) : '',
        aiIsProject: bioData && typeof bioData === 'object' ? bioData.isProject : undefined,
        aiRole: bioData && typeof bioData === 'object' ? bioData.role : undefined,
        accountType: p.accountType,
        ffMatch: p.ffMatch,
        followers: parseFollowers(p.followers) || 0,
        following: p.following ? parseFollowers(p.following) : 0,
        tags: p.tags,
        userTag: kolUserTags[p.username],
      });
      // 只对 unsure 或低信心（<0.6）触发 AI 复核
      return layer1.type === 'unsure' || layer1.confidence < 0.6;
    });

    if (unsureAccounts.length === 0) return;
    setKolLayer2Loading(true);
    try {
      // 分批：50 个/批
      const CHUNK = 50;
      for (let i = 0; i < unsureAccounts.length; i += CHUNK) {
        const chunk = unsureAccounts.slice(i, i + CHUNK);
        const enriched = chunk.map(p => {
          const bioData = kolBios[p.username] || kolBios['@' + p.username];
          const aiBio = bioData && typeof bioData === 'object'
            ? (bioData.zh || bioData.en || '')
            : (typeof bioData === 'string' ? bioData : '');
          return {
            username: p.username,
            name: p.name,
            bio: aiBio,
            twitterBio: p.twitterBio || '',
            accountType: p.accountType,
            followers: p.followers,
            tags: p.tags || [],
          };
        });
        const results = await fetchLayer2Classifications(enriched, RD_BASE);
        if (results && Object.keys(results).length > 0) {
          saveLayer2Cache(results);
          // merge 入 state，触发 UI 重渲染
          setKolLayer2Map(prev => ({ ...prev, ...results }));
        }
        // 间隔避免速率限制
        if (i + CHUNK < unsureAccounts.length) {
          await new Promise(r => setTimeout(r, 800));
        }
      }
    } finally {
      setKolLayer2Loading(false);
    }
  };

  // ── Layer 3 视觉头像分析：对 Layer 1+2 后仍低信心的账号调 /api/kol-vision ──
  const batchTriggerLayer3 = async (items) => {
    if (!Array.isArray(items) || items.length === 0) return;
    const layer2Cache = loadLayer2Cache();
    const layer3Cache = loadLayer3Cache();
    // 仅对仍是 unsure / confidence<0.5 且有 avatar 的账号触发
    const targets = items.filter(p => {
      if (!p.username || !p.avatar) return false;
      if (layer3Cache[p.username]) return false; // 已分析过，跳过
      const bioData = kolBios[p.username] || kolBios['@' + p.username];
      const layer1 = classifyKolAccount({
        username: p.username,
        name: p.name,
        bio: p.twitterBio || '',
        aiBio: bioData && typeof bioData === 'object' ? (bioData.bio || bioData.zh || bioData.en) : '',
        aiIsProject: bioData && typeof bioData === 'object' ? bioData.isProject : undefined,
        aiRole: bioData && typeof bioData === 'object' ? bioData.role : undefined,
        accountType: p.accountType,
        ffMatch: p.ffMatch,
        followers: parseFollowers(p.followers) || 0,
        following: p.following ? parseFollowers(p.following) : 0,
        tags: p.tags,
        userTag: kolUserTags[p.username],
      });
      const merged = mergeWithLayer2(layer1, layer2Cache[p.username]);
      return merged.type === 'unsure' || merged.confidence < 0.5;
    }).slice(0, 30); // 限制：单次最多 30 个，避免视觉 API 成本失控

    if (targets.length === 0) return;
    setKolLayer3Loading(true);
    try {
      const CHUNK = 5; // 视觉端点单批 5 个
      for (let i = 0; i < targets.length; i += CHUNK) {
        const chunk = targets.slice(i, i + CHUNK).map(p => ({
          username: p.username,
          avatarUrl: p.avatar,
        }));
        const results = await fetchLayer3Classifications(chunk, RD_BASE);
        if (results && Object.keys(results).length > 0) {
          saveLayer3Cache(results);
          setKolLayer3Map(prev => ({ ...prev, ...results }));
        }
        if (i + CHUNK < targets.length) {
          await new Promise(r => setTimeout(r, 2000)); // 间隔 2s
        }
      }
    } finally {
      setKolLayer3Loading(false);
    }
  };

  // 早期信号得分（综合 newKols / vcCount 加权）
  const signalScore = (p) => (p.newKols || 0) * 3 + (p.vcCount || 0) * 5;

  // 保存用户手动标注
  const setKolUserTag = (username, cat) => {
    const next = { ...kolUserTags, [username]: cat };
    setKolUserTags(next);
    try { localStorage.setItem('kol_user_tags', JSON.stringify(next)); } catch {}
  };

  // Fetch Notable KOLs on demand (click → open modal with image avatars + lazy detail)
  // 共享的原始拉取逻辑：leak.me /main/request_project_info/ 每天限额很低（共享出口 IP），
  // 弹窗（移动端）和展开行内联（桌面端）两个入口都复用这一份请求 + 24h 服务端缓存。
  const loadNotableKolsRaw = async (username, fallbackImages) => {
    const r = await fetch(`${RD_BASE}/api/notable-kols?username=${encodeURIComponent(username)}&timeframe=${kolTimeframe}`);
    const json = await r.json();
    if (!json.ok) {
      return { loading: false, error: json.error || 'Failed to load', quotaExceeded: !!json.quotaExceeded };
    }
    const apiImages = json.images && json.images.length > 0 ? json.images : fallbackImages;
    return {
      kols: json.kols || [],
      images: apiImages,
      loading: false,
      hasDetailData: (json.kols || []).length > 0,
    };
  };

  const fetchNotableKols = async (p) => {
    // Always open the modal immediately with whatever avatar images we have from the main feed
    const images = p.notableImages || [];
    setKolNotablePopup({
      name: p.name || p.username,
      images,
      kols: [],
      total: p.notableCount || images.length,
      loading: true,
    });
    setKolNotableLoading(true);
    try {
      const result = await loadNotableKolsRaw(p.username, images);
      setKolNotablePopup(prev => prev ? ({ ...prev, ...result }) : null);
    } catch (e) {
      console.warn('[notable-kols]', e.message);
      setKolNotablePopup(prev => prev ? ({ ...prev, loading: false, error: e.message }) : null);
    } finally {
      setKolNotableLoading(false);
    }
  };

  // 展开行内联版：结果落到 kolNotableInline[username]，不弹窗，直接嵌在详情面板里
  const fetchNotableKolsInline = async (p) => {
    const images = p.notableImages || [];
    setKolNotableInline(prev => ({ ...prev, [p.username]: { images, kols: [], loading: true } }));
    try {
      const result = await loadNotableKolsRaw(p.username, images);
      setKolNotableInline(prev => ({ ...prev, [p.username]: result }));
    } catch (e) {
      console.warn('[notable-kols-inline]', e.message);
      setKolNotableInline(prev => ({ ...prev, [p.username]: { loading: false, error: e.message } }));
    }
  };

  // KOL Radar: 从 leak.me 拉取指定时间窗口热榜
  const pullKol = async (tf) => {
    const timeframe = tf || kolTimeframe || '24h';
    setKolLoading(true);
    setKolStatus(lang === 'en' ? `🔍 Fetching KOL radar (${timeframe})...` : `🔍 从 leak.me 拉取 KOL 雷达 (${timeframe})...`);
    try {
      const r = await fetch(`${RD_BASE}/api/leak-radar?timeframe=${timeframe}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const json = await r.json();
      if (!json.ok) throw new Error(json.error || 'Unknown error');
      const items = json.data || [];
      setKolItems(items);
      setKolCachedAt(json.cachedAt || null);
      const total = items.length;
      const projectCount = items.filter(p => p.accountType === 'project').length;
      const cacheNote = json.stale
        ? (lang === 'en' ? ' (leak.me unavailable — showing last known data)' : '（leak.me 暂时拉不到数据，显示的是上次成功的结果）')
        : (json.cached ? (lang === 'en' ? ' (cached)' : '（缓存）') : '');
      setKolStatus(
        lang === 'en'
          ? `✅ ${total} accounts · ${projectCount} projects · ${timeframe} window${cacheNote}`
          : `✅ ${total} 个账号 · ${projectCount} 个项目型 · ${timeframe} 时间窗口${cacheNote}`
      );
      // Auto-generate bios for ALL accounts (chunked in batchGenerateBios)
      setTimeout(() => batchGenerateBios(items), 800);
      // Layer 2 AI 文本复核：对 Layer 1 unsure 的账号触发（bio 加载后）
      setTimeout(() => batchTriggerLayer2(items), 6000);
      // Layer 3 视觉头像分析：对 Layer 1+2 后仍低信心的触发（Layer 2 完成后）
      setTimeout(() => batchTriggerLayer3(items), 14000);

      // 跨表查 project_cache 拿 firstDoc（仅 displayType=project 的账号）
      // P0：用 classifyAccount（多信号评分）替代旧的 p.accountType === 'project'
      // 注意：此时 kolBios / Layer2 / Layer3 还未加载，只用 Layer 1 启发式作初筛
      const projectItems = items.filter(p => p.name && classifyAccount(p).displayType === 'project');
      const projectNames = projectItems.map(p => p.name);
      if (projectNames.length > 0) {
        const { supabase } = await import('./lib/supabase');
        const { data } = await supabase.from('project_cache')
          .select('project_name, raw_data, analysis, scored_at')
          .in('project_name', projectNames)
          .gt('expires_at', new Date().toISOString());
        const sigMap = {};
        const cacheMap = {}; // { name: { cached, composite, scoredAt } } — 供"查看"按钮状态 + 时效/热度用，跟 sigMap（firstDoc专用）分开存避免改动下游消费的 shape
        (data || []).forEach(row => {
          const tl = row.raw_data?.timeline || [];
          const firstDoc = tl.find(t => t.key === 'firstDoc');
          if (firstDoc) sigMap[row.project_name] = { date: firstDoc.date, source: firstDoc.source, sourceType: firstDoc.sourceType, url: firstDoc.url };
          cacheMap[row.project_name] = {
            cached: cacheIsDisplayable(row),
            composite: row.analysis?.composite ?? null,
            scoredAt: row.scored_at || null,
          };
        });
        setKolSignalMap(sigMap);
        setKolCacheMap(cacheMap);
        getAnalysisHeatMap(projectNames).then(setKolHeatMap);

        // P1：扩大覆盖 — 处理所有未缓存项目（不再限 top 10）
        // 分批 8 个/批，间隔 800ms 节流避开 archive.org 限流
        const uncached = projectItems.filter(p => !sigMap[p.name]);
        if (uncached.length > 0) {
          const updatedSigMap = { ...sigMap };
          const CHUNK = 8;
          for (let i = 0; i < uncached.length; i += CHUNK) {
            const chunk = uncached.slice(i, i + CHUNK);
            try {
              const batchRes = await fetch(`${RD_BASE}/api/firstdoc-batch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  projects: chunk.map(p => {
                    // 即便 twitter-bios 还没加载，localStorage 里可能有上次的缓存
                    const tw = kolTwitterBios[p.username] || {};
                    const ai = kolBios[p.username] || kolBios['@' + p.username] || {};
                    const aiMainWebsite = typeof ai === 'object' ? ai.mainWebsite : null;
                    return {
                      project_name: p.name,
                      ...(tw.url ? { twitterUrl: tw.url } : {}),
                      ...(tw.bioUrls?.length ? { bioUrls: tw.bioUrls } : {}),
                      ...(tw.bio ? { twitterBio: tw.bio } : {}),
                      ...(aiMainWebsite ? { aiMainWebsite } : {}),
                    };
                  }),
                }),
              });
              if (batchRes.ok) {
                const { results = {} } = await batchRes.json();
                Object.entries(results).forEach(([name, fd]) => {
                  if (fd && typeof fd === 'object' && fd.error) return;
                  if (fd?.date) updatedSigMap[name] = { date: fd.date, source: fd.source, sourceType: fd.sourceType, url: fd.url };
                });
                setKolSignalMap({ ...updatedSigMap });
              }
            } catch { /* 静默 */ }
            // 间隔 800ms
            if (i + CHUNK < uncached.length) await new Promise(r => setTimeout(r, 800));
          }
        }
      }
    } catch (e) {
      setKolStatus("⚠ " + e.message);
    } finally {
      setKolLoading(false);
    }
  };

  useEffect(() => { if (subTab === 'kol' && kolItems.length === 0) pullKol('24h'); }, [subTab]);

  // 机构互动动向——数据由 scripts/match-investor-signals.mjs 批量写入，这里只读
  useEffect(() => { if (subTab === 'kol') fetchRecentSignals(30).then(setRecentInvestorSignals); }, [subTab]);

  // P0+P1：AI bios / Layer2 / Layer3 加载后，重新筛选项目并补查 firstDoc
  // 这样原本被 Layer 1 标为 unsure 的账号，被 AI 复核为 project 后也能补全 firstDoc
  useEffect(() => {
    if (kolItems.length === 0) return;
    if (subTab !== 'kol' && subTab !== 'pipeline') return; // 仅在相关 tab 时探测
    let cancelled = false;
    const reProbeNew = async () => {
      // 筛出新被识别为项目、且 sigMap 还没缓存的
      const projectItems = kolItems.filter(p => p.name && classifyAccount(p).displayType === 'project');
      const newlyDiscovered = projectItems.filter(p => !kolSignalMap[p.name]);
      if (newlyDiscovered.length === 0) return;

      const CHUNK = 8;
      const updated = { ...kolSignalMap };
      for (let i = 0; i < newlyDiscovered.length; i += CHUNK) {
        if (cancelled) return;
        const chunk = newlyDiscovered.slice(i, i + CHUNK);
        try {
          const batchRes = await fetch(`${RD_BASE}/api/firstdoc-batch`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projects: chunk.map(p => {
                const tw = kolTwitterBios[p.username] || {};
                const ai = kolBios[p.username] || kolBios['@' + p.username] || {};
                const aiMainWebsite = typeof ai === 'object' ? ai.mainWebsite : null;
                return {
                  project_name: p.name,
                  // Layer A/B/C：传 Twitter url + bio + AI 抽出的网站
                  ...(tw.url ? { twitterUrl: tw.url } : {}),
                  ...(tw.bioUrls?.length ? { bioUrls: tw.bioUrls } : {}),
                  ...(tw.bio ? { twitterBio: tw.bio } : {}),
                  ...(aiMainWebsite ? { aiMainWebsite } : {}),
                };
              }),
            }),
          });
          if (batchRes.ok) {
            const { results = {} } = await batchRes.json();
            Object.entries(results).forEach(([name, fd]) => {
              if (fd && typeof fd === 'object' && fd.error) return;
              if (fd?.date) updated[name] = { date: fd.date, source: fd.source, sourceType: fd.sourceType, url: fd.url };
            });
            if (!cancelled) setKolSignalMap({ ...updated });
          }
        } catch {}
        if (i + CHUNK < newlyDiscovered.length) await new Promise(r => setTimeout(r, 800));
      }
    };
    // 防抖：所有 Map 变化触发后等 2s 再统一处理
    const t = setTimeout(reProbeNew, 2000);
    return () => { cancelled = true; clearTimeout(t); };
  }, [kolBios, kolLayer2Map, kolLayer3Map, kolTwitterBios]);

  // KOL sort helper
  const kolSortFn = (a, b) => {
    const dir = kolSortDir === 'desc' ? -1 : 1;
    const v = (p, col) => {
      if (col === 'newKols')    return p.newKols || 0;
      if (col === 'vcCount')    return p.vcCount || 0;
      if (col === 'degenCount') return p.degenCount || 0;
      if (col === 'corpCount')  return p.corpCount || 0;
      if (col === 'followers')  return parseFollowers(p.followers);
      if (col === 'ageDays')    return parseAgeDays(p.age) || 0;
      if (col === 'signal')     return signalScore(p);
      if (col === 'watchlist')  return p.watchlistCount || 0;
      if (col === 'growth24h')  {
        const total = p.totalKols || 0;
        const k24h = p.kols24h || 0;
        return total > 0 ? (k24h / total) : 0;
      }
      return 0;
    };
    return (v(a, kolSortCol) - v(b, kolSortCol)) * dir;
  };

  // KOL filter + sort pipeline
  const kolCategories = ['AI', 'DePIN', 'RWA', 'GameFi', 'NFT', 'DeFi', 'L2', 'L1', 'Infra', 'Social', 'CeFi', 'Other', 'Person'];
  const kolIdentities = ['Project', 'Trader', 'TradFi', 'Researcher', 'Builder', 'Newcomer', 'Influencer'];
  const kolFiltered = kolItems
    .filter(p => {
      // Watchlist mode: show only watchlisted
      if (kolShowWatchlist && !kolWatchlist.includes(p.username)) return false;
      // 加密只筛
      if (kolCryptoOnly && !isCrypto(p)) return false;
      // 账号类型（用 displayType 强制 2 态，unsure 已偏倚归类）
      if (kolTypeFilter === 'project' || kolTypeFilter === 'personal') {
        const cls = classifyAccount(p).displayType;
        const target = kolTypeFilter === 'personal' ? 'person' : 'project';
        if (cls !== target) return false;
      }
      // 赛道分类
      const cat = kolUserTags[p.username] || inferKolCat(p.username, p.name);
      if (kolCatFilter !== 'all' && cat !== kolCatFilter) return false;
      // 身份分层
      if (kolIdentityFilter !== 'all' && inferKolIdentity(p) !== kolIdentityFilter) return false;
      // 账号年龄
      if (kolAgeFilter !== 'all') {
        const days = parseAgeDays(p.age) || 0;
        if (kolAgeFilter === '<30'    && days >= 30)    return false;
        if (kolAgeFilter === '30-90'  && (days < 30 || days > 90))  return false;
        if (kolAgeFilter === '90-365' && (days < 90 || days > 365)) return false;
        if (kolAgeFilter === '>365'   && days <= 365)  return false;
      }
      // 数值范围筛选
      const fol = parseFollowers(p.followers);
      if (kolFollowersMin !== '' && fol < Number(kolFollowersMin)) return false;
      if (kolFollowersMax !== '' && fol > Number(kolFollowersMax)) return false;
      if (kolVcMin   !== '' && (p.vcCount   || 0) < Number(kolVcMin))   return false;
      if (kolVcMax   !== '' && (p.vcCount   || 0) > Number(kolVcMax))   return false;
      if (kolDegenMin !== '' && (p.degenCount || 0) < Number(kolDegenMin)) return false;
      if (kolDegenMax !== '' && (p.degenCount || 0) > Number(kolDegenMax)) return false;
      if (kolCorpMin  !== '' && (p.corpCount  || 0) < Number(kolCorpMin))  return false;
      if (kolCorpMax  !== '' && (p.corpCount  || 0) > Number(kolCorpMax))  return false;
      if (kolNewKolsMin !== '' && (p.newKols || 0) < Number(kolNewKolsMin)) return false;
      if (kolNewKolsMax !== '' && (p.newKols || 0) > Number(kolNewKolsMax)) return false;
      return true;
    })
    .sort(kolSortFn);

  // 融资状态徽章（双源）
  const getFundBadge = (name) => {
    const f  = fundingMap[name];
    const ff = ffMap[name]; // fundraise.fun 结果

    if (!f || f.querying) return { dot: "#a1a1aa", label: lang === 'en' ? 'Loading…' : '查询中…', color: "#71717a" };

    const rdEmpty = !f.totalFunding || f.totalFunding === 0;
    const ffEmpty = ff ? !ff.found : null; // null = 未查到（ffMap 还没加载完）

    // 双源零融资 → 强早期信号
    if (rdEmpty && ffEmpty === true) {
      return { dot: "#f97316", label: lang === 'en' ? 'Zero (2-src)' : '零融资·双源', color: "#c2410c", dual: true };
    }
    // RootData 零融资，但 fundraise.fun 有记录
    if (rdEmpty && ff?.found) {
      const ffAmt = ff.totalFunding || 0;
      const ffLabel = ffAmt > 0 ? `FF:$${ffAmt >= 1e6 ? (ffAmt/1e6).toFixed(1)+'M' : (ffAmt/1000).toFixed(0)+'K'}` : 'FF:有记录';
      return { dot: "#eab308", label: ffLabel, color: "#a16207", tooltip: `fundraise.fun 有融资记录（via ${ff.matchBy}）` };
    }
    // RootData 零融资，fundraise.fun 未查到（可能还在加载）
    if (rdEmpty && ffEmpty === null) {
      return { dot: "#ef4444", label: lang === 'en' ? 'No Funding' : '无融资', color: "#b91c1c" };
    }

    // RootData 有融资数据
    if (f.totalFunding < 500000)
      return { dot: "#eab308", label: `$${(f.totalFunding / 1000).toFixed(0)}K`, color: "#a16207" };
    if (f.totalFunding < 5000000)
      return { dot: "#22c55e", label: `$${(f.totalFunding / 1e6).toFixed(1)}M`, color: "#15803d" };
    if (f.totalFunding < 50000000)
      return { dot: "#3b82f6", label: `$${(f.totalFunding / 1e6).toFixed(0)}M`, color: "#1d4ed8" };
    // $50M+ — 已有机构背书，非早期 Alpha
    return { dot: "#a1a1aa", label: `$${(f.totalFunding / 1e6).toFixed(0)}M+`, color: "#52525b" };
  };

  // 所有赛道（从 hot_index tags 提取）
  const allTracks = [...new Set(hotItems.flatMap(p => p.tags || []))].sort();

  // 排序
  const sorted = [...hotItems].sort((a, b) => {
    if (sortBy === "score") {
      // 有分数的排前，按分数 desc
      const sa = fundingMap[a.project_name]?.composite ?? -1;
      const sb = fundingMap[b.project_name]?.composite ?? -1;
      return sb - sa;
    }
    if (sortBy === "earliest_signal" || sortBy === "earliest_signal_desc") {
      const sa = fundingMap[a.project_name]?.firstDoc?.date;
      const sb = fundingMap[b.project_name]?.firstDoc?.date;
      // 无文档日期的排到最后
      if (!sa && !sb) return 0;
      if (!sa) return 1;
      if (!sb) return -1;
      // earliest_signal: 最新文档的排前（日期 desc）— 找「刚发文档的新项目」
      // earliest_signal_desc: 最老文档的排前（日期 asc）— 找「老牌项目」
      return sortBy === "earliest_signal" ? sb.localeCompare(sa) : sa.localeCompare(sb);
    }
    // 默认：无融资/低融资排前
    const fa = fundingMap[a.project_name];
    const fb = fundingMap[b.project_name];
    const va = fa ? (fa.totalFunding ?? 0) : 999999999;
    const vb = fb ? (fb.totalFunding ?? 0) : 999999999;
    return va - vb;
  });

  // 筛选
  const filtered = sorted.filter(p => {
    // 代币筛选：未发币（同时检查 hot_index 字段 和 get_item 补全的 tokenSym）
    const enrichedToken = fundingMap[p.project_name]?.tokenSym;
    const hasToken = p.token_symbol || enrichedToken;
    if (tokenFilter === "no_token" && hasToken) return false;
    // 赛道筛选
    if (trackFilter !== "all" && !(p.tags || []).includes(trackFilter)) return false;
    // 年龄筛选（按首次文档日期）
    if (ageFilter !== "all") {
      const fd = fundingMap[p.project_name]?.firstDoc;
      if (!fd || fd.sourceType === 'no_doc_found' || !fd.date) return false;  // 无文档的项目在筛选状态下不显示
      const days = Math.floor((Date.now() - new Date(fd.date).getTime()) / 86400000);
      if (ageFilter === "<90d" && days >= 90) return false;
      if (ageFilter === "<1y" && days >= 365) return false;
      if (ageFilter === "1y+" && days < 365) return false;
    }
    // 最低评分筛选（只筛选有分数的项目）
    if (minScoreFilter !== "") {
      const composite = fundingMap[p.project_name]?.composite;
      if (!composite || composite < Number(minScoreFilter)) return false;
    }
    // 融资筛选
    const f  = fundingMap[p.project_name];
    const ff = ffMap[p.project_name];
    const rdEmpty = !f || !f.totalFunding || f.totalFunding === 0;
    if (fundFilter === "dual_clean") {
      // 双源零融资：RootData 零融资 + fundraise.fun 无记录
      return rdEmpty && ff && !ff.found;
    }
    if (fundFilter === "unfunded") {
      if (!f) return true;
      return rdEmpty;
    }
    if (fundFilter === "low") {
      if (!f) return true;
      return !f.totalFunding || f.totalFunding < 500000;
    }
    return true;
  });

  return (
    <div>
      {/* 切换按钮已经拉平到父组件 PipelinePage 的统一四 tab 切换器里，这里只保留
          KOL 数据时效性的说明文字（跟切换按钮本身无关，不需要一起挪走） */}
      {subTab === 'kol' && kolCachedAt && (
        <div style={{ fontSize: 10, color: C.muted, marginBottom: 14 }}>
          {lang === 'en' ? 'Data from' : '数据来自'} leak.me · {new Date(kolCachedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}

      {/* ── Early Bird Panel ─────────────────────────── */}
      {subTab === 'earlybird' && <EarlyBirdTab />}

      {/* ── KOL Radar Panel ─────────────────────────── */}
      {subTab === 'kol' && (() => {
        // sortable header helper
        const SortTh = ({ col, label, style: thStyle, thStyle: thStyleProp }) => {
          const mergedStyle = { ...(thStyle || {}), ...(thStyleProp || {}) };
          const active = kolSortCol === col;
          const ArrowIcon = active ? (kolSortDir === 'desc' ? ArrowDown : ArrowUp) : ArrowUpDown;
          return (
            <th onClick={() => {
              if (kolSortCol === col) setKolSortDir(d => d === 'desc' ? 'asc' : 'desc');
              else { setKolSortCol(col); setKolSortDir('desc'); }
            }} style={{
              padding: "8px 12px", textAlign: "left", cursor: "pointer", userSelect: "none",
              color: active ? "#60a5fa" : C.muted,
              whiteSpace: "nowrap",
              ...mergedStyle,
            }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                {label}
                <ArrowIcon size={11} strokeWidth={2} style={{ opacity: active ? 1 : 0.4, flexShrink: 0 }} />
              </span>
            </th>
          );
        };
        // range input pair helper
        const RangeInput = ({ min, max, setMin, setMax }) => (
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <input value={min} onChange={e => setMin(e.target.value)} placeholder="min"
              style={{ ...S.input, width: 46, padding: "2px 5px", fontSize: 10 }} />
            <span style={{ color: C.muted, fontSize: 10 }}>–</span>
            <input value={max} onChange={e => setMax(e.target.value)} placeholder="max"
              style={{ ...S.input, width: 46, padding: "2px 5px", fontSize: 10 }} />
          </div>
        );
        const catColors = { AI:'#a78bfa', DePIN:'#34d399', RWA:'#fbbf24', GameFi:'#f97316', NFT:'#ec4899', DeFi:'#60a5fa', L2:'#38bdf8', L1:'#818cf8', Infra:'#94a3b8', Social:'#f472b6', CeFi:'#6ee7b7', Other:'#64748b', Person:'#6366f1' };
        const identityColors = { Project:'#818cf8', Trader:'#f59e0b', TradFi:'#34d399', Researcher:'#a78bfa', Builder:'#60a5fa', Newcomer:'#4ade80', Influencer:'#f472b6', Person:'#6366f1' };
        const TF_OPTIONS = ['15m','1h','2h','3h','6h','12h','24h','2d'];

        // 桌面表格行 / 移动端卡片共用的字段推导，避免两套渲染各写一份分类/身份/信号逻辑
        const deriveKolRow = (p) => {
          const classification = classifyAccount(p);
          const isProject = classification.type === 'project';
          const twitterUrl = `https://twitter.com/${p.username}`;
          const autoCat = inferKolCat(p.username, p.name);
          const userCat = kolUserTags[p.username];
          const cat = userCat || autoCat;
          const catColor = catColors[cat] || catColors.Other;
          const rawIdentity = inferKolIdentity(p);
          const identity = kolUserTags[p.username] === 'Person' ? inferPersonalKolIdentity(p) : rawIdentity;
          const identColor = identityColors[identity] || '#94a3b8';
          const days = parseAgeDays(p.age);
          const createdApprox = days != null
            ? (() => { const d = new Date(Date.now() - days * 86400000); return d.toLocaleDateString('zh-CN', { year: 'numeric', month: 'short' }); })()
            : p.age || '—';
          const sig = signalScore(p);
          const bioRaw = kolBios[p.username] || kolBios['@' + p.username] || '';
          const bioStructured = (typeof bioRaw === 'object' && bioRaw) ? bioRaw : null;
          let bio = '';
          if (bioStructured) {
            bio = lang === 'en' ? (bioStructured.en || bioStructured.zh || '') : (bioStructured.zh || bioStructured.en || '');
          } else if (typeof bioRaw === 'string') {
            const hasZh = /[一-龥]/.test(bioRaw);
            if (lang === 'en' && hasZh) bio = '';
            else if (lang !== 'en' && !hasZh) bio = '';
            else bio = bioRaw;
          }
          const isWatched = kolWatchlist.includes(p.username);
          return { classification, isProject, twitterUrl, autoCat, userCat, cat, catColor, rawIdentity, identity, identColor, days, createdApprox, sig, bioStructured, bio, isWatched };
        };
        return (
        <div>
          {/* ── Top info bar ── */}
          <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, padding: "8px 14px", background: C.surface, borderRadius: 8, border: `1px solid ${C.border}`, lineHeight: 1.8, letterSpacing: 0.1, display: "flex", alignItems: "center", flexWrap: "wrap", gap: 4 }}>
            <Radio size={12} strokeWidth={2} style={{ flexShrink: 0 }} /> {lang === 'en' ? 'Data source' : '数据来源'} <a href="https://leak.me" target="_blank" rel="noopener noreferrer" style={{ color: C.text, textDecoration: "none" }}>leak.me</a>
            <span style={{ color: C.border }}>·</span>
            {lang === 'en' ? 'Signal score = New KOL×3 + VC×5' : '信号分 = New KOL×3 + VC×5'}
            <span style={{ color: C.border }}>·</span>
            {lang === 'en' ? 'Track auto-inferred from username (click dropdown to override)' : '赛道由用户名自动判断（点击下拉可手动修改)'}
            <span style={{ color: C.border }}>·</span>
            {lang === 'en' ? 'Registration date is an estimate' : '注册时间为估算值'}
            {kolBiosLoading && <span style={{ marginLeft: 6, color: "#a78bfa", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#a78bfa" }} /> {lang === 'en' ? 'Generating AI bios...' : 'AI 生成简介中...'}</span>}
            {kolLayer2Loading && <span style={{ marginLeft: 6, color: "#d97706", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 5, height: 5, borderRadius: "50%", background: "#d97706" }} /> {lang === 'en' ? 'Layer 2 AI review in progress...' : 'Layer 2 AI 复核中...'}</span>}
            {kolLayer3Loading && <span style={{ marginLeft: 6, color: "#db2777", fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 4 }}><Eye size={11} strokeWidth={2} /> {lang === 'en' ? 'Layer 3 visual analysis in progress...' : 'Layer 3 视觉分析中...'}</span>}
          </div>

          {/* ── 机构互动动向：跟下面的热榜账号列表是两种不同性质的信号源（这是"谁在关注
              候选池项目"，下面是"谁在社交上热"），分开放避免混淆 ── */}
          {recentInvestorSignals.length > 0 && (
            <div style={{ marginBottom: 14, padding: "10px 14px", background: C.surface, borderRadius: 8, border: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 8, display: "flex", alignItems: "center", gap: 5 }}>
                <Eye size={12} strokeWidth={2} /> {lang === 'en' ? 'Institutional attention' : '机构互动动向'}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 220, overflowY: "auto" }}>
                {recentInvestorSignals.map((s, i) => {
                  // 机构官宣是确认信号(不是推测),视觉上跟回复/转发/引用区分开
                  const isAnnouncement = s.interaction_type === 'official_announcement';
                  const actionLabel = isAnnouncement
                    ? (lang === 'en' ? 'announced investment in' : '官宣投资了')
                    : s.interaction_type === 'retweeted' ? (lang === 'en' ? 'retweeted' : '转发了')
                    : s.interaction_type === 'quoted' ? (lang === 'en' ? 'quoted' : '引用了')
                    : (lang === 'en' ? 'replied to' : '回复了');
                  const sourceLabel = s.project_source === 'early_bird' ? (lang === 'en' ? 'Early Bird' : '早期雷达')
                    : s.project_source === 'hot_list' ? (lang === 'en' ? 'Hot list' : '零融资热榜')
                    : (lang === 'en' ? 'New (via announcement)' : '官宣新发现');
                  return (
                    <div key={i} style={{ fontSize: 11, color: C.muted, display: "flex", gap: 6, alignItems: "baseline" }}>
                      <span style={{ fontWeight: 700, color: "#92400e" }}>@{s.investor_handle}</span>
                      <span style={isAnnouncement ? { color: "#16a34a", fontWeight: 700 } : undefined}>
                        {isAnnouncement ? '✅ ' : ''}{actionLabel}
                      </span>
                      <span style={{ fontWeight: 600, color: C.text }}>{s.project_name}</span>
                      <span style={{ fontSize: 9, padding: "1px 6px", borderRadius: 3, background: isAnnouncement ? "#f0fdf4" : C.faint, color: isAnnouncement ? "#16a34a" : C.muted }}>
                        {sourceLabel}
                      </span>
                      <span style={{ marginLeft: "auto", fontSize: 10, color: C.muted, whiteSpace: "nowrap" }}>
                        {s.tweet_created_at ? new Date(s.tweet_created_at).toLocaleDateString('zh-CN') : ''}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Timeframe tabs — leak.me 现在只有 24h 匿名可看，其他时间窗口需要登录
              （见 proxy/server.js /api/leak-radar 的 requiresLogin 分支），灰掉不可点，
              避免用户点了以为坏了 ── */}
          <div style={{ display: "flex", gap: 4, marginBottom: 10, flexWrap: "wrap", alignItems: "center" }}>
            {TF_OPTIONS.map(tf => {
              const isActive = kolTimeframe === tf;
              const isLocked = tf !== '24h';
              return (
                <button key={tf}
                  onClick={() => { if (isLocked) return; setKolTimeframe(tf); pullKol(tf); }}
                  disabled={isLocked}
                  title={isLocked ? (lang === 'en' ? 'Requires a leak.me login — only 24h is available' : '需要 leak.me 登录，仅 24h 可用') : undefined}
                  style={{
                    ...S.btn(isActive ? "primary" : "default"),
                    fontSize: 11, padding: "4px 12px",
                    ...(isActive ? { background: "#639922", borderColor: "#639922", fontWeight: 700 } : {}),
                    ...(isLocked ? { opacity: 0.4, cursor: "not-allowed" } : {}),
                  }}>
                  {tf}
                </button>
              );
            })}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginLeft: 8 }}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: C.muted }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#a78bfa", display: "inline-block" }} />Degen
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: C.muted }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#60a5fa", display: "inline-block" }} />VC
              </span>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, color: C.muted }}>
                <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#34d399", display: "inline-block" }} />Corporate
              </span>
            </div>
            <button onClick={() => pullKol(kolTimeframe)} disabled={kolLoading}
              style={{ ...S.btn(), fontSize: 11, padding: "4px 10px", marginLeft: "auto" }}>
              {kolLoading ? "..." : "↻ " + (lang === 'en' ? 'Refresh' : '刷新')}
            </button>
          </div>

          {/* ── Filter bar ── */}
          <div style={{ marginBottom: 10 }}>
            {/* Row 1: account type + crypto filter + watchlist */}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 6 }}>
              <button onClick={() => setKolCryptoOnly(v => !v)}
                style={{ ...S.btn(kolCryptoOnly ? "primary" : "default"), fontSize: 11, padding: "3px 10px",
                  display: "inline-flex", alignItems: "center", gap: 5,
                  ...(kolCryptoOnly ? {} : { opacity: 0.6 }) }}>
                <ShieldCheck size={12} strokeWidth={2} /> {lang === 'en' ? 'Crypto only' : '仅加密'}
              </button>
              {[
                { key: "all", label: lang === 'en' ? "All" : "全部", Icon: null },
                { key: "project", label: lang === 'en' ? "Project" : "项目", Icon: Building2 },
                { key: "personal", label: lang === 'en' ? "KOL" : "个人", Icon: User },
              ].map(({ key, label, Icon }) => (
                <button key={key} onClick={() => setKolTypeFilter(key)}
                  style={{ ...S.btn(kolTypeFilter === key ? "primary" : "default"), fontSize: 11, padding: "3px 10px", display: "inline-flex", alignItems: "center", gap: 5 }}>
                  {Icon && <Icon size={12} strokeWidth={2} />} {label}
                </button>
              ))}
              {/* Watchlist tab */}
              <button onClick={() => setKolShowWatchlist(v => !v)}
                style={{ ...S.btn(kolShowWatchlist ? "primary" : "default"), fontSize: 11, padding: "3px 10px",
                  display: "inline-flex", alignItems: "center", gap: 5,
                  ...(kolShowWatchlist ? { background: "#92400e", borderColor: "#f59e0b", color: "#fbbf24" } : {}) }}>
                <Star size={12} strokeWidth={2} fill={kolShowWatchlist ? "#fbbf24" : "none"} /> Watchlist {kolWatchlist.length > 0 ? `(${kolWatchlist.length})` : ''}
              </button>
              {/* Category */}
              <select value={kolCatFilter} onChange={e => setKolCatFilter(e.target.value)}
                style={{ ...S.input, fontSize: 11, padding: "3px 8px", height: 28 }}>
                <option value="all">{lang === 'en' ? 'All categories' : '全部赛道'}</option>
                {kolCategories.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
              {/* Identity filter */}
              <select value={kolIdentityFilter} onChange={e => setKolIdentityFilter(e.target.value)}
                style={{ ...S.input, fontSize: 11, padding: "3px 8px", height: 28 }}>
                <option value="all">{lang === 'en' ? 'All identities' : '全部身份'}</option>
                {kolIdentities.map(id => <option key={id} value={id}>{KOL_IDENTITY_LABELS[id] || id}</option>)}
              </select>
            </div>
            {/* Row 2: range filters */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", padding: "8px 10px", background: C.faint, borderRadius: 8 }}>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{lang === 'en' ? 'Age:' : '年龄:'}</span>
                {[
                  { key: "all", label: lang === 'en' ? "All" : "全部" },
                  { key: "<30",    label: "<30d" },
                  { key: "30-90",  label: "30-90d" },
                  { key: "90-365", label: "90d-1y" },
                  { key: ">365",   label: ">1y" },
                ].map(({ key, label }) => (
                  <button key={key} onClick={() => setKolAgeFilter(key)}
                    style={{ ...S.btn(kolAgeFilter === key ? "primary" : "default"), fontSize: 10, padding: "2px 8px" }}>
                    {label}
                  </button>
                ))}
              </div>
              <div style={{ width: 1, height: 20, background: C.border }} />
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#f97316", fontWeight: 700, whiteSpace: "nowrap" }}>New KOL:</span>
                <RangeInput min={kolNewKolsMin} max={kolNewKolsMax} setMin={setKolNewKolsMin} setMax={setKolNewKolsMax} />
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#60a5fa", fontWeight: 700, whiteSpace: "nowrap" }}>VC:</span>
                <RangeInput min={kolVcMin} max={kolVcMax} setMin={setKolVcMin} setMax={setKolVcMax} />
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#a78bfa", fontWeight: 700, whiteSpace: "nowrap" }}>Degen:</span>
                <RangeInput min={kolDegenMin} max={kolDegenMax} setMin={setKolDegenMin} setMax={setKolDegenMax} />
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: "#34d399", fontWeight: 700, whiteSpace: "nowrap" }}>Corporate:</span>
                <RangeInput min={kolCorpMin} max={kolCorpMax} setMin={setKolCorpMin} setMax={setKolCorpMax} />
              </div>
              <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, whiteSpace: "nowrap" }}>{lang === 'en' ? 'Fol:' : '粉丝:'}</span>
                <RangeInput min={kolFollowersMin} max={kolFollowersMax} setMin={setKolFollowersMin} setMax={setKolFollowersMax} />
              </div>
              <button onClick={() => {
                setKolAgeFilter("all"); setKolNewKolsMin(""); setKolNewKolsMax("");
                setKolVcMin(""); setKolVcMax(""); setKolDegenMin(""); setKolDegenMax("");
                setKolCorpMin(""); setKolCorpMax(""); setKolFollowersMin(""); setKolFollowersMax("");
                setKolCatFilter("all"); setKolTypeFilter("all"); setKolIdentityFilter("all"); setKolShowWatchlist(false);
              }} style={{ ...S.btn(), fontSize: 10, padding: "2px 8px", marginLeft: "auto" }}>
                {lang === 'en' ? '✕ Reset' : '✕ 重置'}
              </button>
            </div>
          </div>
          {kolStatus && <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>{kolStatus}</div>}
          <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}>
            {lang === 'en' ? `${kolFiltered.length} accounts · ${kolTimeframe}` : `${kolFiltered.length} 个账号 · ${kolTimeframe}`}
            {kolSortCol !== 'signal' && (
              <span> · <button onClick={() => { setKolSortCol('signal'); setKolSortDir('desc'); }}
                style={{ background: "none", border: "none", color: "#f97316", cursor: "pointer", fontSize: 10, padding: 0 }}>
                {lang === 'en' ? '⚡ Sort by signal score' : '⚡ 按信号分排序'}
              </button></span>
            )}
          </div>
          {/* ── Table（移动端换成可展开卡片，字段太密不适合硬塞进表格横向滚动） ── */}
          {isMobile ? (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {kolFiltered.slice(0, 60).map((p) => {
                const { classification, cat, catColor, identity, identColor, createdApprox, sig, bioStructured, bio, isWatched } = deriveKolRow(p);
                return (
                  <KolMobileCard
                    key={p.username}
                    p={p}
                    classification={classification}
                    cat={cat}
                    catColor={catColor}
                    identity={identity}
                    identColor={identColor}
                    bioStructured={bioStructured}
                    bio={bio}
                    createdApprox={createdApprox}
                    sig={sig}
                    isWatched={isWatched}
                    twitterUrl={`https://twitter.com/${p.username}`}
                    lang={lang}
                    onToggleWatch={() => toggleKolWatchlist(p.username)}
                    onAnalyze={() => onImport(p.name || p.username)}
                    onNotable={() => fetchNotableKols(p)}
                    cacheEntry={kolCacheMap[p.name] || {}}
                    heat={kolHeatMap[p.name]}
                  />
                );
              })}
              {kolLoading && kolFiltered.length === 0 && Array.from({ length: 3 }).map((_, i) => (
                <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
                  <div style={{ height: 13, width: 140, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.25 }} />
                </div>
              ))}
              {!kolLoading && kolFiltered.length === 0 && (
                <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
                  {kolShowWatchlist && kolWatchlist.length === 0
                    ? (lang === 'en' ? 'No watchlist items yet — tap ⭐ to add accounts' : '暂无关注账号 — 点击 ⭐ 添加')
                    : kolItems.length === 0
                      ? (lang === 'en' ? 'Tap refresh to load KOL radar data' : '点击刷新加载 KOL 雷达数据')
                      : (lang === 'en' ? 'No results — try adjusting filters' : '暂无结果，尝试调整筛选条件')}
                </div>
              )}
            </div>
          ) : (
          <div style={{ ...S.card, padding: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: C.faint, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.5 }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", color: C.muted, fontWeight: 700 }}>#</th>
                  <th style={{ padding: "8px 12px", textAlign: "left", color: C.muted, fontWeight: 700, minWidth: 200 }}>{lang === 'en' ? 'Account' : '账号'}</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", color: C.muted, fontWeight: 700, minWidth: 220 }}>{lang === 'en' ? "Why it's trending" : '为什么在热榜'}</th>
                  <SortTh col="signal" label={lang === 'en' ? 'KOL mix' : 'KOL 构成'} />
                  <SortTh col="newKols" label={lang === 'en' ? 'New' : '新增'} thStyle={{ color: "#639922" }} />
                  <th style={{ padding: "8px 10px", textAlign: "left", color: C.muted, fontWeight: 700 }}>{lang === 'en' ? 'Notable' : '知名关注'}</th>
                  <th style={{ padding: "8px 10px", width: 28 }} />
                </tr>
              </thead>
              <tbody>
                {kolFiltered.slice(0, 60).map((p, i) => {
                  const { classification, isProject, twitterUrl, autoCat, userCat, cat, catColor, rawIdentity, identity, identColor, days, createdApprox, sig, bioStructured, bio, isWatched } = deriveKolRow(p);
                  // "为什么在热榜"（p.description，来自 leak.me trending_info.title）永远是
                  // 英文。proxy 的 /api/leak-radar 现在会用 DeepSeek 批量翻译并按文本 hash
                  // 持久化缓存，返回时带上 description_zh（见 migration 034）。中文界面下
                  // 优先用翻译；还没翻译出来（新账号、翻译失败）时退占位文案而不是甩英文原文。
                  const trendReason = lang === 'en' ? (p.description || null) : (p.description_zh || null);
                  return (
                    [
                    <tr key={p.username + '-main'} style={{ borderTop: `1px solid ${C.faint}`, cursor: "pointer" }}
                      onClick={() => setKolExpandedRows(prev => {
                        const next = new Set(prev);
                        next.has(p.username) ? next.delete(p.username) : next.add(p.username);
                        return next;
                      })}
                      onMouseEnter={e => e.currentTarget.style.background = C.faint}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "8px 10px", fontWeight: 700, color: i < 3 ? "#d97706" : C.muted, fontSize: 11 }}>
                        {i < 3 ? ["①","②","③"][i] : i + 1}
                      </td>
                      <td style={{ padding: "8px 12px", minWidth: 200 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                          {p.avatar && (
                            <img src={p.avatar} alt="" style={{ width: 32, height: 32, borderRadius: 8, flexShrink: 0, objectFit: "cover" }}
                              onError={e => e.target.style.display = "none"} />
                          )}
                          <div style={{ minWidth: 0 }}>
                            <a href={twitterUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                              style={{ fontWeight: 600, fontSize: 13, color: C.text, textDecoration: "none" }}>
                              {p.name || p.username}
                            </a>
                            <div style={{ fontSize: 11, color: C.muted }}>@{p.username} · {(() => { const f = p.followers || 0; return f >= 1000 ? `${(f/1000).toFixed(1).replace(/\.0$/,'')}K` : String(f); })()}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "8px 10px", minWidth: 220 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {p.gain && (
                            <span style={{
                              fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 4,
                              background: p.gain.pct >= 0 ? "#EAF3DE" : "#FCEBEB",
                              color: p.gain.pct >= 0 ? "#3B6D11" : "#A32D2D",
                              whiteSpace: "nowrap",
                            }}>
                              ${p.gain.ticker} {p.gain.pct >= 0 ? '+' : ''}{Number(p.gain.pct).toLocaleString(undefined, { maximumFractionDigits: 2 })}%
                            </span>
                          )}
                          {trendReason ? (
                            <span style={{ fontSize: 11, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }} title={trendReason}>
                              {trendReason}
                            </span>
                          ) : (
                            <span style={{ fontSize: 11, color: C.muted, fontStyle: "italic" }}>
                              {lang === 'en' ? 'No clear reason behind the follows' : '暂无明确原因'}
                            </span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: "8px 10px", minWidth: 130 }}>
                        {(() => {
                          const total = (p.vcCount || 0) + (p.degenCount || 0) + (p.corpCount || 0);
                          const instPct = total > 0 ? Math.round(((p.vcCount || 0) + (p.corpCount || 0)) / total * 100) : 0;
                          const degenPct = total > 0 ? (p.degenCount || 0) / total * 100 : 0;
                          const vcPct    = total > 0 ? (p.vcCount    || 0) / total * 100 : 0;
                          const corpPct  = total > 0 ? (p.corpCount  || 0) / total * 100 : 0;
                          return (
                            <div title={`VC ${p.vcCount || 0} · Degen ${p.degenCount || 0} · Corporate ${p.corpCount || 0}`}>
                              <div style={{ display: "flex", height: 5, borderRadius: 3, overflow: "hidden", background: C.faint, marginBottom: 3 }}>
                                {degenPct > 0 && <div style={{ width: `${degenPct}%`, background: "#a78bfa" }} />}
                                {vcPct > 0 && <div style={{ width: `${vcPct}%`, background: "#60a5fa" }} />}
                                {corpPct > 0 && <div style={{ width: `${corpPct}%`, background: "#34d399" }} />}
                              </div>
                              <div style={{ fontSize: 10, color: C.muted, whiteSpace: "nowrap" }}>
                                <strong style={{ color: C.text }}>{p.totalKols || total}</strong> total · {instPct}% inst
                              </div>
                            </div>
                          );
                        })()}
                      </td>
                      <td style={{ padding: "8px 10px" }}>
                        <span style={{ fontWeight: 700, color: "#639922", fontSize: 14 }}>+{p.newKols || 0}</span>
                      </td>
                      <td style={{ padding: "8px 10px", minWidth: 100 }}>
                        {(p.notableImages && p.notableImages.length > 0) || p.notableCount > 0 ? (
                          <div
                            onClick={e => {
                              e.stopPropagation();
                              setKolExpandedRows(prev => new Set(prev).add(p.username));
                              fetchNotableKolsInline(p);
                            }}
                            style={{ display: "flex", gap: 3, flexWrap: "wrap", cursor: "pointer", padding: "2px 4px", borderRadius: 6, alignItems: "center" }}
                            title={`点击查看 Notable KOLs (共 ${p.notableCount} 人)`}
                            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.06)"}
                            onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                            {(p.notableImages || []).slice(0, 4).map((url, ki) => (
                              <img key={ki} src={proxyImg(url)} alt=""
                                style={{ width: 20, height: 20, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.2)", marginLeft: ki > 0 ? -6 : 0 }}
                                onError={e => { e.target.style.display = "none"; }} />
                            ))}
                            {p.notableCount > 0 && (
                              <span style={{ fontSize: 10, color: "#60a5fa", fontWeight: 600, marginLeft: 2 }}>
                                {(p.notableImages || []).length > 0 && p.notableCount > 4 ? `+${p.notableCount - 4}` : p.notableCount}
                              </span>
                            )}
                          </div>
                        ) : (
                          <span style={{ fontSize: 10, color: C.muted }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "8px 10px", width: 28, textAlign: "center", color: C.muted }}>
                        {kolExpandedRows.has(p.username) ? <ChevronDown size={14} strokeWidth={2} /> : <ChevronRight size={14} strokeWidth={2} />}
                      </td>
                    </tr>,
                    kolExpandedRows.has(p.username) && (
                      <tr key={p.username + '-detail'} style={{ background: C.faint }}>
                        <td colSpan={7} style={{ padding: "10px 16px 14px 40px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 8 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: C.text, lineHeight: 1.4, maxWidth: 640 }}>
                              {trendReason || (lang === 'en' ? 'No clear reason behind the follows' : '暂无明确原因')}
                            </div>
                            <a href={twitterUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                              style={{ flexShrink: 0, fontSize: 11, color: "#60a5fa", textDecoration: "none", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 3 }}>
                              {lang === 'en' ? 'Open on X' : '在 X 上查看'} <ExternalLink size={11} strokeWidth={2} />
                            </a>
                          </div>
                          <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", fontSize: 11, color: C.muted, marginBottom: 10 }}>
                            <span>{(() => { const f = p.followers || 0; return f >= 1000 ? `${(f / 1000).toFixed(1).replace(/\.0$/, '')}K` : String(f); })()} {lang === 'en' ? 'followers' : '粉丝'}</span>
                            <span style={{ color: "#639922", fontWeight: 700 }}>+{p.newKols || 0} {lang === 'en' ? `in ${kolTimeframe}` : `${kolTimeframe} 内新增`}</span>
                            <span><strong style={{ color: C.text }}>{p.totalKols || 0}</strong> {lang === 'en' ? 'KOLs total' : 'KOL 总数'}</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#a78bfa" }} />{p.degenCount || 0} Degen</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#60a5fa" }} />{p.vcCount || 0} VC</span>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}><span style={{ width: 6, height: 6, borderRadius: "50%", background: "#34d399" }} />{p.corpCount || 0} Corporate</span>
                          </div>
                          <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 8 }}>
                            <span style={{
                              fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 3,
                              background: `${identColor}22`, color: identColor,
                            }}>
                              {KOL_IDENTITY_LABELS[identity] || identity}
                            </span>
                            {(() => {
                              const { displayType, type, confidence, signals, refined, refinedLayer, reason, avatarKind } = classification;
                              const cfg = {
                                project: { Icon: Building2, label: lang === 'en' ? 'Project' : '项目', color: '#3b82f6' },
                                person:  { Icon: User,      label: lang === 'en' ? 'Person'  : '个人', color: '#a78bfa' },
                              }[displayType];
                              const conf = confidence >= 0.7 ? '高' : confidence >= 0.4 ? '中' : '低';
                              const confColor = type === 'unsure' || confidence < 0.4 ? '#f59e0b' : cfg.color;
                              const tip = refined
                                ? `${refinedLayer === 3 ? 'Layer 3 视觉' : 'Layer 2 AI'}: ${reason || 'refined'}${avatarKind ? `\nAvatar: ${avatarKind}` : ''}\nsignals: ${JSON.stringify(signals)}`
                                : `signals: ${JSON.stringify(signals)}${type === 'unsure' ? '\n启发式信心不足，已按偏倚强制归类' : ''}`;
                              return (
                                <span title={tip}
                                  style={{
                                    fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 3,
                                    background: `${cfg.color}22`, color: cfg.color, cursor: "help",
                                    border: type === 'unsure' ? `1px dashed ${confColor}` : 'none',
                                    display: "inline-flex", alignItems: "center", gap: 3,
                                  }}>
                                  <cfg.Icon size={10} strokeWidth={2} /> {cfg.label} · <span style={{ color: confColor }}>{conf}信心</span>
                                  {refined && (refinedLayer === 3
                                    ? <Eye size={9} strokeWidth={2} />
                                    : <span style={{ width: 4, height: 4, borderRadius: "50%", background: cfg.color, opacity: 0.6 }} />)}
                                </span>
                              );
                            })()}
                            {bioStructured && bioStructured.role && (
                              <span style={{ fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "#eff6ff", color: "#1d4ed8" }}>
                                {bioStructured.role}
                              </span>
                            )}
                            {bioStructured && Array.isArray(bioStructured.focus) && bioStructured.focus.slice(0, 3).map((f, fi) => (
                              <span key={fi} style={{ fontSize: 9, fontWeight: 600, padding: "1px 5px", borderRadius: 3, background: "#f0fdf4", color: "#15803d" }}>
                                {f}
                              </span>
                            ))}
                            {bioStructured && bioStructured.style && (
                              <span style={{ fontSize: 9, fontWeight: 500, padding: "1px 5px", borderRadius: 3, background: "#f5f3ff", color: "#7c3aed" }}>
                                {bioStructured.style}
                              </span>
                            )}
                          </div>
                          {bio && <div style={{ fontSize: 11, color: C.muted, lineHeight: 1.5, marginBottom: 4, maxWidth: 500 }}>{bio}</div>}
                          {bioStructured && bioStructured.signal && (
                            <div style={{ fontSize: 11, color: "#b45309", lineHeight: 1.5, marginBottom: 8, maxWidth: 500, fontStyle: "italic" }}>
                              {bioStructured.signal}
                            </div>
                          )}
                          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center", fontSize: 11 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                              <span style={{ color: C.muted }}>{lang === 'en' ? 'Category:' : '赛道:'}</span>
                              <select value={userCat || ''} onChange={e => setKolUserTag(p.username, e.target.value || null)}
                                style={{
                                  background: "transparent", border: `1px solid ${catColor}40`, borderRadius: 4,
                                  color: catColor, fontSize: 10, fontWeight: 600, padding: "2px 4px", cursor: "pointer",
                                }}>
                                <option value="">{cat}{userCat ? '' : ' (auto)'}</option>
                                {kolCategories.map(c => <option key={c} value={c}>{c}</option>)}
                              </select>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: 4, color: C.muted }} title={`注册日期: ${createdApprox}`}>
                              <Calendar size={11} strokeWidth={2} /> {createdApprox}
                            </div>
                            <FirstDocCell entry={kolSignalMap[p.name]} lang={lang} mutedColor={C.muted} emptyColor="#475569" />
                            <div style={{ color: C.muted }}>{lang === 'en' ? 'Signal' : '信号'}: <strong style={{ color: sig >= 50 ? "#f97316" : sig >= 20 ? "#fbbf24" : C.muted }}>{sig}</strong></div>
                            {isProject && (
                              <div style={{ display: "flex", alignItems: "center", gap: 4, color: C.muted }} title={lang === 'en' ? 'Surf smart-followers change (7d) — independent signal, keyed by project name not this account' : 'Surf 聪明钱关注者变化（7日）——独立信号源，按项目名匹配，不是这个账号本身的数据'}>
                                Surf: <SmartFollowersBadge name={p.name || p.username} />
                              </div>
                            )}
                            {p.watchlistCount > 0 && (
                              <div style={{ color: C.muted }} title={`${p.watchlistCount} 位分析师已收藏`}>⭐ {p.watchlistCount}</div>
                            )}
                            <div style={{ display: "flex", gap: 6, alignItems: "center", marginLeft: "auto" }}>
                              <button onClick={e => { e.stopPropagation(); toggleKolWatchlist(p.username); }}
                                title={isWatched ? "从 Watchlist 移除" : "加入 Watchlist"}
                                style={{ background: "none", border: "none", cursor: "pointer", padding: "0 2px", display: "flex", color: isWatched ? "#f59e0b" : "#4b5563" }}>
                                <Star size={16} strokeWidth={2} fill={isWatched ? "#f59e0b" : "none"} />
                              </button>
                              {cat === 'Person' ? (
                                <a href={twitterUrl} target="_blank" rel="noopener noreferrer" onClick={e => e.stopPropagation()}
                                  style={{ ...S.btn(), fontSize: 11, padding: "3px 8px", textDecoration: "none", display: "inline-block" }}>
                                  Twitter
                                </a>
                              ) : (() => {
                                const kc = kolCacheMap[p.name] || {};
                                return (
                                  <button onClick={e => { e.stopPropagation(); onImport(p.name || p.username); }}
                                    style={kc.cached ? { ...S.btn("primary"), fontSize: 11, padding: "3px 8px" } : { ...S.btn(), fontSize: 11, padding: "3px 8px" }}>
                                    {kc.cached ? (lang === 'en' ? 'View' : '查看') : (lang === 'en' ? 'Analyze' : '分析')}
                                  </button>
                                );
                              })()}
                            </div>
                          </div>

                          {((p.notableImages && p.notableImages.length > 0) || p.notableCount > 0) && (
                            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>
                                {lang === 'en' ? 'Notable Followers' : '知名关注者'}{p.notableCount ? ` · ${p.notableCount}` : ''}
                              </div>
                              {(() => {
                                const inline = kolNotableInline[p.username];
                                if (!inline) {
                                  return (
                                    <button onClick={e => { e.stopPropagation(); fetchNotableKolsInline(p); }}
                                      style={{ ...S.btn(), fontSize: 11, padding: "4px 10px" }}>
                                      {lang === 'en' ? `Load Notable Followers (${p.notableCount || 0})` : `加载 Notable Followers（共 ${p.notableCount || 0} 人）`}
                                    </button>
                                  );
                                }
                                if (inline.loading) {
                                  return <div style={{ fontSize: 11, color: C.muted }}>{lang === 'en' ? 'Loading…' : '加载中…'}</div>;
                                }
                                if (inline.error) {
                                  return (
                                    <div style={{ fontSize: 11, color: inline.quotaExceeded ? "#f59e0b" : "#ef4444" }}>
                                      {inline.quotaExceeded
                                        ? (lang === 'en' ? 'Daily lookup quota reached — try again later' : 'leak.me 详情查询每日额度已用完，请稍后再试')
                                        : `${lang === 'en' ? 'Failed to load' : '加载失败'}: ${inline.error}`}
                                    </div>
                                  );
                                }
                                if (inline.hasDetailData && inline.kols.length > 0) {
                                  return (
                                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                                      {inline.kols.map((k, ki) => (
                                        <a key={ki} href={`https://twitter.com/${k.username}`} target="_blank" rel="noopener noreferrer"
                                          onClick={e => e.stopPropagation()}
                                          style={{ display: "flex", gap: 8, alignItems: "flex-start", textDecoration: "none", padding: 6, borderRadius: 8 }}
                                          onMouseEnter={e => e.currentTarget.style.background = C.faint}
                                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                                          {k.avatar ? (
                                            <img src={proxyImg(k.avatar)} alt="" style={{ width: 30, height: 30, borderRadius: "50%", flexShrink: 0 }}
                                              onError={e => { e.target.style.display = "none"; }} />
                                          ) : (
                                            <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.faint, flexShrink: 0 }} />
                                          )}
                                          <div style={{ minWidth: 0 }}>
                                            <div style={{ fontSize: 12, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                              {k.name || k.username}
                                            </div>
                                            {k.bio && (
                                              <div style={{ fontSize: 10, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                {k.bio}
                                              </div>
                                            )}
                                          </div>
                                        </a>
                                      ))}
                                    </div>
                                  );
                                }
                                const imgs = inline.images || p.notableImages || [];
                                if (imgs.length === 0) return <div style={{ fontSize: 11, color: C.muted }}>—</div>;
                                return (
                                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                                    {imgs.map((url, ki) => (
                                      <img key={ki} src={proxyImg(url)} alt="" style={{ width: 30, height: 30, borderRadius: "50%" }}
                                        onError={e => { e.target.style.display = "none"; }} />
                                    ))}
                                  </div>
                                );
                              })()}
                            </div>
                          )}

                          <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase", marginBottom: 8 }}>
                              {lang === 'en' ? 'Your Notes' : '我的笔记'}
                            </div>
                            <KolNotesBox noteKey={`kol:${p.username}`} notes={kolNotesMap[`kol:${p.username}`] || []} onAdd={addKolNote} lang={lang} />
                          </div>
                        </td>
                      </tr>
                    ),
                    ]
                  );
                })}
                {kolLoading && kolFiltered.length === 0 && Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                    {[20, 200, 70, 60, 50, 50, 40, 40, 40, 60, 100, 70].map((w, j) => (
                      <td key={j} style={{ padding: "10px 10px" }}>
                        <div style={{ height: 13, width: w, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.15 }} />
                      </td>
                    ))}
                  </tr>
                ))}
                {!kolLoading && kolFiltered.length === 0 && (
                  <tr><td colSpan={12} style={{ padding: 24, textAlign: "center", color: C.muted }}>
                    {kolShowWatchlist && kolWatchlist.length === 0
                      ? (lang === 'en' ? 'No watchlist items yet — click ⭐ to add accounts' : '暂无关注账号 — 点击 ⭐ 添加')
                      : kolItems.length === 0
                        ? (lang === 'en' ? 'Click refresh to load KOL radar data' : '点击刷新加载 KOL 雷达数据')
                        : (lang === 'en' ? 'No results — try adjusting filters' : '暂无结果，尝试调整筛选条件')}
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
          )}
        </div>
        );
      })()}

      {/* ── RootData Hot Panel ─────────────────────── */}
      {subTab === 'hot' && (
      <div>
      {/* Sub-header */}
      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 10, lineHeight: 1.5 }}>
          {lang === 'en'
            ? 'Hot projects where capital hasn\'t arrived yet — find Alpha before institutions do'
            : '热度已起、资本尚未入场的项目 — 在机构进场前发现 Alpha'}
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          {/* 代币状态 */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'en' ? 'Token:' : '代币:'}</span>
            {[
              { key: "no_token", label: lang === 'en' ? 'No Token' : '未发币' },
              { key: "all",      label: lang === 'en' ? 'All'      : '全部' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setTokenFilter(key)}
                style={{ ...S.btn(tokenFilter === key ? "primary" : "default"), fontSize: 11, padding: "3px 10px" }}>
                {label}
              </button>
            ))}
          </div>

          {/* 融资状态 */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'en' ? 'Funding:' : '融资:'}</span>
            {[
              { key: "all",        label: lang === 'en' ? 'All'         : '全部' },
              { key: "low",        label: lang === 'en' ? '< $500K'     : '< $500K' },
              { key: "unfunded",   label: lang === 'en' ? 'RD: No Fund' : 'RD零融资' },
              { key: "dual_clean", label: lang === 'en' ? 'Zero (2-src)' : '双源零融资' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setFundFilter(key)}
                style={{
                  ...S.btn(fundFilter === key ? "primary" : "default"),
                  fontSize: 11, padding: "3px 10px",
                  ...(key === "dual_clean" && fundFilter !== key ? { color: "#f97316", borderColor: "#f97316" } : {}),
                }}>
                {label}
              </button>
            ))}
          </div>

          {/* 赛道 */}
          {allTracks.length > 0 && (
            <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'en' ? 'Track:' : '赛道:'}</span>
              <select value={trackFilter} onChange={e => setTrackFilter(e.target.value)}
                style={{ ...S.input, fontSize: 11, padding: "3px 8px", height: 28 }}>
                <option value="all">{lang === 'en' ? 'All' : '全部'}</option>
                {allTracks.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
          )}

          {/* 项目年龄（首次信号距今） */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'en' ? 'Age:' : '年龄:'}</span>
            {[
              { key: "all",    label: lang === 'en' ? 'All'    : '全部' },
              { key: "<90d",   label: lang === 'en' ? '<90d' : '<90天' },
              { key: "<1y",    label: lang === 'en' ? '<1y'    : '<1年' },
              { key: "1y+",    label: lang === 'en' ? '1y+'    : '1年+' },
            ].map(({ key, label }) => (
              <button key={key} onClick={() => setAgeFilter(key)}
                style={{
                  ...S.btn(ageFilter === key ? "primary" : "default"),
                  fontSize: 11, padding: "3px 10px",
                  ...(key === "<90d" && ageFilter !== key ? { color: "#dc2626", borderColor: "#dc2626" } : {}),
                }}>
                {label}
              </button>
            ))}
          </div>

          {/* 最低评分筛选 */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'en' ? 'Min Score:' : '最低分:'}</span>
            {["", "60", "70", "80"].map(v => (
              <button key={v} onClick={() => setMinScoreFilter(v)}
                style={{ ...S.btn(minScoreFilter === v ? "primary" : "default"), fontSize: 11, padding: "3px 10px" }}>
                {v === "" ? (lang === 'en' ? 'All' : '不限') : `${v}+`}
              </button>
            ))}
          </div>

          {/* 排序 */}
          <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
            <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'en' ? 'Sort:' : '排序:'}</span>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              style={{ ...S.input, fontSize: 11, padding: "3px 8px", height: 28 }}>
              <option value="funding">{lang === 'en' ? 'Funding (low→high)' : '融资（少→多）'}</option>
              <option value="score">{lang === 'en' ? 'AI Score (high→low)' : 'AI 评分（高→低）'}</option>
              <option value="earliest_signal">{lang === 'en' ? 'Newest signal' : '最新冒头'}</option>
              <option value="earliest_signal_desc">{lang === 'en' ? 'Oldest signal' : '老牌项目'}</option>
            </select>
          </div>

          <button
            onClick={() => { try { localStorage.removeItem('early_radar_cache_v2'); } catch {} pull(); }}
            disabled={loading}
            title={lang === 'en' ? 'Force refresh (clear local cache)' : '强制刷新（清除本地 30 分钟缓存）'}
            style={{ ...S.btn(), fontSize: 11, marginLeft: "auto" }}>
            {loading ? "..." : "↻ " + (lang === 'en' ? 'Refresh' : '刷新')}
          </button>
        </div>
      </div>

      {status && <div style={{ fontSize: 11, color: C.muted, marginBottom: 10 }}>{status}</div>}

      {/* 表格（移动端换成卡片列表） */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {filtered.slice(0, 40).map((p, i) => {
            const badge = getFundBadge(p.project_name);
            const fund = fundingMap[p.project_name] || {};
            const isCached = !!fund.cached;
            const gap = <span style={{ color: "#a1a1aa", fontStyle: "italic" }}>{lang === 'en' ? 'After analysis' : '分析后可见'}</span>;
            const aiZh = fund.summary_zh ?? (typeof fund.summary === 'string' && /[一-龥]/.test(fund.summary) ? fund.summary : null);
            const aiEn = fund.summary_en ?? (typeof fund.summary === 'string' && !/[一-龥]/.test(fund.summary) ? fund.summary : null);
            const aiSummary = lang === 'en' ? (aiEn || aiZh) : (aiZh || aiEn);
            const oneLinerIsZh = typeof p.one_liner === 'string' && /[一-龥]/.test(p.one_liner);
            const oneLinerLangMismatch = typeof p.one_liner === 'string' && (lang === 'en' ? oneLinerIsZh : !oneLinerIsZh);
            const techBrief = aiSummary || (oneLinerLangMismatch ? null : p.one_liner);
            const teamMembers = fund.teamMembers || [];
            const teamNames = teamMembers.slice(0, 2).map(m => m.name || m.full_name).filter(Boolean).join('、');
            const gs = fund.githubSignal;
            const teamContent = teamNames
              ? `${teamNames}${teamMembers.length > 2 ? ` +${teamMembers.length - 2}` : ''}`
              : gs?.contributorCount != null
                ? (lang === 'en' ? `${gs.contributorCount} GitHub contributors` : `GitHub ${gs.contributorCount} 位贡献者`)
                : !isCached ? gap
                : <span style={{ color: "#a1a1aa" }}>{lang === 'en' ? 'No public team info' : '未查到公开团队信息'}</span>;
            const opsContent = gs
              ? `GitHub${gs.stars != null ? ` ${gs.stars.toLocaleString()}★` : ''}${gs.commits4w != null ? ` · ${gs.commits4w}${lang === 'en' ? ' commits/4w' : ' 次提交/4周'}` : ''}`
              : !isCached ? gap
              : <span style={{ color: "#a1a1aa" }}>{lang === 'en' ? 'No GitHub data' : '未查到 GitHub 数据'}</span>;
            const fresh = freshnessLabel(fund.scoredAt, lang);
            const heat = heatMap[p.project_name];
            return (
              <MobileListCard
                key={p.project_id}
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: i < 3 ? "#d97706" : C.muted, width: 18, flexShrink: 0 }}>{i < 3 ? ["①", "②", "③"][i] : (p.rank || i + 1)}</span>
                    {p.logo && <img src={proxyLogo(p.logo)} alt="" style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.project_name}</div>
                      <div style={{ fontSize: 11, color: "#a1a1aa" }}>{p.token_symbol ? `$${p.token_symbol}` : "—"}</div>
                    </div>
                  </div>
                }
                badge={fund.composite != null && (() => {
                  const r = rating(fund.composite);
                  return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, background: r.bg, border: `1px solid ${r.border}`, borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>
                      <span style={{ color: r.fg }}>{fund.composite}</span>
                      <span style={{ color: r.fg, fontSize: 10 }}>{r.label}</span>
                    </span>
                  );
                })()}
                tags={
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 12, fontWeight: 600, color: badge.color }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: badge.dot, flexShrink: 0 }} />
                      {badge.label}
                    </span>
                    {(p.tags || []).slice(0, 2).map(tg => (
                      <span key={tg} style={{ fontSize: 12, background: C.faint, color: C.muted, padding: "2px 8px", borderRadius: 6 }}>{tg}</span>
                    ))}
                  </div>
                }
                summary={
                  <div style={{ fontSize: 12, color: C.muted, lineHeight: 1.6 }}>
                    <div><span style={{ color: "#a1a1aa", fontWeight: 700, marginRight: 5 }}>{lang === 'en' ? 'Tech' : '技术'}</span>{techBrief || '—'}</div>
                    <div><span style={{ color: "#a1a1aa", fontWeight: 700, marginRight: 5 }}>{lang === 'en' ? 'Team' : '团队'}</span>{teamContent}</div>
                    <div><span style={{ color: "#a1a1aa", fontWeight: 700, marginRight: 5 }}>{lang === 'en' ? 'Ops' : '运营'}</span>{opsContent}</div>
                  </div>
                }
                footerLeft={
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: fresh?.stale ? "#c2410c" : "#a1a1aa" }}>
                    {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={11} strokeWidth={2} />{fresh.text}</span>}
                    {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={11} strokeWidth={2} />{lang === 'en' ? `${heat} analyses` : `分析 ${heat} 次`}</span>}
                  </div>
                }
                footerRight={
                  <button onClick={() => onImport(p.project_name, p.project_id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 4 }}>
                    {isCached ? (lang === 'en' ? 'View' : '查看') : (lang === 'en' ? 'Analyze' : '分析')}
                    <ChevronRight size={14} strokeWidth={2} />
                  </button>
                }
              />
            );
          })}
          {loading && filtered.length === 0 && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ height: 13, width: 140, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.25 }} />
            </div>
          ))}
          {!loading && filtered.length === 0 && (
            <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
              {lang === 'en' ? 'No projects match the filter' : '暂无符合条件的项目'}
            </div>
          )}
        </div>
      ) : (
      <div style={{ ...S.card, padding: 0, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.faint, fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {(lang === 'en'
                ? ["Rank", "Project", "Token", "Track", "Funding Status", "Score", "First Docs", "Smart Followers", "Overview", "Action"]
                : ["排名", "项目", "代币", "赛道", "融资状态", "评分", "首次文档", "KOL关注", "项目速览", "操作"]
              ).map((h, hi) => {
                const isSignalCol = hi === 6;
                const isScoreCol = hi === 5;
                return (
                  <th key={h}
                    onClick={
                      isSignalCol ? () => setSortBy(sortBy === "earliest_signal" ? "earliest_signal_desc" : sortBy === "earliest_signal_desc" ? "funding" : "earliest_signal")
                      : isScoreCol ? () => setSortBy("score")
                      : undefined
                    }
                    style={{
                      padding: "8px 12px",
                      textAlign: "left",
                      cursor: (isSignalCol || isScoreCol) ? "pointer" : "default",
                      userSelect: "none",
                      ...(isSignalCol && (sortBy === "earliest_signal" || sortBy === "earliest_signal_desc") ? { color: "#2563eb" } : {}),
                      ...(isScoreCol && sortBy === "score" ? { color: "#a78bfa" } : {}),
                    }}
                    title={isSignalCol ? (lang === 'en' ? 'First whitepaper/docs release date — click to sort' : '白皮书/技术文档首次公开日期 — 点击切换排序') : isScoreCol ? (lang === 'en' ? 'AI composite score — click to sort' : 'AI 综合评分 — 点击排序') : undefined}>
                    {h}
                    {isSignalCol && sortBy === "earliest_signal" && " ↑"}
                    {isSignalCol && sortBy === "earliest_signal_desc" && " ↓"}
                    {isScoreCol && sortBy === "score" && " ▼"}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {filtered.slice(0, 40).map((p, i) => {
              const badge = getFundBadge(p.project_name);
              const isDual = badge.dual;
              return (
                <tr key={p.project_id} style={{ borderTop: `1px solid ${C.faint}`, background: isDual ? "rgba(249,115,22,0.05)" : "transparent" }}
                  onMouseEnter={e => e.currentTarget.style.background = isDual ? "rgba(249,115,22,0.1)" : C.faint}
                  onMouseLeave={e => e.currentTarget.style.background = isDual ? "rgba(249,115,22,0.05)" : "transparent"}>
                  <td style={{ padding: "8px 12px", fontWeight: 700, color: i < 3 ? "#d97706" : C.muted, fontSize: 12 }}>
                    {i < 3 ? ["①","②","③"][i] : (p.rank || i + 1)}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {p.logo && <img src={proxyLogo(p.logo)} alt="" style={{ width: 20, height: 20, borderRadius: 4 }} onError={e => e.target.style.display = "none"} />}
                      <span style={{ fontWeight: 600, fontSize: 13 }}>{p.project_name}</span>
                      {(() => {
                        const handle = (p.X || "").split("/").filter(Boolean).pop();
                        const sigs = handle ? hotInvestorSignals[handle.toLowerCase()] : null;
                        if (!sigs || sigs.length === 0) return null;
                        const names = [...new Set(sigs.map(s => s.investor_handle))];
                        return (
                          <span title={lang === 'en' ? `${names.map(n => '@' + n).join(', ')} interacted with this project's tweets` : `${names.map(n => '@' + n).join('、')} 互动过这个项目的推文`}
                            style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 9, fontWeight: 700, padding: "1px 7px", borderRadius: 999, background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" }}>
                            <Eye size={10} strokeWidth={2.5} /> {lang === 'en' ? 'Watched' : '机构关注中'}
                          </span>
                        );
                      })()}
                    </div>
                  </td>
                  <td style={{ padding: "8px 12px", color: C.muted, fontSize: 11 }}>
                    {p.token_symbol ? `$${p.token_symbol}` : "—"}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                      {(p.tags || []).slice(0, 2).map(tag => <span key={tag} style={S.tag}>{tag}</span>)}
                    </div>
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <span style={{
                      display: "inline-flex", alignItems: "center", gap: 5,
                      fontSize: 11, fontWeight: 600,
                      color: badge.color,
                    }}>
                      <span style={{ width: 6, height: 6, borderRadius: "50%", background: badge.dot, flexShrink: 0 }} />
                      {badge.label}
                    </span>
                  </td>
                  {/* AI 评分列 */}
                  <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                    {(() => {
                      const fm = fundingMap[p.project_name];
                      if (!fm?.composite) return <span style={{ color: C.muted, fontSize: 10 }}>—</span>;
                      const r = rating(fm.composite);
                      return (
                        <span title={fm.isCron ? (lang === 'en' ? 'Pre-scored overnight' : '隔夜预评分') : (lang === 'en' ? 'Cached analysis' : '已缓存分析')}
                          style={{ display: "inline-flex", alignItems: "center", gap: 3, background: r.bg, border: `1px solid ${r.border}`, borderRadius: 4, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>
                          <span style={{ color: r.fg, fontFamily: "monospace", fontVariantNumeric: "tabular-nums" }}>{fm.composite}</span>
                          <span style={{ color: r.fg, fontSize: 9 }}>{r.label}</span>
                          {fm.isCron && <span style={{ width: 4, height: 4, borderRadius: "50%", background: r.fg, opacity: 0.6 }} />}
                        </span>
                      );
                    })()}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <FirstDocCell
                      entry={fundingMap[p.project_name]?.firstDoc}
                      querying={!!fundingMap[p.project_name]?.firstDocQuerying}
                      lang={lang}
                      mutedColor={C.muted}
                      emptyColor="#475569"
                    />
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    {i < 10 ? <SmartFollowersBadge name={p.project_name} /> : <span style={{ color: "#475569", fontSize: 10 }}>—</span>}
                  </td>
                  <td style={{ padding: "8px 12px", color: C.muted, fontSize: 11, minWidth: 260, maxWidth: 320 }}>
                    {(() => {
                      const fund = fundingMap[p.project_name] || {};
                      const isCached = !!fund.cached;
                      const gap = <span style={{ color: "#a1a1aa", fontStyle: "italic" }}>{lang === 'en' ? 'Available after analysis' : '分析后可见'}</span>;

                      // 技术：AI 简介优先，退化到 RootData one_liner —— 唯一一个未分析项目也可能有内容的分类
                      const aiZh = fund.summary_zh ?? (typeof fund.summary === 'string' && /[一-龥]/.test(fund.summary) ? fund.summary : null);
                      const aiEn = fund.summary_en ?? (typeof fund.summary === 'string' && !/[一-龥]/.test(fund.summary) ? fund.summary : null);
                      const aiSummary = lang === 'en' ? (aiEn || aiZh) : (aiZh || aiEn);
                      // one_liner 兜底同样需要按语言过滤——RootData 的 one_liner 基本是英文，
                      // 未分析项目在中文界面下之前会原样甩出英文，跟其它几处同款修复。
                      const oneLinerIsZh = typeof p.one_liner === 'string' && /[一-龥]/.test(p.one_liner);
                      const oneLinerLangMismatch = typeof p.one_liner === 'string' && (lang === 'en' ? oneLinerIsZh : !oneLinerIsZh);
                      const techBrief = aiSummary || (oneLinerLangMismatch ? null : p.one_liner);
                      const MAX = lang === 'en' ? 70 : 40;
                      const techTrunc = techBrief && techBrief.length > MAX ? techBrief.slice(0, MAX).trim() + '…' : techBrief;

                      // 团队：优先 raw_data.teamMembers（完整分析后的具名团队）；
                      // 未分析项目退化到免费 GitHub 贡献者数（同一批 get_item 顺手查的，见 pull() 步骤 3a）
                      const teamMembers = fund.teamMembers || [];
                      const teamNames = teamMembers.slice(0, 2).map(m => m.name || m.full_name).filter(Boolean).join('、');
                      const gs = fund.githubSignal;
                      const teamContent = teamNames
                        ? `${teamNames}${teamMembers.length > 2 ? ` +${teamMembers.length - 2}` : ''}`
                        : gs?.contributorCount != null
                          ? (lang === 'en' ? `${gs.contributorCount} GitHub contributors` : `GitHub ${gs.contributorCount} 位贡献者`)
                          : !isCached ? gap
                          : <span style={{ color: "#a1a1aa" }}>{lang === 'en' ? 'No public team info' : '未查到公开团队信息'}</span>;

                      // 运营：raw_data.githubSignal（完整分析）或免费预查（未分析项目）共用同一渲染
                      const opsContent = gs
                        ? `GitHub${gs.stars != null ? ` ${gs.stars.toLocaleString()}★` : ''}${gs.commits4w != null ? ` · ${gs.commits4w}${lang === 'en' ? ' commits/4w' : ' 次提交/4周'}` : ''}`
                        : !isCached ? gap
                        : <span style={{ color: "#a1a1aa" }}>{lang === 'en' ? 'No GitHub data' : '未查到 GitHub 数据'}</span>;

                      const catStyle = { display: "flex", gap: 5, marginBottom: 2, alignItems: "flex-start", lineHeight: 1.4 };
                      const labelStyle = { color: C.muted, fontSize: 9.5, fontWeight: 700, minWidth: 24, flexShrink: 0, marginTop: 1 };

                      return (
                        <div>
                          <div style={catStyle} title={techBrief}>
                            <span style={labelStyle}>{lang === 'en' ? 'Tech' : '技术'}</span>
                            <span style={{ flex: 1 }}>
                              {techTrunc ? (<>{aiSummary && <span style={{ color: C.muted, fontWeight: 700, marginRight: 3, fontSize: 9 }}>AI</span>}{techTrunc}</>) : <span style={{ color: "#a1a1aa" }}>—</span>}
                            </span>
                          </div>
                          <div style={catStyle}>
                            <span style={labelStyle}>{lang === 'en' ? 'Biz' : '商业'}</span>
                            <span style={{ flex: 1, color: "#a1a1aa", fontStyle: "italic" }}>{lang === 'en' ? 'No data source yet' : '暂无数据源'}</span>
                          </div>
                          <div style={catStyle}>
                            <span style={labelStyle}>{lang === 'en' ? 'Team' : '团队'}</span>
                            <span style={{ flex: 1 }}>{teamContent}</span>
                          </div>
                          <div style={catStyle}>
                            <span style={labelStyle}>{lang === 'en' ? 'Ops' : '运营'}</span>
                            <span style={{ flex: 1 }}>{opsContent}</span>
                          </div>
                        </div>
                      );
                    })()}
                    {(() => {
                      const fund = fundingMap[p.project_name] || {};
                      const fresh = freshnessLabel(fund.scoredAt, lang);
                      const heat = heatMap[p.project_name];
                      if (!fresh && !heat) return null;
                      return (
                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 3, fontSize: 9.5, color: fresh?.stale ? "#c2410c" : C.muted }}>
                          {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={9} strokeWidth={2} />{fresh.text}</span>}
                          {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={9} strokeWidth={2} />{lang === 'en' ? `${heat}x` : `分析${heat}次`}</span>}
                        </div>
                      );
                    })()}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    {(() => {
                      const cached = !!fundingMap[p.project_name]?.cached;
                      return (
                        <button onClick={() => onImport(p.project_name, p.project_id)}
                          style={cached ? { ...S.btn("primary"), fontSize: 11, padding: "3px 8px" } : { ...S.btn(), fontSize: 11, padding: "3px 8px" }}>
                          {cached ? (lang === 'en' ? 'View' : '查看') : (lang === 'en' ? 'Analyze' : '分析')}
                        </button>
                      );
                    })()}
                  </td>
                </tr>
              );
            })}
            {loading && filtered.length === 0 && Array.from({ length: 6 }).map((_, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                {[24, 140, 60, 80, 100, 50, 80, 100, 200, 60].map((w, j) => (
                  <td key={j} style={{ padding: "10px 12px" }}>
                    <div style={{ height: 13, width: w, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.1 }} />
                  </td>
                ))}
              </tr>
            ))}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={10} style={{ padding: 24, textAlign: "center", color: C.muted }}>
                {lang === 'en' ? 'No projects match the filter' : '暂无符合条件的项目'}
              </td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      <div style={{ marginTop: 8, fontSize: 10, color: C.muted, textAlign: "right" }}>
        {lang === 'en'
          ? `Dual-source = RootData zero + fundraise.fun (9,700+ Western VC deals) zero · ${Object.keys(ffMap).length > 0 ? `FF cache loaded (${Object.keys(ffMap).length} checked)` : 'FF loading...'}`
          : `双源零融资 = RootData无融资 + fundraise.fun（9,700+ 西方VC数据）无记录 · ${Object.keys(ffMap).length > 0 ? `FF已核查 ${Object.keys(ffMap).length} 个` : 'FF加载中...'}`}
      </div>

      </div>
      )}

      {/* ── Notable KOLs Modal (rendered here inside EarlyRadarPanel where state lives) ── */}
      {kolNotablePopup && (
        <div
          onClick={() => setKolNotablePopup(null)}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
            background: "rgba(0,0,0,0.75)", zIndex: 1000,
            display: "flex", alignItems: "center", justifyContent: "center",
            backdropFilter: "blur(4px)",
          }}>
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#1e293b", border: "1px solid #334155",
              borderRadius: 14, padding: "24px 28px", maxWidth: 660, width: "92vw",
              maxHeight: "80vh", overflowY: "auto",
              boxShadow: "0 24px 60px rgba(0,0,0,0.6)",
            }}>
            {/* Header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 700, color: "#f1f5f9" }}>Notable KOLs</div>
                <div style={{ fontSize: 12, color: "#64748b", marginTop: 2 }}>
                  {kolNotablePopup.name} 的关注者
                  {kolNotablePopup.total > 0 && <span style={{ marginLeft: 6, color: "#60a5fa" }}>({kolNotablePopup.total} 人)</span>}
                </div>
              </div>
              <button onClick={() => setKolNotablePopup(null)}
                style={{ background: "none", border: "none", color: "#64748b", cursor: "pointer", fontSize: 22, lineHeight: 1, padding: "0 4px" }}>
                ×
              </button>
            </div>
            {/* Loading pulse */}
            {kolNotablePopup.loading && (
              <div style={{ textAlign: "center", paddingBottom: 16, color: "#60a5fa", fontSize: 11 }}>
                ⏳ 正在加载详细数据...
              </div>
            )}

            {/* Error state: leak.me 详情接口出错（含每日额度用完） */}
            {!kolNotablePopup.loading && kolNotablePopup.error && (
              <div style={{ textAlign: "center", padding: 24, color: kolNotablePopup.quotaExceeded ? "#f59e0b" : "#ef4444", fontSize: 12, lineHeight: 1.6 }}>
                {kolNotablePopup.quotaExceeded
                  ? '⏳ leak.me 详情查询每日额度已用完，请稍后再试'
                  : `⚠️ 加载失败：${kolNotablePopup.error}`}
              </div>
            )}

            {/* Mode A: detail data available → full table */}
            {!kolNotablePopup.loading && kolNotablePopup.hasDetailData && kolNotablePopup.kols.length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <tbody>
                  {kolNotablePopup.kols.map((k, ki) => (
                    <tr key={ki} style={{ borderBottom: "1px solid #1e3a5f" }}
                      onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.04)"}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "10px 8px", width: 44 }}>
                        {k.avatar ? (
                          <img src={proxyImg(k.avatar)} alt="" style={{ width: 36, height: 36, borderRadius: "50%", display: "block" }}
                            onError={e => e.target.style.display="none"} />
                        ) : (
                          <div style={{ width: 36, height: 36, borderRadius: "50%", background: "#334155", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, color: "#94a3b8" }}>
                            {(k.username||'?')[0].toUpperCase()}
                          </div>
                        )}
                      </td>
                      <td style={{ padding: "10px 12px" }}>
                        <a href={`https://twitter.com/${k.username}`} target="_blank" rel="noopener noreferrer"
                          style={{ fontWeight: 600, fontSize: 14, color: "#f1f5f9", textDecoration: "none" }}>
                          {k.name || k.username}
                        </a>
                        {k.bio && <div style={{ fontSize: 11, color: "#94a3b8", marginTop: 2 }}>{k.bio}</div>}
                      </td>
                      <td style={{ padding: "10px 12px", color: "#64748b", fontSize: 12, textAlign: "right", whiteSpace: "nowrap" }}>
                        {k.followers ? Number(k.followers).toLocaleString() : ''}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* Mode B: 4-column avatar grid (always show when not loading + no detail table) */}
            {!kolNotablePopup.loading && !kolNotablePopup.error && !kolNotablePopup.hasDetailData && (() => {
              const imgs = kolNotablePopup.images || [];
              const total = Math.max(kolNotablePopup.total || 0, imgs.length);
              const PALETTE = ["#6366f1","#f59e0b","#10b981","#ef4444","#3b82f6","#ec4899","#14b8a6","#8b5cf6"];
              if (total === 0) return null;
              const totalSlots = Math.min(total, 8);
              return (
                <div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <span style={{ fontSize: 12, color: "#94a3b8" }}>
                      {`共 ${total} 位 Notable KOL 关注了该账号`}
                    </span>
                    <a href="https://leak.me" target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: 11, color: "#60a5fa", textDecoration: "none" }}>
                      leak.me 查看全部 →
                    </a>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 14, padding: "4px 0 18px" }}>
                    {Array.from({ length: totalSlots }).map((_, idx) => {
                      const url = imgs[idx];
                      const color = PALETTE[idx % PALETTE.length];
                      return (
                        <div key={idx} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                          {url ? (
                            <img src={proxyImg(url)} alt=""
                              style={{ width: 56, height: 56, borderRadius: "50%", border: `2px solid ${color}`, objectFit: "cover", display: "block" }}
                              onError={e => {
                                const parent = e.target.parentNode;
                                e.target.style.display = "none";
                                const ph = document.createElement("div");
                                ph.style.cssText = `width:56px;height:56px;border-radius:50%;background:${color}44;border:2px solid ${color};display:flex;align-items:center;justify-content:center;color:#fff;font-size:20px;font-weight:700`;
                                ph.textContent = String(idx + 1);
                                parent.insertBefore(ph, e.target);
                              }} />
                          ) : (
                            <div style={{ width: 56, height: 56, borderRadius: "50%", background: `${color}44`, border: `2px solid ${color}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 20, fontWeight: 700 }}>
                              {idx + 1}
                            </div>
                          )}
                          <span style={{ fontSize: 10, color: "#94a3b8" }}>#{idx + 1}</span>
                        </div>
                      );
                    })}
                    {total > totalSlots && (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 6 }}>
                        <div style={{ width: 56, height: 56, borderRadius: "50%", background: "#1e3a5f", border: "2px dashed #334155", display: "flex", alignItems: "center", justifyContent: "center", color: "#60a5fa", fontSize: 13, fontWeight: 700 }}>
                          +{total - totalSlots}
                        </div>
                        <span style={{ fontSize: 10, color: "#94a3b8" }}>more</span>
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: 11, color: "#475569", padding: "10px 0 0", borderTop: "1px solid #1e3a5f", lineHeight: 1.6 }}>
                    💡 仅有头像缩略图，暂无完整用户名和角色信息。
                  </div>
                </div>
              );
            })()}

            {/* Empty state */}
            {!kolNotablePopup.loading && !kolNotablePopup.error && !kolNotablePopup.hasDetailData &&
              !(Math.max(kolNotablePopup.total || 0, (kolNotablePopup.images || []).length) > 0) && (
              <div style={{ textAlign: "center", padding: 32, color: "#475569" }}>
                <div style={{ fontSize: 13 }}>暂无 Notable KOL 数据</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  TRENDING PAGE
// ══════════════════════════════════════════════════════
// 人才背景信号徽章——GitHub bio/company 关键词直接命中原文，不是 AI 猜的，用紫色实心
// pill 跟其他信号区分开（见 EarlyBirdTab.jsx 的 PedigreeRow 同一套视觉逻辑）。
// 只显示第一条命中，多条时用 title 悬浮展示全部，避免卡片/表格行被撑开。
function PedigreeBadge({ hits }) {
  if (!hits?.length) return null;
  const first = hits[0];
  const title = hits.map(h => `${h.name}：${h.evidence}`).join('\n');
  return (
    <span title={title} style={{
      display: "inline-flex", alignItems: "center", gap: 3, fontSize: 10,
      padding: "2px 7px", borderRadius: 4, fontWeight: 700,
      background: "#faf5ff", color: "#9333ea", whiteSpace: "nowrap",
    }}>
      {first.type === 'repeat_founder' ? '🔁' : '🏛'} {first.name}
    </span>
  );
}

// 从"完整项目对象"（Deal Ranking/收藏夹用的 project 形状，enhancedTeam 直接挂在顶层，
// 不像 project_cache 那样包一层 raw_data）里提取人才背景信号——跟 TrendingPage pull() 里
// 内联那段逻辑等价，只是数据外层结构不同，抽成一个函数避免两处各写一遍。
function pedigreeHitsOf(project) {
  return (project?.enhancedTeam?.topContributors || [])
    .filter(c => c.pedigreeSignal)
    .map(c => ({ name: c.name, type: c.pedigreeSignal.type, evidence: c.pedigreeSignal.evidence }));
}

// ══════════════════════════════════════════════════════
//  TALENT RADAR — 跨项目人才背景信号聚合视图
// ══════════════════════════════════════════════════════
// V1 只覆盖已上线项目（见 services/talentRadar.js 顶部注释）。跨项目参与度
// 不是独立字段，是 hits.length > 1 的自然结果——同一个人在多个项目的
// topContributors 里都命中，本身就说明他同时活跃在几个项目。
function TalentRadarPage({ onImport }) {
  const { lang } = useLang();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'elite_institution' | 'repeat_founder'

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchTalentRadar().then(rows => { if (!cancelled) { setEntries(rows); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  const filtered = entries.filter(e => {
    if (filter === 'all') return true;
    return e.hits.some(h => h.type === filter || h.type === 'both');
  });

  return (
    <div>
      <div style={{ marginBottom: 4 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{lang === 'en' ? 'Talent Radar' : '人才雷达'}</h2>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
          {lang === 'en'
            ? 'Team members flagged for elite-institution or repeat-founder background, across all analyzed projects'
            : '跨所有已分析项目，标记出有顶尖机构背景或连续创业记录的团队成员'}
        </div>
      </div>

      <div style={{ display: "flex", gap: 6, margin: "12px 0" }}>
        {[
          { val: 'all', label: lang === 'en' ? 'All' : '全部' },
          { val: 'elite_institution', label: lang === 'en' ? '🏛 Elite institution' : '🏛 顶尖机构迁移' },
          { val: 'repeat_founder', label: lang === 'en' ? '🔁 Repeat founder' : '🔁 连续创业者' },
        ].map(({ val, label }) => (
          <button key={val} onClick={() => setFilter(val)} style={{ ...S.btn(filter === val ? "primary" : "default"), fontSize: 11, padding: "4px 12px" }}>
            {label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 12 }}>{lang === 'en' ? 'Loading…' : '加载中…'}</div>
      ) : filtered.length === 0 ? (
        <div style={{ padding: 40, textAlign: "center", color: C.muted, fontSize: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 }}>
          {lang === 'en' ? 'No signals found yet in analyzed projects.' : '已分析的项目里暂时还没有查到这类信号。'}
        </div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.faint, fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>
                {(lang === 'en' ? ["Person", "Project(s)", "Signal", "Evidence"] : ["人物", "项目", "信号", "依据"]).map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(e => (
                <tr key={e.key} style={{ borderTop: `1px solid ${C.faint}` }}>
                  <td style={{ padding: "8px 12px", fontWeight: 700 }}>
                    {e.name}
                    {e.hits.length > 1 && (
                      <span title={lang === 'en' ? 'Active across multiple projects' : '同时活跃在多个项目'}
                        style={{ marginLeft: 6, fontSize: 9, padding: "1px 6px", borderRadius: 999, background: "#eff6ff", color: "#2563eb", fontWeight: 700 }}>
                        {e.hits.length} {lang === 'en' ? 'projects' : '个项目'}
                      </span>
                    )}
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {e.hits.map((h, i) => (
                        <button key={i} onClick={() => onImport(h.project)} style={{ background: "none", border: "none", padding: 0, textAlign: "left", color: "#2563eb", cursor: "pointer", fontSize: 11 }}>
                          {h.project}
                        </button>
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: "8px 12px" }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                      {e.hits.map((h, i) => (
                        <span key={i} style={{ fontSize: 10, padding: "2px 7px", borderRadius: 4, fontWeight: 700, background: "#faf5ff", color: "#9333ea", whiteSpace: "nowrap", width: "fit-content" }}>
                          {(h.type === 'repeat_founder' || h.type === 'both') ? '🔁' : ''}{h.type === 'both' ? ' ' : ''}{(h.type === 'elite_institution' || h.type === 'both') ? '🏛' : ''}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td style={{ padding: "8px 12px", color: C.muted, fontSize: 11, maxWidth: 360 }}>
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                      {e.hits.map((h, i) => <div key={i}>{h.evidence}</div>)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function TrendingPage({ onImport }) {
  const t = useT();
  const { lang } = useLang();
  const isMobile = useIsMobile();
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(1);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [signalMap, setSignalMap] = useState({}); // { name: { firstDoc, summary, composite, scores } }
  const [heatMap, setHeatMap] = useState({}); // { name: 分析次数 } — 见 getAnalysisHeatMap
  const [trendSort, setTrendSort] = useState("rank");   // "rank" | "score"
  const [trendMinScore, setTrendMinScore] = useState(""); // "" | "60" | "70" | "80"

  const pull = async () => {
    setLoading(true); setStatus(lang === 'en' ? "Fetching hot list..." : "拉取热榜...");
    try {
      const data = await fetchTrending(days);
      const items20 = (data || []).slice(0, 20);
      setItems(items20);
      setStatus(`Top ${items20.length}`);

      // 1) 反查 project_cache 拿已分析项目的 summary + firstDoc
      //    用 cacheIsDisplayable 与 PipelinePage / getCached 对齐：缓存能渲染
      //    就显示「查看」，避免老的 _warmed / _lightweight 写入弄出空雷达图。
      //    缺 complianceRisk 等新字段时 getCached 会返回数据 + flag，
      //    所以这里不必再过滤。
      const names = items20.map(p => p.project_name).filter(Boolean);
      if (names.length > 0) {
        const { supabase } = await import('./lib/supabase');
        const { data: cachedRows } = await supabase
          .from('project_cache')
          .select('project_name, raw_data, analysis, scored_at')
          .in('project_name', names)
          .gt('expires_at', new Date().toISOString());
        const map = {};
        (cachedRows || []).forEach(row => {
          if (!cacheIsDisplayable(row)) return;
          const tl = row.raw_data?.timeline || [];
          const firstDoc = tl.find(t => t.key === 'firstDoc');
          // 人才背景信号：跟 composite/scores 同一份缓存里已经有的数据（enhancedTeam.topContributors
          // 的 pedigreeSignal，见 teamEnhanced.js），零新增读取，只是这个视图之前没抽出来用
          const pedigreeHits = (row.raw_data?.enhancedTeam?.topContributors || [])
            .filter(c => c.pedigreeSignal)
            .map(c => ({ name: c.name, type: c.pedigreeSignal.type, evidence: c.pedigreeSignal.evidence }));
          map[row.project_name] = {
            summary_zh: row.analysis?.summary || null,
            summary_en: row.analysis?.summary_en || null,
            firstDoc: firstDoc ? { date: firstDoc.date, source: firstDoc.source, sourceType: firstDoc.sourceType, url: firstDoc.url } : null,
            // AI 评分
            composite: row.analysis?.composite ?? null,
            scores: row.analysis?.scores ?? null,
            isCron: !!row.analysis?._cron,
            scoredAt: row.scored_at || null,
            pedigreeHits,
          };
        });
        getAnalysisHeatMap(names).then(setHeatMap);

        // 1.5) 全局 24h localStorage 缓存补齐（覆盖未在 project_cache 中的）
        const lsCache = loadFirstDocCache();
        Object.entries(lsCache).forEach(([name, e]) => {
          if (!map[name]?.firstDoc && names.includes(name)) {
            map[name] = { ...(map[name] || {}), firstDoc: e.firstDoc, probed: true };
          }
        });
        setSignalMap(map);

        // 2) 后台服务端批量探测：未缓存 firstDoc 的全部 20 项（分 2 批，每批 10）
        const uncached = items20.filter(p => !map[p.project_name]?.firstDoc && !map[p.project_name]?.probed);
        if (uncached.length > 0) {
          for (let bi = 0; bi < uncached.length; bi += 10) {
            const batch = uncached.slice(bi, bi + 10);
            try {
              const batchRes = await fetch('/api/firstdoc-batch', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ projects: batch.map(p => ({ project_id: p.project_id, project_name: p.project_name })) }),
              });
              const batchJson = await batchRes.json();
              if (batchJson.ok && batchJson.results) {
                const lsBatch = {};
                Object.entries(batchJson.results).forEach(([name, fd]) => {
                  // 新 error 形状：不缓存，让下次重试
                  if (fd && typeof fd === 'object' && fd.error) {
                    map[name] = { ...(map[name] || {}), firstDoc: null, probed: false };
                    return;
                  }
                  if (fd && fd.date) map[name] = { ...(map[name] || {}), firstDoc: fd, probed: true };
                  else if (!map[name]) map[name] = { firstDoc: null, probed: true };
                  else map[name] = { ...map[name], probed: true };
                  lsBatch[name] = fd; // 包括 null，让缓存知道"已探测无果"
                });
                saveFirstDocCacheBatch(lsBatch);
                setSignalMap({ ...map });
              }
            } catch { /* 静默失败 */ }
            if (bi + 10 < uncached.length) await new Promise(r => setTimeout(r, 500));
          }
        }
      }
    } catch (e) { setStatus("⚠ " + e.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { pull(); }, [days]);

  // 排序 + 筛选
  const trendDisplayItems = [...items]
    .filter(p => {
      if (trendMinScore === "") return true;
      const composite = signalMap[p.project_name]?.composite;
      return composite && composite >= Number(trendMinScore);
    })
    .sort((a, b) => {
      if (trendSort === "score") {
        const sa = signalMap[a.project_name]?.composite ?? -1;
        const sb = signalMap[b.project_name]?.composite ?? -1;
        return sb - sa;
      }
      return 0; // "rank" = 保持原序
    });

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{lang === 'en' ? 'Secondary Hot List' : '二级热榜'}</h2>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{status}</div>
        </div>
        <div style={{ display: "flex", gap: 6 }}>
          {[1, 7].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{ ...S.btn(days === d ? "primary" : "default") }}>
              {d === 1 ? (lang === 'en' ? 'Today' : '今日') : (lang === 'en' ? 'This Week' : '本周')}
            </button>
          ))}
        </div>
      </div>

      {/* 筛选/排序行 */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>{lang === 'en' ? 'Sort:' : '排序:'}</span>
        {[
          { val: "rank",  label: lang === 'en' ? 'Hot Rank' : '热度排名' },
          { val: "score", label: lang === 'en' ? 'AI Score ▼' : 'AI 评分 ▼' },
        ].map(({ val, label }) => (
          <button key={val} onClick={() => setTrendSort(val)}
            style={{ ...S.btn(trendSort === val ? "primary" : "default"), fontSize: 11, padding: "3px 10px" }}>
            {label}
          </button>
        ))}
        <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", marginLeft: 8 }}>{lang === 'en' ? 'Min Score:' : '最低分:'}</span>
        {["", "60", "70", "80"].map(v => (
          <button key={v} onClick={() => setTrendMinScore(v)}
            style={{ ...S.btn(trendMinScore === v ? "primary" : "default"), fontSize: 11, padding: "3px 10px" }}>
            {v === "" ? (lang === 'en' ? 'All' : '不限') : `${v}+`}
          </button>
        ))}
      </div>

      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {trendDisplayItems.map((p, i) => {
            const sg = signalMap[p.project_name] || {};
            const aiZh = sg.summary_zh ?? (typeof sg.summary === 'string' && /[一-龥]/.test(sg.summary) ? sg.summary : null);
            const aiEn = sg.summary_en ?? (typeof sg.summary === 'string' && !/[一-龥]/.test(sg.summary) ? sg.summary : null);
            const aiSummary = lang === 'en' ? (aiEn || aiZh) : (aiZh || aiEn);
            // 双向语言过滤：原来只挡"英文界面显示中文 one_liner"，反过来"中文界面显示
            // 英文 one_liner"完全没挡——RootData 的 one_liner 大多是英文，未分析过的项目
            // 落到这个兜底分支时，中文模式下会原样甩出一整句英文。跟 EN 分支同一套判断，
            // 只是不再单向锁死在 lang==='en'。
            const oneLinerIsZh = typeof p.one_liner === 'string' && /[一-龥]/.test(p.one_liner);
            const oneLinerLangMismatch = typeof p.one_liner === 'string' && (lang === 'en' ? oneLinerIsZh : !oneLinerIsZh);
            const brief = aiSummary || (oneLinerLangMismatch ? null : p.one_liner);
            const briefUntranslated = lang === 'en' && !!aiSummary && !aiEn;
            const fresh = freshnessLabel(sg.scoredAt, lang);
            const heat = heatMap[p.project_name];
            const hasCache = !!(sg.summary_zh || sg.summary_en);
            return (
              <MobileListCard
                key={p.project_id}
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: i < 3 ? "#d97706" : C.muted, width: 18, flexShrink: 0 }}>{i < 3 ? ["①", "②", "③"][i] : p.rank}</span>
                    {p.logo && <img src={proxyLogo(p.logo)} alt="" style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.project_name}</div>
                      <div style={{ fontSize: 11, color: "#a1a1aa" }}>{p.token_symbol ? `$${p.token_symbol}` : "—"}</div>
                    </div>
                    <PedigreeBadge hits={sg.pedigreeHits} />
                  </div>
                }
                badge={sg.composite != null && (() => {
                  const r = rating(sg.composite);
                  return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 3, background: r.bg, border: `1px solid ${r.border}`, borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>
                      <span style={{ color: r.fg }}>{sg.composite}</span>
                      <span style={{ color: r.fg, fontSize: 10 }}>{r.label}</span>
                      {sg.isCron && <Flame size={10} strokeWidth={2} style={{ color: r.fg, opacity: 0.7 }} />}
                    </span>
                  );
                })()}
                tags={(p.tags || []).length > 0 && (
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                    {(p.tags || []).slice(0, 2).map(tg => (
                      <span key={tg} style={{ fontSize: 12, background: C.faint, color: C.muted, padding: "2px 8px", borderRadius: 6 }}>{tg}</span>
                    ))}
                  </div>
                )}
                summary={brief && (
                  <div>
                    <div style={{ fontSize: 13, color: C.muted, lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {aiSummary && <span style={{ color: "#a1a1aa", fontWeight: 700, marginRight: 4, fontSize: 11 }}>AI</span>}
                      {brief}
                    </div>
                    {briefUntranslated && t('misc.ai_content_note') && (
                      <div style={{ color: "#a1a1aa", fontSize: 10, marginTop: 1 }}>{t('misc.ai_content_note')}</div>
                    )}
                  </div>
                )}
                footerLeft={
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: fresh?.stale ? "#c2410c" : "#a1a1aa" }}>
                    {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={11} strokeWidth={2} />{fresh.text}</span>}
                    {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={11} strokeWidth={2} />{lang === 'en' ? `${heat} analyses` : `分析 ${heat} 次`}</span>}
                  </div>
                }
                footerRight={
                  <button onClick={() => onImport(p.project_name, p.project_id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.text, display: "flex", alignItems: "center", gap: 4 }}>
                    {hasCache ? (lang === 'en' ? 'View' : '查看') : (lang === 'en' ? 'Analyze' : '分析')}
                    <ChevronRight size={14} strokeWidth={2} />
                  </button>
                }
              />
            );
          })}
          {loading && items.length === 0 && Array.from({ length: 3 }).map((_, i) => (
            <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 14 }}>
              <div style={{ height: 13, width: 140, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.25 }} />
            </div>
          ))}
          {items.length === 0 && !loading && (
            <div style={{ padding: 24, textAlign: "center", color: C.muted, fontSize: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>暂无数据</div>
          )}
        </div>
      ) : (
      <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ background: C.faint, fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
              {(lang === 'en' ? ["Rank", "Project", "Token", "Track", "Score", "First Docs", "Brief", "Action"] : ["排名", "项目", "代币", "赛道", "评分", "首次文档", "简介", "操作"]).map(h => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", cursor: (h === "Score" || h === "评分") ? "pointer" : "default" }}
                  onClick={(h === "Score" || h === "评分") ? () => setTrendSort(trendSort === "score" ? "rank" : "score") : undefined}
                  title={h.includes('Docs') || h.includes('文档') ? (lang === 'en' ? 'First whitepaper/docs release date' : '白皮书/技术文档首次公开日期') : undefined}>
                  {h}{(h === "Score" || h === "评分") && trendSort === "score" ? " ▼" : ""}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {trendDisplayItems.map((p, i) => (
              <tr key={p.project_id} style={{ borderTop: `1px solid ${C.faint}` }}
                onMouseEnter={e => e.currentTarget.style.background = C.faint}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                <td style={{ padding: "8px 12px", fontWeight: 700, color: i < 3 ? "#d97706" : C.muted }}>
                  {i < 3 ? ["①","②","③"][i] : p.rank}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {p.logo && <img src={proxyLogo(p.logo)} alt="" style={{ width: 22, height: 22, borderRadius: 5 }} onError={e => e.target.style.display = "none"} />}
                    <span style={{ fontWeight: 600 }}>{p.project_name}</span>
                    <PedigreeBadge hits={signalMap[p.project_name]?.pedigreeHits} />
                  </div>
                </td>
                <td style={{ padding: "8px 12px", color: C.muted, fontSize: 11 }}>{p.token_symbol ? `$${p.token_symbol}` : "—"}</td>
                <td style={{ padding: "8px 12px" }}>
                  <div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                    {(p.tags || []).slice(0, 2).map(t => <span key={t} style={S.tag}>{t}</span>)}
                  </div>
                </td>
                {/* AI 评分列 */}
                <td style={{ padding: "8px 12px", whiteSpace: "nowrap" }}>
                  {(() => {
                    const sg = signalMap[p.project_name];
                    if (!sg?.composite) return <span style={{ color: C.muted, fontSize: 10 }}>—</span>;
                    const r = rating(sg.composite);
                    return (
                      <span title={sg.isCron ? (lang === 'en' ? 'Pre-scored overnight' : '隔夜预评分') : (lang === 'en' ? 'Cached analysis' : '已缓存分析')}
                        style={{ display: "inline-flex", alignItems: "center", gap: 3, background: r.bg, border: `1px solid ${r.border}`, borderRadius: 4, padding: "1px 6px", fontSize: 10, fontWeight: 700 }}>
                        <span style={{ color: r.fg }}>{sg.composite}</span>
                        <span style={{ color: r.fg, fontSize: 9 }}>{r.label}</span>
                        {sg.isCron && <span style={{ fontSize: 9, color: r.fg, opacity: 0.7 }}>⚡</span>}
                      </span>
                    );
                  })()}
                </td>
                <td style={{ padding: "8px 12px", fontSize: 11, whiteSpace: "nowrap" }}>
                  <FirstDocCell
                    entry={signalMap[p.project_name]?.firstDoc}
                    probed={!!signalMap[p.project_name]?.probed}
                    lang={lang}
                    mutedColor={C.muted}
                  />
                </td>
                <td style={{ padding: "8px 12px", color: C.muted, fontSize: 11, maxWidth: 280 }}>
                  {(() => {
                    const sg = signalMap[p.project_name] || {};
                    const aiZh = sg.summary_zh ?? (typeof sg.summary === 'string' && /[一-龥]/.test(sg.summary) ? sg.summary : null);
                    const aiEn = sg.summary_en ?? (typeof sg.summary === 'string' && !/[一-龥]/.test(sg.summary) ? sg.summary : null);
                    const aiSummary = lang === 'en' ? (aiEn || aiZh) : (aiZh || aiEn);
                    const oneLinerIsZh = typeof p.one_liner === 'string' && /[一-龥]/.test(p.one_liner);
                    const oneLinerLangMismatch = typeof p.one_liner === 'string' && (lang === 'en' ? oneLinerIsZh : !oneLinerIsZh);
                    const brief = aiSummary || (oneLinerLangMismatch ? null : p.one_liner);
                    if (!brief) return <span style={{ color: "#475569" }}>—</span>;
                    const untranslated = lang === 'en' && !!aiSummary && !aiEn;
                    const MAX = lang === 'en' ? 110 : 60;
                    const truncated = brief.length > MAX ? brief.slice(0, MAX).trim() + '…' : brief;
                    // 未翻译提示放在 WebkitLineClamp 容器外面单独一行——放在里面会被
                    // clamp 直接裁掉（中文字符更宽，2 行经常被正文占满，提示不可见）
                    return (
                      <div>
                        <div style={{ display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", lineHeight: 1.5 }} title={brief}>
                          {aiSummary && <span style={{ color: C.muted, fontWeight: 700, marginRight: 4, fontSize: 9, letterSpacing: 0.4 }}>AI</span>}
                          {truncated}
                        </div>
                        {untranslated && t('misc.ai_content_note') && (
                          <div style={{ color: C.muted, fontSize: 9, marginTop: 1 }}>{t('misc.ai_content_note')}</div>
                        )}
                      </div>
                    );
                  })()}
                  {/* 数据时效 + 分析热度 — 与 Pipeline 一致，不用社交关注数 */}
                  {(() => {
                    const fresh = freshnessLabel(signalMap[p.project_name]?.scoredAt, lang);
                    const heat = heatMap[p.project_name];
                    if (!fresh && !heat) return null;
                    return (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 3, fontSize: 9.5, color: fresh?.stale ? "#c2410c" : C.muted }}>
                        {fresh && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3 }}>
                            <Clock size={10} strokeWidth={2} />{fresh.text}
                          </span>
                        )}
                        {heat >= 3 && (
                          <span style={{ display: "flex", alignItems: "center", gap: 3, color: C.muted }} title={lang === 'en' ? 'Analyzed by real users' : '真实分析次数，非社交关注数'}>
                            <Flame size={10} strokeWidth={2} />{lang === 'en' ? `${heat} analyses` : `分析 ${heat} 次`}
                          </span>
                        )}
                      </div>
                    );
                  })()}
                </td>
                <td style={{ padding: "8px 12px" }}>
                  {(() => {
                    const sg = signalMap[p.project_name];
                    const hasCache = !!(sg?.summary_zh || sg?.summary_en);
                    return (
                      <button
                        onClick={() => onImport(p.project_name, p.project_id)}
                        style={hasCache
                          ? { ...S.btn("primary"), fontSize: 11, padding: "3px 8px" }
                          : { ...S.btn(), fontSize: 11, padding: "3px 8px" }}
                        title={hasCache ? (lang === 'en' ? 'Cached analysis available' : '已有缓存分析') : (lang === 'en' ? 'Run AI analysis' : '运行 AI 分析')}
                      >
                        {hasCache ? (lang === 'en' ? 'View' : '查看') : (lang === 'en' ? 'Analyze' : '分析')}
                      </button>
                    );
                  })()}
                </td>
              </tr>
            ))}
            {loading && items.length === 0 && Array.from({ length: 8 }).map((_, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${C.faint}` }}>
                {[24, 140, 60, 80, 50, 80, 200, 60].map((w, j) => (
                  <td key={j} style={{ padding: "10px 12px" }}>
                    <div style={{ height: 13, width: w, background: C.faint, borderRadius: 4, opacity: 1 - i * 0.08 }} />
                  </td>
                ))}
              </tr>
            ))}
            {items.length === 0 && !loading && (
              <tr><td colSpan={8} style={{ padding: 24, textAlign: "center", color: C.muted }}>暂无数据</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  PASS REASON MODAL
// ══════════════════════════════════════════════════════
function PassReasonModal({ projectName, onConfirm, onCancel }) {
  const [reason, setReason] = useState("");
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: 24, width: 380, boxShadow: "0 8px 32px rgba(0,0,0,0.12)" }}>
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>Pass — {projectName}</div>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 14 }}>记录 Pass 原因（可选），自动存入项目笔记</div>
        <textarea
          autoFocus
          value={reason}
          onChange={e => setReason(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && e.metaKey) onConfirm(reason); if (e.key === "Escape") onCancel(); }}
          placeholder="如：估值过高，赛道竞争激烈，团队背景存疑..."
          rows={3}
          style={{ ...S.input, resize: "vertical", lineHeight: 1.6, marginBottom: 14 }}
        />
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ ...S.btn("default"), fontSize: 12 }}>取消</button>
          <button onClick={() => onConfirm(reason)}
            style={{ ...S.btn("danger"), fontSize: 12, padding: "6px 16px" }}>
            确认 Pass
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  PORTFOLIO / RANKING PAGE
// ══════════════════════════════════════════════════════

// Deal 状态流转定义
const DEAL_STAGES = [
  { id: "watching",   labelKey: "status.watching",    color: "#6b7280", bg: "#f3f4f6" },
  { id: "researching",labelKey: "status.researching", color: "#2563eb", bg: "#eff6ff" },
  { id: "diligence",  labelKey: "status.diligence",   color: "#d97706", bg: "#fffbeb" },
  { id: "approved",   labelKey: "status.pass",        color: "#059669", bg: "#ecfdf5" },
  { id: "invested",   labelKey: "status.invested",    color: "#7c3aed", bg: "#f5f3ff" },
  { id: "passed",     labelKey: "filter.pass_label",  color: "#dc2626", bg: "#fef2f2" },
];

function DealStageBadge({ status, onClick }) {
  const t = useT();
  const stage = DEAL_STAGES.find(s => s.id === status) || DEAL_STAGES[0];
  return (
    <span onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", padding: "2px 8px", background: stage.bg, color: stage.color,
        border: `1px solid ${stage.color}33`, borderRadius: 4, fontSize: 10, fontWeight: 600, cursor: onClick ? "pointer" : "default",
        whiteSpace: "nowrap" }}>
      {t(stage.labelKey)}
    </span>
  );
}

function DealStageDropdown({ currentStatus, onChange }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);

  const handleOpen = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + window.scrollY + 2, left: rect.left + window.scrollX });
    }
    setOpen(v => !v);
  };

  // 点击外部关闭
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  return (
    <div ref={btnRef} style={{ position: "relative", display: "inline-block" }}>
      <DealStageBadge status={currentStatus} onClick={handleOpen} />
      {open && (
        <div
          onMouseDown={e => e.stopPropagation()}
          style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999,
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 6, boxShadow: "0 4px 16px rgba(0,0,0,0.15)", padding: 4, minWidth: 110 }}>
          {DEAL_STAGES.map(s => (
            <div key={s.id} onClick={() => { onChange(s.id); setOpen(false); }}
              style={{ padding: "5px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, fontWeight: 600,
                color: s.color, background: currentStatus === s.id ? s.bg : "transparent" }}
              onMouseEnter={e => e.currentTarget.style.background = s.bg}
              onMouseLeave={e => e.currentTarget.style.background = currentStatus === s.id ? s.bg : "transparent"}>
              {t(s.labelKey)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── 回测验证 Modal ──────────────────────────────────────────────────
function ValidationModal({ project, onClose }) {
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [form, setForm] = useState({
    months_elapsed: "",
    actual_round: "",
    actual_round_amount: "",
    new_investors: "",
    status_notes: "",
    score_accuracy: "accurate",  // accurate | overestimated | underestimated | na
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    const entry = {
      checker: "manual",
      months_elapsed: Number(form.months_elapsed) || null,
      original_composite: project.analysis?.composite ?? null,
      original_fair_value: project.analysis?.fair_value_usd ?? null,
      original_track: project.analysis?.track ?? null,
      original_stage: project.analysis?.stage_key ?? null,
      actual_latest_round: form.actual_round || null,
      actual_round_amount: form.actual_round_amount ? Number(form.actual_round_amount) : null,
      new_investors: form.new_investors ? form.new_investors.split(",").map(s => s.trim()).filter(Boolean) : [],
      status_notes: form.status_notes || null,
      score_accuracy: form.score_accuracy,
    };
    const ok = await appendValidation(project.name, entry);
    setSaving(false);
    if (ok) { setSaved(true); setTimeout(onClose, 1200); }
    else { alert("写入失败，请确认 Supabase migration 003 已执行"); }
  };

  const ACCURACY_OPTS = [
    { value: "accurate",       label: "基本准确", Icon: CircleCheck },
    { value: "overestimated",  label: "高估了",   Icon: TrendingUp },
    { value: "underestimated", label: "低估了",   Icon: TrendingDown },
    { value: "na",             label: "暂无法判断", Icon: HelpCircle },
  ];

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: C.surface, borderRadius: 12, padding: 24, width: 480, maxHeight: "90vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.3)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 800, fontSize: 16, marginBottom: 4 }}><FileText size={16} strokeWidth={2} /> 回测验证 — {project.name}</div>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 16 }}>
          原始评分 {project.analysis?.composite ?? "—"}/100 · {project.analysis?.track} · {project.analysis?.stage_label}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={S.label}>距离评分时间（月）</label>
            <input value={form.months_elapsed} onChange={e => set("months_elapsed", e.target.value)}
              placeholder="如：6" type="number" style={S.input} />
          </div>
          <div>
            <label style={S.label}>最新融资轮次（如有）</label>
            <input value={form.actual_round} onChange={e => set("actual_round", e.target.value)}
              placeholder="如：Series B" style={S.input} />
          </div>
          <div>
            <label style={S.label}>融资金额 USD（如有）</label>
            <input value={form.actual_round_amount} onChange={e => set("actual_round_amount", e.target.value)}
              placeholder="如：30000000" type="number" style={S.input} />
          </div>
          <div>
            <label style={S.label}>新增投资方（逗号分隔）</label>
            <input value={form.new_investors} onChange={e => set("new_investors", e.target.value)}
              placeholder="如：Paradigm, a16z" style={S.input} />
          </div>
          <div>
            <label style={S.label}>项目状态更新</label>
            <textarea value={form.status_notes} onChange={e => set("status_notes", e.target.value)}
              placeholder="如：主网上线，DAU 5万，TVL $2亿；或：项目停止更新"
              rows={3} style={{ ...S.input, resize: "vertical", lineHeight: 1.5 }} />
          </div>
          <div>
            <label style={S.label}>评分准确度</label>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {ACCURACY_OPTS.map(o => (
                <button key={o.value} onClick={() => set("score_accuracy", o.value)}
                  style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 12px", borderRadius: 6, fontSize: 12, cursor: "pointer",
                    border: `1.5px solid ${form.score_accuracy === o.value ? C.accent : C.border}`,
                    background: form.score_accuracy === o.value ? `${C.accent}15` : C.surface,
                    color: form.score_accuracy === o.value ? C.accent : C.text, fontWeight: form.score_accuracy === o.value ? 700 : 400 }}>
                  <o.Icon size={12} strokeWidth={2} /> {o.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
          <button onClick={onClose} style={S.btn()}>取消</button>
          <button onClick={handleSave} disabled={saving || saved}
            style={{ ...S.btn("primary"), minWidth: 80 }}>
            {saved ? "✅ 已保存" : saving ? "保存中..." : "保存回测"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PortfolioPage({ projects, onSelect, onRemove, dealStatuses = {}, onStatusChange, onAddNote, dims = DIMS, weights = DEFAULT_WEIGHTS, isInWatchlist = () => false, onAddWatchlist = () => {}, onRemoveWatchlist = () => {} }) {
  const t = useT();
  const { lang } = useLang();
  const isMobile = useIsMobile();
  const [sort, setSort] = useState("alpha");
  const [tgeFilter, setTgeFilter] = useState("未发币");
  const [minScore, setMinScore] = useState(0);
  const [stageFilter, setStageFilter] = useState("全部");
  const [nameSearch, setNameSearch] = useState("");
  const [passPrompt, setPassPrompt] = useState(null); // { projectId, projectName }
  const [validateProject, setValidateProject] = useState(null); // project to validate
  const [heatMap, setHeatMap] = useState({}); // { projectName: 分析次数 } — 见 getAnalysisHeatMap

  // firstDoc 四层探测流水线：内联 timeline → LS 缓存 → SQL bulk → Wayback batch
  // 由 useFirstDocs hook 统一处理（autoProbe=true 默认开启 Wayback 兜底）
  const firstDocMap = useFirstDocs(projects);

  useEffect(() => {
    const names = projects.map(p => p.name).filter(Boolean);
    if (names.length > 0) getAnalysisHeatMap(names).then(setHeatMap);
  }, [projects]);

  function getAlpha(p) {
    const r = p.fundingRounds?.[0];
    const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
    const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
    const rv = r?.valuation || 0; const ra = r?.amount || 0;
    const entry = cgFdv || (rv > 0 ? rv : ra > 0 ? ra / 0.25 : p.details?.total_funding);
    const irrD = calcIRR(entry, p.analysis?.fair_value_usd);
    return alphaScore(effectiveComposite(p, weights), irrD, p.analysis?.risk_penalty || 0);
  }

  // 有账户级自定义权重覆盖时统一使用；否则回退到该项目分析时的阶段自适应权重
  const getComposite = (p) => effectiveComposite(p, weights);

  const isUnlaunched = (p) => {
    const tge = p.analysis?.tge_status || "";
    const tokenSymbol = p.details?.token_symbol || "";
    return !tokenSymbol && !tge.includes("已上线");
  };

  const handleStatusChange = (projectId, projectName, newStatus) => {
    if (newStatus === "passed") {
      setPassPrompt({ projectId, projectName });
    } else {
      onStatusChange(projectId, newStatus);
    }
  };

  // 拆成两步：先算"评分/TGE/阶段"这三个筛选条件是否通过，再单独算名字是否匹配——
  // 这样当用户在搜索框输入名字时，能把"匹配名字但被其它筛选挡住"的项目单独找出来，
  // 而不是让它们和"压根不匹配名字"的项目一样直接从结果里消失、看起来像是没有这个项目。
  const passesOtherFilters = p => {
    const scoreOk = getComposite(p) >= minScore;
    const tgeOk = tgeFilter === "全部" ? true : tgeFilter === "未发币" ? isUnlaunched(p) : !isUnlaunched(p);
    const stageOk = stageFilter === "全部" ? true : (dealStatuses[p.id] || "watching") === stageFilter;
    return scoreOk && tgeOk && stageOk;
  };
  const nameMatches = p => !nameSearch.trim() || p.name.toLowerCase().includes(nameSearch.toLowerCase());

  const filtered = projects.filter(p => passesOtherFilters(p) && nameMatches(p));
  const hiddenByFilters = nameSearch.trim()
    ? projects.filter(p => nameMatches(p) && !passesOtherFilters(p))
    : [];

  const SORT_DIMS = ["tech", "team", "narrative", "data_score", "token", "compliance"];
  const sorted = [...filtered].sort((a, b) => {
    if (sort === "alpha") return getAlpha(b) - getAlpha(a);
    if (sort === "score") return getComposite(b) - getComposite(a);
    if (sort === "stage") {
      const si = id => DEAL_STAGES.findIndex(s => s.id === (dealStatuses[id] || "watching"));
      return si(a.id) - si(b.id);
    }
    if (SORT_DIMS.includes(sort)) {
      return (b.analysis?.scores?.[sort] || 0) - (a.analysis?.scores?.[sort] || 0);
    }
    return 0;
  });

  return (
    <div>
      {/* Pass 原因弹窗 */}
      {passPrompt && (
        <PassReasonModal
          projectName={passPrompt.projectName}
          onConfirm={(reason) => {
            onStatusChange(passPrompt.projectId, "passed");
            if (reason.trim() && onAddNote) {
              onAddNote(passPrompt.projectId, {
                id: Date.now(),
                text: reason.trim(),
                type: "decision",
                createdAt: new Date().toISOString(),
              });
            }
            setPassPrompt(null);
          }}
          onCancel={() => setPassPrompt(null)}
        />
      )}

      {/* Pipeline 看板（按状态统计） */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, overflowX: "auto", paddingBottom: 2 }}>
        {[{ id: "全部", labelKey: "filter.all", color: C.muted, bg: C.faint }, ...DEAL_STAGES].map(s => {
          const count = s.id === "全部"
            ? projects.length
            : projects.filter(p => (dealStatuses[p.id] || "watching") === s.id).length;
          const active = stageFilter === s.id;
          return (
            <button key={s.id} onClick={() => setStageFilter(s.id)}
              style={{ flexShrink: 0, padding: "5px 12px", background: active ? (s.bg || C.faint) : C.surface,
                border: `1px solid ${active ? s.color : C.border}`, borderRadius: 6, cursor: "pointer",
                color: active ? s.color : C.muted, fontSize: 11, fontWeight: active ? 700 : 400 }}>
              {t(s.labelKey)} {count > 0 && <span style={{ marginLeft: 4, fontWeight: 700 }}>{count}</span>}
            </button>
          );
        })}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ fontSize: 11, color: C.muted }}>
            {lang === 'en' ? <><strong>{sorted.length}</strong> / {projects.length} projects</> : <>筛选后 <strong>{sorted.length}</strong> / 共 {projects.length} 个项目</>}
            {hiddenByFilters.length > 0 && (
              <span style={{ color: "#c2410c", marginLeft: 8 }}>
                {lang === 'en' ? `· ${hiddenByFilters.length} more match but are hidden by filters (see below)` : `· 另有 ${hiddenByFilters.length} 个匹配项目被当前筛选条件挡住，见下方`}
              </span>
            )}
          </div>
          <input
            value={nameSearch}
            onChange={e => setNameSearch(e.target.value)}
            placeholder={lang === 'en' ? 'Search projects...' : '搜索项目名...'}
            style={{ ...S.input, width: 160, padding: "4px 8px", fontSize: 11 }}
          />
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {/* TGE 筛选 */}
          <div style={{ display: "flex", gap: 2 }}>
            {[
              { val: "全部",  labelKey: "filter.all" },
              { val: "未发币", labelKey: "filter.pre_token" },
              { val: "已上线", labelKey: "filter.live" },
            ].map(f => (
              <button key={f.val} onClick={() => setTgeFilter(f.val)}
                style={{ ...S.btn(tgeFilter === f.val ? "primary" : "default"), padding: "4px 10px", fontSize: 11 }}>
                {t(f.labelKey)}
              </button>
            ))}
          </div>
          {/* 最低评分 */}
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: C.muted }}>
            <span>{t('filter.min_score')}</span>
            <input type="number" value={minScore} min={0} max={100}
              onChange={e => setMinScore(Number(e.target.value))}
              style={{ ...S.input, width: 52, textAlign: "center", padding: "4px 6px" }} />
          </div>
          {/* 排序 */}
          <select value={sort} onChange={e => setSort(e.target.value)} style={{ ...S.input, width: "auto" }}>
            <option value="alpha">Alpha Score</option>
            <option value="score">{t('table.score')} (综合)</option>
            <option value="stage">Deal {t('table.stage')}</option>
            <option disabled>── 维度排序 ──</option>
            <option value="tech">技术 (Tech)</option>
            <option value="team">团队 (Team)</option>
            <option value="narrative">叙事 (Narrative)</option>
            <option value="data_score">链上数据 (On-chain)</option>
            <option value="token">代币经济 (Token)</option>
            <option value="compliance">合规性 (Compliance)</option>
          </select>
        </div>
      </div>

      {projects.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><BarChart3 size={24} strokeWidth={2} /></div>
          <div style={{ fontWeight: 600, color: C.text }}>{lang === 'en' ? 'No analyzed projects yet' : '暂无分析项目'}</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>{lang === 'en' ? 'Go to Analyze, or click "Analyze" from Hot List / Pipeline' : '在「项目分析」或从热榜/Pipeline 点击「分析」或「批量分析」'}</div>
        </div>
      )}

      {projects.length > 0 && sorted.length === 0 && (
        <div style={{ ...S.card, textAlign: "center", padding: 30, color: C.muted }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 6 }}><Search size={20} strokeWidth={2} /></div>
          <div style={{ fontWeight: 600, color: C.text }}>{lang === 'en' ? 'No projects match current filters' : '当前筛选条件下无匹配项目'}</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>{lang === 'en' ? 'Try lowering the min score or switching TGE status' : '尝试降低最低评分或切换 TGE 状态'}</div>
        </div>
      )}

      {hiddenByFilters.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 700, color: "#c2410c", marginBottom: 6 }}>
            <Search size={12} strokeWidth={2} />
            {lang === 'en' ? `Matches "${nameSearch}" but hidden by current filters` : `匹配"${nameSearch}"，但被当前筛选条件挡住`}
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {hiddenByFilters.map(p => {
              const composite = getComposite(p);
              const failsScore = composite < minScore;
              const failsTge = tgeFilter !== "全部" && (tgeFilter === "未发币" ? !isUnlaunched(p) : isUnlaunched(p));
              const failsStage = stageFilter !== "全部" && (dealStatuses[p.id] || "watching") !== stageFilter;
              const reasons = [];
              if (failsScore) reasons.push(lang === 'en' ? `score ${composite} < ${minScore}` : `评分 ${composite} 低于 ${minScore}`);
              if (failsTge) reasons.push(lang === 'en' ? (isUnlaunched(p) ? 'no token yet' : 'already has a token') : (isUnlaunched(p) ? '尚未发币' : '已上线代币'));
              if (failsStage) reasons.push(lang === 'en' ? 'different pipeline stage' : '所处阶段不同');
              return (
                <div key={p.id} onClick={() => onSelect(p)} style={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  background: "#fff7ed", border: "1px dashed #f59e0b", borderRadius: 8,
                  padding: "8px 12px", cursor: "pointer",
                }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "#c2410c", marginTop: 1 }}>{reasons.join(' · ') || (lang === 'en' ? 'excluded by current filters' : '不满足当前筛选条件')}</div>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{composite}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {sorted.length > 0 && (isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {sorted.map((p, i) => {
            const a = p.analysis;
            const r = rating(a?.composite || 0);
            const lastRound = p.fundingRounds?.[0];
            const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
            const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
            const rv2 = lastRound?.valuation || 0; const ra2 = lastRound?.amount || 0;
            const entry2 = cgFdv || (rv2 > 0 ? rv2 : ra2 > 0 ? ra2 / 0.25 : p.details?.total_funding);
            const irrData = calcIRR(entry2, a?.fair_value_usd);
            const alpha = alphaScore(a?.composite || 0, irrData, a?.risk_penalty || 0);
            const fresh = freshnessLabel(p.analyzedAt, lang);
            const heat = heatMap[p.name];
            const watched = isInWatchlist(p.name);
            return (
              <MobileListCard
                key={p.id}
                onClick={() => onSelect(p)}
                header={
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: i < 3 ? "#d97706" : C.muted, width: 18, flexShrink: 0 }}>{i < 3 ? ["①", "②", "③"][i] : i + 1}</span>
                    {p.details?.logo && <img src={proxyLogo(p.details.logo)} alt="" style={{ width: 24, height: 24, borderRadius: 6, flexShrink: 0 }} onError={e => { e.target.style.display = "none"; }} />}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 15, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: "#a1a1aa" }}>{lang === 'en' ? (a?.stage_en || enify(a?.stage)) : a?.stage}</div>
                    </div>
                    <PedigreeBadge hits={pedigreeHitsOf(p)} />
                  </div>
                }
                badge={
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 3, background: r.bg, border: `1px solid ${r.border}`, borderRadius: 999, padding: "2px 8px", fontSize: 12, fontWeight: 700 }}>
                    <span style={{ color: r.fg }}>{a?.composite || 0}</span>
                    <span style={{ color: r.fg, fontSize: 10 }}>{r.label}</span>
                  </span>
                }
                tags={
                  <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
                    <span style={{ fontSize: 12, background: C.faint, color: C.muted, padding: "2px 8px", borderRadius: 6 }}>
                      {lang === 'en' ? (a?.track_en || a?.track || "—") : (a?.track || "—")}
                    </span>
                    <div onClick={e => e.stopPropagation()}>
                      <DealStageDropdown currentStatus={dealStatuses[p.id] || "watching"} onChange={(s) => handleStatusChange(p.id, p.name, s)} />
                    </div>
                  </div>
                }
                summary={
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0,1fr))", gap: 8 }}>
                    <div>
                      <div style={{ fontSize: 11, color: C.muted }}>{t('table.fdv')}</div>
                      <div style={{ fontSize: 13, color: (irrData?.underMult || 0) > 2 ? "#059669" : C.text, marginTop: 2 }}>{irrData ? `${irrData.underMult.toFixed(2)}x` : "—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: C.muted }}>{t('table.irr')}</div>
                      <div style={{ fontSize: 13, color: (irrData?.expectedIRR || 0) > 0.3 ? "#059669" : "#d97706", marginTop: 2 }}>{irrData ? `${(irrData.expectedIRR * 100).toFixed(1)}%` : "—"}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: 11, color: C.muted }}>{t('table.alpha')}</div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: alpha > 150 ? "#059669" : alpha > 80 ? "#2563eb" : C.muted, marginTop: 2 }}>{alpha || "—"}</div>
                    </div>
                    {a?.unlock_pressure && (
                      <div style={{ gridColumn: "span 3" }}>
                        <UnlockBadge level={a.unlock_pressure} />
                      </div>
                    )}
                  </div>
                }
                footerLeft={
                  <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 11, color: fresh?.stale ? "#c2410c" : "#a1a1aa" }}>
                    {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={11} strokeWidth={2} />{fresh.text}</span>}
                    {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={11} strokeWidth={2} />{lang === 'en' ? `${heat} analyses` : `分析 ${heat} 次`}</span>}
                  </div>
                }
                footerRight={
                  <div onClick={e => e.stopPropagation()} style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <button onClick={() => watched ? onRemoveWatchlist(p.name) : onAddWatchlist(p)} title={watched ? "取消收藏" : "收藏"} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", color: watched ? "#f59e0b" : C.muted }}>
                      <Star size={16} strokeWidth={2} fill={watched ? "#f59e0b" : "none"} />
                    </button>
                    <button onClick={() => setValidateProject(p)} title="回测验证" style={{ background: "none", border: "none", cursor: "pointer", display: "flex", color: C.muted }}>
                      <FileText size={15} strokeWidth={2} />
                    </button>
                    <button onClick={() => onRemove(p.id)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#dc2626" }}>移除</button>
                  </div>
                }
              />
            );
          })}
        </div>
      ) : (
        <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: C.faint, fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>
                {["#", t('table.project'), t('table.track'), t('table.score'), t('table.fdv'), t('table.irr'), t('table.alpha'), t('table.unlock'), lang === 'en' ? 'First Doc' : '首次文档', t('table.stage'), t('table.saved'), t('table.action')].map(h => (
                  <th key={h} style={{ padding: "8px 12px", textAlign: "left" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((p, i) => {
                const a = p.analysis;
                const r = rating(a?.composite || 0);
                const lastRound = p.fundingRounds?.[0];
                const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
                const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
                const rv2 = lastRound?.valuation || 0; const ra2 = lastRound?.amount || 0;
                const entry2 = cgFdv || (rv2 > 0 ? rv2 : ra2 > 0 ? ra2 / 0.25 : p.details?.total_funding);
                const irrData = calcIRR(entry2, a?.fair_value_usd);
                const alpha = alphaScore(a?.composite || 0, irrData, a?.risk_penalty || 0);
                return (
                  <tr key={p.id} style={{ borderTop: `1px solid ${C.faint}`, cursor: "pointer" }}
                    onClick={() => onSelect(p)}
                    onMouseEnter={e => e.currentTarget.style.background = C.faint}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: i < 3 ? "#d97706" : C.muted }}>
                      {i < 3 ? ["①","②","③"][i] : i + 1}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                        {p.details?.logo && <img src={proxyLogo(p.details.logo)} alt="" style={{ width: 22, height: 22, borderRadius: 5 }} onError={e => e.target.style.display = "none"} />}
                        <div>
                          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontWeight: 700 }}>{p.name}</span>
                            <PedigreeBadge hits={pedigreeHitsOf(p)} />
                          </div>
                          <div style={{ fontSize: 10, color: C.muted }}>{lang === 'en' ? (a?.stage_en || enify(a?.stage)) : a?.stage}</div>
                          {(() => {
                            const fresh = freshnessLabel(p.analyzedAt, lang);
                            const heat = heatMap[p.name];
                            if (!fresh && !heat) return null;
                            return (
                              <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 2, fontSize: 9.5, color: fresh?.stale ? "#c2410c" : C.muted }}>
                                {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={9} strokeWidth={2} />{fresh.text}</span>}
                                {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={9} strokeWidth={2} />{lang === 'en' ? `${heat}x` : `分析${heat}次`}</span>}
                              </div>
                            );
                          })()}
                        </div>
                      </div>
                    </td>
                    <td style={{ padding: "10px 12px" }}><span style={S.tag}>{lang === 'en' ? (a?.track_en || a?.track || "—") : (a?.track || "—")}</span></td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={S.rBadge(a?.composite || 0)}>{r.label}</span>
                      <span style={{ fontWeight: 700, fontSize: 16, color: r.fg, marginLeft: 6 }}>{a?.composite || 0}</span>
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: (irrData?.underMult || 0) > 2 ? "#059669" : C.text }}>
                      {irrData ? `${irrData.underMult.toFixed(2)}x` : "—"}
                    </td>
                    <td style={{ padding: "10px 12px", fontWeight: 600, color: (irrData?.expectedIRR || 0) > 0.3 ? "#059669" : "#d97706" }}>
                      {irrData ? `${(irrData.expectedIRR * 100).toFixed(1)}%` : "—"}
                    </td>
                    <td style={{ padding: "10px 12px" }}>
                      <span style={{ fontSize: 20, fontWeight: 900, color: alpha > 150 ? "#059669" : alpha > 80 ? "#2563eb" : C.muted }}>{alpha || "—"}</span>
                    </td>
                    <td style={{ padding: "10px 12px" }}>{a?.unlock_pressure ? <UnlockBadge level={a.unlock_pressure} /> : "—"}</td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                      <FirstDocCell
                        entry={firstDocMap[p.name]?.firstDoc}
                        probed={!!firstDocMap[p.name]?.probed}
                        lang={lang}
                        mutedColor={C.muted}
                      />
                    </td>
                    <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                      <DealStageDropdown
                        currentStatus={dealStatuses[p.id] || "watching"}
                        onChange={(s) => handleStatusChange(p.id, p.name, s)}
                      />
                    </td>
                    <td style={{ padding: "10px 12px" }} onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => isInWatchlist(p.name) ? onRemoveWatchlist(p.name) : onAddWatchlist(p)}
                        title={isInWatchlist(p.name) ? "取消收藏" : "收藏"}
                        style={{ background: "none", border: "none", cursor: "pointer", display: "flex", color: isInWatchlist(p.name) ? "#f59e0b" : "#d1d5db" }}>
                        <Star size={18} strokeWidth={2} fill={isInWatchlist(p.name) ? "#f59e0b" : "none"} />
                      </button>
                    </td>
                    <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => setValidateProject(p)}
                        title="回测验证：记录实际结果与原始评分的偏差"
                        style={{ ...S.btn(), padding: "3px 8px", fontSize: 11, marginRight: 4, display: "inline-flex", alignItems: "center" }}><FileText size={13} strokeWidth={2} /></button>
                      <button onClick={() => onRemove(p.id)} style={{ ...S.btn("danger"), padding: "3px 8px", fontSize: 11 }}>移除</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}
      {validateProject && (
        <ValidationModal project={validateProject} onClose={() => setValidateProject(null)} />
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  ANALYZER PAGE
// ══════════════════════════════════════════════════════
// ══════════════════════════════════════════════════════
//  COMPARE PAGE
// ══════════════════════════════════════════════════════
function ComparePage({ projects, dims, weights, initialLeftId = "", initialRightId = "" }) {
  const { lang } = useLang();
  const t = useT();
  const [leftId,  setLeftId]  = useState(initialLeftId);
  const [rightId, setRightId] = useState(initialRightId);
  const compareRef = useRef(null);
  const [sharing, setSharing] = useState(false);

  const left  = projects.find(p => String(p.id) === leftId);
  const right = projects.find(p => String(p.id) === rightId);

  const getAlpha = (p) => {
    if (!p) return 0;
    const r = p.fundingRounds?.[0];
    const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
    const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
    const irrD = calcIRR(cgFdv || r?.valuation || p.details?.total_funding, p.analysis?.fair_value_usd);
    return alphaScore(p.analysis?.composite || 0, irrD, p.analysis?.risk_penalty || 0);
  };

  const getIRR = (p) => {
    if (!p) return null;
    const r = p.fundingRounds?.[0];
    const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
    const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
    return calcIRR(cgFdv || r?.valuation || p.details?.total_funding, p.analysis?.fair_value_usd);
  };

  // 叙事×赛道象限：narrative score vs chain/ecosystem breadth
  const getQuadrant = (p) => {
    if (!p?.analysis) return null;
    const nar = p.analysis.scores?.narrative || 0;
    const eco = p.analysis.scores?.data || 0; // data score proxies ecosystem traction
    if (nar >= 70 && eco >= 70) return { Icon: Trophy,    label: "领跑者", color: "#059669", desc: "高叙事 × 强生态" };
    if (nar >= 70 && eco < 70)  return { Icon: Newspaper, label: "叙事型", color: "#3b82f6", desc: "高叙事 × 待验证" };
    if (nar < 70  && eco >= 70) return { Icon: Settings,  label: "实干型", color: "#f59e0b", desc: "低叙事 × 强数据" };
    return                              { Icon: Clock,     label: "观察中", color: "#9ca3af", desc: "叙事&数据待突破" };
  };

  const Selector = ({ value, onChange, exclude }) => (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ ...S.input, fontWeight: 600, fontSize: 13 }}>
      <option value="">{lang === 'en' ? '— Select Project —' : '— 选择项目 —'}</option>
      {projects.filter(p => String(p.id) !== exclude).map(p => (
        <option key={p.id} value={String(p.id)}>{p.name}</option>
      ))}
    </select>
  );

  // 雷达图数据
  const radarData = DIMS.map(d => ({
    dim: d.short,
    [left?.name || "左"]:  left?.analysis?.scores?.[d.key]  || 0,
    [right?.name || "右"]: right?.analysis?.scores?.[d.key] || 0,
  }));

  const COMPARE_ROWS = [
    { label: lang === 'en' ? "Score"        : "综合评分",   left: left?.analysis?.composite,  right: right?.analysis?.composite,  fmt: v => v, color: true },
    { label: "Alpha",                                        left: getAlpha(left),              right: getAlpha(right),             fmt: v => v, color: true },
    { label: lang === 'en' ? "Est. IRR"     : "期望IRR",    left: getIRR(left)?.expectedIRR,  right: getIRR(right)?.expectedIRR,  fmt: v => v != null ? (v * 100).toFixed(1) + "%" : "—", color: true },
    { label: lang === 'en' ? "FDV Multiple" : "低估倍数",   left: getIRR(left)?.underMult,    right: getIRR(right)?.underMult,    fmt: v => v != null ? v.toFixed(2) + "x" : "—", color: true },
    { label: lang === 'en' ? "Track"        : "赛道",       left: left?.analysis?.track,       right: right?.analysis?.track,      fmt: v => v || "—", color: false },
    { label: lang === 'en' ? "Stage"        : "融资阶段",   left: left?.analysis?.stage,       right: right?.analysis?.stage,      fmt: v => v || "—", color: false },
    { label: lang === 'en' ? "Total Raised" : "总融资",     left: left?.details?.total_funding, right: right?.details?.total_funding, fmt: v => fmt(v), color: false },
    { label: "TGE",                                          left: left?.analysis?.tge_status,  right: right?.analysis?.tge_status, fmt: v => v || "—", color: false },
    { label: lang === 'en' ? "Unlock"       : "解锁压力",   left: left?.analysis?.unlock_pressure, right: right?.analysis?.unlock_pressure, fmt: v => v || "—", color: false },
    { label: "TVL",                                          left: left?.defiData?.tvl?.current, right: right?.defiData?.tvl?.current, fmt: v => fmt(v), color: true },
    { label: lang === 'en' ? "24h Volume"   : "24h交易量",  left: left?.defiData?.volume?.daily, right: right?.defiData?.volume?.daily, fmt: v => fmt(v), color: true },
  ];

  const winnerStyle = (lv, rv, row) => {
    if (!row.color || lv == null || rv == null) return {};
    return lv > rv ? { background: "#f0fdf4", fontWeight: 700, color: "#059669" } : {};
  };
  const loserStyle = (lv, rv, row) => {
    if (!row.color || lv == null || rv == null) return {};
    return lv < rv ? { background: "#fef2f2", color: "#dc2626" } : {};
  };

  // 截图分享
  const handleShare = async () => {
    if (!compareRef.current) return;
    setSharing(true);
    try {
      const canvas = await html2canvas(compareRef.current, { scale: 2, backgroundColor: "#f9fafb" });
      const link = document.createElement("a");
      link.download = `compare_${left.name}_vs_${right.name}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
    } finally {
      setSharing(false);
    }
  };

  // 胜者判断（综合评分 + Alpha 综合）
  const leftScore  = (left?.analysis?.composite  || 0) + getAlpha(left)  * 0.3;
  const rightScore = (right?.analysis?.composite || 0) + getAlpha(right) * 0.3;
  const winner = leftScore > rightScore ? left : rightScore > leftScore ? right : null;
  const winnerColor = winner === left ? "#2563eb" : "#d97706";

  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}><Scale size={17} strokeWidth={2} /> {lang === 'en' ? 'Project Comparison' : '项目对比分析'}</h2>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{lang === 'en' ? 'Select two analyzed projects for a side-by-side comparison' : '从已分析项目中选择两个进行并排对比'}</div>
        </div>
        {left && right && (
          <button onClick={handleShare} disabled={sharing}
            style={{ ...S.btn("secondary"), fontSize: 11, display: "flex", alignItems: "center", gap: 5 }}>
            {sharing ? (lang === 'en' ? 'Saving...' : '截图中…') : (<><Camera size={13} strokeWidth={2} /> {lang === 'en' ? 'Export Comparison' : '导出对比图'}</>)}
          </button>
        )}
      </div>

      {projects.length < 2 ? (
        <div style={{ ...S.card, textAlign: "center", padding: 40, color: C.muted }}>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}><Scale size={24} strokeWidth={2} /></div>
          <div style={{ fontWeight: 600, color: C.text, marginBottom: 4 }}>{lang === 'en' ? 'Need at least 2 analyzed projects' : '至少需要 2 个已分析项目'}</div>
          <div style={{ fontSize: 12 }}>先在「项目分析」页分析更多项目</div>
        </div>
      ) : (
        <>
          {/* 选择器 */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 12, marginBottom: 16, alignItems: "center" }}>
            <Selector value={leftId}  onChange={setLeftId}  exclude={rightId} />
            <span style={{ fontSize: 18, color: C.muted, textAlign: "center" }}>VS</span>
            <Selector value={rightId} onChange={setRightId} exclude={leftId} />
          </div>

          {left && right && (
            <div ref={compareRef}>
              {/* 胜者 Banner */}
              {winner && (
                <div style={{ ...S.card, marginBottom: 12, background: `${winnerColor}08`, border: `1.5px solid ${winnerColor}30`,
                  display: "flex", alignItems: "center", gap: 12, padding: "12px 16px" }}>
                  <Trophy size={22} strokeWidth={2} style={{ color: winnerColor, flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 800, fontSize: 14, color: winnerColor }}>{winner.name} 综合领先</div>
                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                      评分差距 {Math.abs(leftScore - rightScore).toFixed(0)} 分 ·
                      综合 {winner.analysis?.composite} · Alpha {getAlpha(winner)}
                    </div>
                  </div>
                  <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    {[left, right].map((p, idx) => {
                      const q = getQuadrant(p);
                      return q ? (
                        <div key={p.id} style={{ textAlign: "center", padding: "6px 12px", background: `${q.color}12`,
                          border: `1px solid ${q.color}30`, borderRadius: 8 }}>
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 12, fontWeight: 700, color: q.color }}>
                            <q.Icon size={12} strokeWidth={2} /> {q.label}
                          </div>
                          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>{p.name}</div>
                          <div style={{ fontSize: 9, color: q.color, opacity: 0.8 }}>{q.desc}</div>
                        </div>
                      ) : null;
                    })}
                  </div>
                </div>
              )}

              {/* 雷达图对比 */}
              <div style={{ ...S.card, marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>六维评分对比</div>
                <ResponsiveContainer width="100%" height={220}>
                  <RadarChart data={radarData}>
                    <PolarGrid stroke={C.faint} />
                    <PolarAngleAxis dataKey="dim" tick={{ fill: C.muted, fontSize: 10 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={false} axisLine={false} />
                    <Radar name={left.name}  dataKey={left.name}  stroke="#3b82f6" fill="#3b82f6" fillOpacity={0.12} strokeWidth={2} />
                    <Radar name={right.name} dataKey={right.name} stroke="#f59e0b" fill="#f59e0b" fillOpacity={0.12} strokeWidth={2} />
                    <Tooltip formatter={(v, name) => [`${v}分`, name]} contentStyle={{ fontSize: 11 }} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </RadarChart>
                </ResponsiveContainer>

                {/* 维度进度条对比 */}
                <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
                  {DIMS.map(d => {
                    const lv = left.analysis?.scores?.[d.key] || 0;
                    const rv = right.analysis?.scores?.[d.key] || 0;
                    return (
                      <div key={d.key}>
                        <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted, marginBottom: 2 }}>
                          <span style={{ color: lv >= rv ? "#2563eb" : C.muted, fontWeight: lv >= rv ? 700 : 400 }}>{lv}</span>
                          <span style={{ fontWeight: 600, color: C.text }}>{d.short}</span>
                          <span style={{ color: rv >= lv ? "#d97706" : C.muted, fontWeight: rv >= lv ? 700 : 400 }}>{rv}</span>
                        </div>
                        <div style={{ position: "relative", height: 6, borderRadius: 3, background: C.faint, overflow: "hidden" }}>
                          {/* left bar from center-left */}
                          <div style={{ position: "absolute", right: "50%", top: 0, height: "100%",
                            width: `${lv / 2}%`, background: "#3b82f6", borderRadius: "3px 0 0 3px" }} />
                          {/* right bar from center */}
                          <div style={{ position: "absolute", left: "50%", top: 0, height: "100%",
                            width: `${rv / 2}%`, background: "#f59e0b", borderRadius: "0 3px 3px 0" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* 关键指标对比表 */}
              <div style={{ ...S.card, padding: 0, overflow: "hidden", marginBottom: 12 }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr style={{ background: C.faint }}>
                      <th style={{ padding: "8px 14px", textAlign: "left", fontSize: 10, color: C.muted, fontWeight: 700, width: "28%" }}>指标</th>
                      <th style={{ padding: "8px 14px", textAlign: "center", fontSize: 12, fontWeight: 800, color: "#2563eb", width: "36%" }}>
                        {left.details?.logo && <img src={proxyLogo(left.details.logo)} alt="" style={{ width: 16, height: 16, borderRadius: 3, marginRight: 5, verticalAlign: "middle" }} onError={e => e.target.style.display = "none"} />}
                        {left.name}
                      </th>
                      <th style={{ padding: "8px 14px", textAlign: "center", fontSize: 12, fontWeight: 800, color: "#f59e0b", width: "36%" }}>
                        {right.details?.logo && <img src={proxyLogo(right.details.logo)} alt="" style={{ width: 16, height: 16, borderRadius: 3, marginRight: 5, verticalAlign: "middle" }} onError={e => e.target.style.display = "none"} />}
                        {right.name}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {COMPARE_ROWS.map((row) => (
                      <tr key={row.label} style={{ borderTop: `1px solid ${C.faint}` }}>
                        <td style={{ padding: "7px 14px", fontSize: 11, color: C.muted, fontWeight: 600 }}>{row.label}</td>
                        <td style={{ padding: "7px 14px", textAlign: "center", fontSize: 13, ...winnerStyle(row.left, row.right, row), ...loserStyle(row.left, row.right, row) }}>
                          {row.fmt(row.left)}
                        </td>
                        <td style={{ padding: "7px 14px", textAlign: "center", fontSize: 13, ...winnerStyle(row.right, row.left, row), ...loserStyle(row.right, row.left, row) }}>
                          {row.fmt(row.right)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 优势/劣势摘要 */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                {[left, right].map((p, idx) => {
                  const r = rating(p.analysis?.composite || 0);
                  const q = getQuadrant(p);
                  return (
                    <div key={p.id} style={{ ...S.card, borderTop: `3px solid ${idx === 0 ? "#3b82f6" : "#f59e0b"}` }}>
                      <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6, display: "flex", alignItems: "center", gap: 6 }}>
                        <span style={S.rBadge(p.analysis?.composite || 0)}>{r.label}</span>
                        {p.name}
                      </div>
                      {q && (
                        <div style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10, marginBottom: 8, padding: "3px 8px", borderRadius: 4,
                          background: `${q.color}12`, color: q.color, fontWeight: 700 }}>
                          <q.Icon size={11} strokeWidth={2} /> {q.label} · {q.desc}
                        </div>
                      )}
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 5 }}>优势</div>
                      {(p.analysis?.strengths || []).map((s, i) => <div key={i} style={{ fontSize: 11, marginBottom: 2, color: "#374151" }}>✓ {safeText(s)}</div>)}
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, margin: "8px 0 5px" }}>风险</div>
                      {(p.analysis?.risks || []).map((s, i) => <div key={i} style={{ fontSize: 11, marginBottom: 2, color: "#6b7280" }}>⚠ {safeText(s)}</div>)}
                      {p.analysis?.recommendation && (
                        <div style={{ marginTop: 10, padding: "7px 10px", background: C.faint, borderRadius: 6, fontSize: 11, color: C.text, fontStyle: "italic", lineHeight: 1.5 }}>
                          💡 {p.analysis.recommendation}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {(!left || !right) && leftId && rightId === "" && (
            <div style={{ ...S.card, color: C.muted, textAlign: "center", padding: 24 }}>请选择第二个项目</div>
          )}
        </>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  COMPARE PAGE
// ══════════════════════════════════════════════════════
//  SETTINGS PAGE — 自定义评分权重
// ══════════════════════════════════════════════════════
const PRESETS = {
  "balanced":    { narrative: 0.20, tech: 0.20, team: 0.18, tokenomics: 0.16, data: 0.14, compliance: 0.12 },
  "tech":        { narrative: 0.15, tech: 0.30, team: 0.20, tokenomics: 0.12, data: 0.15, compliance: 0.08 },
  "narrative":   { narrative: 0.30, tech: 0.15, team: 0.18, tokenomics: 0.15, data: 0.12, compliance: 0.10 },
  "compliance":  { narrative: 0.15, tech: 0.18, team: 0.20, tokenomics: 0.15, data: 0.12, compliance: 0.20 },
  "data":        { narrative: 0.12, tech: 0.22, team: 0.16, tokenomics: 0.14, data: 0.26, compliance: 0.10 },
  "team":        { narrative: 0.15, tech: 0.18, team: 0.35, tokenomics: 0.12, data: 0.10, compliance: 0.10 },
};

const PRESET_LABELS = {
  balanced:   { zh: "默认均衡", en: "Balanced" },
  tech:       { zh: "技术优先", en: "Tech First" },
  narrative:  { zh: "叙事优先", en: "Narrative First" },
  compliance: { zh: "机构合规", en: "Institutional" },
  data:       { zh: "数据驱动", en: "Data Driven" },
  team:       { zh: "团队押注", en: "Team Bet" },
};

const DIM_META = {
  narrative:  { label: "叙事与愿景", label_en: "Narrative & Vision", desc: "项目叙事吸引力、市场定位与长期愿景", desc_en: "Narrative appeal, market positioning and long-term vision", color: "#3b82f6" },
  tech:       { label: "技术与产品", label_en: "Tech & Product",     desc: "技术创新度、产品成熟度与开发进展",   desc_en: "Tech innovation, product maturity and dev progress",  color: "#10b981" },
  team:       { label: "团队背景",   label_en: "Team",               desc: "创始人经历、执行能力与行业资源",     desc_en: "Founder background, execution and industry resources", color: "#f59e0b" },
  tokenomics: { label: "代币经济",   label_en: "Tokenomics",         desc: "代币设计合理性、通胀模型与解锁结构", desc_en: "Token design, inflation model and unlock structure",  color: "#8b5cf6" },
  data:       { label: "数据表现",   label_en: "On-chain Data",      desc: "链上活跃度、TVL、用户增长等实证数据", desc_en: "On-chain activity, TVL, user growth and empirical data", color: "#ec4899" },
  compliance: { label: "合规风险",   label_en: "Compliance",         desc: "监管合规程度与法律风险敞口",         desc_en: "Regulatory compliance degree and legal risk exposure", color: "#ef4444" },
};

// ── AlertConfigModal ──────────────────────────────────────────────────────
function AlertConfigModal({ projectName, userId, lang, onClose }) {
  const [tvlThreshold, setTvlThreshold] = useState(20);
  const [scoreThreshold, setScoreThreshold] = useState(5);
  const [priceThreshold, setPriceThreshold] = useState(30);
  const [complianceAlert, setComplianceAlert] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    fetch(`${RD_BASE}/api/portfolio/alert-config?userId=${encodeURIComponent(userId)}&projectName=${encodeURIComponent(projectName)}`)
      .then(r => r.json())
      .then(data => {
        const a = data.alert;
        if (a) {
          setTvlThreshold(a.tvl_drop_threshold ?? 20);
          setScoreThreshold(a.score_change_threshold ?? 5);
          setPriceThreshold(a.price_drop_threshold ?? 30);
          setComplianceAlert(a.compliance_alert !== false);
        }
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []); // eslint-disable-line

  const handleSave = async () => {
    setSaving(true); setMsg('');
    try {
      const res = await fetch(`${RD_BASE}/api/portfolio/alert-config`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          projectName,
          tvlDropThreshold: tvlThreshold,
          scoreChangeThreshold: scoreThreshold,
          priceDropThreshold: priceThreshold,
          complianceAlert,
        }),
      });
      const data = await res.json();
      if (data.success) {
        setMsg(lang === 'en' ? '✅ Saved' : '✅ 已保存');
        setTimeout(onClose, 1200);
      } else {
        setMsg(`⚠ ${data.error || 'save failed'}`);
      }
    } catch (e) {
      setMsg(`⚠ ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 500, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, width: 340, maxWidth: "90vw" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontWeight: 700, fontSize: 14 }}>
            <Bell size={14} strokeWidth={2} /> {lang === 'en' ? 'Alert Config' : '告警设置'} — {projectName}
          </div>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 18, lineHeight: 1 }}>✕</button>
        </div>

        {!loaded ? (
          <div style={{ color: C.muted, fontSize: 12, textAlign: "center", padding: 20 }}>
            {lang === 'en' ? 'Loading…' : '加载中…'}
          </div>
        ) : (
          <>
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...S.label }}>{lang === 'en' ? `TVL weekly drop threshold: ${tvlThreshold}%` : `TVL 周跌告警阈值：${tvlThreshold}%`}</label>
              <input type="range" min={5} max={50} step={5} value={tvlThreshold}
                onChange={e => setTvlThreshold(Number(e.target.value))}
                style={{ width: "100%", accentColor: C.accent }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted }}>
                <span>5%</span><span>25%</span><span>50%</span>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={{ ...S.label }}>{lang === 'en' ? `Score change threshold: ${scoreThreshold}pts` : `评分变化阈值：${scoreThreshold}分`}</label>
              <input type="range" min={1} max={20} step={1} value={scoreThreshold}
                onChange={e => setScoreThreshold(Number(e.target.value))}
                style={{ width: "100%", accentColor: C.accent }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted }}>
                <span>1</span><span>10</span><span>20</span>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ ...S.label }}>{lang === 'en' ? `Price 7d drop threshold: ${priceThreshold}%` : `价格7日跌幅阈值：${priceThreshold}%`}</label>
              <input type="range" min={10} max={70} step={5} value={priceThreshold}
                onChange={e => setPriceThreshold(Number(e.target.value))}
                style={{ width: "100%", accentColor: C.accent }} />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted }}>
                <span>10%</span><span>40%</span><span>70%</span>
              </div>
            </div>

            <div style={{ marginBottom: 20, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <span style={{ fontSize: 12 }}>{lang === 'en' ? 'Compliance event alerts' : '合规事件告警'}</span>
              <button
                onClick={() => setComplianceAlert(v => !v)}
                style={{ padding: "4px 14px", background: complianceAlert ? "#059669" : C.faint, border: `1px solid ${complianceAlert ? "#059669" : C.border}`, borderRadius: 12, color: complianceAlert ? "#fff" : C.muted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {complianceAlert ? (lang === 'en' ? 'ON' : '已开启') : (lang === 'en' ? 'OFF' : '已关闭')}
              </button>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button onClick={handleSave} disabled={saving}
                style={{ ...S.btn("primary"), padding: "7px 18px" }}>
                {saving ? (lang === 'en' ? 'Saving…' : '保存中…') : (lang === 'en' ? 'Save' : '保存')}
              </button>
              <button onClick={onClose}
                style={{ ...S.btn("default"), padding: "7px 14px", fontSize: 11 }}>
                {lang === 'en' ? 'Cancel' : '取消'}
              </button>
              {msg && <span style={{ fontSize: 11, color: msg.startsWith('✅') ? "#059669" : "#dc2626" }}>{msg}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SettingsPage({ weights, onSave, onClear, projects = [], dealStatuses = {}, projectNotes = {}, analysisHistory = {}, onImportData, user }) {
  const { lang } = useLang();
  // weights 为 null 表示未设置账户级覆盖（各项目实际使用其自身的阶段自适应权重）；
  // 编辑区仍需要一组具体数字起步，未覆盖时从系统默认权重开始编辑。
  const [local, setLocal] = useState({ ...(weights || DEFAULT_WEIGHTS) });
  const [saved, setSaved] = useState(false);
  const [importMsg, setImportMsg] = useState("");
  const importRef = useRef(null);

  // ── Deal Flow 推送订阅状态 ──────────────────────────────────────────
  const [dfChatId, setDfChatId] = useState('');
  const [dfEnabled, setDfEnabled] = useState(true);
  const [dfMinHot, setDfMinHot] = useState(70);
  const [dfFundingStatus, setDfFundingStatus] = useState('zero_only');
  const [dfTracks, setDfTracks] = useState([]);
  const [dfSaving, setDfSaving] = useState(false);
  const [dfMsg, setDfMsg] = useState('');
  const [dfLoaded, setDfLoaded] = useState(false);

  const DF_TRACK_OPTIONS = ['DePIN', 'AI', 'RWA', 'L1', 'L2', 'DeFi', 'GameFi', 'NFT', 'Infra', 'Social'];

  useEffect(() => {
    if (!user?.id || dfLoaded) return;
    fetch(`${RD_BASE}/api/dealflow/subscription?userId=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(data => {
        const sub = data.subscription;
        if (sub) {
          setDfChatId(sub.telegram_chat_id || '');
          const f = sub.filters || {};
          setDfEnabled(f.enabled !== false);
          setDfMinHot(Number(f.min_hot_index ?? 70));
          setDfFundingStatus(f.funding_status || 'zero_only');
          setDfTracks(Array.isArray(f.tracks) ? f.tracks : []);
        }
        setDfLoaded(true);
      })
      .catch(() => setDfLoaded(true));
  }, [user?.id]); // eslint-disable-line

  const handleDfSave = async () => {
    if (!user?.id) return;
    setDfSaving(true); setDfMsg('');
    try {
      const res = await fetch(`${RD_BASE}/api/dealflow/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId: user.id,
          telegramChatId: dfChatId.trim() || null,
          filters: { tracks: dfTracks, min_hot_index: dfMinHot, funding_status: dfFundingStatus, enabled: dfEnabled },
        }),
      });
      const data = await res.json();
      if (data.success) {
        setDfMsg(lang === 'en' ? '✅ Saved' : '✅ 已保存');
      } else {
        setDfMsg(`⚠ ${data.error || 'save failed'}`);
      }
    } catch (e) {
      setDfMsg(`⚠ ${e.message}`);
    } finally {
      setDfSaving(false);
      setTimeout(() => setDfMsg(''), 3000);
    }
  };

  const handleDfTest = async () => {
    if (!dfChatId.trim()) {
      setDfMsg(lang === 'en' ? '⚠ Enter Telegram Chat ID first' : '⚠ 请先填入 Telegram Chat ID');
      return;
    }
    setDfSaving(true); setDfMsg('');
    try {
      const res = await fetch(`${RD_BASE}/api/dealflow/test-push`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId: dfChatId.trim() }),
      });
      const data = await res.json();
      if (res.status === 503) {
        setDfMsg(lang === 'en' ? '⚠ TELEGRAM_BOT_TOKEN not set on Railway — add it in Railway Dashboard' : '⚠ 未配置 TELEGRAM_BOT_TOKEN，请在 Railway Dashboard 添加该环境变量');
      } else if (!res.ok || !data.success) {
        setDfMsg(`⚠ ${data.error || 'Send failed'}`);
      } else {
        setDfMsg(lang === 'en' ? '✅ Test message sent! Check your Telegram.' : '✅ 测试消息已发送，请查看 Telegram');
      }
    } catch (e) {
      setDfMsg(`⚠ ${e.message}`);
    } finally {
      setDfSaving(false);
      setTimeout(() => setDfMsg(''), 6000);
    }
  };

  const total = Object.values(local).reduce((a, b) => a + b, 0);
  const isValid = Math.abs(total - 1.0) < 0.001;

  const handleSlider = (key, val) => {
    const v = Math.round(val * 100) / 100;
    setLocal(prev => ({ ...prev, [key]: v }));
    setSaved(false);
  };

  const applyPreset = (name) => {
    setLocal({ ...PRESETS[name] });
    setSaved(false);
  };

  const handleSave = () => {
    if (!isValid) return;
    onSave(local);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  // 重置：只重置编辑区到系统默认的起始数值，不改变已保存的账户覆盖（需要点保存才生效）
  const handleReset = () => {
    setLocal({ ...DEFAULT_WEIGHTS });
    setSaved(false);
  };

  // 清除：彻底移除账户级覆盖，各项目恢复各自的阶段自适应权重（不是重置成某一组固定数字）
  const handleClearOverride = () => {
    onClear?.();
    setLocal({ ...DEFAULT_WEIGHTS });
    setSaved(false);
  };

  const hasOverride = !!weights;

  return (
    <div style={{ maxWidth: 680 }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}><Settings size={17} strokeWidth={2} /> {lang === 'en' ? 'Score Weight Settings' : '评分权重设置'}</h2>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{lang === 'en' ? 'Adjust the 6-dimension weights to match your investment philosophy' : '调整六维评分的权重，让评分体系匹配你的投资哲学'}</div>
        <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
          <span style={{
            padding: "2px 8px", borderRadius: 10, fontWeight: 700,
            background: hasOverride ? "#eff6ff" : "#f3f4f6",
            color: hasOverride ? "#2563eb" : "#6b7280",
          }}>
            {hasOverride
              ? (lang === 'en' ? 'Using custom weights' : '当前使用：自定义权重')
              : (lang === 'en' ? 'Using system default (auto-adjusts by project stage)' : '当前使用：系统默认（按项目阶段自动调整）')}
          </span>
          {hasOverride && (
            <button onClick={handleClearOverride} style={{ ...S.btn("default"), fontSize: 10, padding: "2px 8px" }}>
              {lang === 'en' ? 'Clear override' : '清除自定义，恢复默认'}
            </button>
          )}
          {!user && (
            <span style={{ color: C.muted }}>
              {lang === 'en' ? '(not signed in — saved to this browser only)' : '（未登录，仅保存在本浏览器）'}
            </span>
          )}
        </div>
      </div>

      {/* 预设模板 */}
      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>{lang === 'en' ? 'Quick Presets' : '快速预设'}</div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {Object.keys(PRESETS).map(name => (
            <button key={name} onClick={() => applyPreset(name)}
              style={{ ...S.btn("default"), fontSize: 11, padding: "4px 12px" }}>
              {lang === 'en' ? (PRESET_LABELS[name]?.en || name) : (PRESET_LABELS[name]?.zh || name)}
            </button>
          ))}
        </div>
      </div>

      {/* 权重调节 */}
      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 14, textTransform: "uppercase", display: "flex", justifyContent: "space-between" }}>
          <span>{lang === 'en' ? 'Weight Adjustment' : '权重调节'}</span>
          <span style={{ color: isValid ? "#059669" : "#dc2626", fontWeight: 700 }}>
            {lang === 'en' ? `Total ${Math.round(total * 100)}%` : `合计 ${Math.round(total * 100)}%`} {isValid ? "✓" : (lang === 'en' ? '(must equal 100%)' : '（需等于 100%）')}
          </span>
        </div>
        {Object.keys(DEFAULT_WEIGHTS).map(key => {
          const meta = DIM_META[key];
          const pct = Math.round(local[key] * 100);
          return (
            <div key={key} style={{ marginBottom: 18 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                <div>
                  <span style={{ fontWeight: 600, fontSize: 13, color: meta.color }}>{lang === 'en' ? meta.label_en : meta.label}</span>
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{lang === 'en' ? meta.desc_en : meta.desc}</span>
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <input type="number" min={0} max={60} value={pct}
                    onChange={e => handleSlider(key, Number(e.target.value) / 100)}
                    style={{ ...S.input, width: 56, textAlign: "center", padding: "3px 6px", fontSize: 13, fontWeight: 700 }}
                  />
                  <span style={{ fontSize: 11, color: C.muted }}>%</span>
                </div>
              </div>
              <div style={{ position: "relative", height: 6, background: C.faint, borderRadius: 3 }}>
                <div style={{ width: `${Math.min(pct, 60) / 60 * 100}%`, height: "100%", background: meta.color, borderRadius: 3, transition: "width .2s" }} />
                <input type="range" min={0} max={60} step={1} value={pct}
                  onChange={e => handleSlider(key, Number(e.target.value) / 100)}
                  style={{ position: "absolute", inset: 0, opacity: 0, cursor: "pointer", width: "100%", margin: 0 }}
                />
              </div>
            </div>
          );
        })}
      </div>

      {/* 预览 */}
      <div style={{ ...S.card, marginBottom: 14 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>{lang === 'en' ? 'Weight Distribution Preview' : '权重分布预览'}</div>
        <div style={{ display: "flex", height: 24, borderRadius: 6, overflow: "hidden", gap: 1 }}>
          {Object.keys(DEFAULT_WEIGHTS).map(key => (
            <div key={key} title={`${DIM_META[key].label} ${Math.round(local[key]*100)}%`}
              style={{ flex: local[key], background: DIM_META[key].color, minWidth: local[key] > 0 ? 2 : 0, transition: "flex .3s" }} />
          ))}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 8, flexWrap: "wrap" }}>
          {Object.keys(DEFAULT_WEIGHTS).map(key => (
            <div key={key} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: C.muted }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: DIM_META[key].color }} />
              {(lang === 'en' ? DIM_META[key].label_en : DIM_META[key].label).slice(0, lang === 'en' ? 4 : 2)} {Math.round(local[key]*100)}%
            </div>
          ))}
        </div>
      </div>

      {/* 操作按钮 */}
      <div style={{ display: "flex", gap: 8 }}>
        <button onClick={handleSave} disabled={!isValid}
          style={{ ...S.btn("primary"), padding: "8px 20px", opacity: isValid ? 1 : 0.5 }}>
          {saved ? "✅ " + (lang === 'en' ? 'Saved' : '已保存') : (lang === 'en' ? 'Save Weights' : '保存权重')}
        </button>
        <button onClick={handleReset} style={{ ...S.btn("default"), padding: "8px 16px" }}>
          {lang === 'en' ? 'Reset Defaults' : '恢复默认'}
        </button>
      </div>
      {saved && (
        <div style={{ marginTop: 10, fontSize: 11, color: "#059669" }}>
          {lang === 'en' ? '✓ Weights updated. Rankings composite scores will recalculate (no re-analysis needed)' : '✓ 权重已更新。Deal Ranking 的综合评分将按新权重重新计算（已分析项目无需重新分析）'}
        </div>
      )}

      {/* ── 数据管理 ── */}
      <div style={{ marginTop: 28 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}><Database size={15} strokeWidth={2} /> {lang === 'en' ? 'Data Management' : '数据管理'}</h2>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>
          {lang === 'en' ? <>Export all analysis data to a local file for backup and cross-device restore · <strong>{projects.length}</strong> projects analyzed</> : <>导出所有分析数据到本地文件，可在其他设备导入恢复 · 当前已分析 <strong>{projects.length}</strong> 个项目</>}
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
          {/* 导出 */}
          <button
            onClick={() => {
              const payload = {
                version: "alpha_os_v2",
                exportedAt: new Date().toISOString(),
                weights: local,
                dealStatuses,
                projectNotes,
                analysisHistory,
                projects: projects.map(p => ({
                  ...p,
                  // 精简 cgData，只保留有用字段
                  cgData: p.cgData ? {
                    market_data: {
                      fully_diluted_valuation: p.cgData.market_data?.fully_diluted_valuation,
                      total_volume: p.cgData.market_data?.total_volume,
                      current_price: p.cgData.market_data?.current_price,
                    }
                  } : null,
                })),
              };
              const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json" });
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `primarylens_backup_${new Date().toISOString().slice(0, 10)}.json`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            style={{ ...S.btn("primary"), padding: "8px 18px", display: "flex", alignItems: "center", gap: 6 }}>
            <Download size={14} strokeWidth={2} /> {lang === 'en' ? 'Export Backup' : '导出数据备份'}
          </button>

          {/* 导入 */}
          <button
            onClick={() => importRef.current?.click()}
            style={{ ...S.btn("default"), padding: "8px 18px", display: "flex", alignItems: "center", gap: 6 }}>
            <Upload size={14} strokeWidth={2} /> {lang === 'en' ? 'Import Backup' : '导入数据备份'}
          </button>
          <input ref={importRef} type="file" accept=".json" style={{ display: "none" }}
            onChange={e => {
              const file = e.target.files?.[0];
              if (!file) return;
              const reader = new FileReader();
              reader.onload = ev => {
                try {
                  const data = JSON.parse(ev.target.result);
                  if (data.version !== "alpha_os_v2") throw new Error(lang === 'en' ? "Version mismatch" : "版本不匹配");
                  onImportData(data);
                  setImportMsg(lang === 'en' ? `✅ Imported: ${data.projects?.length || 0} projects, weights restored` : `✅ 导入成功：${data.projects?.length || 0} 个项目，权重已恢复`);
                } catch (err) {
                  setImportMsg(`⚠ 导入失败：${err.message}`);
                }
                e.target.value = "";
              };
              reader.readAsText(file);
            }}
          />
        </div>

        {importMsg && (
          <div style={{ fontSize: 11, color: importMsg.startsWith("✅") ? "#059669" : "#dc2626",
            background: importMsg.startsWith("✅") ? "#ecfdf5" : "#fef2f2",
            padding: "6px 12px", borderRadius: 6, marginBottom: 10 }}>
            {importMsg}
          </div>
        )}

        {/* 清空数据 */}
        <details style={{ marginTop: 4 }}>
          <summary style={{ fontSize: 11, color: C.muted, cursor: "pointer", userSelect: "none" }}>
            {lang === 'en' ? 'Danger Zone ▸' : '危险操作 ▸'}
          </summary>
          <div style={{ marginTop: 10, padding: 12, background: "#fef2f2", borderRadius: 6, border: "1px solid #fca5a5" }}>
            <div style={{ fontSize: 12, color: "#dc2626", fontWeight: 600, marginBottom: 6 }}>⚠ {lang === 'en' ? 'Clear All Analysis Data' : '清空所有分析数据'}</div>
            <div style={{ fontSize: 11, color: "#6b7280", marginBottom: 10 }}>
              {lang === 'en' ? 'This will delete all analyzed project data. This action is irreversible. Export a backup first.' : '将删除所有已分析项目数据，此操作不可撤销。建议先导出备份。'}
            </div>
            <button
              onClick={() => {
                if (window.confirm(lang === 'en' ? "Confirm clear all analysis data? This cannot be undone." : "确认清空所有分析数据？此操作不可撤销。")) {
                  onImportData({ version: "alpha_os_v2", projects: [], dealStatuses: {}, weights: local });
                  setImportMsg(lang === 'en' ? "✅ Data cleared" : "✅ 数据已清空");
                }
              }}
              style={{ ...S.btn("danger"), fontSize: 11 }}>
              {lang === 'en' ? 'Clear All Data' : '清空所有数据'}
            </button>
          </div>
        </details>
      </div>

      {/* ── Deal Flow 推送 ── */}
      <div style={{ marginTop: 28 }}>
        <h2 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}><Radio size={15} strokeWidth={2} /> {lang === 'en' ? 'Deal Flow Push' : 'Deal Flow 推送'}</h2>
        <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>
          {lang === 'en'
            ? 'Get daily early-stage project alerts on Telegram, filtered by your preferences'
            : '每天早上自动推送符合你偏好的早期项目到 Telegram'}
        </div>

        {!user ? (
          <div style={{ ...S.card, padding: "16px 14px", color: C.muted, fontSize: 12 }}>
            {lang === 'en' ? 'Sign in to configure Deal Flow push notifications' : '请登录后配置 Deal Flow 推送'}
          </div>
        ) : (
          <div style={{ ...S.card, marginBottom: 14 }}>
            {/* 启用开关 */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <span style={{ fontSize: 12, fontWeight: 600 }}>{lang === 'en' ? 'Enable daily push' : '启用每日推送'}</span>
              <button
                onClick={() => setDfEnabled(v => !v)}
                style={{ padding: "4px 14px", background: dfEnabled ? "#059669" : C.faint, border: `1px solid ${dfEnabled ? "#059669" : C.border}`, borderRadius: 12, color: dfEnabled ? "#fff" : C.muted, fontSize: 11, fontWeight: 600, cursor: "pointer" }}>
                {dfEnabled ? (lang === 'en' ? 'ON' : '已开启') : (lang === 'en' ? 'OFF' : '已关闭')}
              </button>
            </div>

            {/* Telegram Chat ID */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...S.label }}>{lang === 'en' ? 'Telegram Chat ID' : 'Telegram Chat ID'}</label>
              <input
                type="text"
                value={dfChatId}
                onChange={e => setDfChatId(e.target.value)}
                placeholder={lang === 'en' ? 'e.g. -1001234567890 or 123456789' : '如 -1001234567890 或 123456789'}
                style={{ ...S.input }}
              />
              <div style={{ fontSize: 10, color: C.muted, marginTop: 4 }}>
                {lang === 'en'
                  ? 'Send /start to @userinfobot on Telegram to get your Chat ID'
                  : '在 Telegram 发送 /start 给 @userinfobot 获取你的 Chat ID'}
              </div>
            </div>

            {/* 最低热度阈值 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...S.label }}>{lang === 'en' ? `Min Hot Index: ${dfMinHot}` : `最低热度阈值：${dfMinHot}`}</label>
              <input
                type="range" min={0} max={100} step={5} value={dfMinHot}
                onChange={e => setDfMinHot(Number(e.target.value))}
                style={{ width: "100%", accentColor: C.accent }}
              />
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10, color: C.muted }}>
                <span>0</span><span>50</span><span>100</span>
              </div>
            </div>

            {/* 融资过滤 */}
            <div style={{ marginBottom: 14 }}>
              <label style={{ ...S.label }}>{lang === 'en' ? 'Funding Filter' : '融资过滤'}</label>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { value: 'zero_only', label: lang === 'en' ? 'Zero funding only' : '仅零融资' },
                  { value: 'all',       label: lang === 'en' ? 'All projects'       : '全部项目' },
                ].map(opt => (
                  <button key={opt.value}
                    onClick={() => setDfFundingStatus(opt.value)}
                    style={{ ...S.btn(dfFundingStatus === opt.value ? "primary" : "default"), fontSize: 11, padding: "4px 12px" }}>
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* 赛道过滤 */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...S.label }}>{lang === 'en' ? 'Track Filter (empty = all)' : '赛道过滤（空 = 全部）'}</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {DF_TRACK_OPTIONS.map(track => {
                  const isSelected = dfTracks.includes(track);
                  return (
                    <button key={track}
                      onClick={() => setDfTracks(prev => isSelected ? prev.filter(t => t !== track) : [...prev, track])}
                      style={{ ...S.btn(isSelected ? "primary" : "default"), fontSize: 11, padding: "3px 10px" }}>
                      {track}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* 操作按钮 */}
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <button
                onClick={handleDfSave}
                disabled={dfSaving}
                style={{ ...S.btn("primary"), padding: "7px 18px" }}>
                {dfSaving ? (lang === 'en' ? 'Saving…' : '保存中…') : (lang === 'en' ? 'Save' : '保存设置')}
              </button>
              <button
                onClick={handleDfTest}
                disabled={dfSaving}
                style={{ ...S.btn("default"), padding: "7px 14px", fontSize: 11 }}>
                {lang === 'en' ? 'Test Push' : '发送测试'}
              </button>
              {dfMsg && (
                <span style={{ fontSize: 11, color: dfMsg.startsWith("✅") ? "#059669" : "#dc2626" }}>{dfMsg}</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ── Portfolio Pulse ── */}
      <PortfolioPulseSection user={user} lang={lang} />

      {/* ── API Keys ── */}
      <ApiKeysSection user={user} lang={lang} />
    </div>
  );
}

function PortfolioPulseSection({ user, lang }) {
  const [ppSaving, setPpSaving] = useState(false);
  const [ppMsg, setPpMsg] = useState('');

  const handleTestPulse = async () => {
    if (!user?.id) return;
    setPpSaving(true); setPpMsg('');
    try {
      const res = await fetch(`${RD_BASE}/api/portfolio/test-pulse`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });
      const data = await res.json();
      if (res.status === 503) {
        setPpMsg(lang === 'en' ? '⚠ TELEGRAM_BOT_TOKEN not set on Railway' : '⚠ 未配置 TELEGRAM_BOT_TOKEN，请在 Railway Dashboard 添加');
      } else if (res.status === 400 && data.error?.includes('No Telegram')) {
        setPpMsg(lang === 'en' ? '⚠ No Telegram Chat ID — configure Deal Flow push first' : '⚠ 未配置 Telegram Chat ID，请先在 Deal Flow 推送中设置');
      } else if (!res.ok || !data.success) {
        setPpMsg(`⚠ ${data.error || 'Send failed'}`);
      } else {
        setPpMsg(lang === 'en' ? '✅ Test Pulse sent! Check your Telegram.' : '✅ 测试 Pulse 已发送，请查看 Telegram');
      }
    } catch (e) {
      setPpMsg(`⚠ ${e.message}`);
    } finally {
      setPpSaving(false);
      setTimeout(() => setPpMsg(''), 6000);
    }
  };

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
        <BarChart3 size={15} strokeWidth={2} /> {lang === 'en' ? 'Portfolio Pulse' : 'Portfolio Pulse 周报'}
      </h2>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>
        {lang === 'en'
          ? 'Weekly automated report of your Watchlist projects, pushed to Telegram every Monday at 09:00'
          : '每周一 09:00 自动推送 Watchlist 项目变化到你的 Telegram'}
      </div>

      {!user ? (
        <div style={{ ...S.card, padding: "16px 14px", color: C.muted, fontSize: 12 }}>
          {lang === 'en' ? 'Sign in to use Portfolio Pulse' : '请登录后使用 Portfolio Pulse'}
        </div>
      ) : (
        <div style={{ ...S.card }}>
          <div style={{ fontSize: 12, color: C.text, marginBottom: 12, lineHeight: 1.6 }}>
            {lang === 'en' ? (
              <>
                Portfolio Pulse reads your <b>Watchlist</b> every Monday and sends a summary of TVL changes, score shifts, and compliance events.<br />
                Requires a Telegram Chat ID configured in <b>Deal Flow Push</b> above.
              </>
            ) : (
              <>
                Portfolio Pulse 每周一读取你的 <b>Watchlist</b>，将 TVL 变化、评分异动、合规事件汇总后推送到 Telegram。<br />
                需要先在上方 <b>Deal Flow 推送</b>中配置 Telegram Chat ID。
              </>
            )}
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <button
              onClick={handleTestPulse}
              disabled={ppSaving}
              style={{ ...S.btn("default"), padding: "7px 14px", fontSize: 11 }}>
              {ppSaving ? (lang === 'en' ? 'Sending…' : '发送中…') : (lang === 'en' ? 'Send Test Pulse' : '发送测试 Pulse')}
            </button>
            {ppMsg && (
              <span style={{ fontSize: 11, color: ppMsg.startsWith('✅') ? "#059669" : "#dc2626" }}>{ppMsg}</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ApiKeysSection({ user, lang }) {
  const [keys, setKeys] = useState([]);
  const [keysLoaded, setKeysLoaded] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [revealedKey, setRevealedKey] = useState(null); // { key, id }
  const [copied, setCopied] = useState(false);
  const [apiMsg, setApiMsg] = useState('');

  useEffect(() => {
    if (!user?.id || keysLoaded) return;
    fetch(`${RD_BASE}/api/keys?userId=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(data => { setKeys(data.keys || []); setKeysLoaded(true); })
      .catch(() => setKeysLoaded(true));
  }, [user?.id]); // eslint-disable-line

  const handleGenerate = async () => {
    if (!user?.id) return;
    setGenerating(true); setApiMsg(''); setRevealedKey(null);
    try {
      const res = await fetch(`${RD_BASE}/api/keys/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, name: newKeyName.trim() || 'Default' }),
      });
      const data = await res.json();
      if (data.error) { setApiMsg(`⚠ ${data.error}`); return; }
      setRevealedKey({ key: data.key, id: data.id });
      setNewKeyName('');
      // Refresh list
      const listRes = await fetch(`${RD_BASE}/api/keys?userId=${encodeURIComponent(user.id)}`);
      const listData = await listRes.json();
      setKeys(listData.keys || []);
    } catch (e) {
      setApiMsg(`⚠ ${e.message}`);
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (keyId) => {
    if (!user?.id) return;
    if (!window.confirm(lang === 'en' ? 'Delete this API key? This cannot be undone.' : '删除此 API Key？此操作不可撤销。')) return;
    try {
      const res = await fetch(`${RD_BASE}/api/keys/${keyId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id }),
      });
      const data = await res.json();
      if (data.success) {
        setKeys(prev => prev.filter(k => k.id !== keyId));
        if (revealedKey?.id === keyId) setRevealedKey(null);
      } else {
        setApiMsg(`⚠ ${data.error || 'delete failed'}`);
      }
    } catch (e) {
      setApiMsg(`⚠ ${e.message}`);
    }
  };

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div style={{ marginTop: 28 }}>
      <h2 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}><KeyRound size={15} strokeWidth={2} /> {lang === 'en' ? 'Data API Keys' : '数据 API Key'}</h2>
      <div style={{ fontSize: 11, color: C.muted, marginBottom: 14 }}>
        {lang === 'en'
          ? 'Use API keys to query project data programmatically from your own tools'
          : '使用 API Key 从你自己的工具程序化查询项目数据'}
      </div>

      {!user ? (
        <div style={{ ...S.card, padding: "16px 14px", color: C.muted, fontSize: 12 }}>
          {lang === 'en' ? 'Sign in to manage API keys' : '请登录后管理 API Key'}
        </div>
      ) : (
        <>
          {/* 生成新 Key */}
          <div style={{ ...S.card, marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>
              {lang === 'en' ? 'Generate New Key' : '生成新 Key'}
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
              <input
                type="text"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                placeholder={lang === 'en' ? 'Key name (optional)' : 'Key 名称（可选）'}
                style={{ ...S.input, flex: 1 }}
              />
              <button
                onClick={handleGenerate}
                disabled={generating}
                style={{ ...S.btn("primary"), padding: "7px 16px", whiteSpace: "nowrap" }}>
                {generating ? (lang === 'en' ? 'Generating…' : '生成中…') : (lang === 'en' ? '+ Generate' : '+ 生成')}
              </button>
            </div>

            {/* 显示刚生成的明文 Key（仅一次） */}
            {revealedKey && (
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 6, padding: "10px 12px" }}>
                <div style={{ fontSize: 11, color: "#15803d", fontWeight: 600, marginBottom: 6 }}>
                  {lang === 'en' ? '✅ Key generated — copy it now, it will not be shown again' : '✅ Key 已生成 — 请立即复制，之后不会再显示'}
                </div>
                <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <code style={{ flex: 1, fontSize: 11, background: "#fff", border: "1px solid #bbf7d0", borderRadius: 4, padding: "5px 8px", wordBreak: "break-all", fontFamily: "monospace" }}>
                    {revealedKey.key}
                  </code>
                  <button
                    onClick={() => handleCopy(revealedKey.key)}
                    style={{ ...S.btn("default"), fontSize: 11, padding: "5px 10px", whiteSpace: "nowrap" }}>
                    {copied ? (lang === 'en' ? '✓ Copied' : '✓ 已复制') : (lang === 'en' ? 'Copy' : '复制')}
                  </button>
                </div>
              </div>
            )}

            {apiMsg && (
              <div style={{ marginTop: 8, fontSize: 11, color: apiMsg.startsWith("✅") ? "#059669" : "#dc2626" }}>{apiMsg}</div>
            )}
          </div>

          {/* Key 列表 */}
          {keys.length > 0 && (
            <div style={{ ...S.card, marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 10, textTransform: "uppercase" }}>
                {lang === 'en' ? 'Your Keys' : '你的 Key 列表'}
              </div>
              {keys.map(k => (
                <div key={k.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 600 }}>{k.name || 'Default'}</div>
                    <div style={{ fontSize: 11, color: C.muted, fontFamily: "monospace" }}>{k.key_prefix}…</div>
                    <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                      {lang === 'en' ? `${k.calls_used}/${k.call_limit} calls · created ${new Date(k.created_at).toLocaleDateString()}` : `已用 ${k.calls_used}/${k.call_limit} 次 · 创建于 ${new Date(k.created_at).toLocaleDateString()}`}
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(k.id)}
                    style={{ ...S.btn("danger"), fontSize: 11, padding: "4px 10px" }}>
                    {lang === 'en' ? 'Delete' : '删除'}
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* 使用说明 */}
          <div style={{ ...S.card }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, marginBottom: 8, textTransform: "uppercase" }}>
              {lang === 'en' ? 'API Usage' : 'API 使用说明'}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              {lang === 'en' ? 'Query project data with your API key:' : '用你的 API Key 查询项目数据：'}
            </div>
            <code style={{ display: "block", fontSize: 10, background: C.faint, border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 12px", wordBreak: "break-all", fontFamily: "monospace", color: C.text, lineHeight: 1.6 }}>
              GET https://primary-lens-production.up.railway.app/api/v1/project?name=ProjectName&key=pl_xxx
            </code>
            <div style={{ fontSize: 10, color: C.muted, marginTop: 8 }}>
              {lang === 'en' ? 'Free tier: 100 calls/day per key. Returns composite scores, funding rounds, on-chain metrics, and compliance status.' : '免费额度：每个 Key 每天 100 次。返回综合评分、融资轮次、链上指标和合规状态。'}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function AnalyzerPage({ onAnalyzed, onProgress, initialQuery = "", initialProjectId = null, forceRefresh = false }) {
  const { lang } = useLang();
  const [input, setInput] = useState(initialQuery);
  const [loading, setLoading] = useState(false);
  const [statusRaw, setStatusRaw] = useState("");
  const [error, setError] = useState("");
  // 当前正在分析的项目名——只用于把进度往上抛给 App 顶层的悬浮进度组件展示。
  // 关键点：用 ref + 直接函数调用（而不是 useEffect 监听 state）往上抛进度——
  // 这个页面在用户跳到别的 tab 时会整个卸载（{page === "analyze" && <AnalyzerPage/>}），
  // 卸载后 useEffect 不会再跑，但 analyze() 里已经在跑的异步流程仍持有 onProgress 这个
  // 闭包引用，直接调用普通函数（不是 React 的 state setter）不受组件卸载影响，
  // 这样才能保证用户跳走之后悬浮组件还能继续收到进度更新。
  const analyzingNameRef = useRef("");
  const status = statusRaw;
  const setStatus = (text) => {
    setStatusRaw(text);
    if (onProgress) onProgress(analyzingNameRef.current, text);
  };
  const inputRef = useRef(null);
  const [uploadedFile, setUploadedFile] = useState(null); // { file, name, size }
  const [docExtracting, setDocExtracting] = useState(false);
  // ── autocomplete ───────────────────────────────────────────────
  const [suggestions, setSuggestions] = useState([]);
  const [suggestOpen, setSuggestOpen] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const suggestTimerRef = useRef(null);
  const suggestSeqRef = useRef(0);
  const lockedProjectIdRef = useRef(initialProjectId);   // 用户点选后锁定 id，回车直接进分析
  // 锁定名跟随 initialQuery 一起初始化 —— 否则 DetailPanel 重新分析时 useEffect 首跑
  // 会发现 input="X" 但 lockedName="" 不一致，错误地把 id 锁清掉，导致 fetchFullProject
  // 走 resolver 路径而非 by_id 路径 → 同名项目歧义命中错的那个
  const lockedProjectNameRef = useRef(initialQuery || '');

  // 输入触发：debounce 250ms，调 ser_inv 拉 type=1 项目（不要 type=3 人物）
  // 排序：有 hot_index 优先，funding 次之；同名项目里 Uniswap 这种知名的会浮上来
  useEffect(() => {
    if (suggestTimerRef.current) clearTimeout(suggestTimerRef.current);
    const q = (input || '').trim();
    if (q.length < 2) { setSuggestions([]); setSuggestOpen(false); return; }
    // 用户继续输入 → 取消已锁定的 id（用 ref 存的锁定名比对，suggestions 已清空时也能比）
    if (lockedProjectIdRef.current && q.toLowerCase() !== (lockedProjectNameRef.current || '').toLowerCase()) {
      lockedProjectIdRef.current = null;
      lockedProjectNameRef.current = '';
    }
    suggestTimerRef.current = setTimeout(async () => {
      const seq = ++suggestSeqRef.current;
      setSuggestLoading(true);
      try {
        const raw = await rdSkill('ser_inv', { query: q, precise_x_search: false });
        const hits = Array.isArray(raw) ? raw : (raw?.list || raw?.data || []);
        // 只要项目（type=1），按 hot+funding 排序
        // ⚠ 不再按名字去重 —— 同名项目（如新老 Pharos）都保留，靠 id/symbol/tag 区分
        // 同 id 才去重（防 RootData 自身返回重复行）
        const seenIds = new Set();
        const ranked = hits
          .filter(h => h.type === 1 && h.name)
          .filter(h => { if (seenIds.has(h.id)) return false; seenIds.add(h.id); return true; })
          .map(h => ({
            id:     h.id,
            name:   h.name,
            logo:   h.logo || '',
            tag:    h.one_liner || (Array.isArray(h.tags) ? h.tags.slice(0, 2).join(' / ') : '') || '',
            symbol: h.token_symbol || '',
            score:  (Number(h.hot_index) || 0) * 1000 + (Number(h.total_funding) || 0),
          }))
          .sort((a, b) => b.score - a.score)
          .slice(0, 8);
        if (seq !== suggestSeqRef.current) return; // 旧请求过期，丢弃
        setSuggestions(ranked);
        setSuggestOpen(ranked.length > 0);
        setActiveIdx(-1);
      } catch {
        if (seq === suggestSeqRef.current) { setSuggestions([]); setSuggestOpen(false); }
      } finally {
        if (seq === suggestSeqRef.current) setSuggestLoading(false);
      }
    }, 250);
    return () => clearTimeout(suggestTimerRef.current);
  }, [input]);

  const pickSuggestion = (s) => {
    setInput(s.name);
    lockedProjectIdRef.current = s.id;
    lockedProjectNameRef.current = s.name;
    setSuggestOpen(false);
    setSuggestions([]);
    setActiveIdx(-1);
    // 直接触发分析（携带 projectId 走精确路径）
    setTimeout(() => analyze(s.name), 50);
  };

  const extractDocText = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1]; // remove data:...;base64, prefix
        try {
          const res = await fetch(`${RD_BASE}/api/doc-extract`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ filename: file.name, base64 }),
          });
          const data = await res.json();
          if (data.error) reject(new Error(data.error));
          else resolve(data.extractedText || '');
        } catch (e) { reject(e); }
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const analyze = async (directQuery, force = false) => {
    const rawArg = directQuery !== undefined ? directQuery : input;
    // 兼容对象传入（如 Pipeline/Trending 传了整个 deal 而非 name）
    const q = typeof rawArg === 'string' ? rawArg.trim() : (rawArg?.name || rawArg?.project_name || String(rawArg || '')).trim();
    // 允许纯文档模式（无项目名，但有上传文件）
    if ((!q || q.startsWith('[object')) && !uploadedFile) return;
    if (loading) return;
    analyzingNameRef.current = q || uploadedFile?.name?.replace(/\.[^/.]+$/, '') || '';
    setLoading(true); setError(""); setStatus("");
    // 关掉 autocomplete — 决策已做，下拉框还存在会占屏幕 + 误点会触发新分析
    setSuggestOpen(false); setSuggestions([]);

    try {
      // ── 纯文档模式：无项目名，跳过 RootData，直接用文档内容分析 ─────
      if (!q && uploadedFile) {
        const docName = uploadedFile.name.replace(/\.[^/.]+$/, ''); // 去掉扩展名作为项目名
        setStatus(lang === 'en' ? '📎 Extracting document...' : '📎 提取文档内容...');
        let docText = '';
        try {
          docText = await extractDocText(uploadedFile.file);
        } catch (e) {
          setError(lang === 'en' ? `Document extraction failed: ${e.message}` : `文档提取失败：${e.message}`);
          return;
        }
        if (!docText?.trim()) {
          setError(lang === 'en' ? 'Could not extract text from document' : '无法从文档中提取文本，请检查文件格式');
          return;
        }
        const stubRaw = {
          details: { project_name: docName, one_liner: '', description: '', tags: [], ecosystem: [], investors: [], establishment_date: '', token_symbol: '', total_funding: 0, social_media: {} },
          fundingRounds: [], cgData: null, defiData: null, webIntel: null,
          githubSignal: null, socialSignals: null, duneTokenInfo: null, duneDashboard: null,
          teamMembers: [], docContent: docText,
        };
        const analysis = await scoreProject(stubRaw, setStatus);
        onAnalyzed({
          id: Date.now(), name: docName,
          details: stubRaw.details, fundingRounds: [], cgData: null, defiData: null,
          webIntel: null, githubSignal: null, socialSignals: null, duneTokenInfo: null,
          duneDashboard: null, duneAnalytics: null, complianceCheck: null,
          teamMembers: [], analysis,
          analyzedAt: new Date().toISOString(), docOnly: true,
        });
        setStatus("✅ 分析完成");
        setInput("");
        return;
      }

      // ── 1. 检查缓存（force=true 时跳过，强制重新拉取）──────────────────
      setStatus("⚡ 检查分析缓存...");
      // 如果 autocomplete 锁定了 rootdata_id，传给缓存层做严格匹配
      // 避免：搜"Pharos Network"选中 RWA 公链 → 缓存返回另一个"Pharos"
      const cacheKeyId = lockedProjectIdRef.current || initialProjectId;
      const cached = !force && await getCached(q, { rootdataId: cacheKeyId });
      if (cached?.analysis && cached?.raw_data) {
        setStatus("⚡ 命中缓存 · 跳过 AI 重新评分");
        const r = cached.raw_data;
        const tokenSym = r.details?.token_symbol?.trim() || "";

        // 缓存命中时补拉缺失的 Dune 数据（保留 AI 评分）
        let patchedDuneAnalytics = r.duneAnalytics || null;
        let patchedDuneDashboard = r.duneDashboard || null;

        // 补拉 Analytics（缺失时）
        if (!patchedDuneAnalytics && tokenSym) {
          setStatus("⚡ 命中缓存 · 补充 Dune 链上数据...");
          try {
            const freshDA = await fetchDuneAnalytics(r.cgData?.platforms || null, tokenSym);
            if (freshDA) {
              patchedDuneAnalytics = freshDA;
              const isHLNative = freshDA.source === 'hyperliquid_market_data';
              if (isHLNative && freshDA.query_url && (!patchedDuneDashboard || patchedDuneDashboard.type === 'none')) {
                patchedDuneDashboard = { type: 'activate', activateUrl: freshDA.query_url, chain: 'hyperliquid', coin: freshDA.coin };
              }
            }
          } catch { /* 补拉失败不影响显示 */ }
        }

        // 重拉 duneDashboard：旧缓存没有 candidates 字段，或 type=activate/none 时重新发现
        const needRefreshDashboard =
          !patchedDuneDashboard ||
          patchedDuneDashboard.type === 'none' ||
          (patchedDuneDashboard.type === 'activate' && !Array.isArray(patchedDuneDashboard.candidates));
        if (needRefreshDashboard) {
          try {
            const projectName = r.details?.project_name || q;
            const freshDD = await findOrPrepareDuneDashboard(r.cgData?.platforms || null, projectName);
            if (freshDD && freshDD.type !== 'none') patchedDuneDashboard = freshDD;
          } catch { /* 失败静默 */ }
        }

        // 缓存命中：所有新字段一律并行重新拉取（旧缓存不含这些字段）
        // marketContext、tokenFutures：实时数据，每次必须重拉
        // artemisMetrics、tokenUnlock：12h 服务端缓存，快速返回
        // topHolders：EVM 代币从 Surf 拉，HYPE 等原生链无合约地址则跳过
        setStatus("⚡ 命中缓存 · 补充实时 & 链上数据...");

        const ethContract = r.cgData?.platforms?.ethereum;
        // 与非缓存路径一致：token-unlock 必须带项目名做交叉校验，否则短/重名 symbol（如 H、X）会取错项目
        const cachedProjectName = r.details?.project_name || '';
        const tokenUnlockQs = new URLSearchParams({ symbol: tokenSym });
        if (cachedProjectName) tokenUnlockQs.set('name', cachedProjectName);
        const [freshMarketCtx, freshTokenFutures, freshArtemis, freshUnlock, freshHolders] = await Promise.all([
          fetch(`${RD_BASE}/api/market-context`).then(r2 => r2.ok ? r2.json() : null).catch(() => null),
          tokenSym ? fetch(`${RD_BASE}/api/token-futures?symbol=${encodeURIComponent(tokenSym)}`).then(r2 => r2.ok ? r2.json() : null).catch(() => null) : Promise.resolve(null),
          tokenSym ? fetch(`${RD_BASE}/api/artemis-metrics?symbol=${encodeURIComponent(tokenSym)}`).then(r2 => r2.ok ? r2.json() : null).catch(() => null) : Promise.resolve(null),
          tokenSym ? fetch(`${RD_BASE}/api/token-unlock?${tokenUnlockQs}`).then(r2 => r2.ok ? r2.json() : null).catch(() => null) : Promise.resolve(null),
          ethContract ? fetch(`${RD_BASE}/api/surf?path=/token/holders&address=${ethContract}&chain=ethereum&limit=10`).then(r2 => r2.ok ? r2.json() : null).catch(() => null) : Promise.resolve(null),
        ]);

        // 处理 topHolders（Surf 返回 {data:[{percentage,entity_name,...}]}）
        let freshHoldersProcessed = null;
        if (freshHolders?.data?.length) {
          const holders = freshHolders.data;
          const top10Pct = holders.slice(0, 10).reduce((s, h) => s + (h.percentage || 0), 0);
          freshHoldersProcessed = {
            top10Concentration: parseFloat(top10Pct.toFixed(2)),
            holders: holders.slice(0, 10).map(h => ({
              address: h.address, percentage: parseFloat((h.percentage || 0).toFixed(3)),
              entityName: h.entity_name || null, entityType: h.entity_type || null,
              balance: h.balance ? parseFloat(h.balance) : null,
            })),
            chain: 'ethereum',
          };
        }

        // DefiLlama revenue：旧缓存 defiData 可能没有 revenue 字段，尝试补充
        // chain 类型跳过 — /summary/fees 是 protocol 端点，chain 走 /overview/fees 已在 fetchDefiLlama 内
        let patchedDefiData = r.defiData || null;
        if (patchedDefiData && !patchedDefiData.revenue && patchedDefiData.protocol?.slug && patchedDefiData.protocol?.type !== 'chain') {
          try {
            const revRes = await fetch(`https://api.llama.fi/summary/fees/${patchedDefiData.protocol.slug}?dataType=dailyRevenue&excludeTotalDataChartBreakdown=true`);
            if (revRes.ok) {
              const revData = await revRes.json();
              if (revData?.total30d != null || revData?.total24h != null) {
                patchedDefiData = { ...patchedDefiData, revenue: { daily: revData.total24h || null, weekly: revData.total7d || null, monthly: revData.total30d || null } };
              }
            }
          } catch { /* 静默失败 */ }
        }

        onAnalyzed({
          id: Date.now(),
          name: r.details?.project_name || q,
          details: r.details,
          fundingRounds: r.fundingRounds,
          cgData: r.cgData,
          defiData: patchedDefiData,
          webIntel: r.webIntel,
          githubSignal: r.githubSignal || null,
          socialSignals: r.socialSignals || null,
          duneTokenInfo: r.duneTokenInfo || null,
          duneDashboard: patchedDuneDashboard,
          duneAnalytics: patchedDuneAnalytics,
          complianceCheck: r.complianceCheck || null,
          waybackSnapshot: r.waybackSnapshot || null,
          timeline: r.timeline || [],
          teamMembers: r.teamMembers || [],
          teamSignals: r.teamSignals || null,
          // ── 所有新字段：并行重拉，不依赖旧缓存 ──
          marketContext:  freshMarketCtx  || null,
          tokenFutures:   freshTokenFutures || null,
          artemisMetrics: (freshArtemis?.ok ? freshArtemis : null) || r.artemisMetrics || null,
          // 二次校验跟非缓存路径对称：Surf symbol 冲突的脏数据也能在 client 层拦截
          tokenUnlock:    validateTokenUnlock(freshUnlock?.ok ? freshUnlock : null, r.cgData) || r.tokenUnlock || null,
          topHolders:     freshHoldersProcessed || r.topHolders || null,
          enhancedTeam:   r.enhancedTeam || null, // 团队增强（官网 + GitHub + Twitter）
          recentUpdatesSummary: r.recentUpdatesSummary || null, // 项目近况摘要
          projectContext: r.projectContext || null, // Step 2c: 项目画像综合
          complianceRisk: r.complianceRisk || null, // A3: 合规风险评估——之前这条缓存命中路径漏了这个字段，
          // 导致"监管合规尽调"整块内容永远到不了 React state，只有触发全新分析时才看得到（Q67）
          marketVolume:   r.marketVolume   || null, // 全市场 24h 交易（CG 聚合）
          analysis: cached.analysis,
          analyzedAt: cached.scored_at,
          fromCache: true,
          missingFeatures: cached._missingFeatures || [], // banner 触发
        });
        const mf = cached._missingFeatures || [];
        if (mf.length > 0) {
          setStatus(`✅ 已加载缓存 · ⚠️ 缺少 ${mf.join('/')}，点 🔄 强刷可补齐`);
        } else {
          setStatus("✅ 分析完成（来自缓存）");
        }
        setInput("");
        return;
      }

      // ── 2. 缓存未命中 — 全量拉取 + Claude 评分 ────────────────────────
      setStatus("🔍 多源交叉识别中（RootData + CoinGecko + DeFiLlama）...");
      // 优先使用 autocomplete 选中的 projectId（避免 RootData 同名歧义匹配）
      const pickedId = lockedProjectIdRef.current || initialProjectId;
      const raw = await fetchFullProject(q, { projectId: pickedId });
      // 显示匹配信息让用户立刻验证
      const matchedName = raw.details?.project_name || q;
      const resInfo = raw.details?._resolveInfo;
      if (raw.details?._lightweight) {
        setStatus(`🧠 未在公开数据源找到「${q}」，已切换至 AI 轻量分析模式`);
      } else if (raw.details?._matchMethod === "by_id") {
        const mm = raw.details?._byIdNameMismatch;
        if (mm) {
          setStatus(`⚠ 你选的「${mm.queried}」(ID ${mm.id})对应项目实际名为「${mm.returned}」— 若不是你要的项目，请回搜索框换 ID 重选`);
        } else {
          setStatus(`✓ 已加载: ${matchedName} (ID: ${raw.details._matchedId})`);
        }
      } else if (raw.details?._matchMethod === "multi_source") {
        const srcs = Object.keys(resInfo?.best?.sources || {}).join("/");
        setStatus(`✓ 多源确认: ${matchedName} (来源: ${srcs})`);
      } else if (raw.details?._matchMethod === "exact") {
        setStatus(`✓ 精确匹配: ${matchedName}`);
      } else if (raw.details?._matchMethod === "substring") {
        setStatus(`≈ 模糊匹配: 「${q}」→ ${matchedName} (建议确认)`);
      }

      // If a document was uploaded, extract its text and add to raw
      if (uploadedFile) {
        setStatus(lang === 'en' ? '📎 Extracting document...' : '📎 提取文档内容...');
        try {
          const docText = await extractDocText(uploadedFile.file);
          if (docText) raw.docContent = docText;
        } catch (e) {
          console.warn('[doc-extract]', e.message);
          // Non-fatal: continue analysis without document
        }
      }

      setStatus("🌐 爬取官网情报...");
      // Q58: AI 评分失败（lanyi + DeepSeek 双 timeout）时降级到旧 cache
      //  目的：lanyi 政策切换/超时让新分析报错时，用户至少能看到一份"X 天前的分析"
      //  而非空白红色错误页。stale 标记走 analysis._staleFromAITimeout，
      //  下游 UI（DetailPanel / PdfReport）展示橙色 banner。
      let analysis;
      try {
        analysis = await scoreProject(raw, setStatus);
      } catch (scoringErr) {
        const errMsg = scoringErr?.message || '';
        const isAITimeout = /AI 评分服务繁忙|timeout|504/i.test(errMsg);
        if (!isAITimeout) throw scoringErr;
        const projectName = raw.details?.project_name || q;
        const staleCache = await getCached(projectName, {
          rootdataId: raw.details?.id || raw.details?.project_id,
        }).catch(() => null);
        if (!staleCache?.analysis) throw scoringErr;
        console.warn(`[Q58 cache-fallback] AI 评分失败 → 使用 ${staleCache.scored_at?.slice(0, 10)} 旧评分（原因：${errMsg.slice(0, 80)}）`);
        analysis = {
          ...staleCache.analysis,
          _staleFromAITimeout: true,
          _staleAnalysisDate: staleCache.scored_at,
          _staleErrorMessage: errMsg.slice(0, 200),
        };
        setStatus(`⚠️ AI 评分暂忙 · 显示 ${(staleCache.scored_at || '').slice(0, 10)} 旧评分（点 🔄 重试）`);
      }

      const projectName = raw.details.project_name;
      onAnalyzed({
        id: Date.now(),
        name: projectName,
        details: raw.details,
        fundingRounds: raw.fundingRounds,
        cgData: raw.cgData,
        defiData: raw.defiData,
        webIntel: raw.webIntel,
        githubSignal: raw.githubSignal || null,
        socialSignals: raw.socialSignals || null,
        duneTokenInfo: raw.duneTokenInfo || null,
        duneDashboard: raw.duneDashboard || null,
        duneAnalytics: raw.duneAnalytics || null,
        complianceCheck: raw.complianceCheck || null,
        waybackSnapshot: raw.waybackSnapshot || null,
        timeline: raw.timeline || [],
        teamMembers: raw.teamMembers || [],
        teamSignals: raw.teamSignals || null,
        // ── 补齐所有新字段（fetchFullProject 已并行拉取）──
        marketContext:  raw.marketContext  || null,
        tokenFutures:   raw.tokenFutures   || null,
        artemisMetrics: raw.artemisMetrics || null,
        tokenUnlock:    raw.tokenUnlock    || null,
        topHolders:     raw.topHolders     || null,
        enhancedTeam:   raw.enhancedTeam   || null, // 团队增强（官网 + GitHub + Twitter）
        recentUpdatesSummary: raw.recentUpdatesSummary || null, // 项目近况摘要
        projectContext: raw.projectContext || null, // Step 2c: 项目画像综合
        complianceRisk: raw.complianceRisk || null, // A3: 合规风险评估
        marketVolume:   raw.marketVolume   || null, // 全市场 24h 交易（CG 聚合）
        _debug:         raw._debug         || null, // 数据源健康度（footer 🔍 诊断按钮用）
        analysis,
        analyzedAt: new Date().toISOString(),
      });

      // ── 3. 异步写缓存 + structured_labels ────────────────────────────
      writeCache(projectName, raw.details.id || raw.details.project_id, analysis, raw).catch(() => {});

      setStatus("✅ 分析完成");
      setInput("");
    } catch (e) {
      setError(e.message);
      setStatus("");
    } finally { setLoading(false); }
  };

  // key={analyzeQuery} in App remounts this component for each new query,
  // so this effect fires exactly once per navigation from Pipeline/热榜.
  useEffect(() => { if (initialQuery) analyze(initialQuery, forceRefresh); }, []);

  const SAMPLES = ["Pharos Network", "Monad", "Babylon", "Berachain", "EigenLayer", "Hyperliquid", "Initia", "ARQ"];

  return (
    <div style={{ maxWidth: 680, margin: "0 auto" }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 6 }}>{lang === 'en' ? 'Project Analysis' : '项目分析'}</h2>
      <p style={{ fontSize: 12, color: C.muted, marginBottom: 20 }}>{lang === 'en' ? 'Enter a project name — see team, product, cash flow, tokenomics, and risk in 30 seconds' : '输入项目名，30 秒看清团队、产品、资金流、代币模型、风险'}</p>

      <div style={{ ...S.card, padding: 18, marginBottom: 20 }}>
        <label style={S.label}>{lang === 'en' ? 'Project Name / Token Symbol' : '项目名称 / 代币符号'}</label>
        <div style={{ display: "flex", gap: 8, position: "relative" }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Search size={14} color={C.muted} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
            <input ref={inputRef} value={input}
              onChange={e => setInput(e.target.value)}
              onFocus={() => suggestions.length > 0 && setSuggestOpen(true)}
              onBlur={() => setTimeout(() => setSuggestOpen(false), 180)}
              onKeyDown={e => {
                if (suggestOpen && suggestions.length > 0) {
                  if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(suggestions.length - 1, i + 1)); return; }
                  if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(-1, i - 1)); return; }
                  if (e.key === 'Escape')    { setSuggestOpen(false); return; }
                  if (e.key === 'Enter' && activeIdx >= 0) { e.preventDefault(); pickSuggestion(suggestions[activeIdx]); return; }
                }
                if (e.key === 'Enter') analyze();
              }}
              placeholder={lang === 'en' ? 'e.g. Pharos Network, ARQ, EigenLayer...' : '如：Pharos Network, ARQ, EigenLayer...'}
              style={{ ...S.input, width: "100%", paddingLeft: 30 }} disabled={loading}
              autoComplete="off" />
            {suggestOpen && (suggestions.length > 0 || suggestLoading) && (
              <div role="listbox" style={{
                position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 20,
                background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)", maxHeight: 360, overflowY: "auto",
              }}>
                {suggestLoading && suggestions.length === 0 && (
                  <div style={{ padding: "10px 12px", fontSize: 11, color: C.muted }}>
                    {lang === 'en' ? 'Searching RootData…' : '搜索 RootData…'}
                  </div>
                )}
                {(() => {
                  // 标记同名项目，给用户视觉提醒
                  const nameCount = {};
                  suggestions.forEach(s => { const k = (s.name || '').toLowerCase(); nameCount[k] = (nameCount[k] || 0) + 1; });
                  return suggestions.map((s, i) => {
                    const isDup = nameCount[(s.name || '').toLowerCase()] > 1;
                    return (
                  <div key={s.id} role="option" aria-selected={i === activeIdx}
                    onMouseDown={e => { e.preventDefault(); pickSuggestion(s); }}
                    onMouseEnter={() => setActiveIdx(i)}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "8px 12px",
                      cursor: "pointer", borderBottom: i < suggestions.length - 1 ? `1px solid ${C.faint}` : "none",
                      background: i === activeIdx ? C.faint : "transparent",
                    }}>
                    {s.logo
                      ? <img src={proxyLogo(s.logo)} alt="" style={{ width: 24, height: 24, borderRadius: 4, objectFit: "cover", flexShrink: 0 }} onError={e => e.target.style.display = "none"} />
                      : <div style={{ width: 24, height: 24, borderRadius: 4, background: C.faint, flexShrink: 0 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700, fontSize: 13, color: C.text }}>{s.name}</span>
                        {s.symbol && <span style={{ fontSize: 10, color: C.muted }}>${s.symbol}</span>}
                        {isDup && (
                          <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: "#FAEEDA", color: "#854F0B", fontWeight: 700 }}
                            title={lang === 'en' ? 'Multiple projects share this name — check ID / tag to pick correctly' : '存在同名项目，请通过 ID / 简介区分'}>
                            {lang === 'en' ? 'same name' : '同名'}
                          </span>
                        )}
                      </div>
                      {s.tag && <div style={{ fontSize: 10, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.tag}</div>}
                    </div>
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
                      <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 3, background: C.faint, color: C.muted }}>{lang === 'en' ? 'Project' : '项目'}</span>
                      <span style={{ fontSize: 8, color: C.muted, fontFamily: "monospace" }} title="RootData ID">#{s.id}</span>
                    </div>
                  </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
          <button onClick={() => analyze()} disabled={loading || (!input.trim() && !uploadedFile)} style={{ ...S.btn("primary"), minWidth: 80 }}>
            {loading ? (lang === 'en' ? 'Analyzing...' : '分析中') : (lang === 'en' ? 'Analyze' : '开始分析')}
          </button>
          <button
            onClick={() => analyze(input || undefined, true)}
            disabled={loading || (!input.trim() && !uploadedFile)}
            title={lang === 'en'
              ? 'Force re-fetch: bypass cache and re-run all data sources + AI scoring. Use when you suspect stale data.'
              : '强制刷新：跳过 6-72 小时缓存，重新拉取所有数据源并跑一次 AI 评分。怀疑数据不对时点这个。'}
            style={{ ...S.btn("default"), minWidth: 64, padding: "0 10px", fontSize: 12, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <RotateCw size={12} strokeWidth={2} /> <span style={{ fontSize: 11 }}>{lang === 'en' ? 'Refresh' : '强刷'}</span>
          </button>
        </div>
        {status && <div style={{ fontSize: 11, color: C.muted, marginTop: 8 }}>{status}</div>}
        {error && <div style={{ fontSize: 11, color: "#dc2626", marginTop: 8 }}>⚠ {error}</div>}
        {/* 文档上传区域 */}
        <div style={{ marginTop: 12, padding: "10px 12px", background: "#f8fafc", border: `1px dashed ${C.border}`, borderRadius: 8 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <label style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              <input
                type="file"
                accept=".pdf,.md,.txt,.docx,.pptx,.xlsx,.zip"
                style={{ display: "none" }}
                onChange={e => {
                  const f = e.target.files?.[0];
                  if (f) setUploadedFile({ file: f, name: f.name, size: f.size });
                  e.target.value = '';
                }}
              />
              <span style={{ ...S.btn("default"), fontSize: 11, padding: "4px 10px", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 5 }}>
                <Paperclip size={12} strokeWidth={2} /> {lang === 'en' ? 'Upload Doc' : '上传文档'}
              </span>
            </label>
            {uploadedFile ? (
              <div style={{ display: "flex", alignItems: "center", gap: 6, flex: 1 }}>
                <span style={{ fontSize: 11, color: C.text, fontWeight: 600, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 5 }}>
                  <CircleCheck size={12} strokeWidth={2} color="#16a34a" /> {uploadedFile.name}
                </span>
                <span style={{ fontSize: 10, color: C.muted }}>
                  {(uploadedFile.size / 1024).toFixed(0)}KB
                </span>
                <button onClick={() => setUploadedFile(null)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, fontSize: 12, padding: "0 4px" }}>✕</button>
              </div>
            ) : (
              <span style={{ fontSize: 10, color: C.muted }}>
                {lang === 'en' ? 'Upload whitepaper, pitch deck, or docs for deeper AI analysis (optional)' : '上传白皮书、Pitch Deck 或技术文档，AI 将结合文档做更深度分析（可选）'}
              </span>
            )}
          </div>
          {uploadedFile && (
            <div style={{ fontSize: 10, color: "#059669", marginTop: 5 }}>
              {lang === 'en' ? '📄 Document will be extracted and combined with project data for analysis' : '📄 文档内容将与项目数据合并，提升分析深度'}
            </div>
          )}
        </div>
        <div style={{ marginTop: 12, display: "flex", gap: 4, flexWrap: "wrap" }}>
          {SAMPLES.map(n => (
            <button key={n} onClick={() => { setInput(n); inputRef.current?.focus(); }} style={{ ...S.btn(), fontSize: 11, padding: "3px 8px" }}>{n}</button>
          ))}
        </div>
      </div>

      <RecentAnalyses onPick={(name) => { setInput(name); setTimeout(() => analyze(name), 50); }} />
    </div>
  );
}

// ══════════════════════════════════════════════════════
//  RECENT ANALYSES — project_cache 没有 user_id 列，这里读到的是全站共享缓存，
//  不是个人分析历史；文案必须如实反映范围，不能叫"我的最近分析"
// ══════════════════════════════════════════════════════
function RecentAnalyses({ onPick }) {
  const { lang } = useLang();
  const [rows, setRows] = useState(null); // null = 加载中，[] = 加载完成但为空

  useEffect(() => {
    let cancelled = false;
    import('./lib/supabase').then(({ supabase }) => {
      if (!supabase) { if (!cancelled) setRows([]); return; }
      supabase
        .from('project_cache')
        .select('project_name, analysis, scored_at')
        .order('scored_at', { ascending: false })
        .limit(25)
        .then(({ data }) => {
          if (cancelled) return;
          // project_cache 每次分析都是新 insert（非 upsert），同一项目可能有多行——
          // 按项目名去重，只保留最新一行，凑够 5 个不同项目
          const seen = new Set();
          const deduped = [];
          for (const row of (data || [])) {
            const key = (row.project_name || '').toLowerCase();
            if (!key || seen.has(key) || row.analysis?.composite == null) continue;
            seen.add(key);
            deduped.push(row);
            if (deduped.length >= 5) break;
          }
          setRows(deduped);
        });
    });
    return () => { cancelled = true; };
  }, []);

  if (!rows || rows.length === 0) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <div style={S.label}>{lang === 'en' ? 'Recently analyzed (site-wide)' : '全站最近分析'}</div>
      <div style={{ marginTop: 8, display: "flex", flexDirection: "column", gap: 6 }}>
        {rows.map(row => {
          const composite = row.analysis?.composite || 0;
          return (
            <div key={row.project_name}
              onClick={() => onPick(row.project_name)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: `1px solid ${C.border}`, borderRadius: 6, cursor: "pointer" }}>
              <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.project_name}</span>
              <span style={S.rBadge(composite)}>{rating(composite).label} {composite}</span>
              <span style={{ fontSize: 11, color: C.muted, width: 56, textAlign: "right", flexShrink: 0 }}>{relativeTime(row.scored_at, lang)}</span>
              <ChevronRight size={14} color={C.muted} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function relativeTime(iso, lang) {
  if (!iso) return '';
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return lang === 'en' ? 'just now' : '刚刚';
  if (mins < 60) return lang === 'en' ? `${mins}m ago` : `${mins} 分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return lang === 'en' ? `${hours}h ago` : `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days === 1) return lang === 'en' ? 'yesterday' : '昨天';
  if (days < 30) return lang === 'en' ? `${days}d ago` : `${days} 天前`;
  return new Date(iso).toLocaleDateString(lang === 'en' ? 'en-US' : 'zh-CN');
}

// ══════════════════════════════════════════════════════
//  WATCHLIST SCORE DELTA
// ══════════════════════════════════════════════════════
function WatchlistScoreDelta({ projectName, savedScore }) {
  const [delta, setDelta] = useState(null);
  useEffect(() => {
    if (!projectName) return;
    import('./lib/supabase').then(({ supabase }) => {
      if (!supabase) return;
      // 大小写不敏感：用户搜 "uniswap" 也能匹配以 "Uniswap" 写入的 history
      const safeNameHist = String(projectName || '').replace(/[%_\\]/g, '\\$&');
      supabase
        .from('score_history')
        .select('composite, scored_at')
        .ilike('project_name', safeNameHist)
        .order('scored_at', { ascending: false })
        .limit(20)
        .then(({ data }) => {
          if (!data?.length) return;
          const latest = data[0].composite;
          const sevenDaysAgo = Date.now() - 7 * 86400 * 1000;
          const older = data.find(r => new Date(r.scored_at).getTime() < sevenDaysAgo);
          const base = older ? older.composite : savedScore;
          if (base != null && latest != null) setDelta(latest - base);
        });
    });
  }, [projectName, savedScore]);

  if (delta === null) return null;
  if (delta === 0) return null;
  const isUp = delta > 0;
  return (
    <span style={{
      fontSize: 9, fontWeight: 700,
      color: isUp ? "#10b981" : "#ef4444",
      marginLeft: 4,
    }}>
      {isUp ? '▲' : '▼'}{Math.abs(delta)}
    </span>
  );
}

// ══════════════════════════════════════════════════════
//  MAIN APP
// ══════════════════════════════════════════════════════
const PAGES = ["trending", "pipeline", "analyze", "portfolio", "watchlist", "compare", "settings"];

function App() {
  const t = useT();
  const { lang } = useLang();
  const [page, setPage] = useState(() => {
    const hash = window.location.hash.slice(1);
    const hashPage = hash.split("/")[0];
    return PAGES.includes(hashPage) ? hashPage : "pipeline";
  });
  // 侧边栏折叠：纯展示偏好，跟 page/selected 这些路由状态无关，单独存一个 key，
  // 刷新/重新打开都保留上次的选择
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1"; } catch { return false; }
  });
  const toggleSidebarCollapsed = () => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0"); } catch {}
      return next;
    });
  };
  // onAnalyzed 是通过 AnalyzerPage 挂载时那次渲染的闭包传下去的——用户中途跳走后
  // AnalyzerPage 卸载，但正在跑的分析仍持有那个旧闭包，里面的 page 是"挂载时那一刻"
  // 的值（永远是 "analyze"），不会跟着后续渲染更新。要拿到"分析真正完成那一刻"
  // 用户实际在哪个页面，必须读一个随渲染同步更新的 ref，不能直接读 page 变量本身。
  const pageRef = useRef(page);
  useEffect(() => { pageRef.current = page; }, [page]);
  const [projects, setProjects] = useState(() => {
    try {
      const parsed = JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
      if (parsed.length > MAX_STORED_PROJECTS) {
        // 已经超限的历史累积（老用户升级前存的）：加载时立即裁剪回写，
        // 在用户点登录前就把配额腾出来，而不是等下次分析项目才生效。
        const trimmed = parsed.slice(0, MAX_STORED_PROJECTS);
        try { localStorage.setItem(STORE_KEY, JSON.stringify(trimmed)); } catch {}
        return trimmed;
      }
      return parsed;
    } catch { return []; }
  });
  const [selected, setSelected] = useState(() => {
    // 同步从 hash + localStorage 初始化，避免 effect 执行顺序问题覆盖 hash
    const hash = window.location.hash.slice(1);
    const parts = hash.split("/");
    if (parts[0] === "portfolio" && parts[1]) {
      try {
        const stored = JSON.parse(localStorage.getItem(STORE_KEY) || "[]");
        const projectName = decodeURIComponent(parts[1]);
        return stored.find(p => p.name === projectName) || null;
      } catch { return null; }
    }
    return null;
  });
  const [analyzeQuery, setAnalyzeQuery] = useState(() => {
    // 支持直接访问 #analyze/ProjectName 链接时自动加载对应项目
    const hash = window.location.hash.slice(1);
    const parts = hash.split("/");
    return parts[0] === "analyze" && parts[1] ? decodeURIComponent(parts[1]) : "";
  });
  const [analyzeForce, setAnalyzeForce] = useState(false);
  const [compareInit, setCompareInit] = useState({ leftId: "", rightId: "" });
  const [ffMap, setFfMap] = useState({}); // fundraise.fun 核查结果（EarlyRadar 更新后同步至此）
  const [shareToast, setShareToast] = useState(null); // { name, composite, grade } — 分析完成后的分享 Toast
  const [analyzeTask, setAnalyzeTask] = useState(null); // { name, stage, pct, done, composite, grade, fg } | null — 跨页面悬浮进度
  const [analyzeWidgetOpen, setAnalyzeWidgetOpen] = useState(false); // 悬浮进度圆环是否展开成气泡
  // 悬浮进度圆环的位置（相对右下角的偏移），可拖动，记住上次拖到哪
  const [widgetPos, setWidgetPos] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('analyze_widget_pos'));
      return saved && typeof saved.right === 'number' && typeof saved.bottom === 'number' ? saved : { right: 20, bottom: 20 };
    } catch { return { right: 20, bottom: 20 }; }
  });
  const widgetDragRef = useRef(null); // 拖动中的临时状态,不需要触发渲染,用 ref 存
  const isMobile = useIsMobile();

  // 悬浮圆环的 mousedown——用位移量区分"点击"（导航到详情页/展开气泡）和"拖动"
  // （挪动位置并记住），阈值 5px 内算点击，避免拖和点互相打架。
  const handleWidgetMouseDown = (e) => {
    if (e.button !== 0) return;
    widgetDragRef.current = {
      startX: e.clientX, startY: e.clientY,
      startRight: widgetPos.right, startBottom: widgetPos.bottom,
      lastRight: widgetPos.right, lastBottom: widgetPos.bottom,
      moved: false,
    };
    const handleMove = (ev) => {
      const ds = widgetDragRef.current;
      if (!ds) return;
      const dx = ev.clientX - ds.startX;
      const dy = ev.clientY - ds.startY;
      if (Math.abs(dx) > 5 || Math.abs(dy) > 5) ds.moved = true;
      const newRight = Math.max(4, ds.startRight - dx);
      const newBottom = Math.max(4, ds.startBottom - dy);
      ds.lastRight = newRight; ds.lastBottom = newBottom;
      setWidgetPos({ right: newRight, bottom: newBottom });
    };
    const handleUp = () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
      const ds = widgetDragRef.current;
      widgetDragRef.current = null;
      if (!ds) return;
      if (ds.moved) {
        try { localStorage.setItem('analyze_widget_pos', JSON.stringify({ right: ds.lastRight, bottom: ds.lastBottom })); } catch {}
        return;
      }
      // 没有明显位移 → 当成点击：有数据就直接进详情页，还没有数据（全新项目分析中）
      // 就展开气泡,好过完全没反应
      const proj = projects.find(p => p.name === analyzeTask?.name);
      if (proj) { setSelected(proj); setAnalyzeTask(null); }
      else { setAnalyzeWidgetOpen(v => !v); }
    };
    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
  };

  // ── Hash routing: keep URL in sync with active page + project ─────
  // 格式：
  //   #analyze/ProjectName    — 正在分析某个项目
  //   #portfolio/ProjectName  — 详情面板打开中
  //   #earlybird/ProjectId    — Early Bird wiki 弹窗打开中（EarlyBirdTab.jsx 自己维护，
  //                             这里只负责不要把它踩掉）
  //   #pipeline/earlybird|kol|hot|deals — 早期雷达四个子 tab 各自的深链
  //                             （PipelinePage 自己维护，同样只负责不要踩掉）
  //   #pageName               — 其他页面
  useEffect(() => {
    const currentPath = window.location.hash.slice(1);
    if (page === "analyze" && analyzeQuery) {
      window.location.hash = `analyze/${encodeURIComponent(analyzeQuery)}`;
    } else if (page === "portfolio" && selected?.name) {
      window.location.hash = `portfolio/${encodeURIComponent(selected.name)}`;
    } else if (page === "pipeline" && (currentPath.startsWith("earlybird/") || currentPath.startsWith("pipeline/"))) {
      // 不覆盖——否则子 tab 一切换/Early Bird wiki 弹窗一打开，这个 effect 立刻把 hash 重置回 "pipeline"
    } else {
      window.location.hash = page;
    }
  }, [page, analyzeQuery, selected]);

  // ── Supabase Auth + Watchlist ───────────────────────────────────────
  const { user, session, loading: authLoading, signInWithEmail, signUpWithEmail, signInWithGoogle, signOut, authError, dismissAuthError, reportAuthError } = useAuth();
  const { watchlist, addToWatchlist, removeFromWatchlist, isInWatchlist } = useWatchlist(user);
  const [showAuth, setShowAuth] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [alertConfigProject, setAlertConfigProject] = useState(null); // project_name for alert modal
  const [wlSort, setWlSort] = useState("added"); // watchlist sort: "added"|"alpha"|"score"|dim
  const [wlHeatMap, setWlHeatMap] = useState({}); // { projectName: 分析次数 } — 见 getAnalysisHeatMap
  const [wlFreshMap, setWlFreshMap] = useState({}); // { projectName: scored_at } — watchlists.project_data 快照本身不带这个字段，单独查 project_cache

  useEffect(() => {
    const names = watchlist.map(w => w.project_name).filter(Boolean);
    if (names.length === 0) return;
    getAnalysisHeatMap(names).then(setWlHeatMap);
    (async () => {
      const { supabase } = await import('./lib/supabase');
      const { data } = await supabase
        .from('project_cache')
        .select('project_name, scored_at')
        .in('project_name', names)
        .gt('expires_at', new Date().toISOString());
      const map = {};
      (data || []).forEach(row => { map[row.project_name] = row.scored_at; });
      setWlFreshMap(map);
    })();
  }, [watchlist]);

  const handleAuth = async (mode, email, password) => {
    if (mode === 'signin') return signInWithEmail(email, password);
    return signUpWithEmail(email, password);
  };

  // ── 自定义权重（账户级持久化，见 hooks/useScoringWeights）───────────────
  // weights 为 null 表示未设置覆盖：各处显示应回退到项目分析时的阶段自适应权重
  // （project.analysis.stage_weights），而不是本地默认权重 —— 见 effectiveComposite()。
  const { weights, setWeights: saveWeights, clearWeights } = useScoringWeights(user);
  const dims = buildDims(weights || DEFAULT_WEIGHTS);

  // ── 数据导入（跨设备恢复） ──────────────────────────────────────────
  const importData = (data) => {
    if (data.projects)      save(data.projects);
    if (data.dealStatuses) {
      setDealStatuses(data.dealStatuses);
      try { localStorage.setItem(DEAL_STATUS_KEY, JSON.stringify(data.dealStatuses)); } catch {}
    }
    if (data.weights)       saveWeights(data.weights);
    if (data.projectNotes) {
      importNotes(data.projectNotes);
    }
    if (data.analysisHistory) {
      setAnalysisHistory(data.analysisHistory);
      try { localStorage.setItem(HISTORY_KEY, JSON.stringify(data.analysisHistory)); } catch {}
    }
  };

  // ── Deal 状态 ───────────────────────────────────────────────────────
  const [dealStatuses, setDealStatuses] = useState(() => {
    try { return JSON.parse(localStorage.getItem(DEAL_STATUS_KEY) || "{}"); } catch { return {}; }
  });
  const saveDealStatus = (id, status) => {
    const next = { ...dealStatuses, [id]: status };
    setDealStatuses(next);
    try { localStorage.setItem(DEAL_STATUS_KEY, JSON.stringify(next)); } catch {}
  };

  // ── 会话生命周期追踪（访问时长、设备、来源）─────────────────────────
  useEffect(() => {
    const startTime = Date.now();
    track('session_start', { initial_page: page });
    const handleHide = () => {
      if (document.visibilityState === 'hidden') {
        track('session_end', { duration_s: Math.round((Date.now() - startTime) / 1000) });
      }
    };
    document.addEventListener('visibilitychange', handleHide);
    return () => document.removeEventListener('visibilitychange', handleHide);
  }, []); // eslint-disable-line

  // ── 项目跟进笔记 { [projectName]: [{id, text, type, createdAt}] } ────
  const { notes: projectNotes, addNote, importNotes } = useNotes(user);

  // ── 分析版本历史 { [projectName]: [{composite, analyzedAt, scores}] } ─
  const [analysisHistory, setAnalysisHistory] = useState(() => {
    try { return JSON.parse(localStorage.getItem(HISTORY_KEY) || "{}"); } catch { return {}; }
  });
  const appendHistory = (name, analysis, analyzedAt) => {
    const entry = { composite: analysis.composite, analyzedAt, scores: analysis.scores };
    const prev = analysisHistory[name] || [];
    const next = { ...analysisHistory, [name]: [...prev, entry].slice(-10) }; // 最多保留 10 个版本
    setAnalysisHistory(next);
    try { localStorage.setItem(HISTORY_KEY, JSON.stringify(next)); } catch {}
  };

  // 批量分析状态
  const [batchQueue, setBatchQueue] = useState([]);    // 待分析的项目名队列
  const [batchStatus, setBatchStatus] = useState(""); // 当前进度描述
  const [batchRunning, setBatchRunning] = useState(false);


  const save = (ps) => {
    const trimmed = ps.slice(0, MAX_STORED_PROJECTS);
    setProjects(trimmed);
    try { localStorage.setItem(STORE_KEY, JSON.stringify(trimmed)); } catch {}
  };

  // AnalyzerPage 卸载后仍会通过闭包直接调用这个函数（不是 state setter，不受组件
  // 卸载影响），所以哪怕用户已经跳到别的页面，进度也能继续更新到这个悬浮组件上。
  // name 为 null 表示"这一路分析结束了但没有有效结果"（比如报错），直接收起悬浮组件。
  const handleAnalyzeProgress = (name, statusText) => {
    if (!name || !statusText) { setAnalyzeTask(null); return; }
    setAnalyzeTask(prev => {
      if (!prev || prev.name !== name) setAnalyzeWidgetOpen(false); // 新的一轮分析：气泡重新收起
      return {
        name, done: false,
        stage: statusText,
        pct: estimateAnalyzeProgressPct(statusText, prev?.name === name ? prev?.pct : 0),
      };
    });
  };

  const onAnalyzed = (p) => {
    save([p, ...projects.filter(x => x.name !== p.name)]);
    appendHistory(p.name, p.analysis, p.analyzedAt);
    // 只有用户还留在「项目分析」页时才自动跳去看结果——如果已经跳到别的页面在看
    // 别的东西，分析完成不应该把人强制拉走，只更新悬浮进度组件，让用户自己点「查看」
    if (pageRef.current === "analyze") {
      setSelected(p);
      setPage("portfolio");
    }
    setAnalyzeQuery("");   // 清空避免返回「项目分析」tab 时自动重新触发
    setAnalyzeForce(false);
    setAnalyzeProjectId(null); // reset 避免跨项目泄漏（onReanalyze 也会 reset）
    track('project_analyzed', {
      project_name: p.name,
      track:        p.analysis?.track        || null,
      stage:        p.analysis?.stage_key    || null,
      composite:    p.analysis?.composite    ?? null,
      from_cache:   p.fromCache              ?? false,
    });
    // 方案C：分析完成后弹出分享 Toast（3秒后自动消失）
    if (!p.fromCache && p.analysis?.composite) {
      const r = rating(p.analysis.composite);
      setShareToast({ name: p.name, composite: p.analysis.composite, grade: r.label, fg: r.fg });
      setTimeout(() => setShareToast(null), 5000);
    }
    // 悬浮进度组件：如果这次完成的项目正好是它在跟踪的，切到"完成"态展示评分，
    // 过几秒自动收起（用户也可以随时手动点掉）
    if (p.analysis?.composite != null) {
      const r = rating(p.analysis.composite);
      setAnalyzeTask(prev => {
        if (!prev || prev.name !== p.name) return prev;
        setAnalyzeWidgetOpen(true);
        return { ...prev, done: true, pct: 100, composite: p.analysis.composite, grade: r.label, fg: r.fg };
      });
      setTimeout(() => {
        setAnalyzeTask(prev => (prev && prev.name === p.name && prev.done) ? null : prev);
      }, 6000);
    }
  };

  // 从详情面板竞品列一键跳到对比页
  // currentProject = 当前已打开的项目，competitorName = 竞品名称
  const handleCompareWith = (currentProject, newCompetitor) => {
    // newCompetitor 是分析结果对象（已经存入 projects），currentProject 已在 projects 中
    const allProjects = [newCompetitor, ...projects.filter(x => x.name !== newCompetitor.name)];
    save(allProjects);
    appendHistory(newCompetitor.name, newCompetitor.analysis, newCompetitor.analyzedAt);

    const leftId  = String(currentProject.id);
    const rightId = String(newCompetitor.id);
    setCompareInit({ leftId, rightId });
    setSelected(null);
    setPage("compare");
    track('compare_from_comp_table', { from: currentProject.name, to: newCompetitor.name });
  };

  // 单条分析完成后静默添加（批量用）
  const onAnalyzedSilent = (p, currentProjects) => {
    const next = [p, ...currentProjects.filter(x => x.name !== p.name)];
    save(next);
    appendHistory(p.name, p.analysis, p.analyzedAt);
    return next;
  };

  // 批量分析主逻辑（顺序执行，每项间隔 1.5s 防速率限制）
  const startBatchAnalyze = async (names) => {
    if (batchRunning) return;
    setBatchRunning(true);
    setBatchQueue(names);
    setPage("portfolio");
    let currentProjects = projects;
    for (let i = 0; i < names.length; i++) {
      const name = names[i];
      setBatchStatus(`⏳ [${i + 1}/${names.length}] 正在分析：${name}`);
      try {
        const raw = await fetchFullProject(name);
        const analysis = await scoreProject(raw, () => {});
        const p = { id: Date.now() + i, name: raw.details.project_name, details: raw.details, fundingRounds: raw.fundingRounds, teamMembers: raw.teamMembers || [], cgData: raw.cgData, defiData: raw.defiData, webIntel: raw.webIntel, githubSignal: raw.githubSignal || null, socialSignals: raw.socialSignals || null, duneTokenInfo: raw.duneTokenInfo || null, duneDashboard: raw.duneDashboard || null, duneAnalytics: raw.duneAnalytics || null, complianceCheck: raw.complianceCheck || null, waybackSnapshot: raw.waybackSnapshot || null, timeline: raw.timeline || [], marketContext: raw.marketContext || null, tokenFutures: raw.tokenFutures || null, artemisMetrics: raw.artemisMetrics || null, tokenUnlock: raw.tokenUnlock || null, topHolders: raw.topHolders || null, complianceRisk: raw.complianceRisk || null, analysis, analyzedAt: new Date().toISOString() };
        currentProjects = onAnalyzedSilent(p, currentProjects);
      } catch (e) {
        setBatchStatus(`⚠ [${i + 1}/${names.length}] ${name} 失败：${e.message}`);
      }
      if (i < names.length - 1) await new Promise(r => setTimeout(r, 1500));
    }
    setBatchStatus(`✅ 批量分析完成，共 ${names.length} 个项目`);
    setBatchRunning(false);
    setBatchQueue([]);
  };

  const onRemove = (id) => {
    save(projects.filter(p => p.id !== id));
    if (selected?.id === id) setSelected(null);
  };

  const [analyzeProjectId, setAnalyzeProjectId] = useState(null);
  const importProject = (name, source = "unknown", projectId = null) => {
    // 每次从列表选新项目都要显式清掉 force 标记 —— 否则上一次"重新分析"若中途
    // 被打断（未走到 onAnalyzed 成功回调），analyzeForce 会一直卡在 true，
    // 导致下次点"查看"任何有缓存的项目都被误判成强制全量重跑
    setAnalyzeForce(false);
    setAnalyzeQuery(name);
    setAnalyzeProjectId(projectId);
    setPage("analyze");
    track('import_project', { project_name: name, source });
  };

  const isAdmin = user?.id === ADMIN_UID;

  const NAV = [
    { id: "trending",  label: t('nav.trending'), Icon: Flame },
    { id: "pipeline",  label: t('nav.pipeline'), Icon: Search },
    // 人才雷达暂时只对管理员可见——数据刚上线，大部分已缓存项目还没有 pedigreeSignal
    // 字段（见 scripts/backfill-talent-pedigree.mjs），放给所有用户看到的现在是个空页面
    ...(isAdmin ? [{ id: "talent-radar", label: t('nav.talentRadar'), Icon: Users }] : []),
    { id: "analyze",   label: t('nav.analyze'), Icon: Microscope },
    { id: "portfolio", label: t('nav.ranking'), Icon: BarChart3 },
    { id: "watchlist", label: t('nav.watchlist'), Icon: Star },
    { id: "compare",   label: t('nav.compare'), Icon: Scale },
    { id: "settings",  label: t('nav.settings'), Icon: Settings },
    ...(isAdmin ? [{ id: "admin", label: "数据看板", Icon: LineChartIcon }] : []),
  ];

  const highAlpha = projects.filter(p => {
    const r = p.fundingRounds?.[0];
    const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
    const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
    const rv3 = r?.valuation || 0; const ra3 = r?.amount || 0;
    const entry3 = cgFdv || (rv3 > 0 ? rv3 : ra3 > 0 ? ra3 / 0.25 : p.details?.total_funding);
    const irrD = calcIRR(entry3, p.analysis?.fair_value_usd);
    return alphaScore(p.analysis?.composite || 0, irrD, p.analysis?.risk_penalty || 0) > 150;
  }).length;

  return (
    <div style={{ ...S.page, paddingBottom: isMobile ? 60 : 0 }}>
      <div style={isMobile ? { minHeight: "100vh" } : { display: "flex", minHeight: "100vh" }}>

        {/* Sidebar — desktop only。折叠态只保留导航图标 + 展开按钮，标题/统计/登录区/
            行情组件这些收起后用户本来就不需要马上看到的内容整块隐藏，不做图标化简化版
            （半吊子的图标化比直接隐藏更容易让人误触/看不懂）。*/}
        <aside style={{
          ...S.sidebar, display: isMobile ? "none" : "flex",
          width: sidebarCollapsed ? 52 : S.sidebar.width,
          padding: sidebarCollapsed ? "16px 8px" : S.sidebar.padding,
          alignItems: sidebarCollapsed ? "center" : "stretch",
          transition: "width .15s ease, padding .15s ease",
        }}>
          <div style={{ marginBottom: 12, width: "100%", display: "flex", alignItems: "center", justifyContent: sidebarCollapsed ? "center" : "space-between" }}>
            {!sidebarCollapsed && (
              <div>
                <div style={{ fontWeight: 900, fontSize: 14, letterSpacing: -0.5 }}>Simon Capital Lab</div>
                <div style={{ fontSize: 10, color: C.muted, marginTop: 1 }}>Alpha OS v2 · RootData Skill</div>
              </div>
            )}
            <button
              onClick={toggleSidebarCollapsed}
              title={sidebarCollapsed ? (lang === 'en' ? 'Expand sidebar' : '展开侧边栏') : (lang === 'en' ? 'Collapse sidebar' : '收起侧边栏')}
              style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex", flexShrink: 0, padding: 2 }}
            >
              {sidebarCollapsed ? <ChevronRight size={15} strokeWidth={2} /> : <ChevronLeft size={15} strokeWidth={2} />}
            </button>
          </div>
          <div style={{ height: 1, background: C.border, margin: "4px 0", width: "100%" }} />

          {NAV.map(n => {
            const active = page === n.id;
            return (
              <button
                key={n.id}
                onClick={() => { setPage(n.id); track('page_view', { page: n.id }); }}
                title={sidebarCollapsed ? n.label : undefined}
                style={{ ...S.navBtn(active), justifyContent: sidebarCollapsed ? "center" : "flex-start", padding: sidebarCollapsed ? "8px" : S.navBtn(active).padding }}
              >
                <n.Icon size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
                {!sidebarCollapsed && <span style={{ flex: 1 }}>{n.label}</span>}
                {!sidebarCollapsed && n.id === "portfolio" && projects.length > 0 && (
                  <span style={{ fontSize: 10, background: active ? "rgba(255,255,255,0.16)" : C.faint, padding: "1px 6px", borderRadius: 9, color: active ? "#ffffff" : C.muted }}>{projects.length}</span>
                )}
              </button>
            );
          })}

          {!sidebarCollapsed && (<>
            <div style={{ height: 1, background: C.border, margin: "4px 0" }} />

            <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 8 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.muted, textTransform: "uppercase" }}>{t('nav.overview_label')}</div>
              {[
                [t('nav.analyzed'), projects.length],
                [t('nav.sa_grade'), projects.filter(p => (p.analysis?.composite || 0) >= 80).length],
                [t('nav.high_alpha'), highAlpha],
              ].map(([k, v]) => (
                <div key={k} style={{ display: "flex", justifyContent: "space-between", fontSize: 11 }}>
                  <span style={{ color: C.muted }}>{k}</span>
                  <span style={{ fontWeight: 700 }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ marginTop: "auto" }}>
              {/* 用户登录区域 */}
              <div style={{ marginBottom: 10, padding: "8px 0", borderTop: `1px solid ${C.border}` }}>
                {user ? (
                  <div>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 4, marginBottom: 4 }}>
                      <div style={{ fontSize: 10, color: C.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{user.email}</div>
                      <button
                        onClick={() => setShowFeedback(true)}
                        title={lang === 'en' ? 'Send feedback' : '发送反馈'}
                        style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex", flexShrink: 0, padding: 2 }}
                      >
                        <MessageSquarePlus size={13} strokeWidth={2} />
                      </button>
                    </div>
                    <button onClick={signOut} style={{ width: "100%", fontSize: 10, padding: "4px 0", background: "none", border: `1px solid ${C.border}`, borderRadius: 4, color: C.muted, cursor: "pointer" }}>{t('nav.logout')}</button>
                  </div>
                ) : (
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => setShowAuth(true)} style={{ flex: 1, fontSize: 11, padding: "6px 0", background: C.accent, border: "none", borderRadius: 5, color: "#fff", cursor: "pointer", fontWeight: 600 }}>
                      {t('nav.login')}
                    </button>
                    <button
                      onClick={() => setShowFeedback(true)}
                      title={lang === 'en' ? 'Send feedback' : '发送反馈'}
                      style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 5, cursor: "pointer", color: C.muted, display: "flex", alignItems: "center", padding: "0 8px" }}
                    >
                      <MessageSquarePlus size={14} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>
              <LangToggle />
              {/* 全局市场背景（常驻侧边栏底部，每次进入 App 拉取）*/}
              <MarketContextWidget lang={lang} />
              <div style={{ display: "flex", gap: 8, fontSize: 10, color: C.muted, marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}`, flexWrap: "wrap" }}>
                <a href="/updates.html" target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "none" }}
                  onMouseEnter={e => e.target.style.color = C.accent} onMouseLeave={e => e.target.style.color = C.muted}>
                  {t('misc.updates_link')}
                </a>
                <span>·</span>
                <a href="/privacy.html" target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "none" }}
                  onMouseEnter={e => e.target.style.color = C.accent} onMouseLeave={e => e.target.style.color = C.muted}>
                  {t('misc.privacy_link')}
                </a>
                <span>·</span>
                <a href="/terms.html" target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "none" }}
                  onMouseEnter={e => e.target.style.color = C.accent} onMouseLeave={e => e.target.style.color = C.muted}>
                  {t('misc.terms_link')}
                </a>
                <span>·</span>
                <a href="/contact.html" target="_blank" rel="noreferrer" style={{ color: "inherit", textDecoration: "none" }}
                  onMouseEnter={e => e.target.style.color = C.accent} onMouseLeave={e => e.target.style.color = C.muted}>
                  {t('misc.contact_link')}
                </a>
              </div>
            </div>
          </>)}
        </aside>

        {/* Main */}
        <main style={{ ...S.main, padding: isMobile ? "12px 12px 16px" : 20 }}>
          {/* 批量分析进度条 */}
          {batchRunning && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 16px", marginBottom: 14, fontSize: 12, display: "flex", alignItems: "center", gap: 10 }}>
              <RefreshCw size={15} strokeWidth={2} color={C.muted} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600, color: C.text }}>{batchStatus.replace(/^⏳\s*/, '')}</div>
                <div style={{ height: 4, background: C.faint, borderRadius: 2, marginTop: 6 }}>
                  <div style={{ width: `${((batchQueue.length - batchQueue.filter((_, i) => i >= parseInt(batchStatus.match(/\[(\d+)/)?.[1] || 0)).length) / batchQueue.length) * 100}%`, height: "100%", background: C.accent, borderRadius: 2, transition: "width .4s" }} />
                </div>
              </div>
            </div>
          )}
          {!batchRunning && batchStatus.startsWith("✅") && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 16px", marginBottom: 14, fontSize: 12, color: C.text, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <span>{batchStatus.replace(/^✅\s*/, '')}</span>
              <button onClick={() => setBatchStatus("")} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}><X size={13} strokeWidth={2} /></button>
            </div>
          )}
          {page === "trending"  && <TrendingPage  onImport={(name, projectId) => importProject(name, "trending", projectId)} />}
          {page === "talent-radar" && isAdmin && <TalentRadarPage onImport={(name) => importProject(name, "talent-radar")} />}
          {page === "pipeline"  && <PipelinePage  onImport={(name, projectId) => importProject(name, "pipeline", projectId)} onBatchAnalyze={startBatchAnalyze} onFfMapUpdate={setFfMap} />}
          {page === "analyze"   && <AnalyzerPage  onAnalyzed={onAnalyzed} onProgress={handleAnalyzeProgress} initialQuery={analyzeQuery} initialProjectId={analyzeProjectId} forceRefresh={analyzeForce} key={analyzeQuery + String(analyzeForce)} />}
          {page === "portfolio" && <PortfolioPage projects={projects} onSelect={setSelected} onRemove={onRemove} dealStatuses={dealStatuses} onStatusChange={saveDealStatus} onAddNote={addNote} dims={dims} weights={weights} isInWatchlist={isInWatchlist} onAddWatchlist={user ? addToWatchlist : () => setShowAuth(true)} onRemoveWatchlist={removeFromWatchlist} />}
          {page === "compare"   && <ComparePage   projects={projects} dims={dims} weights={weights} initialLeftId={compareInit.leftId} initialRightId={compareInit.rightId} key={compareInit.leftId + compareInit.rightId} />}
          {page === "settings"  && <SettingsPage weights={weights} onSave={saveWeights} onClear={clearWeights} projects={projects} dealStatuses={dealStatuses} projectNotes={projectNotes} analysisHistory={analysisHistory} onImportData={importData} user={user} />}
          {page === "admin" && isAdmin && <AdminDashboard userId={user?.id} accessToken={session?.access_token} />}
          {page === "watchlist" && (() => {
            const WL_SORT_DIMS = ["tech", "team", "narrative", "data_score", "token", "compliance"];
            const WL_DIM_LABELS = { tech: "技术", team: "团队", narrative: "叙事", data_score: "链上", token: "代币", compliance: "合规" };
            const getWlAlpha = (w) => {
              const p = w.project_data || {};
              const r = p.fundingRounds?.[0];
              const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
              const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
              const rv = r?.valuation || 0; const ra = r?.amount || 0;
              const entry = cgFdv || (rv > 0 ? rv : ra > 0 ? ra / 0.25 : p.details?.total_funding);
              const irrD = calcIRR(entry, p.analysis?.fair_value_usd);
              return alphaScore(p.analysis?.composite || 0, irrD, p.analysis?.risk_penalty || 0);
            };
            const sortedWl = [...watchlist].sort((a, b) => {
              if (wlSort === "alpha") return getWlAlpha(b) - getWlAlpha(a);
              if (wlSort === "score") return (b.project_data?.analysis?.composite || 0) - (a.project_data?.analysis?.composite || 0);
              if (WL_SORT_DIMS.includes(wlSort)) return (b.project_data?.analysis?.scores?.[wlSort] || 0) - (a.project_data?.analysis?.scores?.[wlSort] || 0);
              return 0; // "added" keeps server order
            });
            // 当前排序的表头标签（加 ▼ 指示）
            const wlSortLabel = wlSort === "added" ? (lang === 'en' ? "Recently Added" : "收藏时间")
              : wlSort === "alpha" ? "Alpha ▼"
              : wlSort === "score" ? (lang === 'en' ? "Composite ▼" : "综合评分 ▼")
              : `${WL_DIM_LABELS[wlSort] || wlSort} ▼`;
            return (
            <div style={{ padding: "20px 0" }}>
              {!user ? (
                <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><Lock size={36} strokeWidth={2} /></div>
                  <div style={{ fontSize: 14, marginBottom: 16 }}>{lang === 'en' ? 'Sign in to view your saved projects' : '登录后查看你的收藏项目'}</div>
                  <button onClick={() => setShowAuth(true)}
                    style={{ padding: "8px 24px", background: C.accent, border: "none", borderRadius: 6, color: "#fff", cursor: "pointer", fontWeight: 600 }}>
                    {t('nav.login')}
                  </button>
                </div>
              ) : watchlist.length === 0 ? (
                <div style={{ textAlign: "center", padding: 60, color: C.muted }}>
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}><Inbox size={36} strokeWidth={2} /></div>
                  <div style={{ fontSize: 14 }}>{lang === 'en' ? 'No saved projects yet' : '还没有收藏的项目'}</div>
                  <div style={{ fontSize: 11, marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 3 }}>{lang === 'en' ? <>Click <Star size={11} strokeWidth={2} /> in the project detail panel to save</> : <>在项目详情页点击 <Star size={11} strokeWidth={2} /> 收藏</>}</div>
                </div>
              ) : (
                <div>
                  {/* 排序控制 + 当前排序标签 */}
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, background: C.faint, borderRadius: 8, padding: "6px 12px", border: `1px solid ${C.border}` }}>
                      <span style={{ fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase", whiteSpace: "nowrap" }}>
                        {lang === 'en' ? 'Sort by' : '排序方式'}
                      </span>
                      <select value={wlSort} onChange={e => setWlSort(e.target.value)} style={{ ...S.input, fontSize: 11, width: "auto", background: "transparent", border: "none", padding: "2px 4px" }}>
                        <option value="added">{lang === 'en' ? 'Recently Added' : '收藏时间'}</option>
                        <option value="alpha">Alpha Score</option>
                        <option value="score">{lang === 'en' ? 'Composite Score' : '综合评分'}</option>
                        <option disabled>── {lang === 'en' ? 'By dimension' : '按维度'} ──</option>
                        <option value="tech">技术 (Tech)</option>
                        <option value="team">团队 (Team)</option>
                        <option value="narrative">叙事 (Narrative)</option>
                        <option value="data_score">链上数据 (On-chain)</option>
                        <option value="token">代币经济 (Token)</option>
                        <option value="compliance">合规性 (Compliance)</option>
                      </select>
                    </div>
                    {wlSort !== "added" && (
                      <span style={{ fontSize: 11, color: "#a78bfa", fontWeight: 700, background: "#1e1b4b", border: "1px solid #4c1d95", borderRadius: 6, padding: "3px 10px" }}>
                        ↕ {wlSortLabel}
                      </span>
                    )}
                    <span style={{ fontSize: 11, color: C.muted, marginLeft: 2 }}>{sortedWl.length} {lang === 'en' ? 'projects' : '个项目'}</span>
                  </div>
                  <div style={{ ...S.card, padding: 0, overflow: "hidden" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr style={{ background: C.faint, fontSize: 10, color: C.muted, fontWeight: 700, textTransform: "uppercase" }}>
                          {(lang === 'en'
                            ? ["#","Project","Track",
                               wlSort === "score" ? "Composite ▼" : wlSort === "alpha" ? "Composite" : WL_SORT_DIMS.includes(wlSort) ? `${WL_DIM_LABELS[wlSort]}维度 ▼` : "Composite",
                               "Under.","IRR",
                               wlSort === "alpha" ? "Alpha ▼" : "Alpha",
                               "Unlock","Action"]
                            : ["#","项目","赛道",
                               wlSort === "score" ? "综合评分 ▼" : wlSort === "alpha" ? "综合评分" : WL_SORT_DIMS.includes(wlSort) ? `${WL_DIM_LABELS[wlSort]}维度 ▼` : "综合评分",
                               "低估倍数","期望IRR",
                               wlSort === "alpha" ? "Alpha ▼" : "Alpha",
                               "解锁","操作"]
                          ).map(h => (
                            <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: h.includes("▼") ? "#a78bfa" : C.muted }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {sortedWl.map((w, i) => {
                          const p = w.project_data || {};
                          const a = p.analysis || {};
                          const r = rating(a?.composite || 0);
                          const lastRound = p.fundingRounds?.[0];
                          const cgFdvRaw = p.cgData?.market_data?.fully_diluted_valuation?.usd;
                          const cgFdv = cgFdvRaw && cgFdvRaw > (p.details?.total_funding || 0) * 0.5 ? cgFdvRaw : null;
                          const rv2 = lastRound?.valuation || 0; const ra2 = lastRound?.amount || 0;
                          const entry2 = cgFdv || (rv2 > 0 ? rv2 : ra2 > 0 ? ra2 / 0.25 : p.details?.total_funding);
                          const irrData = calcIRR(entry2, a?.fair_value_usd);
                          const alpha = alphaScore(a?.composite || 0, irrData, a?.risk_penalty || 0);
                          const unlock = a?.unlock_pressure || "—";
                          // 当前排序维度的分数 highlight
                          const dimScore = WL_SORT_DIMS.includes(wlSort) ? (a?.scores?.[wlSort] ?? null) : null;
                          return (
                            <tr key={w.id} style={{ borderTop: `1px solid ${C.faint}`, cursor: "pointer" }}
                              onClick={() => setSelected(w.project_data || {})}
                              onMouseEnter={e => e.currentTarget.style.background = C.faint}
                              onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                              <td style={{ padding: "10px 12px", fontWeight: 700, color: i < 3 ? "#d97706" : C.muted }}>
                                {i < 3 ? ["①","②","③"][i] : i + 1}
                              </td>
                              <td style={{ padding: "10px 12px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                  {p.details?.logo && <img src={proxyLogo(p.details.logo)} alt="" style={{ width: 22, height: 22, borderRadius: 5 }} onError={e => e.target.style.display = "none"} />}
                                  <div>
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                      <span style={{ fontWeight: 700 }}>{w.project_name}</span>
                                      <PedigreeBadge hits={pedigreeHitsOf(p)} />
                                    </div>
                                    <div style={{ fontSize: 10, color: C.muted }}>{lang === 'en' ? (a?.stage_en || enify(a?.stage)) : a?.stage}</div>
                                    {(() => {
                                      const fresh = freshnessLabel(wlFreshMap[w.project_name], lang);
                                      const heat = wlHeatMap[w.project_name];
                                      if (!fresh && !heat) return null;
                                      return (
                                        <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 2, fontSize: 9.5, color: fresh?.stale ? "#c2410c" : C.muted }}>
                                          {fresh && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Clock size={9} strokeWidth={2} />{fresh.text}</span>}
                                          {heat >= 3 && <span style={{ display: "flex", alignItems: "center", gap: 3 }}><Flame size={9} strokeWidth={2} />{lang === 'en' ? `${heat}x` : `分析${heat}次`}</span>}
                                        </div>
                                      );
                                    })()}
                                  </div>
                                </div>
                              </td>
                              <td style={{ padding: "10px 12px" }}><span style={S.tag}>{lang === 'en' ? (a?.track_en || a?.track || "—") : (a?.track || "—")}</span></td>
                              <td style={{ padding: "10px 12px" }}>
                                {dimScore !== null ? (
                                  // 按维度排序时：主显维度分，子显综合分
                                  <div>
                                    <span style={{ fontWeight: 800, fontSize: 18, color: "#a78bfa" }}>{dimScore}</span>
                                    <span style={{ fontSize: 10, color: C.muted, marginLeft: 4 }}>{WL_DIM_LABELS[wlSort]}</span>
                                    <div style={{ marginTop: 2 }}>
                                      <span style={S.rBadge(a?.composite || 0)}>{r.label}</span>
                                      <span style={{ fontWeight: 600, fontSize: 12, color: r.fg, marginLeft: 4 }}>{a?.composite || 0}</span>
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <span style={S.rBadge(a?.composite || 0)}>{r.label}</span>
                                    <span style={{ fontWeight: 700, fontSize: 16, color: r.fg, marginLeft: 6 }}>{a?.composite || 0}</span>
                                    <WatchlistScoreDelta projectName={w.project_name} savedScore={a?.composite} />
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: "10px 12px", fontWeight: 600, color: (irrData?.underMult || 0) > 2 ? "#059669" : C.text }}>
                                {irrData ? `${irrData.underMult.toFixed(2)}x` : "—"}
                              </td>
                              <td style={{ padding: "10px 12px", fontWeight: 600, color: (irrData?.expectedIRR || 0) > 0.3 ? "#059669" : "#d97706" }}>
                                {irrData ? `${(irrData.expectedIRR * 100).toFixed(1)}%` : "—"}
                              </td>
                              <td style={{ padding: "10px 12px" }}>
                                <span style={{ fontSize: 20, fontWeight: 900, color: alpha > 150 ? "#059669" : alpha > 80 ? "#2563eb" : C.muted }}>{alpha || "—"}</span>
                              </td>
                              <td style={{ padding: "10px 12px" }}>
                                <UnlockBadge level={unlock} />
                              </td>
                              <td style={{ padding: "10px 12px", whiteSpace: "nowrap" }}>
                                <button onClick={(e) => { e.stopPropagation(); setAlertConfigProject(w.project_name); }}
                                  title={lang === 'en' ? 'Alert config' : '告警设置'}
                                  style={{ background: "none", border: "none", color: C.muted, cursor: "pointer", display: "inline-flex", marginRight: 4 }}>
                                  <Bell size={15} strokeWidth={2} />
                                </button>
                                <button onClick={(e) => { e.stopPropagation(); removeFromWatchlist(w.project_name); }}
                                  title="取消收藏"
                                  style={{ background: "none", border: "none", color: "#f59e0b", cursor: "pointer", display: "inline-flex" }}>
                                  <Star size={16} strokeWidth={2} fill="#f59e0b" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
            );
          })()}
        </main>

        {/* Detail Panel */}
        {selected && (
          <div style={isMobile ? {
            position: "fixed", top: 0, left: 0, right: 0, bottom: 60,
            zIndex: 300, overflowY: "auto", background: C.surface,
          } : {}}>
            {/* Mobile back button */}
            {isMobile && (
              <div style={{ position: "sticky", top: 0, zIndex: 10, background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8 }}>
                <button onClick={() => setSelected(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.text, lineHeight: 1, padding: "4px 6px" }}>←</button>
                <span style={{ fontWeight: 700, fontSize: 14 }}>项目详情</span>
              </div>
            )}
            <DetailPanel
              project={selected}
              onClose={() => setSelected(null)}
              onReanalyze={(name, projectId = null) => {
                // 重新分析：如果当前项目带 rootdata_id 就一起传，让 fetchFullProject + getCached
                // 都按 ID 精确匹配；否则按名字（与旧行为一致，配合 setAnalyzeProjectId(null)
                // 避免上次 importProject 留下的 ID 跨项目泄漏，曾导致 EDGE Markets 重新分析跳到 ETHGAS）
                setAnalyzeForce(true);
                setAnalyzeQuery(name);
                setAnalyzeProjectId(projectId || null);
                setPage("analyze");
                setSelected(null);
              }}
              notes={projectNotes[selected.name] || []}
              onAddNote={(note) => addNote(selected.name, note)}
              history={analysisHistory[selected.name] || []}
              ffMap={ffMap}
              projects={projects}
              onCompareWith={handleCompareWith}
              isInWatchlist={isInWatchlist}
              isMobile={isMobile}
              onAddWatchlist={user ? (p) => { addToWatchlist(p); track('watchlist_add', { project_name: p.name || p.details?.project_name }); } : () => setShowAuth(true)}
              onRemoveWatchlist={(name) => { removeFromWatchlist(name); track('watchlist_remove', { project_name: name }); }}
              onRefreshCompliance={async () => {
                const proj = selected;
                const projectName = proj?.name || proj?.details?.project_name;
                if (!proj || !projectName) return;
                const rootdataId = proj.details?._matchedId || proj.details?.id || proj.rootdataId || null;
                const facts = factsFromProject({
                  details: proj.details,
                  projectContext: proj.projectContext,
                  defiData: proj.defiData,
                });
                const freshCR = await synthesizeComplianceRisk(facts, { onlyJurisdictions: ['hk', 'eu'] });
                setSelected(prev => prev ? { ...prev, complianceRisk: freshCR } : prev);
                // 落库不阻塞 UI：读最新缓存行，只 patch complianceRisk 再写回，
                // 避免用扁平化后的 project 反推整份 rawData（字段形状可能对不上，见 Q66/Q68 教训）
                getCached(projectName, { rootdataId }).then(cached => {
                  if (cached?.raw_data) {
                    writeCache(projectName, rootdataId, cached.analysis, { ...cached.raw_data, complianceRisk: freshCR }).catch(() => {});
                  }
                }).catch(() => {});
              }}
              weights={weights}
            />
          </div>
        )}
      </div>

      {/* Mobile bottom nav */}
      {isMobile && <MobileNav page={page} setPage={(p) => { setPage(p); setSelected(null); }} NAV={NAV} t={t} />}

      {!isMobile && (
        <div style={{ borderTop: `1px solid ${C.border}`, padding: "6px 20px", fontSize: 10, color: C.muted, background: C.surface, display: "flex", justifyContent: "space-between" }}>
          <span>Simon Capital Lab Alpha OS · 数据仅供内部参考，不构成投资建议</span>
          <span>IRR：牛30%×3x | 中性50%×1.5x | 熊20%×0.8x · 持有 {HOLD_YEARS} 年</span>
        </div>
      )}

      {authError && (
        <div style={{
          position: "fixed", top: 12, left: "50%", transform: "translateX(-50%)", zIndex: 10000,
          background: C.text, color: "#fff", borderRadius: 8, padding: "10px 16px",
          display: "flex", alignItems: "center", gap: 12, fontSize: 12.5, boxShadow: "0 4px 16px rgba(0,0,0,0.25)",
          maxWidth: "90vw",
        }}>
          <span>{describeAuthError(authError, lang)}</span>
          <button onClick={async () => {
            dismissAuthError();
            const { error } = await signInWithGoogle();
            if (error) reportAuthError(error.message);
          }} style={{
            background: "#fff", color: C.text, border: "none", borderRadius: 6,
            padding: "4px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap",
          }}>{lang === 'en' ? 'Retry' : '重试登录'}</button>
          <button onClick={dismissAuthError} style={{
            background: "none", border: "none", cursor: "pointer", padding: 0, display: "flex",
            color: "rgba(255,255,255,0.6)",
          }} aria-label="close">
            <X size={14} strokeWidth={2} />
          </button>
        </div>
      )}

      {showAuth && (
        <AuthModal
          onClose={() => setShowAuth(false)}
          onAuth={handleAuth}
          onGoogleAuth={signInWithGoogle}
        />
      )}

      {showFeedback && (
        <FeedbackModal
          onClose={() => setShowFeedback(false)}
          onSubmit={(type, message) => submitUserFeedback({
            userId: user?.id, email: user?.email, type, message,
            page, projectName: selected?.name, lang,
          })}
        />
      )}

      {alertConfigProject && user && (
        <AlertConfigModal
          projectName={alertConfigProject}
          userId={user.id}
          lang={lang}
          onClose={() => setAlertConfigProject(null)}
        />
      )}

      {/* 分析完成后的分享 Toast */}
      {shareToast && (
        <div style={{
          position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)",
          background: C.text,
          borderRadius: 12, padding: "12px 18px",
          display: "flex", alignItems: "center", gap: 14,
          boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
          zIndex: 9998, whiteSpace: "nowrap",
          animation: "slideUpToast 0.3s ease-out",
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8, flexShrink: 0,
            background: "rgba(255,255,255,0.12)", color: shareToast.fg,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            <BarChart3 size={18} strokeWidth={2} />
          </div>
          <div style={{ lineHeight: 1.4 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#ffffff" }}>
              {shareToast.name} · <span style={{ color: shareToast.fg }}>{shareToast.grade}级 {shareToast.composite}分</span>
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>生成分享卡片，扩大你的研究影响力</div>
          </div>
          <div style={{ display: "flex", gap: 8, marginLeft: 4 }}>
            <button
              onClick={() => { setSelected(projects.find(p => p.name === shareToast.name) || selected); setShareToast(null); }}
              style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 14px", borderRadius: 8, fontSize: 12, background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.16)", color: "#ffffff", cursor: "pointer" }}>
              <Share2 size={13} strokeWidth={2} />
              分享
            </button>
          </div>
          <button
            onClick={() => setShareToast(null)}
            style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", padding: "0 2px", marginLeft: 4, display: "flex" }}>
            <X size={15} strokeWidth={2} />
          </button>
        </div>
      )}

      {/* 分析进度悬浮圆环 — 用户跳去别的页面时，正在跑的分析仍然可见；跑完自动展开提醒 */}
      {analyzeTask && (() => {
        const CIRC = 2 * Math.PI * 22;
        const ringColor = analyzeTask.done ? (analyzeTask.fg || "#7fd88f") : "#ffffff";
        const showPopover = analyzeWidgetOpen || analyzeTask.done;
        return (
          <div style={{ position: "fixed", right: widgetPos.right, bottom: widgetPos.bottom, zIndex: 9997, display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 8 }}>
            {showPopover && (
              <div style={{ position: "relative", width: 220, background: C.text, borderRadius: 12, padding: "10px 14px", boxShadow: "0 8px 24px rgba(0,0,0,0.25)" }}>
                <button
                  onClick={() => { setAnalyzeTask(null); setAnalyzeWidgetOpen(false); }}
                  aria-label={lang === 'en' ? 'Dismiss' : '关闭'}
                  style={{ position: "absolute", top: 6, right: 6, width: 20, height: 20, borderRadius: "50%", background: "none", border: "none", color: "rgba(255,255,255,0.5)", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <X size={13} strokeWidth={2} />
                </button>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#ffffff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", paddingRight: 16 }}>{analyzeTask.name}</div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.6)", marginTop: 2 }}>{analyzeTask.done ? (lang === 'en' ? 'Done' : '分析完成') : `${analyzeTask.stage} · ${analyzeTask.pct}%`}</div>
                {analyzeTask.done && (
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 8 }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 4, background: "rgba(255,255,255,0.12)", color: analyzeTask.fg, fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999 }}>
                      {analyzeTask.composite} {analyzeTask.grade}
                    </span>
                    <button
                      onClick={() => { setSelected(projects.find(p => p.name === analyzeTask.name) || selected); setAnalyzeTask(null); }}
                      style={{ background: "none", border: "none", color: "#ffffff", fontSize: 12, fontWeight: 700, cursor: "pointer", display: "flex", alignItems: "center", gap: 3 }}>
                      {lang === 'en' ? 'View' : '查看'} <ChevronRight size={13} strokeWidth={2} />
                    </button>
                  </div>
                )}
              </div>
            )}
            <button
              onMouseDown={handleWidgetMouseDown}
              aria-label={lang === 'en' ? 'Analysis progress — click to open, drag to move' : '分析进度——点击查看，拖动可移动位置'}
              style={{ position: "relative", width: 52, height: 52, borderRadius: "50%", background: C.text, border: "none", cursor: "grab", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 14px rgba(0,0,0,0.2)", flexShrink: 0 }}>
              <svg width="52" height="52" style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}>
                <circle cx="26" cy="26" r="22" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="3" />
                <circle cx="26" cy="26" r="22" fill="none" stroke={ringColor} strokeWidth="3" strokeLinecap="round"
                  strokeDasharray={CIRC} strokeDashoffset={CIRC * (1 - analyzeTask.pct / 100)}
                  style={{ transition: "stroke-dashoffset .5s ease" }} />
              </svg>
              {analyzeTask.done
                ? <CircleCheck size={18} strokeWidth={2} style={{ color: analyzeTask.fg || "#7fd88f" }} />
                : <span style={{ fontSize: 13, fontWeight: 700, color: "#ffffff" }}>{analyzeTask.pct}%</span>}
            </button>
          </div>
        );
      })()}

      <style>{`
        @keyframes slideUpToast {
          from { opacity: 0; transform: translateX(-50%) translateY(12px); }
          to   { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
      `}</style>
    </div>
  );
}

// Re-export wrapped with LangProvider so all components share the same context
const AppWithLang = () => (
  <LangProvider>
    <App />
  </LangProvider>
);
export default AppWithLang;
